import React, { useState, useEffect, useRef, useCallback, useMemo, startTransition, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseApi, customerApi, deviceApi, formsApi, internalJobApi, tsfadApi, messageApi, notificationApi, usersApi } from '../services/api';
import { getBaseUrl } from '../services/baseUrl';
import { showInAppAlert, showInAppConfirm } from '../services/inAppDialog';
import {
  Case,
  CaseServiceReport,
  CaseTask,
  Customer,
  Device,
  CustomerLocation,
  CustomerContact,
  InternalJob,
  TSFADAnalysisRecord
} from '../types';
import { PortalMessage, PortalMessageAttachment } from '../utils/portalMessages';
import { buildMeaningfulTimeline, formatTimelineEventLabel } from '../utils/caseTimeline';
import { normalizeQuoteLineColumns, type QuoteLineColumnConfig } from '../utils/quoteLineColumns';
import { normalizeQuoteTotalSettings, type QuoteTotalSettings } from '../utils/quoteTotals';
import {
  getCurrentDeviceSlaAssignment,
  getDeviceSlaLabel,
  resolveDeviceSlaEndDate
} from '../utils/deviceSla';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { techDashboardStyles } from './TechDashboard.styles';
import {
  TechDashboardCasesHeader,
  TechDashboardCasesStats
} from './TechDashboardCasesHeaderStats';
import { TechDashboardCasesList } from './TechDashboardCasesList';
import { TechDashboardQuotePreviewModal } from './TechDashboardQuotePreviewModal';
import { TechDashboardInternalJobModal } from './TechDashboardInternalJobModal';
import { AppNotificationPopups, type AppNotification, type AppNotificationAttachment } from './AppNotificationPopups';
import { TechDashboardNotificationModal } from './TechDashboardNotificationModal';
import { TechDashboardOrgNotificationsTab } from './TechDashboardOrgNotificationsTab';
import { TechDashboardUtilityModals } from './TechDashboardUtilityModals';
import { TechDashboardCaseDetailModal } from './TechDashboardCaseDetailModal';
import { TechDashboardCustomerResourceModal } from './TechDashboardCustomerResourceModal';
import { TechDashboardMessagesTab } from './TechDashboardMessagesTab';
import { TechDashboardCustomersTab } from './TechDashboardCustomersTab';
import { TechDashboardInsightsTab } from './TechDashboardInsightsTab';
import RepairServiceReportModal from './RepairServiceReportModal';
import SupportTicketLauncher from './SupportTicketLauncher';
import { setUserActivityPage } from '../services/userActivity';

const BRAND_WORDMARK_SRC = `${process.env.PUBLIC_URL}/bonzaiq-logo..png`;

const Settings = lazy(() => import('./Settings'));
const Documents = lazy(() => import('./Documents'));
const DeviceModels = lazy(() => import('./DeviceModels'));
const TechDashboardSlaTab = lazy(() => import('./TechDashboardSlaTab'));
const TechDashboardSchedulingTab = lazy(() => import('./TechDashboardSchedulingTab'));
const DocumentationFeedbackReport = lazy(() => import('./DocumentationFeedbackReport'));
const KnowledgeQualityWorkbench = lazy(() => import('./KnowledgeQualityWorkbench'));
const DeviceExpertWorkspace = lazy(() => import('./DeviceExpertWorkspace'));
const AdminUsers = lazy(() => import('./AdminUsers'));
const Inventory = lazy(() => import('./Inventory'));

type UiStatus =
  | 'NEW'
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'ON_HOLD'
  | 'WAITING_CUSTOMER'
  | 'WAITING_PARTS'
  | 'RESOLVED'
  | 'CLOSED';
type DashboardTab =
  | 'cases'
  | 'messages'
  | 'orgNotifications'
  | 'customers'
  | 'models'
  | 'sla'
  | 'scheduling'
  | 'documents'
  | 'inventory'
  | 'knowledge'
  | 'feedback'
  | 'quality'
  | 'settings'
  | 'metrics'
  | 'users';
type MetricsWorkspaceTab = 'overview' | 'service' | 'reliability' | 'customers' | 'sla' | 'inventory' | 'team';
type MetricsDatePreset = '7d' | '30d' | '90d' | 'ytd' | 'custom';
type DeviceWorkspaceCustomerFilter = {
  customerId: string;
  customerName: string;
  modelIds: string[];
  deviceCountsByModelId: Record<string, number>;
};
type MetricsLegacyWidgetConfig = { id: string; enabled: boolean; order: number };
type MetricsDashboardConfig = {
  version?: number;
  widgets?: MetricsLegacyWidgetConfig[];
  workspaces?: Partial<Record<MetricsWorkspaceTab, { includes?: string[] }>>;
};
type MetricsWorkspaceIncludeBlock = {
  id: string;
  label: string;
  description: string;
};
type MetricsUserFilterOption = {
  id: string;
  label: string;
  detail: string;
  roleKey: string;
  isActive: boolean;
};

const DASHBOARD_TAB_KEY = 'techDashboard.activeTab';
const METRICS_WORKSPACE_TAB_KEY = 'techDashboard.metricsWorkspaceTab';
const DEFAULT_CASES_PAGE_SIZE = 25;
const DEFAULT_CUSTOMERS_PAGE_SIZE = 25;
const DASHBOARD_COMPACT_BREAKPOINT = 860;
const DASHBOARD_TABLET_BREAKPOINT = 1180;
const DASHBOARD_NARROW_BREAKPOINT = 1400;
const DASHBOARD_TABS: DashboardTab[] = [
  'cases',
  'messages',
  'orgNotifications',
  'knowledge',
  'customers',
  'models',
  'sla',
  'scheduling',
  'documents',
  'inventory',
  'metrics',
  'feedback',
  'quality',
  'users',
  'settings'
];
const UNIT_DASHBOARD_TABS: DashboardTab[] = DASHBOARD_TABS.filter(tab => tab !== 'orgNotifications');
const ORG_DASHBOARD_TABS: DashboardTab[] = [
  'orgNotifications',
  'knowledge',
  'models',
  'sla',
  'documents',
  'metrics',
  'feedback',
  'quality',
  'users',
  'settings'
];
const isDashboardTab = (value: unknown): value is DashboardTab =>
  typeof value === 'string' && DASHBOARD_TABS.includes(value as DashboardTab);
const METRICS_WORKSPACE_TAB_IDS: MetricsWorkspaceTab[] = [
  'overview',
  'service',
  'reliability',
  'customers',
  'sla',
  'inventory',
  'team'
];
const isMetricsWorkspaceTab = (value: unknown): value is MetricsWorkspaceTab =>
  typeof value === 'string' && METRICS_WORKSPACE_TAB_IDS.includes(value as MetricsWorkspaceTab);

const DEFAULT_DASHBOARD_VIEWPORT_WIDTH = 1600;
const CLOSED_CASE_STATUSES = new Set(['RESOLVED', 'CLOSED']);
const RESOLVE_CASE_CONFIRM_TITLE = 'Confirm Case Resolution';
const RESOLVE_CASE_CONFIRM_MESSAGE =
  'Confirm this case is complete and ready to be resolved. A resolved case is locked and cannot be reopened.';
const METRICS_DATE_PRESETS: Array<{ id: MetricsDatePreset; label: string }> = [
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'custom', label: 'Custom range' }
];
const METRICS_CASE_STATUS_FILTERS = [
  { id: 'NEW', label: 'New' },
  { id: 'OPEN', label: 'Open' },
  { id: 'IN_PROGRESS', label: 'In progress' },
  { id: 'WAITING_CUSTOMER', label: 'Waiting customer' },
  { id: 'WAITING_PARTS', label: 'Waiting parts' },
  { id: 'RESOLVED', label: 'Resolved' },
  { id: 'CLOSED', label: 'Closed' }
];
const METRICS_USER_FILTER_ROLE_KEYS = new Set(['ADMIN', 'SUPERUSER', 'TECH']);
const LEGACY_METRICS_WIDGETS = [
  { id: 'signals', label: 'Signals strip' },
  { id: 'overview-kpis', label: 'Overview KPIs' },
  { id: 'overview-grid', label: 'Overview widgets' },
  { id: 'service-kpis', label: 'Service Delivery KPIs' },
  { id: 'service-grid', label: 'Service Delivery widgets' },
  { id: 'reliability-kpis', label: 'Reliability KPIs' },
  { id: 'reliability-grid', label: 'Reliability widgets' },
  { id: 'customers-kpis', label: 'Customers KPIs' },
  { id: 'customers-grid', label: 'Customers widgets' },
  { id: 'sla-kpis', label: 'SLA KPIs' },
  { id: 'sla-grid', label: 'SLA widgets' },
  { id: 'inventory-kpis', label: 'Inventory KPIs' },
  { id: 'inventory-grid', label: 'Inventory widgets' },
  { id: 'team-kpis', label: 'Team KPIs' },
  { id: 'team-grid', label: 'Team widgets' }
];
const METRICS_WORKSPACE_INCLUDE_BLOCKS: Record<MetricsWorkspaceTab, MetricsWorkspaceIncludeBlock[]> = {
  overview: [
    {
      id: 'signals',
      label: 'Signals strip',
      description: 'Repeat-failure, schedule, and survey alerts at the top of Overview.'
    },
    {
      id: 'kpis',
      label: 'Overview KPIs',
      description: 'Open backlog, upcoming work, aged work, completion, schedule, and survey summary cards.'
    },
    {
      id: 'backlog-by-task-group',
      label: 'Backlog by task group',
      description: 'Open workload mix grouped by task type.'
    },
    {
      id: 'open-age-buckets',
      label: 'Open age buckets',
      description: 'Backlog aging split into 0-2, 3-7, 8-14, and 15+ day buckets.'
    },
    {
      id: 'upcoming-work',
      label: 'Upcoming work',
      description: 'Next 7 days of planned visits in the current scope.'
    },
    {
      id: 'completed-work-by-task-type',
      label: 'Completed work by task type',
      description: 'Completed task mix across the selected date range.'
    },
    {
      id: 'service-load-by-focus',
      label: 'Service load by focus',
      description: 'Current grouped workload by model or serial.'
    },
    {
      id: 'service-load-by-customer',
      label: 'Service load by customer',
      description: 'Customer demand concentration across open and active work.'
    }
  ],
  service: [
    {
      id: 'kpis',
      label: 'Service Delivery KPIs',
      description: 'Open case, active task, age, schedule, and quote summary cards.'
    },
    {
      id: 'open-by-status',
      label: 'Open by status',
      description: 'Current service case flow state across the scoped workload.'
    },
    {
      id: 'schedule-readiness',
      label: 'Schedule readiness',
      description: 'Planned, pending-confirmation, and unassigned task counts.'
    },
    {
      id: 'quote-pipeline',
      label: 'Quote pipeline',
      description: 'Latest customer quote decision state across current service cases.'
    },
    {
      id: 'upcoming-planned-work',
      label: 'Upcoming planned work',
      description: 'Next 7 days of scheduled visits in the current service scope.'
    },
    {
      id: 'jobs-waiting-on-parts',
      label: 'Jobs waiting on parts',
      description: 'Quote-backed repair work still blocked on parts availability or ETA.'
    },
    {
      id: 'upcoming-sla-work',
      label: 'Upcoming SLA work',
      description: 'Scheduled visits tied to devices with active SLA coverage.'
    },
    {
      id: 'contributing-cases',
      label: 'Contributing cases',
      description: 'Case reference list currently included by the Service Delivery filter row.'
    }
  ],
  reliability: [
    {
      id: 'kpis',
      label: 'Reliability KPIs',
      description: 'First-time-fix, MTBF, repair time, warranty, and maintenance summary cards.'
    },
    {
      id: 'device-mtbf',
      label: 'Device MTBF',
      description: 'Average repeat-repair interval by the current model or serial grouping.'
    },
    {
      id: 'user-mtbf',
      label: 'User MTBF',
      description: 'Average repeat-repair interval after technician-owned repairs.'
    },
    {
      id: 'time-to-repair',
      label: 'Time to repair',
      description: 'Average close time for repair cases completed in the selected range.'
    },
    {
      id: 'hold-reasons',
      label: 'On hold reasons',
      description: 'Current operational delays across parts, quotes, schedule, and ownership.'
    },
    {
      id: 'warranty-repairs',
      label: 'Warranty repairs',
      description: 'Repair workload split by warranty coverage at time of service.'
    },
    {
      id: 'common-failure-modes',
      label: 'Common failure modes',
      description: 'Top TSFAD causes in the current repair scope.'
    },
    {
      id: 'common-parts-used',
      label: 'Common parts used',
      description: 'Most frequently consumed repair parts in the filtered range.'
    },
    {
      id: 'repair-frequency',
      label: 'Repair frequency vs installed base',
      description: 'Repair cases as a percentage of the active installed base by model.'
    },
    {
      id: 'maintenance-compliance',
      label: 'Maintenance compliance',
      description: 'Devices with maintenance completed on or before the planned due date.'
    },
    {
      id: 'contributing-cases',
      label: 'Contributing cases',
      description: 'Case reference list currently included by the Reliability filter row.'
    }
  ],
  customers: [
    {
      id: 'kpis',
      label: 'Customers KPIs',
      description: 'Customer workload, schedule, quote, response, closure, and survey summary cards.'
    },
    {
      id: 'job-status',
      label: 'Job status',
      description: 'Current customer case flow state across the visible workload.'
    },
    {
      id: 'quote-status',
      label: 'Quote status',
      description: 'Latest quote state across customer cases in scope.'
    },
    {
      id: 'open-load-by-customer',
      label: 'Open load by customer',
      description: 'Current open case and active task concentration by customer.'
    },
    {
      id: 'scheduled-jobs-by-customer',
      label: 'Scheduled jobs by customer',
      description: 'Upcoming planned work in the next 7 days by customer.'
    },
    {
      id: 'completed-work-by-customer',
      label: 'Top customers by work carried out',
      description: 'Completed task volume by customer in the selected range.'
    },
    {
      id: 'survey-scores-by-customer',
      label: 'Survey scores by customer',
      description: 'Completed survey outcomes ordered by lowest average first.'
    },
    {
      id: 'service-cycle-stages',
      label: 'Service cycle stages',
      description: 'Average timing through assignment, quote, repair, and closure stages.'
    },
    {
      id: 'contributing-cases',
      label: 'Contributing cases',
      description: 'Case reference list currently included by the Customers filter row.'
    }
  ],
  sla: [
    {
      id: 'kpis',
      label: 'SLA KPIs',
      description: 'Active coverage, expiry, SLA job, first-time-fix, and maintenance summary cards.'
    },
    {
      id: 'open-slas-by-focus',
      label: 'Open SLAs by focus',
      description: 'Current active SLA coverage grouped by model or serial.'
    },
    {
      id: 'sla-expiry-watchlist',
      label: 'SLA expiry watchlist',
      description: 'Active SLA assignments nearing expiry within 90 days.'
    },
    {
      id: 'sla-lifecycle',
      label: 'SLA lifecycle',
      description: 'Coverage created, renewed, and expired in the selected range.'
    },
    {
      id: 'work-under-sla-by-customer',
      label: 'Work under SLA by customer',
      description: 'Cases and jobs currently tied to covered devices by customer.'
    },
    {
      id: 'scheduled-sla-jobs',
      label: 'Scheduled SLA jobs',
      description: 'Upcoming planned work on devices with active SLA coverage.'
    },
    {
      id: 'sla-mtbf-by-focus',
      label: 'SLA MTBF by focus',
      description: 'Average repeat-repair interval for covered devices by model or serial.'
    },
    {
      id: 'maintenance-deviation-by-month',
      label: 'Maintenance deviation by month',
      description: 'Average schedule deviation and on-time rate for SLA maintenance work.'
    },
    {
      id: 'breakdowns-after-maintenance',
      label: 'Breakdowns after maintenance',
      description: 'Repair demand following SLA maintenance completion within 30 days.'
    },
    {
      id: 'maintenance-compliance',
      label: 'Maintenance compliance',
      description: 'SLA-covered devices completed on or before maintenance due date.'
    },
    {
      id: 'contributing-cases',
      label: 'Contributing cases',
      description: 'Case reference list currently included by the SLA filter row.'
    }
  ],
  inventory: [
    {
      id: 'kpis',
      label: 'Inventory KPIs',
      description: 'Stock value, variance, van stock, and delayed repair-part summary cards.'
    },
    {
      id: 'stock-value-by-warehouse',
      label: 'Stock value by warehouse',
      description: 'Current on-hand stock posture across accessible warehouses, valued at landed cost.'
    },
    {
      id: 'transit-on-order-posture',
      label: 'Transit and on-order posture',
      description: 'Stock already moving or ordered into the visible warehouse network.'
    },
    {
      id: 'stock-variance-by-count-type',
      label: 'Stock variance by count type',
      description: 'Approved count variance across full, cycle, spot, and van-stock counts.'
    },
    {
      id: 'recent-approved-counts',
      label: 'Recent approved counts',
      description: 'Latest approved stock counts and variance outcomes in the selected range.'
    },
    {
      id: 'van-stock-by-user-warehouse',
      label: 'Van stock by user warehouse',
      description: 'Current user-held stock value across technician and warehouse-user vans.'
    },
    {
      id: 'delayed-parts-repair-delays',
      label: 'Delayed parts causing repair delays',
      description: 'Open repair cases waiting on missing parts or replenishment ETA.'
    },
    {
      id: 'contributing-cases',
      label: 'Contributing cases',
      description: 'Case reference list currently included by the Inventory filter row.'
    }
  ],
  team: [
    {
      id: 'kpis',
      label: 'Team KPIs',
      description: 'Assigned backlog, tracked hours, MTBF, first-time-fix, survey, and schedule summary cards.'
    },
    {
      id: 'backlog-by-user',
      label: 'Backlog by user',
      description: 'Active assigned workload split by technician or named assignee.'
    },
    {
      id: 'hours-on-task-by-user',
      label: 'Hours on task by user',
      description: 'Tracked task duration from start to completion by technician.'
    },
    {
      id: 'customer-satisfaction-by-user',
      label: 'Customer satisfaction by user',
      description: 'Completed survey outcomes grouped by named technician.'
    },
    {
      id: 'user-mtbf',
      label: 'User MTBF',
      description: 'Average repair-return interval for technician-owned repair work.'
    },
    {
      id: 'first-time-fix-by-user',
      label: 'First time fix by user',
      description: 'Technician repair closures with no repeat repair within 30 days.'
    },
    {
      id: 'scheduled-load-by-user',
      label: 'Scheduled load by user',
      description: 'Upcoming scheduled work in the next 7 days by technician.'
    },
    {
      id: 'repair-performance',
      label: 'Repair performance',
      description: 'MTBF and first-time-fix by technician and current model or serial grouping.'
    },
    {
      id: 'workload-detail',
      label: 'User workload detail',
      description: 'Backlog, scheduled work, pending confirmations, and tracked hours by user.'
    },
    {
      id: 'contributing-cases',
      label: 'Contributing cases',
      description: 'Case reference list currently included by the Team filter row.'
    }
  ]
};
const OVERVIEW_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.overview
  .map(block => block.id)
  .filter(blockId => blockId !== 'signals' && blockId !== 'kpis');
const SERVICE_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.service
  .map(block => block.id)
  .filter(blockId => blockId !== 'kpis');
const RELIABILITY_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.reliability
  .map(block => block.id)
  .filter(blockId => blockId !== 'kpis');
const CUSTOMERS_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.customers
  .map(block => block.id)
  .filter(blockId => blockId !== 'kpis');
const SLA_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.sla
  .map(block => block.id)
  .filter(blockId => blockId !== 'kpis');
const INVENTORY_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.inventory
  .map(block => block.id)
  .filter(blockId => blockId !== 'kpis');
const TEAM_METRICS_GRID_BLOCK_IDS = METRICS_WORKSPACE_INCLUDE_BLOCKS.team
  .map(block => block.id)
  .filter(blockId => blockId !== 'kpis');
const METRICS_DASHBOARD_CONFIG_VERSION = 6;
const METRICS_DASHBOARD_INCLUDE_BACKFILL_VERSIONS: Partial<Record<MetricsWorkspaceTab, Record<string, number>>> = {
  reliability: {
    'contributing-cases': 2
  },
  customers: {
    'contributing-cases': 3
  },
  sla: {
    'contributing-cases': 4
  },
  inventory: {
    'contributing-cases': 5
  },
  team: {
    'contributing-cases': 6
  }
};
const buildDefaultMetricsDashboardConfig = (): MetricsDashboardConfig => ({
  version: METRICS_DASHBOARD_CONFIG_VERSION,
  workspaces: (Object.keys(METRICS_WORKSPACE_INCLUDE_BLOCKS) as MetricsWorkspaceTab[]).reduce<
    NonNullable<MetricsDashboardConfig['workspaces']>
  >((acc, workspace) => {
    const includeIds = METRICS_WORKSPACE_INCLUDE_BLOCKS[workspace].map(block => block.id);
    if (includeIds.length === 0) return acc;
    acc[workspace] = { includes: includeIds };
    return acc;
  }, {})
});

const normaliseMetricsLegacyWidgets = (widgets: unknown): MetricsLegacyWidgetConfig[] => {
  const sourceWidgets = Array.isArray(widgets) && widgets.length > 0 ? widgets : LEGACY_METRICS_WIDGETS;
  return sourceWidgets
        .map((widget, index) => {
          const raw = widget && typeof widget === 'object' ? (widget as Partial<MetricsLegacyWidgetConfig>) : {};
          const id = String(raw.id || '').trim();
          if (!id) return null;
          return {
            id,
            enabled: raw.enabled !== false,
            order:
              typeof raw.order === 'number' && Number.isFinite(raw.order)
                ? raw.order
                : index + 1
          };
        })
        .filter((widget): widget is MetricsLegacyWidgetConfig => Boolean(widget));
};

const getMetricsDashboardConfigVersion = (config: unknown): number => {
  if (!config || typeof config !== 'object') return 0;
  const raw = config as { version?: unknown; schemaVersion?: unknown };
  const value = Number(raw.version ?? raw.schemaVersion ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const normaliseMetricsWorkspaceIncludes = (
  workspace: MetricsWorkspaceTab,
  includes: unknown,
  legacyWidgets: MetricsLegacyWidgetConfig[],
  configVersion: number
): string[] => {
  const allowed = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS[workspace].map(block => block.id));
  if (allowed.size === 0) return [];

  const providedIncludes = Array.isArray(includes)
    ? Array.from(
        new Set(
          includes
            .map(value => String(value || '').trim())
            .filter(value => value && allowed.has(value))
        )
      )
    : null;

  if (providedIncludes !== null) {
    const backfills = METRICS_DASHBOARD_INCLUDE_BACKFILL_VERSIONS[workspace] || {};
    Object.entries(backfills).forEach(([includeId, addedVersion]) => {
      if (
        configVersion < addedVersion &&
        allowed.has(includeId) &&
        !providedIncludes.includes(includeId)
      ) {
        providedIncludes.push(includeId);
      }
    });
    return providedIncludes;
  }

  if (workspace === 'overview' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.overview.map(block => block.id));
    if (enabledWidgets.get('signals') === false) {
      fallbackIncludes.delete('signals');
    }
    if (enabledWidgets.get('overview-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('overview-grid') === false) {
      OVERVIEW_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.overview
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  if (workspace === 'service' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.service.map(block => block.id));
    if (enabledWidgets.get('service-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('service-grid') === false) {
      SERVICE_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.service
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  if (workspace === 'reliability' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.reliability.map(block => block.id));
    if (enabledWidgets.get('reliability-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('reliability-grid') === false) {
      RELIABILITY_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.reliability
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  if (workspace === 'customers' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.customers.map(block => block.id));
    if (enabledWidgets.get('customers-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('customers-grid') === false) {
      CUSTOMERS_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.customers
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  if (workspace === 'sla' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.sla.map(block => block.id));
    if (enabledWidgets.get('sla-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('sla-grid') === false) {
      SLA_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.sla
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  if (workspace === 'inventory' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.inventory.map(block => block.id));
    if (enabledWidgets.get('inventory-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('inventory-grid') === false) {
      INVENTORY_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.inventory
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  if (workspace === 'team' && legacyWidgets.length > 0) {
    const enabledWidgets = new Map(
      legacyWidgets.map(widget => [widget.id, widget.enabled !== false] as const)
    );
    const fallbackIncludes = new Set(METRICS_WORKSPACE_INCLUDE_BLOCKS.team.map(block => block.id));
    if (enabledWidgets.get('team-kpis') === false) {
      fallbackIncludes.delete('kpis');
    }
    if (enabledWidgets.get('team-grid') === false) {
      TEAM_METRICS_GRID_BLOCK_IDS.forEach(blockId => fallbackIncludes.delete(blockId));
    }
    return METRICS_WORKSPACE_INCLUDE_BLOCKS.team
      .map(block => block.id)
      .filter(blockId => fallbackIncludes.has(blockId));
  }

  return METRICS_WORKSPACE_INCLUDE_BLOCKS[workspace].map(block => block.id);
};

const normalizeMetricsDashboardConfig = (config: unknown): MetricsDashboardConfig => {
  const raw = config && typeof config === 'object' ? (config as MetricsDashboardConfig) : {};
  const configVersion = getMetricsDashboardConfigVersion(config);
  const widgets = normaliseMetricsLegacyWidgets(raw.widgets);
  const workspaces: MetricsDashboardConfig['workspaces'] = {};

  (Object.keys(METRICS_WORKSPACE_INCLUDE_BLOCKS) as MetricsWorkspaceTab[]).forEach(workspace => {
    if (!METRICS_WORKSPACE_INCLUDE_BLOCKS[workspace].length) return;
    workspaces![workspace] = {
      includes: normaliseMetricsWorkspaceIncludes(
        workspace,
        raw.workspaces?.[workspace]?.includes,
        widgets,
        configVersion
      )
    };
  });

  return {
    version: METRICS_DASHBOARD_CONFIG_VERSION,
    widgets,
    workspaces
  };
};

const getMetricsWorkspaceIncludes = (
  config: MetricsDashboardConfig | null | undefined,
  workspace: MetricsWorkspaceTab
): string[] => normalizeMetricsDashboardConfig(config).workspaces?.[workspace]?.includes || [];

const buildMetricsUserFilterOptions = (rows: any[]): MetricsUserFilterOption[] => {
  return (Array.isArray(rows) ? rows : [])
    .map(row => {
      const id = String(row?.id || '').trim();
      const roleKey = String(row?.role?.key || '').trim().toUpperCase();
      const label =
        String(row?.fullName || '').trim() ||
        String(row?.username || '').trim() ||
        String(row?.email || '').trim() ||
        'User';
      const roleLabel = String(row?.role?.label || roleKey || '').trim();
      const isActive = row?.isActive !== false;
      return {
        id,
        label,
        detail: roleLabel,
        roleKey,
        isActive
      };
    })
    .filter(row => row.id && METRICS_USER_FILTER_ROLE_KEYS.has(row.roleKey))
    .sort((left, right) => Number(right.isActive) - Number(left.isActive) || left.label.localeCompare(right.label));
};

const getDashboardViewportWidth = (): number => {
  if (typeof window === 'undefined' || typeof window.innerWidth !== 'number') {
    return DEFAULT_DASHBOARD_VIEWPORT_WIDTH;
  }
  return window.innerWidth;
};

const toIsoDate = (value: Date): string => value.toISOString().slice(0, 10);

const buildMetricsPresetRange = (
  preset: Exclude<MetricsDatePreset, 'custom'>
): { start: string; end: string } => {
  const now = new Date();
  const end = toIsoDate(now);
  const start = new Date(now);

  if (preset === '7d') {
    start.setDate(start.getDate() - 6);
  } else if (preset === '30d') {
    start.setDate(start.getDate() - 29);
  } else if (preset === '90d') {
    start.setDate(start.getDate() - 89);
  } else {
    start.setMonth(0, 1);
  }

  return {
    start: toIsoDate(start),
    end
  };
};

const normalizeStatus = (s: string): UiStatus => (s === 'OPEN' ? 'NEW' : (s as UiStatus));
const getCaseStatusSelectValue = (status?: string | null): UiStatus => {
  const normalized = normalizeStatus((status || 'OPEN').toUpperCase());
  return normalized === 'NEW' ? 'OPEN' : normalized;
};
const formatCaseStatusLabel = (status?: string | null) => {
  const normalized = String(status || '').trim().toUpperCase();
  switch (normalized) {
    case 'NEW':
    case 'OPEN':
      return 'Open';
    case 'IN_PROGRESS':
      return 'In progress';
    case 'ON_HOLD':
      return 'On hold';
    case 'WAITING_CUSTOMER':
      return 'Waiting customer';
    case 'WAITING_PARTS':
      return 'Waiting parts';
    case 'RESOLVED':
      return 'Resolved';
    case 'CLOSED':
      return 'Closed';
    default:
      return normalized
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(' ') || 'Unknown';
  }
};
type CaseEvent = {
  id: string;
  eventType: string;
  actorId?: string | null;
  actorName?: string | null;
  occurredAt?: string | null;
  metadata?: Record<string, unknown> | null;
  notes?: string | null;
  isCustomerVisible?: boolean;
  createdAt?: string | null;
};

type CaseQuoteLine = {
  id?: string;
  lineType?: string;
  sku?: string | null;
  partNumber?: string | null;
  description?: string;
  quantity?: number;
  unitValue?: number;
  isFree?: boolean;
  lineTotal?: number;
  originalUnitValue?: number | null;
  slaCoverageStatus?: string | null;
  slaLabel?: string | null;
  slaDiscountPercent?: number | null;
};

type CaseQuotePartsStatus = {
  code?: string;
  label?: string;
  tone?: string;
  etaAt?: string | null;
  requestedQty?: number;
  availableQty?: number;
  remainingQty?: number;
  transferShortQty?: number;
  pendingDispatchQty?: number;
  inTransitQty?: number;
  hasPendingReplenishmentApproval?: boolean;
  hasApprovedReplenishment?: boolean;
  detail?: string;
};

type CaseQuoteRecord = {
  source?: 'TSFAD' | 'WORKFLOW';
  analysisId?: string | null;
  quoteId: string;
  status?: string | null;
  templateId?: string | null;
  title: string;
  description?: string;
  notes?: string;
  templateName?: string | null;
  savedAt?: string | null;
  actorName?: string | null;
  totals?: {
    parts?: number;
    labor?: number;
    grandTotal?: number;
    lineColumns?: QuoteLineColumnConfig[];
    totalSettings?: QuoteTotalSettings;
    approvalSettings?: {
      responseActions?: string[];
      approvalPlacement?: string;
      approvalFooterSlots?: string[];
      requireSignature?: boolean;
      signatureLabel?: string;
      requireTechnicianSignature?: boolean;
      technicianSignatureLabel?: string;
    };
  };
  totalSettings?: QuoteTotalSettings | null;
  responseActions?: string[];
  approvalPlacement?: string;
  approvalFooterSlots?: string[];
  requireSignature?: boolean;
  signatureLabel?: string;
  requireTechnicianSignature?: boolean;
  technicianSignatureLabel?: string;
  lineColumns?: QuoteLineColumnConfig[];
  lineItems: CaseQuoteLine[];
  customerApprovalName?: string;
  customerApprovalSignature?: string;
  customerDecision?: 'ACCEPT' | 'DENY' | 'ADJUST' | null;
  decisionAt?: string | null;
  decisionBy?: string | null;
  decisionNote?: string;
  partsStatus?: CaseQuotePartsStatus | null;
};

type QuoteTemplateField = {
  id: string;
  label: string;
  sourceGroup: string;
  sourceKey: string;
  included: boolean;
};

const getInsightQuoteDecisionLabel = (
  quote: Pick<CaseQuoteRecord, 'status' | 'customerDecision'>
) => {
  const status = String(quote.status || '').trim().toUpperCase();
  const decision = (quote.customerDecision || '').toUpperCase();
  if (status === 'INACTIVE') return 'INACTIVE';
  if (status === 'DRAFT') return 'DRAFT';
  if (status === 'SENT' || status === 'PENDING') return 'WAITING RESPONSE';
  if (decision === 'ACCEPT') return 'ACCEPTED';
  if (decision === 'DENY') return 'DENIED';
  if (decision === 'ADJUST') return 'ADJUST REQUESTED';
  return 'PENDING';
};

const getInsightQuotePartsSummary = (
  quote: Pick<CaseQuoteRecord, 'status' | 'customerDecision' | 'partsStatus'>
) => {
  const quoteStatus = String(quote?.status || '').trim().toUpperCase();
  const decision = String(quote?.customerDecision || '').trim().toUpperCase();
  const isActionableStatus =
    quoteStatus === 'SENT' || quoteStatus === 'PENDING' || quoteStatus === 'ACCEPTED';
  const isWaitingOrApprovedDecision = !decision || decision === 'ACCEPT';
  if (!isActionableStatus || !isWaitingOrApprovedDecision) return null;

  const status = quote?.partsStatus;
  if (!status || typeof status !== 'object') return null;
  const label = String(status.label || '').trim();
  if (!label) return null;
  const tone = String(status.tone || '').trim().toLowerCase() || 'warning';
  const detail = String(status.detail || '').trim();
  return {
    label,
    tone,
    detail,
    etaAt: typeof status.etaAt === 'string' ? status.etaAt : null
  };
};

const SERVICE_QUOTE_STATUS_ORDER = [
  'WAITING RESPONSE',
  'ACCEPTED',
  'ADJUST REQUESTED',
  'DENIED',
  'INACTIVE',
  'DRAFT',
  'PENDING'
];

const FIRST_TIME_FIX_WINDOW_DAYS = 30;
const RELIABILITY_HOLD_REASON_ORDER = ['Waiting on parts', 'Waiting on quote', 'Pending schedule', 'Unassigned'];

type QuoteTemplateInfoBlock = {
  id: string;
  label: string;
  included: boolean;
  fields: QuoteTemplateField[];
};

type QuoteTemplateLayout = {
  id: string;
  name: string;
  isPrimary: boolean;
  headerRows: number;
  headerAssignments: Record<string, string | null>;
  infoBlocks: QuoteTemplateInfoBlock[];
  lineColumns: QuoteLineColumnConfig[];
  totalSettings: QuoteTotalSettings;
  responseActions: string[];
  approvalPlacement: 'after_lines' | 'after_totals' | 'footer_c1' | 'footer_c2' | 'footer_c3';
  approvalFooterSlots: ('footer_c1' | 'footer_c2' | 'footer_c3')[];
  requireSignature: boolean;
  signatureLabel: string;
  requireTechnicianSignature: boolean;
  technicianSignatureLabel: string;
};
const QUOTE_HEADER_SLOTS = [
  { id: 'h1c1', row: 1, col: 1 },
  { id: 'h1c2', row: 1, col: 2 },
  { id: 'h1c3', row: 1, col: 3 },
  { id: 'h2c1', row: 2, col: 1 },
  { id: 'h2c2', row: 2, col: 2 },
  { id: 'h2c3', row: 2, col: 3 }
] as const;
const QUOTE_HEADER_SLOT_IDS = QUOTE_HEADER_SLOTS.map(slot => slot.id);
const getQuoteHeaderSlotAlign = (slotId: string): 'left' | 'center' | 'right' => {
  const normalized = String(slotId || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  if (normalized.endsWith('c3')) return 'right';
  if (normalized.endsWith('c2')) return 'center';
  return 'left';
};
const getQuoteHeaderSlotMeta = (
  slotId: string
): { row: number; col: number } => {
  const normalizedSlotId = String(slotId || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  const slot = QUOTE_HEADER_SLOTS.find(item => item.id === normalizedSlotId);
  return slot ? { row: slot.row, col: slot.col } : { row: 1, col: 1 };
};

type CaseAssigneeOption = {
  id: string;
  username: string;
  fullName?: string | null;
  isActive?: boolean;
  roleKey?: string | null;
  roleLabel?: string | null;
};

type PortalInvitePreview = {
  contactName: string;
  organizationSlug: string;
  username: string;
  temporaryPassword: string;
  loginUrl: string;
  emailTo?: string;
};

type InternalJobSerialAuditRow = {
  serial: string;
  submittedIdentifier?: string;
  matchType?: 'asset' | 'serial' | 'unknown';
  assetNumber?: string | null;
  serialNumber?: string | null;
  deviceModelLabel?: string;
  customerLabel: string;
  statusLabel: string;
  included: boolean;
  reason: string;
  deviceId?: string | null;
};

interface CustomModelInfo {
  type: string;
  modelNumber: string;
  manufacturer: string;
  needsVerification: boolean;
}

interface DeviceModel {
  id: string;
  modelNumber: string;
  manufacturer: string;
  modelName?: string;
  description?: string | null;
  deviceType?: string | null;
  isActive?: boolean;
}

type LocationNode = CustomerLocation & { children: LocationNode[] };

const buildLocationTree = (locations: CustomerLocation[] = []): LocationNode[] => {
  const map = new Map<string, LocationNode>();
  const roots: LocationNode[] = [];

  locations.forEach(location => {
    const node: LocationNode = { ...location, children: [] };
    map.set(location.id, node);
  });

  map.forEach(node => {
    if (node.parentLocationId && map.has(node.parentLocationId)) {
      map.get(node.parentLocationId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: LocationNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach(child => sortNodes(child.children));
  };

  sortNodes(roots);
  return roots;
};

const flattenLocationOptions = (nodes: LocationNode[], depth = 0): { value: string; label: string }[] => {
  return nodes.flatMap(node => {
    const prefix = depth > 0 ? `${'  '.repeat(depth)}- ` : '';
    return [
      { value: node.id, label: `${prefix}${node.name}` },
      ...flattenLocationOptions(node.children, depth + 1)
    ];
  });
};

const locationDisplayPath = (locationMap: Map<string, CustomerLocation>, locationId?: string | null): string | null => {
  if (!locationId) return null;
  const parts: string[] = [];
  const visited = new Set<string>();
  let current: CustomerLocation | undefined | null = locationMap.get(locationId);

  while (current && !visited.has(current.id)) {
    parts.unshift(current.name);
    visited.add(current.id);
    current = current.parentLocationId ? locationMap.get(current.parentLocationId) ?? null : null;
  }

  return parts.join(' > ');
};

const formatLocationAddress = (location?: CustomerLocation | null): string => {
  if (!location) return '';
  const structured = [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.stateProvince,
    location.postalCode,
    location.country
  ]
    .map(part => String(part || '').trim())
    .filter(Boolean);
  if (structured.length > 0) {
    return structured.join(', ');
  }
  return '';
};

const deriveCustomerCity = (locations: CustomerLocation[] = []): string | null => {
  if (!Array.isArray(locations) || locations.length === 0) return null;
  const topLevel = locations.find(loc => !loc.parentLocationId) || locations[0];
  const topCity = String(topLevel?.city || '').trim();
  if (topCity) return topCity;
  const fallback = locations
    .map(loc => String(loc?.city || '').trim())
    .find(Boolean);
  return fallback || null;
};

const getCaseDisplayNumber = (caseItem: any): string => {
  if (!caseItem) return 'Unknown';
  return (
    caseItem.caseCode ??
    caseItem.caseNumber ??
    caseItem.case?.caseCode ??
    caseItem.case?.caseNumber ??
    caseItem.id ??
    caseItem.caseId ??
    'Unknown'
  );
};

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveFrontendWarrantyStatus = (
  device?: Pick<Device, 'warrantyStartAt' | 'warrantyEndAt'> | null,
  occurredAt?: string | Date | null
) => {
  if (!device?.warrantyEndAt) return 'OUT';
  const end = toDate(device.warrantyEndAt);
  if (!end) return 'OUT';
  const start = toDate(device.warrantyStartAt);
  const when = occurredAt ? toDate(occurredAt) : new Date();
  if (!when) return 'OUT';
  if (start && when < start) return 'OUT';
  return when <= end ? 'IN' : 'OUT';
};

const formatDurationDaysLabel = (days?: number | null) => {
  if (typeof days !== 'number' || !Number.isFinite(days)) return '-';
  if (days < 1) {
    return `${Math.max(1, Math.round(days * 24))}h`;
  }
  return `${days.toFixed(1)}d`;
};

const formatInsightsCurrency = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};

const averageDurationDays = (values: Array<number | null | undefined>) => {
  const filtered = values.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0
  );
  if (filtered.length === 0) return null;
  return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
};

const diffDurationDays = (from?: Date | null, to?: Date | null) => {
  if (!from || !to) return null;
  const delta = (to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000);
  return Number.isFinite(delta) && delta >= 0 ? delta : null;
};

const getEarliestTaskAssignmentAt = (tasks: CaseTask[]) => {
  const dates = tasks.flatMap(task =>
    Array.isArray((task as any)?.taskAssignments)
      ? ((task as any).taskAssignments as any[])
          .map(entry => toDate(entry?.assignedAt))
          .filter((value): value is Date => Boolean(value))
      : []
  );
  if (dates.length === 0) return null;
  return dates.reduce((earliest, current) =>
    current.getTime() < earliest.getTime() ? current : earliest
  );
};

const getEarliestTaskStartedAt = (tasks: CaseTask[]) => {
  const dates = tasks
    .map(task => toDate(task?.startedAt))
    .filter((value): value is Date => Boolean(value));
  if (dates.length === 0) return null;
  return dates.reduce((earliest, current) =>
    current.getTime() < earliest.getTime() ? current : earliest
  );
};

const getLatestTaskCompletedAt = (tasks: CaseTask[]) => {
  const dates = tasks
    .map(task => toDate(task?.completedAt))
    .filter((value): value is Date => Boolean(value));
  if (dates.length === 0) return null;
  return dates.reduce((latest, current) =>
    current.getTime() > latest.getTime() ? current : latest
  );
};

const sumDurationHours = (values: Array<number | null | undefined>) => {
  const filtered = values.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0
  );
  if (filtered.length === 0) return null;
  return filtered.reduce((sum, value) => sum + value, 0);
};

const formatDateTime = (value?: string | Date | null): string | null => {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const getLatestTsfadAnalysis = (
  caseItem: Partial<Case> | null | undefined
): TSFADAnalysisRecord | null => {
  if (!caseItem || !Array.isArray(caseItem.tsfadAnalyses) || caseItem.tsfadAnalyses.length === 0) {
    return null;
  }
  const validAnalyses = caseItem.tsfadAnalyses.filter(Boolean) as TSFADAnalysisRecord[];
  if (validAnalyses.length === 0) return null;
  return validAnalyses.reduce<TSFADAnalysisRecord | null>((latest, current) => {
    if (!current) return latest;
    if (!latest) return current;
    const currentTime = toDate(current.createdAt)?.getTime() ?? 0;
    const latestTime = toDate(latest.createdAt)?.getTime() ?? 0;
    return currentTime > latestTime ? current : latest;
  }, null);
};

const getCaseAttachmentCount = (caseItem: any): number => {
  if (Array.isArray(caseItem?.media)) {
    return caseItem.media.length;
  }
  const count = Number(caseItem?._count?.media || 0);
  return Number.isFinite(count) ? count : 0;
};

const parseProblemDetails = (value?: string | null) => {
  const raw = typeof value === 'string' ? value : '';
  if (!raw) return { summary: '', details: [] as string[] };
  const marker = 'Additional Details:';
  const markerIndex = raw.indexOf(marker);
  if (markerIndex === -1) {
    return { summary: raw.trim(), details: [] as string[] };
  }
  const summary = raw.slice(0, markerIndex).trim();
  const detailText = raw.slice(markerIndex + marker.length).trim();
  const details = detailText
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.startsWith('- '))
    .map(line => line.slice(2).trim())
    .filter(Boolean);
  return { summary, details };
};

const buildCaseSummarySnippet = (value?: string | null, limit = 120) => {
  const summary = typeof value === 'string' ? value.trim() : '';
  if (!summary) return '';
  if (summary.length <= limit) return summary;
  return `${summary.slice(0, limit).trim()}...`;
};

const readPathValue = (source: any, path?: string) => {
  if (!path || !source || typeof source !== 'object') return '';
  const value = String(path)
    .split('.')
    .reduce((acc: any, key: string) => {
      if (!acc || typeof acc !== 'object') return undefined;
      return acc[key];
    }, source);
  if (value === undefined || value === null) return '';
  return String(value);
};

const normaliseQuoteTemplateLayout = (raw: any): QuoteTemplateLayout => {
  const normalizeQuoteHeaderSlotId = (rawSlotId: any): string | null => {
    if (typeof rawSlotId !== 'string' && typeof rawSlotId !== 'number') return null;
    const normalized = String(rawSlotId)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim();
    const slotMatch = normalized.match(/^h([12])c([123])$/);
    if (!slotMatch) return null;
    return `h${slotMatch[1]}c${slotMatch[2]}`;
  };

  const rawHeaderAssignments =
    raw?.headerAssignments && typeof raw.headerAssignments === 'object'
      ? raw.headerAssignments
      : {};
  const headerAssignments: Record<string, string | null> = {};
  Object.entries(rawHeaderAssignments).forEach(([slotKey, slotValue]) => {
    const normalizedSlotId = normalizeQuoteHeaderSlotId(slotKey);
    if (!normalizedSlotId) return;
    headerAssignments[normalizedSlotId] =
      typeof slotValue === 'string' ? slotValue.trim() : null;
  });

  const infoBlocks = Array.isArray(raw?.infoBlocks)
    ? raw.infoBlocks.map((block: any, index: number) => ({
        id: String(block?.id || `block-${index + 1}`),
        label: String(block?.label || `Block ${index + 1}`),
        included: block?.included !== false,
        fields: Array.isArray(block?.fields)
          ? block.fields.map((field: any, fieldIndex: number) => ({
              id: String(field?.id || `${String(block?.id || `block-${index + 1}`)}-field-${fieldIndex + 1}`),
              label: String(field?.label || `Field ${fieldIndex + 1}`),
              sourceGroup: String(field?.sourceGroup || ''),
              sourceKey: String(field?.sourceKey || ''),
              included: field?.included !== false
            }))
          : []
      }))
    : [];

  const rawApprovalPlacement = String(raw?.approvalPlacement || raw?.totals?.approvalSettings?.approvalPlacement || '').trim();
  const rawApprovalFooterSlots = raw?.approvalFooterSlots || raw?.totals?.approvalSettings?.approvalFooterSlots;
  const approvalFooterSlots: ('footer_c1' | 'footer_c2' | 'footer_c3')[] = [];
  const addApprovalFooterSlot = (value: any) => {
    const rawSlot = String(value || '').trim();
    if (
      (rawSlot === 'footer_c1' || rawSlot === 'footer_c2' || rawSlot === 'footer_c3') &&
      !approvalFooterSlots.includes(rawSlot)
    ) {
      approvalFooterSlots.push(rawSlot);
    }
  };
  if (Array.isArray(rawApprovalFooterSlots)) rawApprovalFooterSlots.forEach(addApprovalFooterSlot);
  if (approvalFooterSlots.length === 0) addApprovalFooterSlot(rawApprovalPlacement);
  approvalFooterSlots.sort(
    (a, b) => ['footer_c1', 'footer_c2', 'footer_c3'].indexOf(a) - ['footer_c1', 'footer_c2', 'footer_c3'].indexOf(b)
  );
  return {
    id: String(raw?.id || ''),
    name: String(raw?.name || 'Quote Template'),
    isPrimary: Boolean(raw?.isPrimary),
    headerRows: Number(raw?.headerRows) === 1 ? 1 : 2,
    headerAssignments,
    infoBlocks,
    lineColumns: normalizeQuoteLineColumns(raw?.lineColumns),
    totalSettings: normalizeQuoteTotalSettings(raw?.totalSettings || raw?.totals?.totalSettings),
    responseActions: Array.isArray(raw?.responseActions)
      ? raw.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
      : ['accept', 'deny', 'adjust'],
    approvalPlacement:
      approvalFooterSlots.length > 0
        ? approvalFooterSlots[0]
        : rawApprovalPlacement === 'footer_c1' ||
          rawApprovalPlacement === 'footer_c2' ||
          rawApprovalPlacement === 'footer_c3'
          ? rawApprovalPlacement
        : rawApprovalPlacement === 'footer'
          ? 'footer_c1'
        : 'footer_c3',
    approvalFooterSlots,
    requireSignature: raw?.requireSignature === true,
    signatureLabel: String(raw?.signatureLabel || 'Customer approval signature'),
    requireTechnicianSignature: raw?.requireTechnicianSignature === true,
    technicianSignatureLabel: String(raw?.technicianSignatureLabel || 'Technician signature')
  };
};

const resolveMediaCategory = (media: any) => {
  const mime = (media?.mimeType || media?.fileType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.includes('image')) return 'image';
  if (mime.includes('video')) return 'video';
  if (mime.includes('audio')) return 'audio';
  return 'file';
};

type TsfadBadgeVariant = 'none' | 'pending' | 'accepted';

const getTsfadStatusMeta = (
  caseItem: Case
): { variant: TsfadBadgeVariant; label: string; detail: string } => {
  const latest = getLatestTsfadAnalysis(caseItem);
  if (!latest) {
    return {
      variant: 'none',
      label: 'TSFAD not started',
      detail: 'Run TSFAD to prepare the troubleshooting plan.'
    };
  }

  const status = (latest.analysisStatus || '').toUpperCase();
  if (status === 'ACCEPTED') {
    const acceptedAt = formatDateTime(latest.acceptedAt || latest.updatedAt || latest.createdAt);
    const parts: string[] = [];
    if (acceptedAt) parts.push(`Accepted ${acceptedAt}`);
    if (latest.acceptedBy) parts.push(`by ${latest.acceptedBy}`);
    return {
      variant: 'accepted',
      label: 'TSFAD saved',
      detail: parts.join(' ') || 'Accepted analysis is ready to use in repairs.'
    };
  }

  const generatedAt = formatDateTime(latest.createdAt);
  return {
    variant: 'pending',
    label: 'TSFAD ready',
    detail: generatedAt ? `Generated ${generatedAt}` : 'Latest run is waiting for acceptance.'
  };
};


type TaskGroupCode =
  | 'REPAIR'
  | 'MAINTENANCE'
  | 'MAINTENANCE_PREVENTATIVE'
  | 'MAINTENANCE_CHECKUP'
  | 'INSTALL'
  | 'DECOMMISSION'
  | 'SAFETY'
  | 'OTHER';

interface TaskVariantMeta {
  code: TaskGroupCode;
  label: string;
  description?: string;
}

interface TaskGroupMeta {
  code?: TaskGroupCode;
  label: string;
  color: string;
  description: string;
  variants?: TaskVariantMeta[];
  parentCode?: TaskGroupCode;
}

const INSTALL_PHASES = [
  { id: 'PREP', label: 'Pre-install preparation', color: '#A16207', actionable: true },
  { id: 'EXECUTION', label: 'Installation execution', color: '#CA8A04', actionable: true },
  { id: 'VERIFICATION', label: 'Post-install verification', color: '#854D0E', actionable: true }
];

const TASK_GROUPS: Array<TaskGroupMeta & { code: TaskGroupCode }> = [
  {
    code: 'REPAIR',
    label: 'Repair',
    color: '#2563EB',
    description: 'Use TSFAD-driven guidance to diagnose and execute repairs.'
  },
  {
    code: 'MAINTENANCE',
    label: 'Maintenance',
    color: '#16A34A',
    description: 'Track preventative maintenance and service visits.',
    variants: [
      {
        code: 'MAINTENANCE_PREVENTATIVE',
        label: 'Preventative Maintenance',
        description: 'Scheduled preventative maintenance visit using the latest defaults.'
      },
      {
        code: 'MAINTENANCE_CHECKUP',
        label: 'Check Up',
        description: 'Quick inspection or light service between major intervals.'
      }
    ]
  },
  {
    code: 'INSTALL',
    label: 'Install',
    color: '#CA8A04',
    description: 'Coordinate device installs and commissioning handoffs.'
  },
  {
    code: 'DECOMMISSION',
    label: 'Decommission',
    color: '#7C3AED',
    description: 'Plan decommission or removal workflows with proper documentation.'
  },
  {
    code: 'SAFETY',
    label: 'Safety / Upgrades',
    color: '#DC2626',
    description: 'Manage safety inspections and compliance-driven upgrades.'
  },
  {
    code: 'OTHER',
    label: 'Other',
    color: '#64716F',
    description: 'Capture ad-hoc tasks that fall outside standard workflows.'
  }
];

const INTERNAL_JOB_TASK_GROUP_OPTIONS: Array<{ value: TaskGroupCode; label: string }> = [
  { value: 'REPAIR', label: 'Repair' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'INSTALL', label: 'Installation' },
  { value: 'DECOMMISSION', label: 'Decommission' },
  { value: 'SAFETY', label: 'Safety / Upgrade' },
  { value: 'OTHER', label: 'Other' }
];

const TASK_GROUP_META: Record<string, TaskGroupMeta> = {
  UNASSIGNED: {
    label: 'Unassigned',
    color: '#C8BEB1',
    description: 'No task has been assigned to this case yet.'
  }
};

TASK_GROUPS.forEach(group => {
  const { variants = [], ...baseMeta } = group;
  TASK_GROUP_META[group.code] = { ...baseMeta, variants, parentCode: group.code };
  variants.forEach(variant => {
    TASK_GROUP_META[variant.code] = {
      ...baseMeta,
      code: variant.code,
      label: variant.label,
      description: variant.description ?? baseMeta.description,
      parentCode: group.code
    };
  });
});

const MAINTENANCE_VARIANTS = TASK_GROUP_META.MAINTENANCE?.variants ?? [];
const MAINTENANCE_COLOR = TASK_GROUP_META.MAINTENANCE?.color ?? '#16A34A';
const MAINTENANCE_VARIANT_ROUTES: Partial<Record<TaskGroupCode, (caseId: string) => string>> = {
  MAINTENANCE_CHECKUP: caseId => `/maintenance/${encodeURIComponent(caseId)}/checkup`,
  MAINTENANCE_PREVENTATIVE: caseId => `/maintenance/${encodeURIComponent(caseId)}/preventative`
};

const TASK_GROUP_ALIASES: Record<string, TaskGroupCode> = {
  INSTALL_PREP: 'INSTALL',
  PRE_INSTALL: 'INSTALL',
  PREINSTALL: 'INSTALL',
  INSTALL_EXEC: 'INSTALL',
  INSTALL_EXECUTION: 'INSTALL',
  INSTALLATION: 'INSTALL',
  INSTALL_VERIFICATION: 'INSTALL',
  INSTALL_VERIFY: 'INSTALL',
  INSTALL_TASK: 'INSTALL',
  INSTALL_DECOM: 'DECOMMISSION',
  INSTALL_DECOMMISSION: 'DECOMMISSION',
  DECOMMISSION_REPACK: 'DECOMMISSION',
  REPACK: 'DECOMMISSION',
  MAINT: 'MAINTENANCE',
  MAINTENANCE_PREVENTATIVE: 'MAINTENANCE',
  MAINTENANCE_CHECKUP: 'MAINTENANCE',
  MAINTENANCE_CHECK_UP: 'MAINTENANCE'
};

const resolveTaskGroupCode = (code?: string | null): TaskGroupCode | '' => {
  if (!code) return '';
  const upper = code.trim().toUpperCase();
  const meta = TASK_GROUP_META[upper];
  if (meta) {
    if (meta.parentCode && meta.parentCode !== meta.code) {
      return meta.parentCode;
    }
    if (meta.code) {
      return meta.code as TaskGroupCode;
    }
  }
  return TASK_GROUP_ALIASES[upper] || ('OTHER' as TaskGroupCode);
};

// Active workflow controls should bind only to open/planned tasks.
// Completed/cancelled/archived tasks remain visible in history, not as active context.
const INACTIVE_TASK_STATUSES = new Set(['COMPLETED', 'CANCELLED', 'ARCHIVED']);

const normaliseTaskStatus = (status?: string | null): string => (status || 'PLANNED').toUpperCase();

const formatTaskStatus = (status?: string | null): string =>
  normaliseTaskStatus(status)
    .split('_')
    .map(segment => segment.charAt(0) + segment.slice(1).toLowerCase())
    .join(' ');

const getActiveCaseTask = (tasks?: CaseTask[]): CaseTask | null => {
  if (!Array.isArray(tasks) || tasks.length === 0) return null;

  return tasks
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const timeA = toDate(a?.createdAt || a?.updatedAt)?.getTime() ?? 0;
      const timeB = toDate(b?.createdAt || b?.updatedAt)?.getTime() ?? 0;
      return timeB - timeA;
    })
    .find(task => !INACTIVE_TASK_STATUSES.has(normaliseTaskStatus(task?.status)) ) || null;
};

const isInternalAssigneeValue = (value?: string | null): boolean => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) return false;
  if (normalized.includes('@')) return false;
  const upper = normalized.toUpperCase();
  if (upper === 'SYSTEM' || upper.includes('TSFAD ENGINE')) return false;
  return true;
};

const normalizeAssigneeIdList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .map(entry => String(entry || '').trim())
    .filter(Boolean)
    .filter(entry => {
      if (seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
};

const getTaskAssigneeIds = (task?: CaseTask | null): string[] => {
  if (!task) return [];
  return normalizeAssigneeIdList([
    ...(Array.isArray(task.assigneeUserIds) ? task.assigneeUserIds : []),
    (task as any).assignedUserId
  ]);
};

const getTaskAssigneeDisplayNames = (task?: CaseTask | null): string[] => {
  if (!task) return [];
  const names = Array.isArray(task.assignees)
    ? task.assignees
        .map(entry => String(entry?.fullName || entry?.username || '').trim())
        .filter(Boolean)
    : [];
  const fallbackName = String((task as any).assignedUserName || '').trim();
  return Array.from(new Set([...names, fallbackName].filter(Boolean)));
};

const areSameAssigneeLists = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((value, index) => value === b[index]);
};

export default function TechDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission, hasAnyPermission } = useAuth();
  const userRoleKey = String(user?.role || '').toUpperCase();
  const isDeviceExpertOnlyUser = userRoleKey === 'DEVICE_EXPERT_USER';
  const canReadCases = hasAnyPermission(['case.read.assigned', 'case.read.own', 'case.assign']);
  const canUpdateCaseStatus = hasPermission('case.status.update');
  const canCloseCases = canUpdateCaseStatus && ['ADMIN', 'SUPERUSER'].includes(userRoleKey);
  const canVerifySubmittedDevices = ['ADMIN', 'SUPERUSER'].includes(userRoleKey);
  const canUpdateCasePriority = hasPermission('case.priority.update');
  const canAssignCases = hasPermission('case.assign');
  const canEditCaseProblem = hasAnyPermission(['case.status.update', 'task.run.assigned']);
  const canManageCustomers = hasPermission('customers.manage');
  const canReadCustomers = hasAnyPermission(['customers.read', 'customers.manage']);
  const canReadDevices = hasAnyPermission(['devices.manage', 'device.read.own', 'case.read.assigned']);
  const canAccessSla = ['ADMIN', 'SUPERUSER'].includes(userRoleKey);
  const canAccessSchedulingPlanner = ['ADMIN', 'SUPERUSER'].includes(userRoleKey);
  const canManageSerializedDevices =
    hasPermission('devices.manage') &&
    !(
      String(user?.scopeType || '').toUpperCase() === 'GROUP' ||
      userRoleKey === 'GLOBAL_ANALYST'
    );
  const canAccessModelLibrary = hasAnyPermission(['devices.manage', 'document.upload', 'library.maintain', 'library.maintain.group']);
  const canAccessDocuments = hasAnyPermission(['document.upload', 'forms.manage']);
  const canAccessMessages = canReadCases;
  const canAccessKnowledge = hasAnyPermission([
    'knowledge.query.unit',
    'library.maintain.group',
    'knowledge.quality.manage.group'
  ]);
  const canAccessLibraryMaintenance = hasAnyPermission([
    'library.maintain',
    'snippets.edit',
    'library.maintain.group',
    'snippets.edit.group'
  ]);
  const canAccessFeedbackReview = canAccessLibraryMaintenance;
  const canManageUsers = hasPermission('users.manage');
  const userScopeType = String(user?.scopeType || '').toUpperCase();
  const canAccessInventory =
    userScopeType === 'UNIT' &&
    ['ADMIN', 'SUPERUSER', 'TECH', 'WAREHOUSE_USER'].includes(userRoleKey);
  const canAccessInventoryInsights =
    userScopeType === 'UNIT' && ['ADMIN', 'WAREHOUSE_USER'].includes(userRoleKey);
  const hasMetricsReadAccess = hasAnyPermission(['metrics.read.self', 'metrics.read.group', 'reports.run.all']);
  const canAccessMetrics =
    hasMetricsReadAccess || (userRoleKey === 'WAREHOUSE_USER' && canAccessInventoryInsights);
  const canViewFullInsights = userRoleKey === 'ADMIN';
  const canViewTeamInsights = userRoleKey === 'ADMIN';
  const isWarehouseInsightsOnlyUser = userRoleKey === 'WAREHOUSE_USER';
  const isGlobalScopeUser =
    userScopeType === 'GROUP' ||
    userRoleKey === 'GLOBAL_ANALYST';
  const canAccessOrgNotifications = userRoleKey === 'ADMIN' && isGlobalScopeUser;
  const canViewCasesTab = canReadCases && !isGlobalScopeUser;
  const canViewMessagesTab = canAccessMessages && !isGlobalScopeUser;
  const canViewCustomersTab = canReadCustomers && !isGlobalScopeUser;
  const canViewModelsTab = canReadDevices || canAccessModelLibrary;
  const canOpenInternalJobs = canAssignCases && ['ADMIN', 'SUPERUSER'].includes(userRoleKey);
  const canUseCaseUserFilter = canAssignCases && ['ADMIN', 'SUPERUSER'].includes(userRoleKey);
  const metricsWorkspaceTabs = useMemo<
    Array<{ id: MetricsWorkspaceTab; label: string; helper: string }>
  >(
    () => {
      const operationalTabs: Array<{ id: MetricsWorkspaceTab; label: string; helper: string }> = [
        { id: 'overview', label: 'Overview', helper: 'Top operational health signals and live service posture.' },
        { id: 'service', label: 'Service Delivery', helper: 'Backlog flow, schedule readiness, and queue pressure.' },
        { id: 'reliability', label: 'Reliability', helper: 'Device reliability, repeat failures, and warranty mix.' },
        { id: 'customers', label: 'Customers', helper: 'Customer demand, long-open work, and health signals.' },
        { id: 'sla', label: 'SLAs', helper: 'Coverage posture, expiry risk, and SLA-backed service outcomes.' }
      ];
      const inventoryTab = {
        id: 'inventory' as const,
        label: 'Inventory',
        helper: 'Stock value, variance, transit, and repair-part pressure.'
      };
      if (isWarehouseInsightsOnlyUser) {
        return canAccessInventoryInsights ? [inventoryTab] : [];
      }
      return [
        ...operationalTabs,
        ...(canAccessInventoryInsights ? [inventoryTab] : []),
        ...(canViewTeamInsights
          ? [{ id: 'team' as const, label: 'Team', helper: 'User performance, repair quality, and workload ownership.' }]
          : [])
      ];
    },
    [canAccessInventoryInsights, canViewTeamInsights, isWarehouseInsightsOnlyUser]
  );
  const allowedTabs = useMemo<DashboardTab[]>(
    () => {
      if (isDeviceExpertOnlyUser) return canAccessKnowledge ? ['knowledge'] : [];
      const scopedTabs = isGlobalScopeUser ? ORG_DASHBOARD_TABS : UNIT_DASHBOARD_TABS;
      return scopedTabs.filter(tab => {
        if (tab === 'cases') return canViewCasesTab;
        if (tab === 'messages') return canViewMessagesTab;
        if (tab === 'orgNotifications') return canAccessOrgNotifications;
        if (tab === 'customers') return canViewCustomersTab;
        if (tab === 'models') return canViewModelsTab;
        if (tab === 'sla') return canAccessSla;
        if (tab === 'documents') return canAccessDocuments;
        if (tab === 'inventory') return canAccessInventory;
        if (tab === 'knowledge') return canAccessKnowledge;
        if (tab === 'quality') return canAccessLibraryMaintenance;
        if (tab === 'feedback') return canAccessFeedbackReview;
        if (tab === 'metrics') return canAccessMetrics;
        if (tab === 'users') return canManageUsers;
        return true;
      });
    },
    [
      canAccessDocuments,
      canAccessInventory,
      canAccessOrgNotifications,
      canAccessFeedbackReview,
      canAccessKnowledge,
      canAccessLibraryMaintenance,
      canAccessSla,
      canViewMessagesTab,
      canAccessMetrics,
      canManageUsers,
      canViewCasesTab,
      canViewCustomersTab,
      canViewModelsTab,
      isGlobalScopeUser,
      isDeviceExpertOnlyUser
    ]
  );
  const defaultAllowedTab = allowedTabs[0] || 'settings';
  const tabFallback = <div style={{ padding: '1rem' }}>Loading tab...</div>;
  const [dashboardViewportWidth, setDashboardViewportWidth] = useState<number>(() =>
    getDashboardViewportWidth()
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
const [activeTab, setActiveTab] = useState<DashboardTab>(() => {
  const navStateTab = (location.state as { activeTab?: unknown } | null)?.activeTab;
  if (isDashboardTab(navStateTab) && allowedTabs.includes(navStateTab)) return navStateTab;
  try {
    const stored = localStorage.getItem(DASHBOARD_TAB_KEY);
    if (isDashboardTab(stored) && allowedTabs.includes(stored)) return stored;
  } catch {}
  return defaultAllowedTab;
});
  const [baseUrl, setBaseUrl] = useState('');
  const [techMessages, setTechMessages] = useState<PortalMessage[]>([]);
  const [selectedTechMessageId, setSelectedTechMessageId] = useState<string | null>(null);
  const [showTechComposePanel, setShowTechComposePanel] = useState(false);
  const [messageWorkspaceMode, setMessageWorkspaceMode] = useState<'messages' | 'notifications'>('messages');
  const [messageChannelType, setMessageChannelType] = useState<'external' | 'internal'>('external');
  const [messageView, setMessageView] = useState<'active' | 'archived' | 'all'>('active');
  const [messageFocusFilter, setMessageFocusFilter] = useState<'all' | 'general' | 'device' | 'case'>('all');
  const [messageCaseFilter, setMessageCaseFilter] = useState('');
  const [messageDeviceFilter, setMessageDeviceFilter] = useState('');
  const [messageReplyBody, setMessageReplyBody] = useState('');
  const [messageReplyAttachments, setMessageReplyAttachments] = useState<PortalMessageAttachment[]>([]);
  const [messageShowSent, setMessageShowSent] = useState(false);
  const [messageSortOrder, setMessageSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [readTechMessageIds, setReadTechMessageIds] = useState<string[]>([]);
  const [messageUnreadCounts, setMessageUnreadCounts] = useState<{ external: number; internal: number }>({
    external: 0,
    internal: 0
  });
  const [techNotifications, setTechNotifications] = useState<AppNotification[]>([]);
  const [notificationPopupItems, setNotificationPopupItems] = useState<AppNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationView, setNotificationView] = useState<'active' | 'all' | 'dismissed'>('active');
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationModalMode, setNotificationModalMode] = useState<'unit' | 'org'>('unit');
  const [orgNotificationUnits, setOrgNotificationUnits] = useState<Array<{ id: string; name?: string | null; slug?: string | null }>>([]);
  const [orgNotificationUnitsLoading, setOrgNotificationUnitsLoading] = useState(false);
  const [orgNotificationCampaigns, setOrgNotificationCampaigns] = useState<any[]>([]);
  const [orgNotificationCampaignsLoading, setOrgNotificationCampaignsLoading] = useState(false);
  const [pendingLeaveApprovalCount, setPendingLeaveApprovalCount] = useState(0);
  const [messageVisibleCount, setMessageVisibleCount] = useState(10);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadedMessageQueryKey, setLoadedMessageQueryKey] = useState('');
  const [messageActionBusy, setMessageActionBusy] = useState<'compose' | 'reply' | null>(null);
  const [messageComposeCustomerId, setMessageComposeCustomerId] = useState('');
  const [messageComposeFocus, setMessageComposeFocus] = useState<'general' | 'device' | 'case'>('general');
  const [messageComposeCaseId, setMessageComposeCaseId] = useState('');
  const [messageComposeDeviceId, setMessageComposeDeviceId] = useState('');
  const [messageComposeBody, setMessageComposeBody] = useState('');
  const [messageComposeAttachments, setMessageComposeAttachments] = useState<PortalMessageAttachment[]>([]);
  const [messageComposeRecipientUserIds, setMessageComposeRecipientUserIds] = useState<string[]>([]);
  const [messageComposeExternalRecipientUserIds, setMessageComposeExternalRecipientUserIds] = useState<string[]>([]);
  const [internalMessageContacts, setInternalMessageContacts] = useState<
    Array<{ id: string; username: string; fullName?: string | null; roleKey?: string | null; roleLabel?: string | null }>
  >([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerDetailsById, setCustomerDetailsById] = useState<Record<string, Customer>>({});
  const [customerDetailLoadingId, setCustomerDetailLoadingId] = useState<string | null>(null);
  const [customerDetailError, setCustomerDetailError] = useState<{ customerId: string; message: string } | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [deviceWorkspaceCustomerFilter, setDeviceWorkspaceCustomerFilter] =
    useState<DeviceWorkspaceCustomerFilter | null>(null);
  const [internalJobDraft, setInternalJobDraft] = useState<InternalJob | null>(null);
  const [showInternalJobModal, setShowInternalJobModal] = useState(false);
  const [internalJobSerialAudit, setInternalJobSerialAudit] = useState<InternalJobSerialAuditRow[]>([]);
  const [internalJobSubmitting, setInternalJobSubmitting] = useState(false);
  const [internalJobError, setInternalJobError] = useState<string | null>(null);
  const [internalJobUploads, setInternalJobUploads] = useState<Array<{
    fileName: string;
    fileUrl: string;
    fileType: string;
    mimeType?: string | null;
    fileSize: number;
  }>>([]);
  const [internalJobUploadError, setInternalJobUploadError] = useState<string | null>(null);
  const [isInternalJobDragging, setIsInternalJobDragging] = useState(false);
  const messageListRequestIdRef = useRef(0);
  const unreadSummaryRequestRef = useRef<Promise<void> | null>(null);
  const messageListQueryKey = useMemo(
    () =>
      `${messageChannelType}:${messageView}:${messageChannelType === 'external' ? messageShowSent : true}`,
    [messageChannelType, messageShowSent, messageView]
  );
  const [internalJobForm, setInternalJobForm] = useState({
    mode: 'SIMPLE',
    title: '',
    description: '',
    instructionRef: '',
    taskGroup: 'SAFETY',
    priority: 'HIGH',
    assigneeUserId: '',
    deviceId: '',
    modelId: '',
    customerId: '',
    locationId: '',
    serialPrefix: '',
    serialNumbers: '',
    serialRangeFrom: '',
    serialRangeTo: '',
    manufacturedFrom: '',
    manufacturedTo: '',
    caseCap: '100',
    warrantyStatus: '',
    minAgeDays: '',
    maxAgeDays: '',
    includeInactive: false
  });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [caseUserFilter, setCaseUserFilter] = useState('all');
  const [taskGroupFilter, setTaskGroupFilter] = useState<'all' | 'UNASSIGNED' | TaskGroupCode>('all');
  const [deviceModelFilter, setDeviceModelFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [caseSortOrder, setCaseSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [casesTopPanelCollapsed, setCasesTopPanelCollapsed] = useState(
    () => getDashboardViewportWidth() <= DASHBOARD_NARROW_BREAKPOINT
  );
  const [casesPage, setCasesPage] = useState(1);
  const [casesPageSize, setCasesPageSize] = useState(DEFAULT_CASES_PAGE_SIZE);
  const [metricsDatePreset, setMetricsDatePreset] = useState<MetricsDatePreset>('30d');
  const [metricsStart, setMetricsStart] = useState(() => buildMetricsPresetRange('30d').start);
  const [metricsEnd, setMetricsEnd] = useState(() => buildMetricsPresetRange('30d').end);
  const [metricsGroupBy, setMetricsGroupBy] = useState<'model' | 'device'>('model');
  const [metricsCustomerId, setMetricsCustomerId] = useState('');
  const [metricsDeviceId, setMetricsDeviceId] = useState('');
  const [metricsModelId, setMetricsModelId] = useState('');
  const [metricsTaskGroup, setMetricsTaskGroup] = useState('');
  const [metricsStatus, setMetricsStatus] = useState('');
  const [metricsCaseId, setMetricsCaseId] = useState('');
  const [metricsUserIds, setMetricsUserIds] = useState<string[]>([]);
  const [metricsUserOptions, setMetricsUserOptions] = useState<MetricsUserFilterOption[]>([]);
  const [metricsUserOptionsLoading, setMetricsUserOptionsLoading] = useState(false);
  const [metricsUserFilterOpen, setMetricsUserFilterOpen] = useState(false);
  const [metricsWorkspaceTab, setMetricsWorkspaceTab] = useState<MetricsWorkspaceTab>(() => {
    try {
      const stored = localStorage.getItem(METRICS_WORKSPACE_TAB_KEY);
      if (isMetricsWorkspaceTab(stored)) return stored;
    } catch {}
    return 'overview';
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsSourceDataLoaded, setMetricsSourceDataLoaded] = useState(false);
  const currentMetricsWorkspace = useMemo(
    () => metricsWorkspaceTabs.find(tab => tab.id === metricsWorkspaceTab) || metricsWorkspaceTabs[0] || null,
    [metricsWorkspaceTab, metricsWorkspaceTabs]
  );
  const canUseMetricsUserFilter = userRoleKey === 'ADMIN' && canManageUsers;
  const [serviceInsightQuotes, setServiceInsightQuotes] = useState<CaseQuoteRecord[]>([]);
  const [serviceInsightQuotesLoading, setServiceInsightQuotesLoading] = useState(false);
  const [slaInsightsData, setSlaInsightsData] = useState<{
    slaSummary?: {
      activeDevices: number;
      coverageViews: number;
      expiring30: number;
      expiring90: number;
      scheduledJobs: number;
    };
    slaLifecycleSummary?: {
      newCount: number;
      renewedCount: number;
      expiredCount: number;
    };
    assignments?: Array<{
      id: string;
      deviceId: string;
      customerId: string;
      slaTemplateId: string;
      startDate: string;
      endDate?: string | null;
      assignedAt: string;
      endedAt?: string | null;
      endReason?: string | null;
      createdAt?: string | null;
      updatedAt?: string | null;
      customer?: { id: string; company?: string | null } | null;
      device?: {
        id: string;
        modelId?: string | null;
        modelNumber?: string | null;
        serialNumber?: string | null;
      } | null;
      slaTemplate?: {
        id: string;
        name?: string | null;
        brandingName?: string | null;
        tierBrandingName?: string | null;
        slaType?: string | null;
        durationQuantity?: number | null;
        durationPeriodUnit?: string | null;
        status?: string | null;
      } | null;
    }>;
    slaCoveredCustomerWorkRows?: Array<{
      customer: string;
      cases: number;
      tasks: number;
      scheduled: number;
    }>;
    scheduledSlaRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      assigneeLabel: string;
      taskGroup: string;
      plannedStart?: string | null;
      plannedDue?: string | null;
      slaLabel: string;
    }>;
    slaFirstTimeFixSummary?: {
      eligible: number;
      fixedFirstTime: number;
      rate: number | null;
    };
    slaMtbfRows?: Array<{
      key: string;
      label: string;
      repairs: number;
      deviceCount: number;
      mtbfDays: number | null;
    }>;
    slaMaintenanceComplianceSummary?: {
      devicesDue: number;
      compliantDevices: number;
      rate: number | null;
    };
    slaMaintenanceDeviationRows?: Array<{
      key: string;
      label: string;
      averageDeviationDays: number | null;
      onTimeRate: number | null;
      completed: number;
    }>;
    breakdownAfterMaintenanceRows?: Array<{
      key: string;
      label: string;
      breakdowns: number;
      devices: number;
      averageDaysAfterMaintenance: number | null;
    }>;
    slaCaseDrilldownRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
      modelLabel?: string;
      deviceLabel?: string;
      slaLabel?: string;
      openedAt?: string | null;
      closedAt?: string | null;
      lastActivityAt?: string | null;
      activeTasks: number;
      scheduledTasks: number;
      maintenanceTasks: number;
      repairTasks: number;
      taskGroups?: string[];
      assigneeLabel?: string;
      scheduleState: string;
      nextPlannedAt?: string | null;
    }>;
    slaCaseFilterRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
    }>;
  }>({});
  const [inventoryInsightsData, setInventoryInsightsData] = useState<{
    summary?: {
      stockOnHandValue: number;
      stockInTransitValue: number;
      stockOnOrderValue: number;
      vanStockValue: number;
      varianceValue: number;
      delayedRepairCases: number;
      stockWarehouseCount?: number;
      vanStockWarehouseCount?: number;
      approvedCountSessionCount?: number;
    };
    warehouseValueRows?: Array<{
      warehouseId: string;
      warehouseName: string;
      warehouseType: string;
      userLabel?: string | null;
      skuCount: number;
      quantityOnHand: number;
      quantityInTransit: number;
      quantityOnOrder: number;
      onHandValue: number;
      inTransitValue: number;
      onOrderValue: number;
    }>;
    transitRows?: Array<{
      warehouseId: string;
      warehouseName: string;
      quantityInTransit: number;
      quantityOnOrder: number;
      inTransitValue: number;
      onOrderValue: number;
    }>;
    varianceCategoryRows?: Array<{
      label: string;
      sessionCount: number;
      varianceLineCount: number;
      varianceValue: number;
    }>;
    countSessionRows?: Array<{
      id: string;
      warehouseName: string;
      warehouseType: string;
      countType: string;
      userLabel?: string | null;
      lineCount: number;
      varianceLineCount: number;
      varianceValue: number;
      approvedAt?: string | null;
    }>;
    vanStockRows?: Array<{
      warehouseId: string;
      warehouseName: string;
      userLabel?: string | null;
      skuCount: number;
      quantityOnHand: number;
      onHandValue: number;
    }>;
    delayedPartRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel?: string;
      deviceLabel: string;
      shortageQty: number;
      etaAt?: string | null;
      activeTasks?: number;
      taskGroups?: string[];
      assigneeLabel?: string;
      planStatus?: string;
      detail: string;
    }>;
    inventoryCaseFilterRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
    }>;
    inventoryCaseDrilldownRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
      deviceLabel?: string;
      shortageQty: number;
      etaAt?: string | null;
      detail: string;
      activeTasks: number;
      taskGroups?: string[];
      assigneeLabel?: string;
      planStatus?: string;
    }>;
  }>({});
  const [slaInsightsLoading, setSlaInsightsLoading] = useState(false);
  const [inventoryInsightsLoading, setInventoryInsightsLoading] = useState(false);
  const [metricsData, setMetricsData] = useState<{
    summary?: {
      totalCases: number;
      openCases: number;
      warrantyIn: number;
      warrantyOut: number;
      byStatus: Record<string, number>;
    };
    tasksByDevice?: Array<{
      key: string;
      label: string;
      caseCount: number;
      taskCount: number;
    }>;
    mtbf?: Array<{
      key: string;
      label: string;
      mtbfDays: number | null;
      timeToFirstFailureDays: number | null;
      failureCount: number;
    }>;
    overviewSummary?: {
      totalCases: number;
      openCases: number;
      agedOpenCases: number;
      completedTasks: number;
      pendingSchedule: number;
      upcomingWork: number;
      surveyAveragePercent: number | null;
      completedSurveys: number;
    };
    signals?: Array<{ id: string; tone: 'danger' | 'warning' | 'info'; title: string; detail: string }>;
    taskGroupRows?: Array<{ label: string; count: number }>;
    ageBucketRows?: Array<{ label: string; count: number }>;
    upcomingWorkRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      assigneeLabel: string;
      taskGroup: string;
      plannedStart?: string | null;
      plannedDue?: string | null;
    }>;
    completedTaskGroupRows?: Array<{ label: string; count: number }>;
    customerLoadRows?: Array<{
      id: string;
      customer: string;
      totalCases: number;
      taskCount: number;
      openCases: number;
    }>;
    serviceSummary?: {
      openCases: number;
      activeTasks: number;
      unassignedTasks: number;
      averageOpenAgeDays: number | null;
      scheduledThisWeek: number;
      casesWithQuotes: number;
      plannedTasks: number;
      pendingSchedule: number;
    };
    serviceStatusDistributionRows?: Array<{ label: string; count: number }>;
    serviceUpcomingWorkRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      assigneeLabel: string;
      taskGroup: string;
      plannedStart?: string | null;
      plannedDue?: string | null;
    }>;
    serviceQuoteStatusRows?: Array<{ label: string; count: number }>;
    servicePartsWaitingRows?: Array<{
      id: string;
      caseId?: string;
      caseCode: string;
      customerName: string;
      statusLabel: string;
      etaAt?: string | null;
      detail: string;
    }>;
    serviceUpcomingSlaRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      assigneeLabel: string;
      taskGroup: string;
      plannedStart?: string | null;
      plannedDue?: string | null;
      slaLabel: string;
    }>;
    serviceCaseDrilldownRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
      ageDays?: number | null;
      activeTasks: number;
      taskGroups?: string[];
      assigneeLabel?: string;
      scheduleState: string;
      quoteState: string;
      partsState: string;
      partsDetail?: string;
      partsEtaAt?: string | null;
      hasPartsIssue?: boolean;
      nextPlannedAt?: string | null;
      slaLabel?: string;
    }>;
    serviceCaseFilterRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
    }>;
    customerSummary?: {
      openCases: number;
      scheduledJobs: number;
      casesWithQuotes: number;
      surveyAveragePercent: number | null;
      completedSurveys: number;
      requestToResponseAverageDays: number | null;
      requestToResponseSampleCount: number;
      requestToTaskCloseAverageDays: number | null;
      requestToTaskCloseSampleCount: number;
    };
    customerStatusDistributionRows?: Array<{ label: string; count: number }>;
    customerQuoteStatusRows?: Array<{ label: string; count: number }>;
    customerOpenLoadRows?: Array<{
      id: string;
      customer: string;
      totalCases: number;
      taskCount: number;
      openCases: number;
    }>;
    customerScheduledRows?: Array<{
      customer: string;
      jobs: number;
      cases: number;
      nextPlannedAt?: string | null;
    }>;
    customerCompletedWorkRows?: Array<{
      customer: string;
      tasks: number;
      cases: number;
      latestCompletedAt?: string | null;
    }>;
    customerSurveyRows?: Array<{
      customer: string;
      surveys: number;
      average: number | null;
    }>;
    customerStageTimingRows?: Array<{
      key: string;
      label: string;
      averageDays: number | null;
      sampleCount: number;
    }>;
    customerCaseDrilldownRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
      ageDays?: number | null;
      activeTasks: number;
      scheduledTasks: number;
      scheduleState: string;
      quoteState: string;
      taskGroups?: string[];
      assigneeLabel?: string;
      openedAt?: string | null;
      closedAt?: string | null;
      lastActivityAt?: string | null;
    }>;
    customerCaseFilterRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
    }>;
    reliabilityDeviceRepairMtbfSummary?: {
      averageDays: number | null;
      groupedViews: number;
    };
    reliabilityUserRepairMtbfSummary?: {
      averageDays: number | null;
      technicians: number;
    };
    reliabilityRepairDurationSummary?: {
      averageDays: number | null;
      groupedViews: number;
    };
    reliabilityFirstTimeFixSummary?: {
      eligible: number;
      fixedFirstTime: number;
      rate: number | null;
    };
    reliabilityDeviceRepairMtbfRows?: Array<{
      key: string;
      label: string;
      failures: number;
      deviceCount: number;
      mtbfDays: number | null;
    }>;
    reliabilityUserRepairMtbfRows?: Array<{
      technician: string;
      mtbfDays: number | null;
      repeatRepairs: number;
      repairedCases: number;
    }>;
    reliabilityRepairDurationRows?: Array<{
      key: string;
      label: string;
      cases: number;
      averageDays: number | null;
    }>;
    reliabilityHoldReasonRows?: Array<{ label: string; count: number }>;
    reliabilityRepairWarrantySummary?: {
      total: number;
      inWarranty: number;
      outWarranty: number;
    };
    reliabilityMaintenanceComplianceSummary?: {
      devicesDue: number;
      compliantDevices: number;
      rate: number | null;
    };
    reliabilityCommonFailureModeRows?: Array<{ label: string; count: number }>;
    reliabilityCommonPartUsageRows?: Array<{ label: string; count: number; quantity: number }>;
    reliabilityRepairFrequencyRows?: Array<{
      key: string;
      label: string;
      repairs: number;
      installedBase: number;
      affectedDevices: number;
      percent: number | null;
    }>;
    reliabilityCaseDrilldownRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
      modelLabel?: string;
      deviceLabel?: string;
      openedAt?: string | null;
      closedAt?: string | null;
      durationDays?: number | null;
      activeTasks: number;
      repairTasks: number;
      taskGroups?: string[];
      assigneeLabel?: string;
      warrantyStatus?: string;
    }>;
    reliabilityCaseFilterRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
    }>;
    teamSummary?: {
      assignedBacklog: number;
      totalTaskHours: number | null;
      completedTaskCount: number;
      userMtbfAverage: number | null;
      mtbfTechnicians: number;
      firstTimeFixAverage: number | null;
      firstTimeFixTechnicians: number;
      surveyAveragePercent: number | null;
      completedSurveys: number;
      scheduledUsers: number;
    };
    teamLoadByAssigneeRows?: Array<{
      assignee: string;
      tasks: number;
      scheduled: number;
      pendingSchedule: number;
    }>;
    teamUserTaskHoursRows?: Array<{
      technician: string;
      totalHours: number;
      taskCount: number;
      averageHours: number | null;
    }>;
    teamScheduledUserRows?: Array<{
      technician: string;
      tasks: number;
      customers: number;
      nextPlannedAt?: string | null;
    }>;
    teamSurveyByTechnicianRows?: Array<{
      technician: string;
      surveys: number;
      average: number | null;
    }>;
    teamUserRepairMtbfRows?: Array<{
      technician: string;
      mtbfDays: number | null;
      repeatRepairs: number;
      repairedCases: number;
    }>;
    teamUserFirstTimeFixRows?: Array<{
      technician: string;
      eligible: number;
      fixed: number;
      rate: number | null;
    }>;
    teamRepairPerformanceRows?: Array<{
      technician: string;
      focusLabel: string;
      repairs: number;
      mtbfDays: number | null;
      firstTimeFixRate: number | null;
      eligible: number;
    }>;
    teamWorkloadDetailRows?: Array<{
      technician: string;
      backlog: number;
      scheduled: number;
      pendingSchedule: number;
      hours: number;
    }>;
    teamCaseFilterRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
    }>;
    teamCaseDrilldownRows?: Array<{
      id: string;
      caseId: string;
      caseCode: string;
      customerName: string;
      statusKey?: string;
      statusLabel: string;
      modelLabel?: string;
      deviceLabel?: string;
      openedAt?: string | null;
      closedAt?: string | null;
      lastActivityAt?: string | null;
      activeTasks: number;
      completedTasks: number;
      scheduledTasks: number;
      pendingSchedule: number;
      taskGroups?: string[];
      assigneeLabel?: string;
    }>;
    filterRows?: Array<{
      customerId?: string | null;
      customerName: string;
      modelId?: string | null;
      modelLabel: string;
      deviceId?: string | null;
      deviceLabel: string;
      serialNumber?: string | null;
    }>;
  }>({});
  const isCompactDashboard = dashboardViewportWidth <= DASHBOARD_COMPACT_BREAKPOINT;
  const isTabletDashboard = dashboardViewportWidth <= DASHBOARD_TABLET_BREAKPOINT;
  const isNarrowDashboard = dashboardViewportWidth <= DASHBOARD_NARROW_BREAKPOINT;
  const handleDashboardTabSelect = useCallback((tab: DashboardTab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setDashboardViewportWidth(getDashboardViewportWidth());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isTabletDashboard) {
      setSidebarOpen(false);
    }
  }, [isTabletDashboard]);

  useEffect(() => {
    if (isTabletDashboard) {
      setSidebarOpen(false);
    }
  }, [activeTab, isTabletDashboard]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);
  const [metricsDashboard, setMetricsDashboard] = useState<{
    id?: string;
    name?: string;
    config?: MetricsDashboardConfig;
  } | null>(null);
  const [metricsDashboardReady, setMetricsDashboardReady] = useState(false);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [metricsConfigDraft, setMetricsConfigDraft] = useState<MetricsDashboardConfig>(
    () => buildDefaultMetricsDashboardConfig()
  );
  const [assignmentPickerCaseId, setAssignmentPickerCaseId] = useState<string | null>(null);
  const [assigningCaseId, setAssigningCaseId] = useState<string | null>(null);
  const [assignmentError, setAssignmentError] = useState<{ caseId: string; message: string } | null>(null);
  const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
  const [editProblemSummary, setEditProblemSummary] = useState('');
  const [editProblemDetails, setEditProblemDetails] = useState<string[]>([]);
  const [savingProblemId, setSavingProblemId] = useState<string | null>(null);
  const [problemEditError, setProblemEditError] = useState<string | null>(null);
  const [customerResourceModal, setCustomerResourceModal] = useState<{ customerId: string; mode: 'devices' | 'cases' | 'contacts' | 'locations' } | null>(null);
  const [customerQuickViewRequest, setCustomerQuickViewRequest] = useState<{ customerId: string; requestKey: number } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingModel, setEditingModel] = useState({
    modelNumber: '',
    manufacturer: '',
    serialNumber: '',
    assetNumber: '',
    deviceId: ''
  });
  const previousTab = useRef(activeTab);
  const customerDetailRequestRef = useRef<Record<string, Promise<Customer | null> | undefined>>({});
  const [selectedFeedbackModel, setSelectedFeedbackModel] = useState<string>('all');
  const [selectedQualityModel, setSelectedQualityModel] = useState<string>('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    primaryContactName: '',
    primaryContactRole: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    notes: ''
  });
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [editCustomer, setEditCustomer] = useState({
    companyName: ''
  });
  const [savingCustomerId, setSavingCustomerId] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSort, setCustomerSort] = useState<
    'recent' | 'nameAsc' | 'nameDesc' | 'deviceCountDesc' | 'caseCountDesc' | 'locationCountDesc'
  >('recent');
  const [customerStatusFilter, setCustomerStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [customerQuickFilter, setCustomerQuickFilter] = useState('all');
  const [customerLocationFilter, setCustomerLocationFilter] = useState('all');
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(DEFAULT_CUSTOMERS_PAGE_SIZE);
  const [locationFormCustomerId, setLocationFormCustomerId] = useState<string | null>(null);
  const [locationForm, setLocationForm] = useState({
    id: null as string | null,
    name: '',
    isPrimary: false,
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    parentLocationId: ''
  });
  const [savingLocation, setSavingLocation] = useState(false);
const [contactFormCustomerId, setContactFormCustomerId] = useState<string | null>(null);
const [contactForm, setContactForm] = useState({
    id: null as string | null,
    fullName: '',
    email: '',
    phone: '',
    role: '',
    portalAccessMode: 'FULL' as 'FULL' | 'LITE',
    locationId: '',
    locationIds: [] as string[],
    isPrimary: false,
    isActive: true
  });
const [savingContact, setSavingContact] = useState(false);
const [expandedContactIds, setExpandedContactIds] = useState<Record<string, boolean>>({});
const [portalContactActionKey, setPortalContactActionKey] = useState<string | null>(null);
const [portalInvitePreview, setPortalInvitePreview] = useState<PortalInvitePreview | null>(null);
const [portalInviteCopied, setPortalInviteCopied] = useState<'username' | 'password' | 'url' | null>(null);
const locationFormRef = useRef<HTMLDivElement | null>(null);
const contactFormRef = useRef<HTMLDivElement | null>(null);
const [stagedDeviceLocationById, setStagedDeviceLocationById] = useState<Record<string, string>>({});
const [armedDeviceActionById, setArmedDeviceActionById] = useState<Record<string, 'deactivate' | 'move'>>({});
const [deviceUpdateInFlight, setDeviceUpdateInFlight] = useState<string | null>(null);
const initialLoadRef = useRef(false);

useEffect(() => {
  if (canManageCustomers) return;
  setShowAddCustomerForm(false);
  setEditingCustomerId(null);
  setLocationFormCustomerId(null);
  setContactFormCustomerId(null);
}, [canManageCustomers]);

useEffect(() => {
  if (!locationFormCustomerId) return;
  const target = locationFormRef.current;
  if (!target) return;
  requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}, [locationFormCustomerId, locationForm.id, locationForm.parentLocationId]);

useEffect(() => {
  if (!contactFormCustomerId) return;
  const target = contactFormRef.current;
  if (!target) return;
  requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}, [contactFormCustomerId, contactForm.id, contactForm.locationId]);

const [caseDetailModal, setCaseDetailModal] = useState<Case | null>(null);
const [caseDetailLoading, setCaseDetailLoading] = useState(false);
const [caseAssigneeOptions, setCaseAssigneeOptions] = useState<CaseAssigneeOption[]>([]);
const [caseAssigneesLoading, setCaseAssigneesLoading] = useState(false);
const [caseAssigneesError, setCaseAssigneesError] = useState('');
const [caseWorkflowDraftById, setCaseWorkflowDraftById] = useState<
  Record<
    string,
    { status: UiStatus; priority: string; assigneeIds: string[]; taskGroup: TaskGroupCode | '' }
  >
>({});
const [caseWorkflowSavingById, setCaseWorkflowSavingById] = useState<Record<string, boolean>>({});
const [caseWorkflowClosingById, setCaseWorkflowClosingById] = useState<Record<string, boolean>>({});
const [caseWorkflowResolvingById, setCaseWorkflowResolvingById] = useState<Record<string, boolean>>({});
const [caseWorkflowErrorById, setCaseWorkflowErrorById] = useState<Record<string, string>>({});
const [caseQuotePreview, setCaseQuotePreview] = useState<CaseQuoteRecord | null>(null);
const [caseQuotePreviewContext, setCaseQuotePreviewContext] = useState<Case | null>(null);
const [caseServiceReportPreview, setCaseServiceReportPreview] = useState<CaseServiceReport | null>(null);
const [caseServiceReportPreviewContext, setCaseServiceReportPreviewContext] = useState<Case | null>(null);
const [caseServiceReportPreviewLoadingId, setCaseServiceReportPreviewLoadingId] = useState<string | null>(null);
const [caseQuotesLoading, setCaseQuotesLoading] = useState(false);
const [caseQuotesByApi, setCaseQuotesByApi] = useState<CaseQuoteRecord[]>([]);
const [selectedCaseQuotesLoading, setSelectedCaseQuotesLoading] = useState(false);
const [selectedCaseQuotes, setSelectedCaseQuotes] = useState<CaseQuoteRecord[]>([]);
const [quoteTemplatesById, setQuoteTemplatesById] = useState<Record<string, QuoteTemplateLayout>>({});
const caseCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
const focusedCaseNavKeyRef = useRef('');
const caseDetailAbortRef = useRef<AbortController | null>(null);
const caseReturnTabAfterCloseRef = useRef<DashboardTab | null>(null);
const quoteTemplatesRequestedRef = useRef(false);

const findScrollContainer = useCallback((element: HTMLElement | null): HTMLElement | null => {
  let current = element?.parentElement || null;
  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const canScroll = overflowY === 'auto' || overflowY === 'scroll';
    if (canScroll && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}, []);

const scrollCaseCardIntoView = useCallback((caseId: string) => {
  const card = caseCardRefs.current[caseId];
  if (!card) return;

  const topOffset = 12;
  const scrollContainer = findScrollContainer(card);

  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const nextTop = scrollContainer.scrollTop + (cardRect.top - containerRect.top) - topOffset;
    scrollContainer.scrollTo({ top: Math.max(nextTop, 0), behavior: 'smooth' });
    return;
  }

  const pageTop = card.getBoundingClientRect().top + window.scrollY - topOffset;
  window.scrollTo({ top: Math.max(pageTop, 0), behavior: 'smooth' });
}, [findScrollContainer]);
const modalCaseAnalysisMeta = useMemo(
  () => {
    if (!caseDetailModal) return null;
    const lifecycleTask =
      caseDetailModal.displayTask ||
      caseDetailModal.activeTask ||
      getActiveCaseTask(caseDetailModal.tasks || []);
    const taskGroup = resolveTaskGroupCode(lifecycleTask?.type?.code || '');
    return taskGroup === 'REPAIR' ? getTsfadStatusMeta(caseDetailModal) : null;
  },
  [caseDetailModal]
);
const modalTimelineEntries = useMemo(() => {
  if (!caseDetailModal) return [];

  const baseEvents: CaseEvent[] = Array.isArray(caseDetailModal.events)
    ? caseDetailModal.events.map(event => ({
        id: event.id,
        eventType: event.eventType,
        actorId: event.actorId ?? null,
        actorName: event.actorName ?? null,
        occurredAt: event.occurredAt ?? null,
        metadata: event.metadata ?? null,
        notes: (event as any).notes ?? null,
        isCustomerVisible: (event as any).isCustomerVisible ?? false,
        createdAt: (event as any).createdAt ?? null
      }))
    : [];
  const timeline: CaseEvent[] = [...baseEvents];
  const existing = new Set(
    baseEvents.map(event => `${event.eventType ?? 'UNKNOWN'}-${event.occurredAt ?? event.id ?? Math.random()}`)
  );

  if (Array.isArray(caseDetailModal.tsfadAnalyses)) {
    caseDetailModal.tsfadAnalyses.forEach(analysis => {
      if (analysis?.createdAt) {
        const createdKey = `TSFAD_ANALYSIS_GENERATED-${analysis.id}-${analysis.createdAt}`;
        if (!existing.has(createdKey)) {
          const createdBy = analysis.createdBy ?? 'TSFAD Engine';
          timeline.push({
            id: createdKey,
            eventType: 'TSFAD_ANALYSIS_GENERATED',
            actorName: createdBy,
            actorId: analysis.createdBy ?? createdBy,
            occurredAt: analysis.createdAt,
            notes: null,
            metadata: {},
            isCustomerVisible: false,
            createdAt: analysis.createdAt ?? null
          });
          existing.add(createdKey);
        }
      }
      if (analysis?.acceptedAt) {
        const acceptedKey = `TSFAD_ANALYSIS_ACCEPTED-${analysis.id}-${analysis.acceptedAt}`;
        if (!existing.has(acceptedKey)) {
          const acceptedBy = analysis.acceptedBy ?? 'Technician';
          timeline.push({
            id: acceptedKey,
            eventType: 'TSFAD_ANALYSIS_ACCEPTED',
            actorName: acceptedBy,
            actorId: analysis.acceptedBy ?? acceptedBy,
            occurredAt: analysis.acceptedAt,
            notes: null,
            metadata: {},
            isCustomerVisible: false,
            createdAt: analysis.acceptedAt ?? null
          });
          existing.add(acceptedKey);
        }
      }
    });
  }

  return buildMeaningfulTimeline(timeline, 'tech').sort((a, b) => {
    const timeA = a?.occurredAt ? new Date(a.occurredAt).getTime() : 0;
    const timeB = b?.occurredAt ? new Date(b.occurredAt).getTime() : 0;
    return timeA - timeB;
  });
}, [caseDetailModal]);

const fetchQuotesForAnalysisIds = useCallback(async (analysisIds: string[]): Promise<CaseQuoteRecord[]> => {
  if (!Array.isArray(analysisIds) || analysisIds.length === 0) return [];
  const responses = await Promise.allSettled(
    analysisIds.map(async id => ({
      analysisId: id,
      payload: await tsfadApi.listQuotes(id)
    }))
  );
  const aggregated: CaseQuoteRecord[] = [];

  responses.forEach(result => {
    if (result.status !== 'fulfilled') return;
    const { analysisId, payload } = result.value;
    const quotes = Array.isArray(payload?.quotes) ? payload.quotes : [];
    quotes.forEach((quote: any, index: number) => {
      aggregated.push({
        analysisId,
        source: 'TSFAD',
        quoteId: String(quote?.quoteId || `quote-${index}`),
        status: typeof quote?.status === 'string' ? quote.status : null,
        templateId: typeof quote?.templateId === 'string' ? quote.templateId : null,
        title: String(quote?.title || 'TSFAD Quote'),
        description: typeof quote?.description === 'string' ? quote.description : '',
        notes: typeof quote?.notes === 'string' ? quote.notes : '',
        templateName: typeof quote?.templateName === 'string' ? quote.templateName : null,
        savedAt: typeof quote?.savedAt === 'string' ? quote.savedAt : null,
        actorName: typeof quote?.actorName === 'string' ? quote.actorName : null,
        totals: quote?.totals && typeof quote.totals === 'object' ? quote.totals : {},
        totalSettings:
          quote?.totalSettings && typeof quote.totalSettings === 'object'
            ? normalizeQuoteTotalSettings(quote.totalSettings)
            : quote?.totals?.totalSettings && typeof quote.totals.totalSettings === 'object'
            ? normalizeQuoteTotalSettings(quote.totals.totalSettings)
            : null,
        responseActions: Array.isArray(quote?.responseActions)
          ? quote.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
          : Array.isArray(quote?.totals?.approvalSettings?.responseActions)
            ? quote.totals.approvalSettings.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
            : undefined,
        approvalPlacement:
          typeof quote?.approvalPlacement === 'string' && quote.approvalPlacement.trim()
            ? quote.approvalPlacement
            : typeof quote?.totals?.approvalSettings?.approvalPlacement === 'string'
              ? quote.totals.approvalSettings.approvalPlacement
              : undefined,
        approvalFooterSlots:
          Array.isArray(quote?.approvalFooterSlots)
            ? quote.approvalFooterSlots
            : Array.isArray(quote?.totals?.approvalSettings?.approvalFooterSlots)
              ? quote.totals.approvalSettings.approvalFooterSlots
              : undefined,
        requireSignature:
          typeof quote?.requireSignature === 'boolean'
            ? quote.requireSignature
            : typeof quote?.totals?.approvalSettings?.requireSignature === 'boolean'
              ? quote.totals.approvalSettings.requireSignature
              : undefined,
        signatureLabel:
          typeof quote?.signatureLabel === 'string' && quote.signatureLabel.trim()
            ? quote.signatureLabel
            : typeof quote?.totals?.approvalSettings?.signatureLabel === 'string'
              ? quote.totals.approvalSettings.signatureLabel
              : undefined,
        requireTechnicianSignature:
          typeof quote?.requireTechnicianSignature === 'boolean'
            ? quote.requireTechnicianSignature
            : typeof quote?.totals?.approvalSettings?.requireTechnicianSignature === 'boolean'
              ? quote.totals.approvalSettings.requireTechnicianSignature
              : undefined,
        technicianSignatureLabel:
          typeof quote?.technicianSignatureLabel === 'string' && quote.technicianSignatureLabel.trim()
            ? quote.technicianSignatureLabel
            : typeof quote?.totals?.approvalSettings?.technicianSignatureLabel === 'string'
              ? quote.totals.approvalSettings.technicianSignatureLabel
              : undefined,
        lineItems: Array.isArray(quote?.lineItems) ? quote.lineItems : [],
        customerApprovalName:
          typeof quote?.customerApprovalName === 'string' ? quote.customerApprovalName : '',
        customerApprovalSignature:
          typeof quote?.customerApprovalSignature === 'string' ? quote.customerApprovalSignature : '',
        customerDecision:
          typeof quote?.customerDecision === 'string'
            ? (quote.customerDecision.toUpperCase() as 'ACCEPT' | 'DENY' | 'ADJUST')
            : null,
        decisionAt: typeof quote?.decisionAt === 'string' ? quote.decisionAt : null,
        decisionBy: typeof quote?.decisionBy === 'string' ? quote.decisionBy : null,
        decisionNote: typeof quote?.decisionNote === 'string' ? quote.decisionNote : '',
        partsStatus:
          quote?.partsStatus && typeof quote.partsStatus === 'object'
            ? {
                code:
                  typeof quote.partsStatus.code === 'string' ? quote.partsStatus.code : undefined,
                label:
                  typeof quote.partsStatus.label === 'string' ? quote.partsStatus.label : undefined,
                tone:
                  typeof quote.partsStatus.tone === 'string' ? quote.partsStatus.tone : undefined,
                etaAt: typeof quote.partsStatus.etaAt === 'string' ? quote.partsStatus.etaAt : null,
                requestedQty: Number(quote.partsStatus.requestedQty || 0),
                availableQty: Number(quote.partsStatus.availableQty || 0),
                remainingQty: Number(quote.partsStatus.remainingQty || 0),
                transferShortQty: Number(quote.partsStatus.transferShortQty || 0),
                pendingDispatchQty: Number(quote.partsStatus.pendingDispatchQty || 0),
                inTransitQty: Number(quote.partsStatus.inTransitQty || 0),
                hasPendingReplenishmentApproval: Boolean(
                  quote.partsStatus.hasPendingReplenishmentApproval
                ),
                hasApprovedReplenishment: Boolean(quote.partsStatus.hasApprovedReplenishment),
                detail:
                  typeof quote.partsStatus.detail === 'string' ? quote.partsStatus.detail : undefined
              }
            : null
      });
    });
  });

  return Array.from(new Map(aggregated.map(item => [item.quoteId, item])).values()).sort((a, b) => {
    const at = a.savedAt ? new Date(a.savedAt).getTime() : 0;
    const bt = b.savedAt ? new Date(b.savedAt).getTime() : 0;
    return bt - at;
  });
}, []);

const fetchWorkflowQuotesForCaseId = useCallback(async (caseId: string): Promise<CaseQuoteRecord[]> => {
  const resolvedCaseId = String(caseId || '').trim();
  if (!resolvedCaseId) return [];
  const payload = await caseApi.listSafetyWorkflowQuotes(resolvedCaseId);
  const quotes = Array.isArray(payload?.quotes) ? payload.quotes : [];
  return quotes
    .map((quote: any, index: number): CaseQuoteRecord => ({
      source: 'WORKFLOW' as const,
      analysisId: null,
      quoteId: String(quote?.quoteId || `workflow-quote-${index}`),
      status: typeof quote?.status === 'string' ? quote.status : null,
      templateId: typeof quote?.templateId === 'string' ? quote.templateId : null,
      title: String(quote?.title || 'Workflow Quote'),
      description: typeof quote?.description === 'string' ? quote.description : '',
      notes: typeof quote?.notes === 'string' ? quote.notes : '',
      templateName: typeof quote?.templateName === 'string' ? quote.templateName : null,
      savedAt: typeof quote?.savedAt === 'string' ? quote.savedAt : null,
      actorName: typeof quote?.actorName === 'string' ? quote.actorName : null,
      totals: quote?.totals && typeof quote.totals === 'object' ? quote.totals : {},
      totalSettings:
        quote?.totalSettings && typeof quote.totalSettings === 'object'
          ? normalizeQuoteTotalSettings(quote.totalSettings)
          : quote?.totals?.totalSettings && typeof quote.totals.totalSettings === 'object'
          ? normalizeQuoteTotalSettings(quote.totals.totalSettings)
          : null,
      responseActions: Array.isArray(quote?.responseActions)
        ? quote.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
        : Array.isArray(quote?.totals?.approvalSettings?.responseActions)
          ? quote.totals.approvalSettings.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
          : undefined,
      approvalPlacement:
        typeof quote?.approvalPlacement === 'string' && quote.approvalPlacement.trim()
          ? quote.approvalPlacement
          : typeof quote?.totals?.approvalSettings?.approvalPlacement === 'string'
            ? quote.totals.approvalSettings.approvalPlacement
            : undefined,
      approvalFooterSlots:
        Array.isArray(quote?.approvalFooterSlots)
          ? quote.approvalFooterSlots
          : Array.isArray(quote?.totals?.approvalSettings?.approvalFooterSlots)
            ? quote.totals.approvalSettings.approvalFooterSlots
            : undefined,
      requireSignature:
        typeof quote?.requireSignature === 'boolean'
          ? quote.requireSignature
          : typeof quote?.totals?.approvalSettings?.requireSignature === 'boolean'
            ? quote.totals.approvalSettings.requireSignature
            : undefined,
      signatureLabel:
        typeof quote?.signatureLabel === 'string' && quote.signatureLabel.trim()
          ? quote.signatureLabel
          : typeof quote?.totals?.approvalSettings?.signatureLabel === 'string'
            ? quote.totals.approvalSettings.signatureLabel
            : undefined,
      requireTechnicianSignature:
        typeof quote?.requireTechnicianSignature === 'boolean'
          ? quote.requireTechnicianSignature
          : typeof quote?.totals?.approvalSettings?.requireTechnicianSignature === 'boolean'
            ? quote.totals.approvalSettings.requireTechnicianSignature
            : undefined,
      technicianSignatureLabel:
        typeof quote?.technicianSignatureLabel === 'string' && quote.technicianSignatureLabel.trim()
          ? quote.technicianSignatureLabel
          : typeof quote?.totals?.approvalSettings?.technicianSignatureLabel === 'string'
            ? quote.totals.approvalSettings.technicianSignatureLabel
            : undefined,
      lineItems: Array.isArray(quote?.lineItems) ? quote.lineItems : [],
      customerApprovalName:
        typeof quote?.customerApprovalName === 'string' ? quote.customerApprovalName : '',
      customerApprovalSignature:
        typeof quote?.customerApprovalSignature === 'string' ? quote.customerApprovalSignature : '',
      customerDecision:
        typeof quote?.customerDecision === 'string'
          ? (quote.customerDecision.toUpperCase() as 'ACCEPT' | 'DENY' | 'ADJUST')
          : null,
      decisionAt: typeof quote?.decisionAt === 'string' ? quote.decisionAt : null,
      decisionBy: typeof quote?.decisionBy === 'string' ? quote.decisionBy : null,
      decisionNote: typeof quote?.decisionNote === 'string' ? quote.decisionNote : '',
      partsStatus:
        quote?.partsStatus && typeof quote.partsStatus === 'object'
          ? {
              code: typeof quote.partsStatus.code === 'string' ? quote.partsStatus.code : undefined,
              label: typeof quote.partsStatus.label === 'string' ? quote.partsStatus.label : undefined,
              tone: typeof quote.partsStatus.tone === 'string' ? quote.partsStatus.tone : undefined,
              etaAt: typeof quote.partsStatus.etaAt === 'string' ? quote.partsStatus.etaAt : null,
              requestedQty: Number(quote.partsStatus.requestedQty || 0),
              availableQty: Number(quote.partsStatus.availableQty || 0),
              remainingQty: Number(quote.partsStatus.remainingQty || 0),
              transferShortQty: Number(quote.partsStatus.transferShortQty || 0),
              pendingDispatchQty: Number(quote.partsStatus.pendingDispatchQty || 0),
              inTransitQty: Number(quote.partsStatus.inTransitQty || 0),
              hasPendingReplenishmentApproval: Boolean(
                quote.partsStatus.hasPendingReplenishmentApproval
              ),
              hasApprovedReplenishment: Boolean(quote.partsStatus.hasApprovedReplenishment),
              detail: typeof quote.partsStatus.detail === 'string' ? quote.partsStatus.detail : undefined
            }
          : null
    }))
    .sort((a: CaseQuoteRecord, b: CaseQuoteRecord) => {
      const at = a.savedAt ? new Date(a.savedAt).getTime() : 0;
      const bt = b.savedAt ? new Date(b.savedAt).getTime() : 0;
      return bt - at;
    });
}, []);

useEffect(() => {
  const shouldLoadTemplates = Boolean(caseQuotePreview || caseDetailModal || selectedCase);
  if (!shouldLoadTemplates || quoteTemplatesRequestedRef.current) return;

  quoteTemplatesRequestedRef.current = true;
  let cancelled = false;

  const loadQuoteTemplates = async () => {
    try {
      const payload = await formsApi.getAll();
      if (cancelled) return;
      const templates: any[] = Array.isArray(payload?.quotes) ? payload.quotes : [];
      const next: Record<string, QuoteTemplateLayout> = templates.reduce((acc, rawTemplate: any) => {
        const template = normaliseQuoteTemplateLayout(rawTemplate);
        if (template.id) {
          acc[template.id] = template;
        }
        return acc;
      }, {});
      setQuoteTemplatesById(next);
    } catch (error) {
      if (!cancelled) {
        console.error('Failed to load quote templates for case preview', error);
      }
    }
  };

  loadQuoteTemplates();
  return () => {
    cancelled = true;
  };
}, [caseDetailModal, caseQuotePreview, selectedCase]);

useEffect(() => {
  let cancelled = false;
  const loadSelectedCaseQuotes = async () => {
    if (!selectedCase) {
      setSelectedCaseQuotes([]);
      return;
    }
    const analysisIds = Array.from(
      new Set(
        (Array.isArray(selectedCase.tsfadAnalyses) ? selectedCase.tsfadAnalyses : [])
          .map(analysis => String(analysis?.id || '').trim())
          .filter(Boolean)
      )
    );
    setSelectedCaseQuotesLoading(true);
    try {
      const [tsfadQuotes, workflowQuotes] = await Promise.all([
        analysisIds.length > 0 ? fetchQuotesForAnalysisIds(analysisIds) : Promise.resolve([]),
        fetchWorkflowQuotesForCaseId(String(selectedCase.id || ''))
      ]);
      if (cancelled) return;
      const quotes = [...tsfadQuotes, ...workflowQuotes].sort((a, b) => {
        const at = a.savedAt ? new Date(a.savedAt).getTime() : 0;
        const bt = b.savedAt ? new Date(b.savedAt).getTime() : 0;
        return bt - at;
      });
      setSelectedCaseQuotes(quotes);
    } catch (error) {
      if (!cancelled) {
        console.error('Failed to load selected case quotes', error);
        setSelectedCaseQuotes([]);
      }
    } finally {
      if (!cancelled) {
        setSelectedCaseQuotesLoading(false);
      }
    }
  };

  loadSelectedCaseQuotes();
  return () => {
    cancelled = true;
  };
}, [fetchQuotesForAnalysisIds, fetchWorkflowQuotesForCaseId, selectedCase]);

useEffect(() => {
  let cancelled = false;
  const loadQuotesFromApi = async () => {
    if (!caseDetailModal) {
      setCaseQuotesByApi([]);
      return;
    }
    const analysisIds = Array.from(
      new Set(
        (Array.isArray(caseDetailModal.tsfadAnalyses) ? caseDetailModal.tsfadAnalyses : [])
          .map(analysis => String(analysis?.id || '').trim())
          .filter(Boolean)
      )
    );

    setCaseQuotesLoading(true);
    try {
      const [tsfadQuotes, workflowQuotes] = await Promise.all([
        analysisIds.length > 0 ? fetchQuotesForAnalysisIds(analysisIds) : Promise.resolve([]),
        fetchWorkflowQuotesForCaseId(String(caseDetailModal.id || ''))
      ]);
      if (cancelled) return;
      const quotes = [...tsfadQuotes, ...workflowQuotes].sort((a, b) => {
        const at = a.savedAt ? new Date(a.savedAt).getTime() : 0;
        const bt = b.savedAt ? new Date(b.savedAt).getTime() : 0;
        return bt - at;
      });
      setCaseQuotesByApi(quotes);
    } catch (error) {
      if (!cancelled) {
        console.error('Failed to load case quotes from API', error);
        setCaseQuotesByApi([]);
      }
    } finally {
      if (!cancelled) {
        setCaseQuotesLoading(false);
      }
    }
  };

  loadQuotesFromApi();
  return () => {
    cancelled = true;
  };
}, [caseDetailModal, fetchQuotesForAnalysisIds, fetchWorkflowQuotesForCaseId]);

const modalCaseQuotes = useMemo<CaseQuoteRecord[]>(() => {
  if (caseQuotesByApi.length > 0) {
    return caseQuotesByApi;
  }
  if (!caseDetailModal || !Array.isArray(caseDetailModal.events)) return [];
  return caseDetailModal.events
    .filter(event => String(event?.eventType || '').toUpperCase() === 'TSFAD_QUOTE_SAVED')
    .map(event => {
      const rawMetadata = event?.metadata;
      const meta =
        typeof rawMetadata === 'string'
          ? (() => {
              try {
                return JSON.parse(rawMetadata) as Record<string, any>;
              } catch {
                return {};
              }
            })()
          : ((rawMetadata || {}) as Record<string, any>);
      const quote = meta?.quote && typeof meta.quote === 'object' ? meta.quote : null;
      if (!quote) return null;
      return {
        quoteId: String(quote.quoteId || event.id),
        status: typeof quote.status === 'string' ? quote.status : null,
        templateId: typeof quote.templateId === 'string' ? quote.templateId : null,
        title: String(quote.title || 'TSFAD Quote'),
        description: typeof quote.description === 'string' ? quote.description : '',
        notes: typeof quote.notes === 'string' ? quote.notes : '',
        templateName: typeof quote.templateName === 'string' ? quote.templateName : null,
        savedAt: (event?.occurredAt || quote.savedAt || null) as string | null,
        actorName: (event?.actorName || null) as string | null,
        totals: quote.totals && typeof quote.totals === 'object' ? quote.totals : {},
        totalSettings:
          quote.totalSettings && typeof quote.totalSettings === 'object'
            ? normalizeQuoteTotalSettings(quote.totalSettings)
            : quote.totals?.totalSettings && typeof quote.totals.totalSettings === 'object'
            ? normalizeQuoteTotalSettings(quote.totals.totalSettings)
            : null,
        responseActions: Array.isArray(quote.responseActions)
          ? quote.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
          : Array.isArray(quote.totals?.approvalSettings?.responseActions)
            ? quote.totals.approvalSettings.responseActions.map((action: any) => String(action || '').trim()).filter(Boolean)
            : undefined,
        approvalPlacement:
          typeof quote.approvalPlacement === 'string' && quote.approvalPlacement.trim()
            ? quote.approvalPlacement
            : typeof quote.totals?.approvalSettings?.approvalPlacement === 'string'
              ? quote.totals.approvalSettings.approvalPlacement
              : undefined,
        approvalFooterSlots:
          Array.isArray(quote.approvalFooterSlots)
            ? quote.approvalFooterSlots
            : Array.isArray(quote.totals?.approvalSettings?.approvalFooterSlots)
              ? quote.totals.approvalSettings.approvalFooterSlots
              : undefined,
        requireSignature:
          typeof quote.requireSignature === 'boolean'
            ? quote.requireSignature
            : typeof quote.totals?.approvalSettings?.requireSignature === 'boolean'
              ? quote.totals.approvalSettings.requireSignature
              : undefined,
        signatureLabel:
          typeof quote.signatureLabel === 'string' && quote.signatureLabel.trim()
            ? quote.signatureLabel
            : typeof quote.totals?.approvalSettings?.signatureLabel === 'string'
              ? quote.totals.approvalSettings.signatureLabel
              : undefined,
        requireTechnicianSignature:
          typeof quote.requireTechnicianSignature === 'boolean'
            ? quote.requireTechnicianSignature
            : typeof quote.totals?.approvalSettings?.requireTechnicianSignature === 'boolean'
              ? quote.totals.approvalSettings.requireTechnicianSignature
              : undefined,
        technicianSignatureLabel:
          typeof quote.technicianSignatureLabel === 'string' && quote.technicianSignatureLabel.trim()
            ? quote.technicianSignatureLabel
            : typeof quote.totals?.approvalSettings?.technicianSignatureLabel === 'string'
              ? quote.totals.approvalSettings.technicianSignatureLabel
              : undefined,
        lineColumns: normalizeQuoteLineColumns((quote as any).lineColumns || quote.totals?.lineColumns),
        lineItems: Array.isArray(quote.lineItems) ? quote.lineItems : [],
        customerApprovalName:
          typeof quote.customerApprovalName === 'string' ? quote.customerApprovalName : '',
        customerApprovalSignature:
          typeof quote.customerApprovalSignature === 'string' ? quote.customerApprovalSignature : '',
        customerDecision: null,
        decisionAt: null,
        decisionBy: null,
        decisionNote: ''
      } as CaseQuoteRecord;
    })
    .filter((value): value is CaseQuoteRecord => Boolean(value))
    .sort((a, b) => {
      const at = a.savedAt ? new Date(a.savedAt).getTime() : 0;
      const bt = b.savedAt ? new Date(b.savedAt).getTime() : 0;
      return bt - at;
    });
}, [caseDetailModal, caseQuotesByApi]);

const caseQuoteTemplate = useMemo(() => {
  if (!caseQuotePreview) return null;
  if (caseQuotePreview.templateId && quoteTemplatesById[caseQuotePreview.templateId]) {
    return quoteTemplatesById[caseQuotePreview.templateId];
  }
  const templates = Object.values(quoteTemplatesById);
  if (templates.length === 0) return null;
  const byName = templates.find(
    template => template.name && template.name === (caseQuotePreview.templateName || '')
  );
  if (byName) return byName;
  return templates.find(template => template.isPrimary) || templates[0] || null;
}, [caseQuotePreview, quoteTemplatesById]);

const caseQuotePreviewHeaderBlocks = useMemo(() => {
  if (!caseQuotePreview || !caseQuoteTemplate) return [];
  const contextCase = caseQuotePreviewContext || caseDetailModal || selectedCase || null;
  const primaryContact =
    contextCase?.customer?.contacts?.find?.((entry: any) => entry?.isPrimary) ||
    contextCase?.customer?.contacts?.[0] ||
    null;
  const primaryTask =
    Array.isArray(contextCase?.tasks) && (contextCase?.tasks?.length || 0) > 0
      ? contextCase?.tasks?.[0]
      : null;
  const context = {
    customer: {
      companyName: contextCase?.customer?.companyName || (contextCase?.customer as any)?.company || '',
      contact: {
        fullName: primaryContact?.fullName || '',
        email: primaryContact?.email || '',
        phone: primaryContact?.phone || ''
      },
      location: {
        name:
          (contextCase?.device as any)?.location?.name ||
          (contextCase as any)?.location?.name ||
          ''
      }
    },
    device: {
      modelNumber: contextCase?.device?.modelNumber || '',
      serialNumber: contextCase?.device?.serialNumber || '',
      manufacturer: contextCase?.device?.manufacturer || '',
      assetNumber: contextCase?.device?.assetNumber || ''
    },
    case: {
      caseNumber: contextCase?.caseCode || contextCase?.caseNumber || contextCase?.id || '',
      priority: contextCase?.priority || '',
      status: contextCase?.status || ''
    },
    task: {
      taskType: primaryTask?.type?.label || '',
      status: primaryTask?.status || '',
      assignedUserName:
        primaryTask?.assignees?.[0]?.fullName ||
        primaryTask?.assignees?.[0]?.username ||
        ''
    },
    quote: {
      title: caseQuotePreview.title || '',
      total: `$${Number(caseQuotePreview?.totals?.grandTotal || 0).toFixed(2)}`
    }
  } as Record<string, any>;

  const byId = caseQuoteTemplate.infoBlocks.reduce<Record<string, QuoteTemplateInfoBlock>>((acc, block) => {
    acc[block.id] = block;
    return acc;
  }, {});

  const orderedSlotIds = QUOTE_HEADER_SLOT_IDS.filter(slotId => {
    if (caseQuoteTemplate.headerRows === 1) {
      return slotId.startsWith('h1');
    }
    return slotId.startsWith('h1') || slotId.startsWith('h2');
  });

  return orderedSlotIds
    .map(slotId => ({
      slotId,
      blockId: caseQuoteTemplate.headerAssignments?.[slotId] || null
    }))
    .filter(({ blockId }) => Boolean(blockId))
    .map(({ slotId, blockId }) =>
      byId[String(blockId)] ? ({ slotId, block: byId[String(blockId)] }) : null
    )
      .filter((entry): entry is { slotId: (typeof QUOTE_HEADER_SLOT_IDS)[number]; block: QuoteTemplateInfoBlock } =>
        Boolean(entry && entry.block && entry.block.included)
      )
      .map(({ slotId, block }) => ({
        id: block.id,
        label: block.label,
        slotId,
        slotRow: getQuoteHeaderSlotMeta(slotId).row,
        slotCol: getQuoteHeaderSlotMeta(slotId).col,
        textAlign: getQuoteHeaderSlotAlign(slotId),
        fields: block.fields
        .filter(field => field.included)
        .map(field => {
          const sourceObj = context[String(field.sourceGroup || '').toLowerCase()] || {};
          const value = readPathValue(sourceObj, field.sourceKey);
          return {
            id: field.id,
            label: field.label,
            value: value || 'Not provided'
          };
        })
    }));
}, [caseQuotePreview, caseQuotePreviewContext, caseDetailModal, selectedCase, caseQuoteTemplate]);

const openCaseServiceReportPreview = useCallback(
  async (report: CaseServiceReport, contextCase: Case | null) => {
    const reportId = String(report?.id || '').trim();
    const caseId = String(contextCase?.id || report?.caseId || '').trim();
    if (!reportId || !caseId) {
      return;
    }

    if (report.document && typeof report.document === 'object') {
      setCaseServiceReportPreview(report);
      setCaseServiceReportPreviewContext(contextCase);
      return;
    }

    try {
      setCaseServiceReportPreviewLoadingId(reportId);
      const fullCase = (await caseApi.getById(caseId)) as Case;
      const fullReports = Array.isArray(fullCase?.serviceReports) ? fullCase.serviceReports : [];
      const matchedReport =
        fullReports.find(entry => String(entry?.id || '').trim() === reportId) || null;
      if (!matchedReport?.document) {
        throw new Error('The saved service report preview is unavailable.');
      }
      setSelectedCase(prev => (prev?.id === caseId ? fullCase : prev));
      setCaseDetailModal(prev => (prev?.id === caseId ? fullCase : prev));
      setCaseServiceReportPreview(matchedReport);
      setCaseServiceReportPreviewContext(fullCase);
    } catch (error: any) {
      showInAppAlert(error?.message || 'Failed to open service report preview.', {
        title: 'Service report unavailable',
        tone: 'danger'
      });
    } finally {
      setCaseServiceReportPreviewLoadingId(null);
    }
  },
  []
);

const normalizedCustomerSearch = customerSearch.trim().toLowerCase();
const loadCustomerDetail = useCallback(
  async (customerId: string, { force = false }: { force?: boolean } = {}) => {
    const resolvedCustomerId = String(customerId || '').trim();
    if (!resolvedCustomerId) return null;

    if (!force && customerDetailsById[resolvedCustomerId]) {
      return customerDetailsById[resolvedCustomerId];
    }

    if (!force && customerDetailRequestRef.current[resolvedCustomerId]) {
      return customerDetailRequestRef.current[resolvedCustomerId] || null;
    }

    setCustomerDetailLoadingId(resolvedCustomerId);
    setCustomerDetailError(current =>
      current?.customerId === resolvedCustomerId ? null : current
    );

    const request = customerApi
      .getById(resolvedCustomerId)
      .then((detail: Customer | null) => {
        if (detail?.id) {
          startTransition(() => {
            setCustomerDetailsById(prev => ({
              ...prev,
              [resolvedCustomerId]: detail
            }));
          });
        }
        setCustomerDetailError(current =>
          current?.customerId === resolvedCustomerId ? null : current
        );
        return detail;
      })
      .catch((error: any) => {
        setCustomerDetailError({
          customerId: resolvedCustomerId,
          message: error?.message || 'Failed to load customer details.'
        });
        throw error;
      })
      .finally(() => {
        if (customerDetailRequestRef.current[resolvedCustomerId] === request) {
          delete customerDetailRequestRef.current[resolvedCustomerId];
        }
        setCustomerDetailLoadingId(current =>
          current === resolvedCustomerId ? null : current
        );
      });

    customerDetailRequestRef.current[resolvedCustomerId] = request;
    return request;
  },
  [customerDetailsById]
);

const filteredCustomers = useMemo(() => {
  if (!Array.isArray(customers)) return [];
  const baseList = customers.filter(customer => {
    const matchesQuickFilter =
      customerQuickFilter === 'all' || String(customer.id || '').trim() === customerQuickFilter;
    const matchesSearch = normalizedCustomerSearch
      ? (() => {
          const name = (customer.companyName ?? (customer as any).company ?? '').toLowerCase();
          const email = (customer.email ?? '').toLowerCase();
          const phone = (customer.phone ?? '').toLowerCase();
          const locations = Array.isArray(customer.locations)
            ? customer.locations
                .map(location =>
                  [
                    location?.name,
                    location?.addressLine1,
                    location?.addressLine2,
                    location?.city,
                    location?.stateProvince,
                    location?.postalCode,
                    location?.country
                  ]
                    .map(value => String(value || '').trim())
                    .filter(Boolean)
                    .join(' ')
                )
                .join(' ')
                .toLowerCase()
            : '';
          return (
            name.includes(normalizedCustomerSearch) ||
            email.includes(normalizedCustomerSearch) ||
            phone.includes(normalizedCustomerSearch) ||
            locations.includes(normalizedCustomerSearch)
          );
        })()
      : true;
    const matchesLocation =
      customerLocationFilter === 'all' ||
      (Array.isArray(customer.locations)
        ? customer.locations.some(location => String(location?.id || '').trim() === customerLocationFilter)
        : false);

    const matchesStatus =
      customerStatusFilter === 'all' ||
      (customerStatusFilter === 'active' ? customer.isActive : !customer.isActive);

    return matchesQuickFilter && matchesSearch && matchesStatus && matchesLocation;
  });

  const sorted = [...baseList];
  sorted.sort((a, b) => {
    const nameA = (a.companyName ?? (a as any).company ?? '').toLowerCase();
    const nameB = (b.companyName ?? (b as any).company ?? '').toLowerCase();
    const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    const devicesA = a._count?.devices ?? a.devices?.length ?? 0;
    const devicesB = b._count?.devices ?? b.devices?.length ?? 0;
    const casesA = a._count?.cases ?? a.cases?.length ?? 0;
    const casesB = b._count?.cases ?? b.cases?.length ?? 0;
    const locationsA = a._count?.locations ?? a.locations?.length ?? 0;
    const locationsB = b._count?.locations ?? b.locations?.length ?? 0;

    switch (customerSort) {
      case 'nameAsc':
        return nameA.localeCompare(nameB);
      case 'nameDesc':
        return nameB.localeCompare(nameA);
      case 'deviceCountDesc':
        return devicesB - devicesA || nameA.localeCompare(nameB);
      case 'caseCountDesc':
        return casesB - casesA || nameA.localeCompare(nameB);
      case 'locationCountDesc':
        return locationsB - locationsA || nameA.localeCompare(nameB);
      case 'recent':
      default:
        return createdB - createdA || nameA.localeCompare(nameB);
    }
  });
  return sorted;
}, [
  customers,
  customerLocationFilter,
  customerQuickFilter,
  normalizedCustomerSearch,
  customerSort,
  customerStatusFilter
]);

const customerQuickOptions = useMemo(() => {
  if (!Array.isArray(customers)) return [];
  return [...customers]
    .map(customer => ({
      value: String(customer.id || '').trim(),
      label: String(customer.companyName ?? (customer as any).company ?? 'Unnamed Customer').trim() || 'Unnamed Customer',
      searchText: `${customer.email || ''} ${customer.phone || ''} ${
        Array.isArray(customer.locations)
          ? customer.locations
              .map(location =>
                [
                  location?.name,
                  location?.addressLine1,
                  location?.addressLine2,
                  location?.city,
                  location?.stateProvince,
                  location?.postalCode,
                  location?.country
                ]
                  .map(value => String(value || '').trim())
                  .filter(Boolean)
                  .join(' ')
              )
              .filter(Boolean)
              .join(' ')
          : ''
      }`.trim()
    }))
    .filter(option => option.value)
    .sort((a, b) => a.label.localeCompare(b.label));
}, [customers]);

const customerLocationOptions = useMemo(() => {
  if (!Array.isArray(customers)) return [];
  const options = customers.flatMap(customer => {
    if (customerQuickFilter !== 'all' && String(customer.id || '').trim() !== customerQuickFilter) {
      return [];
    }
    return (customer.locations || []).map(location => {
      const customerName =
        String(customer.companyName ?? (customer as any).company ?? 'Unnamed Customer').trim() ||
        'Unnamed Customer';
      const primaryLabel = String(location?.name || '').trim() || 'Unnamed location';
      const locationSearch = [
        primaryLabel,
        location?.addressLine1,
        location?.addressLine2,
        location?.city,
        location?.stateProvince,
        location?.postalCode,
        location?.country,
        customerName
      ]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .join(' ');
      return {
        value: String(location?.id || '').trim(),
        label: `${primaryLabel} - ${customerName}`,
        searchText: locationSearch,
        customerId: String(customer.id || '').trim()
      };
    });
  });
  return options
    .filter(option => option.value)
    .sort((a, b) => a.label.localeCompare(b.label));
}, [customerQuickFilter, customers]);

useEffect(() => {
  if (customerQuickFilter !== 'all' && !customerQuickOptions.some(option => option.value === customerQuickFilter)) {
    setCustomerQuickFilter('all');
  }
}, [customerQuickFilter, customerQuickOptions]);

useEffect(() => {
  if (
    customerLocationFilter !== 'all' &&
    !customerLocationOptions.some(option => option.value === customerLocationFilter)
  ) {
    setCustomerLocationFilter('all');
  }
}, [customerLocationFilter, customerLocationOptions]);

useEffect(() => {
  setCustomerPage(1);
}, [customerLocationFilter, customerSearch, customerSort, customerStatusFilter, customerQuickFilter]);

const customerPageCount = Math.max(1, Math.ceil(filteredCustomers.length / customerPageSize));

useEffect(() => {
  setCustomerPage(current => Math.min(current, customerPageCount));
}, [customerPageCount]);

const paginatedCustomers = useMemo(() => {
  const startIndex = (customerPage - 1) * customerPageSize;
  return filteredCustomers.slice(startIndex, startIndex + customerPageSize);
}, [customerPage, customerPageSize, filteredCustomers]);

const customerPageStart =
  filteredCustomers.length === 0 ? 0 : (customerPage - 1) * customerPageSize + 1;
const customerPageEnd =
  filteredCustomers.length === 0 ? 0 : Math.min(customerPage * customerPageSize, filteredCustomers.length);

const clearCustomerFilters = useCallback(() => {
  setCustomerSearch('');
  setCustomerSort('recent');
  setCustomerStatusFilter('all');
  setCustomerQuickFilter('all');
  setCustomerLocationFilter('all');
  setCustomerPage(1);
}, []);

const openDevicesTabForCustomer = useCallback(
  (customerContext: { customerId: string; customerName: string; devices?: Device[] | null }) => {
    const customerId = String(customerContext.customerId || '').trim();
    if (!customerId) return;

    const deviceCountsByModelId: Record<string, number> = {};
    const modelIds = new Set<string>();

    (customerContext.devices || []).forEach(device => {
      const modelId = String(device?.modelId || device?.model?.id || '').trim();
      if (!modelId) return;
      modelIds.add(modelId);
      deviceCountsByModelId[modelId] = (deviceCountsByModelId[modelId] || 0) + 1;
    });

    setDeviceWorkspaceCustomerFilter({
      customerId,
      customerName: String(customerContext.customerName || 'Selected customer').trim() || 'Selected customer',
      modelIds: Array.from(modelIds),
      deviceCountsByModelId
    });
    setActiveTab('models');
  },
  []
);

const openCustomersTabForCustomer = useCallback((customerId: string) => {
  const resolvedCustomerId = String(customerId || '').trim();
  if (!resolvedCustomerId) return;
  setCustomerQuickViewRequest({
    customerId: resolvedCustomerId,
    requestKey: Date.now()
  });
  setActiveTab('customers');
}, []);

const clearDeviceWorkspaceCustomerFilter = useCallback(() => {
  setDeviceWorkspaceCustomerFilter(null);
}, []);

  const metricsWorkspaceNeedsSourceData =
    metricsWorkspaceTab !== 'overview' &&
    metricsWorkspaceTab !== 'service' &&
    metricsWorkspaceTab !== 'reliability' &&
    metricsWorkspaceTab !== 'customers' &&
    metricsWorkspaceTab !== 'sla' &&
    metricsWorkspaceTab !== 'inventory' &&
    metricsWorkspaceTab !== 'team';

  const loadData = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      const bootstrapping = !hasLoadedOnce;
      if (!silent && bootstrapping) {
        setLoading(true);
      }
      if (!allowedTabs.includes(activeTab)) {
        if (!hasLoadedOnce) {
          setHasLoadedOnce(true);
        }
        return false;
      }
      try {
        console.log('Loading data for tab:', activeTab);
        const base = await getBaseUrl();

        if (activeTab === 'cases') {
          const casesResult = await caseApi.getAll({ summary: true });
          const normalisedCases = casesResult || [];
          startTransition(() => {
            setCases(normalisedCases);
          });
        } else if (activeTab === 'messages') {
          const [casesResult, customerData, deviceData] = await Promise.all([
            cases.length > 0 ? Promise.resolve(cases) : caseApi.getAll({ summary: true }).catch(() => []),
            customers.length > 0 ? Promise.resolve(customers) : customerApi.getAll().catch(() => []),
            devices.length > 0 ? Promise.resolve(devices) : deviceApi.getAll({ selector: true }).catch(() => [])
          ]);
          startTransition(() => {
            setCases(Array.isArray(casesResult) ? casesResult : []);
            setCustomers(Array.isArray(customerData) ? customerData : []);
            setDevices(Array.isArray(deviceData) ? deviceData : []);
          });
        } else if (activeTab === 'customers') {
          const customerData = await customerApi.getAll({ locations: true });
          startTransition(() => {
            setCustomers(customerData || []);
          });
          if (canManageSerializedDevices && deviceModels.length === 0) {
            void fetch(`${base}/api/device-models`)
              .then(async response => {
                if (!response.ok) return null;
                return response.json();
              })
              .then(modelData => {
                if (Array.isArray(modelData)) {
                  startTransition(() => {
                    setDeviceModels(modelData);
                  });
                }
              })
              .catch(error => {
                console.warn('Failed to preload customer device models:', error);
              });
          }
        } else if (activeTab === 'scheduling') {
          if (canReadCases) {
            try {
              const casesResult = await caseApi.getAll({ summary: true });
              startTransition(() => {
                setCases(Array.isArray(casesResult) ? casesResult : []);
              });
            } catch (error) {
              console.error('Failed to refresh scheduling cases:', error);
              if (!cases.length) {
                throw error;
              }
              if (!silent) {
                showInAppAlert(
                  (error as any)?.message || 'Scheduling is using the last loaded case data because the refresh failed.',
                  {
                    title: 'Scheduling data refresh failed',
                    tone: 'primary'
                  }
                );
              }
            }
          } else {
            startTransition(() => {
              setCases([]);
            });
          }
        } else if (activeTab === 'metrics') {
          const shouldLoadMetricsSourceData = metricsWorkspaceNeedsSourceData;
          const shouldLoadMetricModels = deviceModels.length === 0;
          const canLoadUnitOperationalSourceData = !isGlobalScopeUser;
          const base = shouldLoadMetricModels ? await getBaseUrl() : null;
          const [casesResult, customerData, deviceData, modelData] = await Promise.all([
            shouldLoadMetricsSourceData && canReadCases && canLoadUnitOperationalSourceData
              ? caseApi.getAll({ summary: true }).catch(() => [])
              : Promise.resolve(canLoadUnitOperationalSourceData ? cases : []),
            canLoadUnitOperationalSourceData && canReadCustomers && customers.length === 0
              ? customerApi.getAll().catch(() => [])
              : Promise.resolve(canLoadUnitOperationalSourceData ? customers : []),
            canLoadUnitOperationalSourceData && canReadDevices && devices.length === 0
              ? deviceApi.getAll().catch(() => [])
              : Promise.resolve(canLoadUnitOperationalSourceData ? devices : []),
            shouldLoadMetricModels && base
              ? fetch(`${base}/api/device-models`)
                  .then(async response => {
                    if (!response.ok) return null;
                    return response.json();
                  })
                  .catch(error => {
                    console.warn('Failed to preload insights device models:', error);
                    return null;
                  })
              : Promise.resolve(null)
          ]);
          startTransition(() => {
            if (shouldLoadMetricsSourceData) {
              setCases(Array.isArray(casesResult) ? casesResult : []);
            }
            setCustomers(Array.isArray(customerData) ? customerData : []);
            setDevices(Array.isArray(deviceData) ? deviceData : []);
            if (Array.isArray(modelData)) {
              setDeviceModels(modelData);
            }
          });
          if (shouldLoadMetricsSourceData) {
            setMetricsSourceDataLoaded(true);
          }
        } else if (activeTab === 'feedback' || activeTab === 'quality') {
          const modelsResponse = await fetch(`${base}/api/device-models`);
          if (modelsResponse.ok) {
            const modelsData = await modelsResponse.json();
            const normalisedModels = Array.isArray(modelsData) ? modelsData : [];
            startTransition(() => {
              setDeviceModels(normalisedModels);
            });
          } else {
            console.error('Failed to load device models', modelsResponse.status);
          }
        }

        if (!hasLoadedOnce) {
          setHasLoadedOnce(true);
        }

        return true;
      } catch (error) {
        console.error('Failed to load data:', error);
        if (activeTab === 'cases' && !silent) {
          showInAppAlert((error as any)?.message || 'Failed to load cases.', {
            title: 'Cases not loaded',
            tone: 'danger'
          });
        }
        if (activeTab === 'customers' && !silent) {
          showInAppAlert((error as any)?.message || 'Failed to load customers.', {
            title: 'Customers not loaded',
            tone: 'danger'
          });
        }
        if (activeTab === 'scheduling' && !silent) {
          showInAppAlert((error as any)?.message || 'Failed to load scheduling case data.', {
            title: 'Scheduling data not loaded',
            tone: 'danger'
          });
        }
        if (!hasLoadedOnce) {
          setHasLoadedOnce(true);
        }
        return false;
      } finally {
        if (!silent && bootstrapping) {
          setLoading(false);
        }
      }
    },
    [
      activeTab,
      hasLoadedOnce,
      cases,
      customers,
      deviceModels.length,
      devices,
      allowedTabs,
      canManageSerializedDevices,
      canReadCases,
      canReadCustomers,
      canReadDevices,
      isGlobalScopeUser,
      metricsWorkspaceNeedsSourceData
    ]
  );

  const refreshCustomerState = useCallback(
    async (customerId?: string | null) => {
      const resolvedCustomerId = String(customerId || '').trim();
      const tasks: Array<Promise<unknown>> = [loadData({ silent: true })];
      if (resolvedCustomerId) {
        tasks.push(
          loadCustomerDetail(resolvedCustomerId, { force: true }).catch(error => {
            console.warn('Failed to refresh customer detail:', error);
            return null;
          })
        );
      }
      await Promise.all(tasks);
    },
    [loadCustomerDetail, loadData]
  );

  // Handle navigation state
  useEffect(() => {
    const navStateTab = (location.state as { activeTab?: unknown } | null)?.activeTab;
    if (isDashboardTab(navStateTab) && allowedTabs.includes(navStateTab)) {
      setActiveTab(navStateTab);
    }
  }, [allowedTabs, location.state]);

  useEffect(() => {
    const navState = location.state as { focusCaseId?: unknown } | null;
    const focusCaseId = typeof navState?.focusCaseId === 'string' ? navState.focusCaseId.trim() : '';
    if (!focusCaseId) return;
    if (!allowedTabs.includes('cases')) return;
    if (!Array.isArray(cases) || cases.length === 0) return;

    const navKey = `${focusCaseId.toLowerCase()}::${cases.length}`;
    if (focusedCaseNavKeyRef.current === navKey) return;

    const match = cases.find(entry => {
      const caseId = String(entry?.id || '').trim().toLowerCase();
      const caseCode = String((entry as any)?.caseCode || '').trim().toLowerCase();
      const caseNumber = String((entry as any)?.caseNumber || '').trim().toLowerCase();
      const lookup = focusCaseId.toLowerCase();
      return caseId === lookup || caseCode === lookup || caseNumber === lookup;
    });

    focusedCaseNavKeyRef.current = navKey;
    if (!match) return;

    setActiveTab('cases');
    setSelectedCase(match);
    requestAnimationFrame(() => {
      scrollCaseCardIntoView(match.id);
    });

    navigate('/tech', { replace: true, state: { activeTab: 'cases' } });
  }, [allowedTabs, cases, location.state, navigate, scrollCaseCardIntoView]);

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_TAB_KEY, activeTab);
    } catch {}
  }, [activeTab]);

  useEffect(() => {
    setUserActivityPage({
      surface: 'TECH_DASHBOARD',
      pageKey: activeTab === 'orgNotifications' ? 'org-notifications' : activeTab
    });
  }, [activeTab]);

  useEffect(() => {
    try {
      localStorage.setItem(METRICS_WORKSPACE_TAB_KEY, metricsWorkspaceTab);
    } catch {}
    if (!['service', 'reliability', 'customers', 'sla', 'inventory'].includes(metricsWorkspaceTab)) {
      setMetricsStatus('');
      setMetricsCaseId('');
    }
  }, [metricsWorkspaceTab]);

  useEffect(() => {
    if (!allowedTabs.includes(activeTab)) {
      setActiveTab(defaultAllowedTab);
    }
  }, [activeTab, allowedTabs, defaultAllowedTab]);

  useEffect(() => {
    if (metricsWorkspaceTabs.some(tab => tab.id === metricsWorkspaceTab)) {
      return;
    }
    setMetricsWorkspaceTab(metricsWorkspaceTabs[0]?.id || 'overview');
  }, [metricsWorkspaceTab, metricsWorkspaceTabs]);

  useEffect(() => {
    if (canUseMetricsUserFilter) return;
    if (metricsUserIds.length > 0) {
      setMetricsUserIds([]);
    }
    setMetricsUserFilterOpen(false);
  }, [canUseMetricsUserFilter, metricsUserIds.length]);

  useEffect(() => {
    if (
      !canUseMetricsUserFilter ||
      metricsUserOptions.length === 0 ||
      metricsUserIds.length === 0
    ) {
      return;
    }
    const availableIds = new Set(metricsUserOptions.map(option => option.id));
    const nextUserIds = metricsUserIds.filter(id => availableIds.has(id));
    if (nextUserIds.length !== metricsUserIds.length) {
      setMetricsUserIds(nextUserIds);
      setMetricsCaseId('');
    }
  }, [canUseMetricsUserFilter, metricsUserIds, metricsUserOptions]);

  useEffect(() => {
    if (
      activeTab !== 'metrics' ||
      !canUseMetricsUserFilter ||
      metricsUserOptions.length > 0
    ) {
      return;
    }

    let cancelled = false;
    setMetricsUserOptionsLoading(true);
    usersApi
      .getAll()
      .then(rows => {
        if (cancelled) return;
        startTransition(() => {
          setMetricsUserOptions(buildMetricsUserFilterOptions(rows));
        });
      })
      .catch(error => {
        if (cancelled) return;
        console.warn('Failed to load Insights user filter options:', error);
        setMetricsUserOptions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setMetricsUserOptionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    canUseMetricsUserFilter,
    metricsUserOptions.length
  ]);

  const loadTechMessages = useCallback(async () => {
    const requestId = messageListRequestIdRef.current + 1;
    messageListRequestIdRef.current = requestId;
    setMessagesLoading(true);
    try {
      const rows = (await messageApi.getAll({
        includeSent: messageChannelType === 'external' ? messageShowSent : true,
        channelType: messageChannelType,
        view: messageView
      })) as PortalMessage[];
      if (messageListRequestIdRef.current !== requestId) {
        return;
      }
      const next = Array.isArray(rows) ? rows : [];
      setTechMessages(next);
      setReadTechMessageIds(next.filter(message => message.readByCurrentUser).map(message => message.id));
      setLoadedMessageQueryKey(messageListQueryKey);
    } catch (error) {
      console.error('Failed to load tech messages:', error);
      if (messageListRequestIdRef.current !== requestId) {
        return;
      }
      setTechMessages([]);
      setReadTechMessageIds([]);
      setLoadedMessageQueryKey(messageListQueryKey);
    } finally {
      if (messageListRequestIdRef.current === requestId) {
        setMessagesLoading(false);
      }
    }
  }, [messageChannelType, messageListQueryKey, messageShowSent, messageView]);

  const loadMessageUnreadCounts = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!canViewMessagesTab) return;
      if (!force && unreadSummaryRequestRef.current) {
        return unreadSummaryRequestRef.current;
      }

      let request: Promise<void> | null = null;
      request = (async () => {
        try {
          const summary = await messageApi.getUnreadSummary();
          const external = Number(summary?.external);
          const internal = Number(summary?.internal);
          setMessageUnreadCounts({
            external: Number.isFinite(external) && external > 0 ? external : 0,
            internal: Number.isFinite(internal) && internal > 0 ? internal : 0
          });
        } catch (error) {
          console.warn('Failed to load message unread counts:', error);
        } finally {
          if (unreadSummaryRequestRef.current === request) {
            unreadSummaryRequestRef.current = null;
          }
        }
      })();

      unreadSummaryRequestRef.current = request;
      return request;
    },
    [canViewMessagesTab]
  );

  const loadTechNotifications = useCallback(async () => {
    if (!canViewMessagesTab) return;
    setNotificationsLoading(true);
    try {
      const rows = await notificationApi.getMine({ view: notificationView, take: 100 });
      setTechNotifications(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.warn('Failed to load notifications:', error);
      setTechNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [canViewMessagesTab, notificationView]);

  const loadOrgNotificationUnits = useCallback(async () => {
    if (!canAccessOrgNotifications) return;
    setOrgNotificationUnitsLoading(true);
    try {
      const result = await notificationApi.getOrgUnits();
      setOrgNotificationUnits(Array.isArray(result?.units) ? result.units : []);
    } catch (error) {
      console.warn('Failed to load ORG notification units:', error);
      setOrgNotificationUnits([]);
    } finally {
      setOrgNotificationUnitsLoading(false);
    }
  }, [canAccessOrgNotifications]);

  const loadOrgNotificationCampaigns = useCallback(async () => {
    if (!canAccessOrgNotifications) return;
    setOrgNotificationCampaignsLoading(true);
    try {
      const rows = await notificationApi.getOrgCampaigns();
      setOrgNotificationCampaigns(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.warn('Failed to load ORG notification campaigns:', error);
      setOrgNotificationCampaigns([]);
    } finally {
      setOrgNotificationCampaignsLoading(false);
    }
  }, [canAccessOrgNotifications]);

  const loadOrgNotificationWorkspace = useCallback(async () => {
    await Promise.all([loadOrgNotificationUnits(), loadOrgNotificationCampaigns()]);
  }, [loadOrgNotificationCampaigns, loadOrgNotificationUnits]);

  const loadNotificationPopups = useCallback(async () => {
    if (!user?.id) return;
    try {
      const rows = await notificationApi.getUnread({ take: 10 });
      const nextRows = Array.isArray(rows) ? rows : [];
      setNotificationPopupItems(nextRows);
      if (notificationView === 'active') {
        setTechNotifications(nextRows);
      }
    } catch (error) {
      console.warn('Failed to load notification popups:', error);
      setNotificationPopupItems([]);
    }
  }, [notificationView, user?.id]);

  const handleDismissNotification = useCallback(
    async (notification: AppNotification) => {
      try {
        const updated = await notificationApi.dismiss(notification.id);
        setNotificationPopupItems(prev => prev.filter(item => item.id !== notification.id));
        setTechNotifications(prev => {
          if (notificationView === 'active') {
            return prev.filter(item => item.id !== notification.id);
          }
          return prev.map(item => (item.id === notification.id ? { ...item, ...updated } : item));
        });
        if (selectedNotificationId === notification.id && notificationView === 'active') {
          setSelectedNotificationId(null);
        }
      } catch (error) {
        console.warn('Failed to dismiss notification:', error);
      }
    },
    [notificationView, selectedNotificationId]
  );

  const handleMarkNotificationRead = useCallback(async (notificationId: string) => {
    try {
      const updated = await notificationApi.markRead(notificationId);
      setTechNotifications(prev => prev.map(item => (item.id === notificationId ? { ...item, ...updated } : item)));
      setNotificationPopupItems(prev =>
        prev.map(item => (item.id === notificationId ? { ...item, readAt: updated?.readAt || new Date().toISOString() } : item))
      );
    } catch (error) {
      console.warn('Failed to mark notification read:', error);
    }
  }, []);

  const loadInternalMessageContacts = useCallback(async () => {
    try {
      const rows = await messageApi.getInternalContacts();
      setInternalMessageContacts(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.warn('Failed to load internal message contacts:', error);
      setInternalMessageContacts([]);
    }
  }, []);

  const loadPendingLeaveApprovalCount = useCallback(async () => {
    if (!canManageUsers) return;
    try {
      const response = await usersApi.getLeaveRequests({ status: 'PENDING', summaryOnly: true });
      const nextCount = Number(response?.summary?.pendingCount);
      setPendingLeaveApprovalCount(Number.isFinite(nextCount) && nextCount > 0 ? nextCount : 0);
    } catch (error) {
      console.warn('Failed to load pending leave approval count:', error);
    }
  }, [canManageUsers]);

  useEffect(() => {
    if (activeTab !== 'messages') return;
    void loadTechMessages();
  }, [activeTab, loadTechMessages]);

  useEffect(() => {
    void loadNotificationPopups();
  }, [loadNotificationPopups]);

  useEffect(() => {
    if (activeTab !== 'messages' || messageWorkspaceMode !== 'notifications') return;
    void loadTechNotifications();
  }, [activeTab, messageWorkspaceMode, loadTechNotifications]);

  useEffect(() => {
    if (activeTab !== 'orgNotifications') return;
    void loadOrgNotificationWorkspace();
  }, [activeTab, loadOrgNotificationWorkspace]);

  useEffect(() => {
    if (!canViewMessagesTab) return;
    void loadMessageUnreadCounts({ force: true });
  }, [canViewMessagesTab, loadMessageUnreadCounts]);

  useEffect(() => {
    if (!canViewMessagesTab || activeTab !== 'messages') return;
    void loadMessageUnreadCounts({ force: true });
  }, [activeTab, canViewMessagesTab, loadMessageUnreadCounts]);

  useEffect(() => {
    if (!canViewMessagesTab) return;
    const pollIntervalMs = activeTab === 'messages' ? 30000 : 300000;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (activeTab === 'messages') {
        void loadTechMessages();
        if (messageWorkspaceMode === 'notifications') {
          void loadTechNotifications();
        }
      }
      void loadMessageUnreadCounts();
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [activeTab, canViewMessagesTab, loadTechMessages, loadMessageUnreadCounts, loadTechNotifications, messageWorkspaceMode]);

  useEffect(() => {
    if (!canManageUsers) {
      setPendingLeaveApprovalCount(0);
      return;
    }
    void loadPendingLeaveApprovalCount();
  }, [canManageUsers, loadPendingLeaveApprovalCount]);

  useEffect(() => {
    if (!canManageUsers || activeTab !== 'users') return;
    void loadPendingLeaveApprovalCount();
  }, [activeTab, canManageUsers, loadPendingLeaveApprovalCount]);

  useEffect(() => {
    if (!canManageUsers) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadPendingLeaveApprovalCount();
    }, 60000);
    return () => window.clearInterval(timer);
  }, [canManageUsers, loadPendingLeaveApprovalCount]);

  useEffect(() => {
    if (activeTab !== 'messages') return;
    if (messageChannelType === 'internal') {
      loadInternalMessageContacts();
    }
  }, [activeTab, messageChannelType, loadInternalMessageContacts]);
  const messagesQuerySettled = loadedMessageQueryKey === messageListQueryKey;

  useEffect(() => {
    if (activeTab !== 'messages') return;
    setMessageFocusFilter('all');
    setMessageCaseFilter('');
    setMessageDeviceFilter('');
    setMessageShowSent(false);
    setMessageSortOrder('newest');
    setShowUnreadOnly(false);
    setMessageChannelType('external');
    setMessageView('active');
    setShowTechComposePanel(false);
  }, [activeTab]);

  useEffect(() => {
    if (initialLoadRef.current) {
      return;
    }
    initialLoadRef.current = true;
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showInternalJobModal) return;

    const shouldLoadCustomers = canReadCustomers && !customers.length;
    const shouldLoadModels = canReadDevices && !deviceModels.length;
    const shouldLoadDevices = canReadDevices && !devices.length;
    const shouldLoadAssignees = canOpenInternalJobs && !internalMessageContacts.length;
    if (!shouldLoadCustomers && !shouldLoadModels && !shouldLoadDevices && !shouldLoadAssignees) return;

    let cancelled = false;
    (async () => {
      try {
        const base = shouldLoadModels ? await getBaseUrl() : null;
        const [customerData, modelData, deviceData, assigneeData] = await Promise.all([
          shouldLoadCustomers
            ? customerApi.getAll().catch(error => {
                console.warn('Failed to preload internal-job customer filters:', error);
                return null;
              })
            : Promise.resolve(null),
          shouldLoadModels && base
            ? fetch(`${base}/api/device-models`)
                .then(async response => {
                  if (!response.ok) return null;
                  return response.json();
                })
                .catch(error => {
                  console.warn('Failed to preload internal-job model filters:', error);
                  return null;
                })
            : Promise.resolve(null),
          shouldLoadDevices
            ? deviceApi.getAll({ full: true }).catch(error => {
                console.warn('Failed to preload internal-job device filters:', error);
                return null;
              })
            : Promise.resolve(null),
          shouldLoadAssignees
            ? messageApi.getInternalContacts().catch(error => {
                console.warn('Failed to preload internal-job assignee options:', error);
                return null;
              })
            : Promise.resolve(null)
        ]);

        if (cancelled) return;
        startTransition(() => {
          if (Array.isArray(customerData)) {
            setCustomers(customerData);
          }
          if (Array.isArray(modelData)) {
            setDeviceModels(modelData);
          }
          if (Array.isArray(deviceData)) {
            setDevices(deviceData);
          }
          if (Array.isArray(assigneeData)) {
            setInternalMessageContacts(assigneeData);
          }
        });
      } catch (error) {
        console.warn('Failed to preload internal-job reference data:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showInternalJobModal,
    canReadCustomers,
    canReadDevices,
    canOpenInternalJobs,
    customers,
    deviceModels.length,
    devices.length,
    internalMessageContacts.length
  ]);

  useEffect(() => {
    if (!showNotificationModal || (!(canReadDevices || notificationModalMode === 'org') || deviceModels.length > 0)) return;
    let cancelled = false;
    (async () => {
      try {
        const base = await getBaseUrl();
        const response = await fetch(`${base}/api/device-models?compact=1`);
        if (!response.ok) return;
        const rows = await response.json();
        if (!cancelled && Array.isArray(rows)) {
          setDeviceModels(rows);
        }
      } catch (error) {
        console.warn('Failed to preload notification device models:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showNotificationModal, canReadDevices, deviceModels.length, notificationModalMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = await getBaseUrl();
        if (!cancelled) {
          setBaseUrl(base);
        }
      } catch (error) {
        console.error('Failed to resolve base URL:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (caseDetailAbortRef.current) {
        caseDetailAbortRef.current.abort();
      }
    };
  }, []);

  // Reload data when switching to cases tab
  useEffect(() => {
    const shouldRefresh = (tab: DashboardTab) =>
      tab === 'cases' ||
      tab === 'messages' ||
      tab === 'customers' ||
      tab === 'metrics' ||
      tab === 'scheduling' ||
      tab === 'feedback' ||
      tab === 'quality';
    if (shouldRefresh(activeTab) && hasLoadedOnce && previousTab.current !== activeTab) {
      loadData({ silent: true });
    }
    previousTab.current = activeTab;
  }, [activeTab, hasLoadedOnce, loadData]);

  const resetNewCustomerForm = () => {
    setNewCustomer({
      companyName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateProvince: '',
      postalCode: '',
      country: '',
      primaryContactName: '',
      primaryContactRole: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      notes: ''
    });
  };

  const uploadSharedImageAsset = useCallback(async (file: File) => {
    const base = await getBaseUrl();
    const imageForm = new FormData();
    imageForm.append('image', file);
    const uploadResp = await fetch(`${base}/api/model-images`, {
      method: 'POST',
      body: imageForm
    });
    if (!uploadResp.ok) {
      const errPayload = await uploadResp.json().catch(() => ({}));
      throw new Error(errPayload.error || 'Failed to upload image');
    }
    const uploadData = await uploadResp.json();
    return uploadData.fileUrl || null;
  }, []);

  const handleCreateCustomer = useCallback(async (
    options: { imageFile?: File | null; imageUrl?: string | null } = {}
  ) => {
    if (!newCustomer.companyName.trim()) {
      showInAppAlert('Company name is required', {
        title: 'Customer missing',
        tone: 'primary'
      });
      return false;
    }
    if (
      !newCustomer.primaryContactName.trim() &&
      (newCustomer.primaryContactRole.trim() ||
        newCustomer.primaryContactEmail.trim() ||
        newCustomer.primaryContactPhone.trim())
    ) {
      showInAppAlert('Primary contact name is required when entering primary contact details.', {
        title: 'Primary contact missing',
        tone: 'primary'
      });
      return false;
    }
    setAddingCustomer(true);
    try {
      let imageUrlToUse =
        typeof options.imageUrl === 'string' ? options.imageUrl.trim() || null : options.imageUrl;
      if (options.imageFile) {
        imageUrlToUse = await uploadSharedImageAsset(options.imageFile);
      }
      await customerApi.create({
        companyName: newCustomer.companyName.trim(),
        addressLine1: newCustomer.addressLine1.trim() || undefined,
        addressLine2: newCustomer.addressLine2.trim() || undefined,
        city: newCustomer.city.trim() || undefined,
        stateProvince: newCustomer.stateProvince.trim() || undefined,
        postalCode: newCustomer.postalCode.trim() || undefined,
        country: newCustomer.country.trim() || undefined,
        email: newCustomer.primaryContactEmail.trim() || undefined,
        phone: newCustomer.primaryContactPhone.trim() || undefined,
        primaryContactName: newCustomer.primaryContactName.trim() || undefined,
        primaryContactRole: newCustomer.primaryContactRole.trim() || undefined,
        primaryContactEmail: newCustomer.primaryContactEmail.trim() || undefined,
        primaryContactPhone: newCustomer.primaryContactPhone.trim() || undefined,
        notes: newCustomer.notes.trim() || undefined,
        ...(imageUrlToUse !== undefined ? { imageUrl: imageUrlToUse } : {})
      });
      resetNewCustomerForm();
      setShowAddCustomerForm(false);
      await loadData({ silent: true });
      return true;
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to create customer', {
        title: 'Customer not created',
        tone: 'danger'
      });
      return false;
    } finally {
      setAddingCustomer(false);
    }
  }, [newCustomer, loadData, uploadSharedImageAsset]);

  const startEditCustomer = (customer: Customer) => {
    setEditingCustomerId(customer.id);
    setEditCustomer({
      companyName: customer.companyName ?? (customer as any).company ?? '' // fallback for legacy data
    });
    setShowAddCustomerForm(false);
  };

  const handleSaveEditedCustomer = useCallback(async (
    options: { imageFile?: File | null; imageUrl?: string | null } = {}
  ) => {
    if (!editingCustomerId) return;
    const targetCustomerId = editingCustomerId;
    if (!editCustomer.companyName.trim()) {
      showInAppAlert('Company name is required', {
        title: 'Customer missing',
        tone: 'primary'
      });
      return;
    }
    setSavingCustomerId(editingCustomerId);
    try {
      let imageUrlToUse =
        typeof options.imageUrl === 'string' ? options.imageUrl.trim() || null : options.imageUrl;
      if (options.imageFile) {
        imageUrlToUse = await uploadSharedImageAsset(options.imageFile);
      }
      await customerApi.update(editingCustomerId, {
        companyName: editCustomer.companyName.trim(),
        ...(imageUrlToUse !== undefined ? { imageUrl: imageUrlToUse } : {})
      });
      setEditingCustomerId(null);
      setEditCustomer({ companyName: '' });
      await refreshCustomerState(targetCustomerId);
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to update customer', {
        title: 'Customer not updated',
        tone: 'danger'
      });
    } finally {
      setSavingCustomerId(null);
    }
  }, [editCustomer.companyName, editingCustomerId, refreshCustomerState, uploadSharedImageAsset]);

  const handleCancelEditCustomer = () => {
    setEditingCustomerId(null);
    setEditCustomer({ companyName: '' });
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    const name = customer.companyName ?? (customer as any).company ?? 'this customer';
    const confirmed = await showInAppConfirm(
      `Delete ${name}? If the customer has linked devices or cases they will be marked inactive instead.`,
      {
        title: 'Delete customer?',
        confirmLabel: 'Delete',
        tone: 'danger'
      }
    );
    if (!confirmed) {
      return;
    }
    try {
      const result = await customerApi.remove(customer.id);
      if (result?.silenced) window.alert('Customer has linked records and was marked inactive instead of being deleted.');
      await refreshCustomerState(customer.id);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to delete customer');
    }
  };

  const handleSetCustomerActive = async (customer: Customer, nextIsActive: boolean) => {
    const customerId = String(customer?.id || '').trim();
    if (!customerId) return;
    const name = customer.companyName ?? (customer as any).company ?? 'this customer';
    const confirmed = await showInAppConfirm(
      nextIsActive
        ? `Activate ${name}?`
        : `Make ${name} inactive? Existing devices, locations, and history remain linked.`,
      {
        title: nextIsActive ? 'Activate customer?' : 'Make customer inactive?',
        confirmLabel: nextIsActive ? 'Activate' : 'Make inactive',
        tone: nextIsActive ? 'primary' : 'danger'
      }
    );
    if (!confirmed) return;

    setSavingCustomerId(customerId);
    try {
      await customerApi.update(customerId, { isActive: nextIsActive });
      await refreshCustomerState(customerId);
      showInAppAlert(
        nextIsActive ? `${name} is active again.` : `${name} has been marked inactive.`,
        { title: 'Customer updated' }
      );
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to update customer status', {
        title: 'Unable to update customer',
        tone: 'danger'
      });
    } finally {
      setSavingCustomerId(null);
    }
  };

  const handleSaveCustomerNotes = async (customer: Customer, notes: string) => {
    const customerId = String(customer?.id || '').trim();
    if (!customerId) return;
    setSavingCustomerId(customerId);
    try {
      await customerApi.update(customerId, {
        notes: String(notes || '').trim() || null
      });
      await refreshCustomerState(customerId);
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to save customer notes', {
        title: 'Unable to save notes',
        tone: 'danger'
      });
      throw err;
    } finally {
      setSavingCustomerId(null);
    }
  };

  const resetLocationForm = () => {
    setLocationFormCustomerId(null);
    setLocationForm({
      id: null,
      name: '',
      isPrimary: false,
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateProvince: '',
      postalCode: '',
      country: '',
      parentLocationId: ''
    });
  };

  const openLocationForm = (
    customerId: string,
    location?: CustomerLocation | null,
    parentLocationId?: string | null
  ) => {
    const sourceCustomer =
      customerDetailsById[customerId] ||
      customers.find(customer => String(customer?.id || '').trim() === String(customerId || '').trim()) ||
      null;
    const availableLocations = Array.isArray(sourceCustomer?.locations) ? sourceCustomer.locations : [];
    const fallbackPrimaryLocationId = String(
      (
        availableLocations.find(
          candidate =>
            candidate?.isPrimary &&
            String(candidate?.id || '').trim() !== String(location?.id || '').trim()
        ) ||
        availableLocations.find(
          candidate => String(candidate?.id || '').trim() !== String(location?.id || '').trim()
        ) ||
        null
      )?.id || ''
    ).trim();
    const resolvedParentLocationId = location?.isPrimary
      ? ''
      : String((parentLocationId ?? location?.parentLocationId ?? fallbackPrimaryLocationId) || '').trim();

    setContactFormCustomerId(null);
    setLocationFormCustomerId(customerId);
    setLocationForm({
      id: location?.id ?? null,
      name: location?.name ?? '',
      isPrimary: location?.isPrimary ?? false,
      addressLine1: location?.addressLine1 ?? '',
      addressLine2: location?.addressLine2 ?? '',
      city: location?.city ?? '',
      stateProvince: location?.stateProvince ?? '',
      postalCode: location?.postalCode ?? '',
      country: location?.country ?? '',
      parentLocationId: resolvedParentLocationId
    });
  };

  const handleSubmitLocation = async () => {
    if (!locationFormCustomerId) return false;
    if (!locationForm.name.trim()) {
      showInAppAlert('Location name is required', {
        title: 'Location missing',
        tone: 'primary'
      });
      return false;
    }

    const sourceCustomer =
      customerDetailsById[locationFormCustomerId] ||
      customers.find(
        customer =>
          String(customer?.id || '').trim() === String(locationFormCustomerId || '').trim()
      ) ||
      null;
    const availableLocations = Array.isArray(sourceCustomer?.locations) ? sourceCustomer.locations : [];
    const fallbackPrimaryLocationId = String(
      (
        availableLocations.find(
          candidate =>
            candidate?.isPrimary &&
            String(candidate?.id || '').trim() !== String(locationForm.id || '').trim()
        ) ||
        availableLocations.find(
          candidate => String(candidate?.id || '').trim() !== String(locationForm.id || '').trim()
        ) ||
        null
      )?.id || ''
    ).trim();
    const resolvedParentLocationId = locationForm.isPrimary
      ? ''
      : String(locationForm.parentLocationId || fallbackPrimaryLocationId || '').trim();

    const payload: any = {
      name: locationForm.name.trim(),
      isPrimary: Boolean(locationForm.isPrimary),
      addressLine1: locationForm.addressLine1.trim() ? locationForm.addressLine1.trim() : null,
      addressLine2: locationForm.addressLine2.trim() ? locationForm.addressLine2.trim() : null,
      city: locationForm.city.trim() ? locationForm.city.trim() : null,
      stateProvince: locationForm.stateProvince.trim() ? locationForm.stateProvince.trim() : null,
      postalCode: locationForm.postalCode.trim() ? locationForm.postalCode.trim() : null,
      country: locationForm.country.trim() ? locationForm.country.trim() : null,
      parentLocationId: resolvedParentLocationId || null
    };

    setSavingLocation(true);
    try {
      if (locationForm.id) {
        await customerApi.updateLocation(locationFormCustomerId, locationForm.id, payload);
      } else {
        await customerApi.addLocation(locationFormCustomerId, payload);
      }
      resetLocationForm();
      await refreshCustomerState(locationFormCustomerId);
      return true;
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to save location', {
        title: 'Location not saved',
        tone: 'danger'
      });
      return false;
    } finally {
      setSavingLocation(false);
    }
  };

  const handleToggleLocationStatus = async (customerId: string, location: CustomerLocation) => {
    if (location.isActive) {
      const name = location.name || 'this location';
      const confirmed = await showInAppConfirm(
        `Deactivate ${name}? Devices and contacts can be reassigned later.`,
        {
          title: 'Deactivate location?',
          confirmLabel: 'Deactivate',
          tone: 'danger'
        }
      );
      if (!confirmed) {
        return;
      }
    }
    try {
      await customerApi.updateLocation(customerId, location.id, { isActive: !location.isActive });
      await refreshCustomerState(customerId);
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to update location', {
        title: 'Location not updated',
        tone: 'danger'
      });
    }
  };

  const handleSetPrimaryLocation = async (customerId: string, location: CustomerLocation) => {
    try {
      await customerApi.updateLocation(customerId, location.id, {
        isPrimary: true,
        isActive: true
      });
      await refreshCustomerState(customerId);
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to update primary location', {
        title: 'Primary location not updated',
        tone: 'danger'
      });
    }
  };

  const resetContactForm = () => {
    setContactFormCustomerId(null);
    setContactForm({
      id: null,
      fullName: '',
      email: '',
      phone: '',
      role: '',
      portalAccessMode: 'FULL',
      locationId: '',
      locationIds: [],
      isPrimary: false,
      isActive: true
    });
  };

  const getContactDirectLocationIds = (
    contact?: CustomerContact | null,
    defaultLocationId?: string | string[] | null
  ): string[] => {
    const linkedIds: string[] = Array.isArray((contact as any)?.locationLinks)
      ? (contact as any).locationLinks
          .map((link: any) => String(link?.locationId || '').trim())
          .filter((value: string) => value.length > 0)
      : [];
    const explicitIds: string[] = Array.isArray((contact as any)?.locationIds)
      ? (contact as any).locationIds
          .map((value: unknown) => String(value || '').trim())
          .filter((value: string) => value.length > 0)
      : [];
    const defaultLocationIds = Array.isArray(defaultLocationId) ? defaultLocationId : [defaultLocationId];
    const rawIds = linkedIds.length
      ? linkedIds
      : (explicitIds.length ? explicitIds : [contact?.locationId, ...defaultLocationIds]);
    const normalizedIds = rawIds
      .map((value: unknown) => String(value || '').trim())
      .filter((value: string) => value.length > 0);
    return Array.from(new Set<string>(normalizedIds));
  };

  const openContactForm = (
    customerId: string,
    contact?: CustomerContact | null,
    defaultLocationId?: string | string[] | null
  ) => {
    const directLocationIds = getContactDirectLocationIds(contact, defaultLocationId);
    setLocationFormCustomerId(null);
    setContactFormCustomerId(customerId);
    setContactForm({
      id: contact?.id ?? null,
      fullName: contact?.fullName ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      role: contact?.role ?? '',
      portalAccessMode:
        contact?.portalAccessMode === 'LITE' || contact?.portalUser?.portalAccessMode === 'LITE'
          ? 'LITE'
          : 'FULL',
      locationId: directLocationIds[0] || '',
      locationIds: directLocationIds,
      isPrimary: contact?.isPrimary ?? false,
      isActive: contact?.isActive ?? true
    });
  };

  const openContactFormForAdditionalLocation = (customerId: string, contact: CustomerContact) => {
    const directLocationIds = getContactDirectLocationIds(contact);
    setLocationFormCustomerId(null);
    setContactFormCustomerId(customerId);
    setContactForm({
      id: null,
      fullName: contact?.fullName ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      role: contact?.role ?? '',
      portalAccessMode:
        contact?.portalAccessMode === 'LITE' || contact?.portalUser?.portalAccessMode === 'LITE'
          ? 'LITE'
          : 'FULL',
      locationId: directLocationIds[0] || '',
      locationIds: directLocationIds,
      isPrimary: false,
      isActive: true
    });
  };

  const handleSubmitContact = async () => {
    if (!contactFormCustomerId) return false;
    if (!contactForm.fullName.trim()) {
      showInAppAlert('Contact name is required', {
        title: 'Contact not saved',
        tone: 'danger'
      });
      return false;
    }

    const directLocationIds = Array.from(
      new Set((contactForm.locationIds || []).map((value) => String(value || '').trim()).filter(Boolean))
    );

    const payload: any = {
      fullName: contactForm.fullName.trim(),
      email: contactForm.email.trim() ? contactForm.email.trim() : null,
      phone: contactForm.phone.trim() ? contactForm.phone.trim() : null,
      role: contactForm.role.trim() ? contactForm.role.trim() : null,
      portalAccessMode: contactForm.portalAccessMode === 'LITE' ? 'LITE' : 'FULL',
      locationId: directLocationIds[0] || null,
      locationIds: directLocationIds,
      isPrimary: contactForm.isPrimary
    };

    if (contactForm.id) {
      payload.isActive = contactForm.isActive;
    }

    setSavingContact(true);
    try {
      if (contactForm.id) {
        await customerApi.updateContact(contactFormCustomerId, contactForm.id, payload);
      } else {
        await customerApi.addContact(contactFormCustomerId, payload);
      }
      resetContactForm();
      await refreshCustomerState(contactFormCustomerId);
      return true;
    } catch (err: any) {
      showInAppAlert(err?.message || 'Failed to save contact', {
        title: 'Contact not saved',
        tone: 'danger'
      });
      return false;
    } finally {
      setSavingContact(false);
    }
  };

  const handleToggleContactStatus = async (customerId: string, contact: CustomerContact) => {
    try {
      await customerApi.updateContact(customerId, contact.id, { isActive: !contact.isActive });
      await refreshCustomerState(customerId);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to update contact');
    }
  };

  const handleSetPrimaryContact = async (customerId: string, contact: CustomerContact) => {
    try {
      await customerApi.updateContact(customerId, contact.id, { isPrimary: true, isActive: contact.isActive });
      await refreshCustomerState(customerId);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to update primary contact');
    }
  };

  const handleInvitePortalUser = async (
    customerId: string,
    contact: CustomerContact,
    options: { resetCredentials?: boolean } = {}
  ) => {
    if (!contact?.email) {
      window.alert('Contact email is required before enabling portal access.');
      return;
    }

    const actionKey = `invite:${contact.id}`;
    setPortalContactActionKey(actionKey);
    try {
      const result = await customerApi.invitePortalUser(customerId, contact.id, {
        resetCredentials: options.resetCredentials !== false,
        portalAccessMode:
          contact.portalAccessMode === 'LITE' || contact.portalUser?.portalAccessMode === 'LITE'
            ? 'LITE'
            : 'FULL'
      });
      await refreshCustomerState(customerId);

      const invite = result?.invite || {};
      setPortalInviteCopied(null);
      setPortalInvitePreview({
        contactName: contact.fullName || 'Contact',
        organizationSlug: invite.organizationSlug || 'default-org',
        username: invite.username || contact.email || '',
        temporaryPassword: invite.temporaryPassword || '',
        loginUrl: invite.loginUrl || '',
        emailTo: invite.emailTo || contact.email || undefined
      });
    } catch (err: any) {
      window.alert(err?.message || 'Failed to provision customer portal user');
    } finally {
      setPortalContactActionKey(null);
    }
  };

  const handleSetPortalUserActive = async (customerId: string, contact: CustomerContact, isActive: boolean) => {
    const actionKey = `status:${contact.id}`;
    setPortalContactActionKey(actionKey);
    try {
      await customerApi.setPortalUserActive(customerId, contact.id, isActive);
      await refreshCustomerState(customerId);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to update portal user status');
    } finally {
      setPortalContactActionKey(null);
    }
  };

  const copyPortalInviteValue = async (value: string, field: 'username' | 'password' | 'url') => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement('textarea');
        el.value = value;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setPortalInviteCopied(field);
      window.setTimeout(() => setPortalInviteCopied(current => (current === field ? null : current)), 1200);
    } catch {
      // Silent fail keeps UI predictable without blocking dialogs.
    }
  };

  const toggleContactExpanded = (contactId: string) => {
    setExpandedContactIds(current => ({
      ...current,
      [contactId]: !current[contactId]
    }));
  };

  const handleDeviceActiveToggle = async (customerId: string, device: Device, nextActive: boolean) => {
    const currentlyDeployed = (device.lifecycleStatus ?? (device.isActive ? 'DEPLOYED' : 'INACTIVE')) === 'DEPLOYED';
    if (currentlyDeployed === nextActive) {
      return;
    }

    if (!nextActive) {
      if (armedDeviceActionById[device.id] !== 'deactivate') {
        setArmedDeviceActionById(prev => ({ ...prev, [device.id]: 'deactivate' }));
        return;
      }
      setDeviceUpdateInFlight(device.id);
      try {
        await deviceApi.release(device.id, {
          targetStatus: 'INACTIVE',
          releaseReason: 'Deactivated from customer dashboard'
        });
        await refreshCustomerState(customerId);
        setStagedDeviceLocationById(prev => {
          const next = { ...prev };
          delete next[device.id];
          return next;
        });
        setArmedDeviceActionById(prev => {
          const next = { ...prev };
          delete next[device.id];
          return next;
        });
      } catch (err: any) {
        window.alert(err?.message || 'Failed to deactivate device');
      } finally {
        setDeviceUpdateInFlight(null);
      }
      return;
    }

    const payload: Record<string, unknown> = { customerId };
    if (device.locationId) {
      payload.locationId = device.locationId;
    }

    setDeviceUpdateInFlight(device.id);
    try {
      await deviceApi.assign(device.id, payload);
      await refreshCustomerState(customerId);
      setStagedDeviceLocationById(prev => {
        const next = { ...prev };
        delete next[device.id];
        return next;
      });
      setArmedDeviceActionById(prev => {
        const next = { ...prev };
        delete next[device.id];
        return next;
      });
    } catch (err: any) {
      window.alert(err?.message || 'Failed to activate device');
    } finally {
      setDeviceUpdateInFlight(null);
    }
  };

  const handleDeviceLocationChange = async (customerId: string, device: Device, nextLocationId: string) => {
    const currentLocation = device.locationId || '';
    if (nextLocationId === currentLocation) {
      setStagedDeviceLocationById(prev => {
        const next = { ...prev };
        delete next[device.id];
        return next;
      });
      setArmedDeviceActionById(prev => {
        const next = { ...prev };
        if (next[device.id] === 'move') {
          delete next[device.id];
        }
        return next;
      });
      return;
    }

    setStagedDeviceLocationById(prev => ({ ...prev, [device.id]: nextLocationId }));
    setArmedDeviceActionById(prev => ({ ...prev, [device.id]: 'move' }));
  };

  const cancelStagedDeviceAction = (deviceId: string) => {
    setStagedDeviceLocationById(prev => {
      const next = { ...prev };
      delete next[deviceId];
      return next;
    });
    setArmedDeviceActionById(prev => {
      const next = { ...prev };
      delete next[deviceId];
      return next;
    });
  };

  const confirmDeviceLocationChange = async (customerId: string, device: Device) => {
    const nextLocationId = stagedDeviceLocationById[device.id];
    if (typeof nextLocationId === 'undefined') {
      return;
    }
    const currentLocation = device.locationId || '';
    if (nextLocationId === currentLocation) {
      cancelStagedDeviceAction(device.id);
      return;
    }

    const isDeployed = (device.lifecycleStatus ?? (device.isActive ? 'DEPLOYED' : 'INACTIVE')) === 'DEPLOYED';
    setDeviceUpdateInFlight(device.id);
    try {
      if (isDeployed) {
        await deviceApi.release(device.id, {
          targetStatus: 'INVENTORY',
          releaseReason: 'Relocated from customer dashboard'
        });
      }

      await deviceApi.assign(device.id, {
        customerId,
        locationId: nextLocationId || undefined
      });

      await refreshCustomerState(customerId);
      cancelStagedDeviceAction(device.id);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to update device assignment');
    } finally {
      setDeviceUpdateInFlight(null);
    }
  };

  const getCustomModelInfo = (caseData: Case): CustomModelInfo | null => {
    if (!caseData.symptoms || !Array.isArray(caseData.symptoms)) return null;
    
    const symptoms = caseData.symptoms as any[];
    const customModel = symptoms.find((s) => s.type === 'CUSTOM_MODEL');
    return customModel ? (customModel as unknown as CustomModelInfo) : null;
  };

  const getPendingDeviceInfo = (caseData: Case): any | null => {
    if (!caseData.symptoms || !Array.isArray(caseData.symptoms)) return null;

    const symptoms = caseData.symptoms as any[];
    return symptoms.find((s) =>
      s &&
      s.type === 'NEW_DEVICE' &&
      s.needsVerification !== false &&
      s.verified !== true
    ) || null;
  };

  const needsModelVerification = (caseData: Case): boolean => {
    const customModel = getCustomModelInfo(caseData);
    const pendingDevice = getPendingDeviceInfo(caseData);
    return Boolean(
      (customModel && customModel.needsVerification !== false) ||
      pendingDevice
    );
  };

  const unverifiedModelCount = cases.filter(c => needsModelVerification(c)).length;

  const resolveCaseAssigneeSelections = useCallback(
    (task?: CaseTask | null): string[] => {
      const assignedIds = getTaskAssigneeIds(task);
      const assignedNames = getTaskAssigneeDisplayNames(task);
      const resolvedIds: string[] = [];
      const seen = new Set<string>();
      const pushUnique = (value: string) => {
        const normalized = String(value || '').trim();
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        resolvedIds.push(normalized);
      };

      assignedIds.forEach(assignedIdRaw => {
        const direct = caseAssigneeOptions.find(option => option.id === assignedIdRaw);
        const byUsername = !direct
          ? caseAssigneeOptions.find(option => option.username === assignedIdRaw)
          : null;
        pushUnique(direct?.id || byUsername?.id || assignedIdRaw);
      });

      if (resolvedIds.length === 0) {
        assignedNames.forEach(assignedNameRaw => {
          const byName = caseAssigneeOptions.find(
            option => (option.fullName || '').trim().toLowerCase() === assignedNameRaw.trim().toLowerCase()
          );
          if (byName?.id) {
            pushUnique(byName.id);
          }
        });
      }

      return resolvedIds;
    },
    [caseAssigneeOptions]
  );

  const getCaseWorkflowCurrent = useCallback(
    (caseItem: Case) => {
      const lifecycleTask =
        caseItem.displayTask || caseItem.activeTask || getActiveCaseTask(caseItem.tasks || []);
      return {
        status: getCaseStatusSelectValue(caseItem.status),
        priority: String(caseItem.priority || 'MEDIUM').toUpperCase(),
        assigneeIds: resolveCaseAssigneeSelections(lifecycleTask),
        taskGroup: resolveTaskGroupCode(lifecycleTask?.type?.code)
      };
    },
    [resolveCaseAssigneeSelections]
  );

  const getCaseWorkflowDraft = useCallback(
    (caseItem: Case) => {
      const current = getCaseWorkflowCurrent(caseItem);
      const draft = caseWorkflowDraftById[caseItem.id];
      if (!draft) return current;
      return {
        status: (draft.status || current.status) as UiStatus,
        priority: String(draft.priority || current.priority).toUpperCase(),
        assigneeIds: Array.isArray(draft.assigneeIds)
          ? normalizeAssigneeIdList(draft.assigneeIds)
          : current.assigneeIds,
        taskGroup:
          typeof draft.taskGroup === 'string'
            ? (draft.taskGroup as TaskGroupCode | '')
            : current.taskGroup
      };
    },
    [caseWorkflowDraftById, getCaseWorkflowCurrent]
  );

  const isTaskAssignedToCurrentUser = useCallback(
    (task?: CaseTask | null) => {
      if (!task) return false;
      const assignedUserIds = getTaskAssigneeIds(task);
      const assignedUserNames = getTaskAssigneeDisplayNames(task).map(value => value.toLowerCase());
      const actorUserId = String((user as any)?.id || '').trim();
      const actorNameCandidates = [
        (user as any)?.username,
        (user as any)?.fullName,
        (user as any)?.name
      ]
        .map(value => String(value || '').trim().toLowerCase())
        .filter(Boolean);

      if (actorUserId && assignedUserIds.includes(actorUserId)) {
        return true;
      }
      const hasNameMatch = assignedUserNames.some(value => actorNameCandidates.includes(value));
      if (hasNameMatch) {
        return true;
      }
      return false;
    },
    [user]
  );

  const guardTaskWorkspaceOpen = useCallback(
    async (task?: CaseTask | null) => {
      const assignedUserIds = getTaskAssigneeIds(task);
      const assignedUserNames = getTaskAssigneeDisplayNames(task);

      if (!task || (assignedUserIds.length === 0 && assignedUserNames.length === 0)) {
        showInAppAlert(
          'There is no assigned user for this case. Please assign a user in the case workflow before proceeding.',
          {
            title: 'Case Unassigned'
          }
        );
        return false;
      }

      if (!isTaskAssignedToCurrentUser(task)) {
        showInAppAlert(
          'You are not assigned to this case and can only review its current state. To edit the task, assign the case to yourself first.',
          {
            title: 'Read-only Task View'
          }
        );
      }

      return true;
    },
    [isTaskAssignedToCurrentUser]
  );

  const isCaseWorkflowDirty = useCallback(
    (caseItem: Case) => {
      const current = getCaseWorkflowCurrent(caseItem);
      const draft = getCaseWorkflowDraft(caseItem);
      return (
        current.status !== draft.status ||
        String(current.priority || '').toUpperCase() !== String(draft.priority || '').toUpperCase() ||
        !areSameAssigneeLists(current.assigneeIds || [], draft.assigneeIds || []) ||
        String(current.taskGroup || '').toUpperCase() !== String(draft.taskGroup || '').toUpperCase()
      );
    },
    [getCaseWorkflowCurrent, getCaseWorkflowDraft]
  );
  const stageCaseWorkflowDraft = useCallback(
    (
      caseItem: Case,
      patch: Partial<{
        status: UiStatus;
        priority: string;
        assigneeIds: string[];
        taskGroup: TaskGroupCode | '';
      }>
    ) => {
      const current = getCaseWorkflowCurrent(caseItem);
      setCaseWorkflowDraftById(prev => ({
        ...prev,
        [caseItem.id]: {
          ...(prev[caseItem.id] || current),
          ...patch
        }
      }));
      setCaseWorkflowErrorById(prev => {
        if (!prev[caseItem.id]) return prev;
        const next = { ...prev };
        delete next[caseItem.id];
        return next;
      });
    },
    [getCaseWorkflowCurrent]
  );

  const resetCaseWorkflowDraft = useCallback((caseId: string) => {
    setCaseWorkflowDraftById(prev => {
      if (!prev[caseId]) return prev;
      const next = { ...prev };
      delete next[caseId];
      return next;
    });
    setCaseWorkflowErrorById(prev => {
      if (!prev[caseId]) return prev;
      const next = { ...prev };
      delete next[caseId];
      return next;
    });
  }, []);

  const updateCaseStatus = useCallback(
    async (
      caseId: string,
      newStatus: UiStatus,
      options: { source?: 'manual' | 'task' | 'system' | string; reason?: string } = {}
    ) => {
    try {
      await caseApi.updateStatus(caseId, newStatus, {
        source: options.source || 'manual',
        reason: options.reason || null
      });
      setCases(prev => {
        const updated = prev.map(c =>
          c.id === caseId ? { ...c, status: newStatus } : c
        );
        return updated;
      });
      setSelectedCase(prev =>
        prev?.id === caseId ? { ...prev, status: newStatus } : prev
      );
      setCaseDetailModal(prev =>
        prev?.id === caseId ? { ...prev, status: newStatus } : prev
      );
    } catch (error) {
      console.error('Failed to update status:', error);
      throw error;
    }
  }, []);

  const autoProgressingCasesRef = useRef<Set<string>>(new Set());

  const requestAutoProgress = useCallback((caseId: string) => {
    if (autoProgressingCasesRef.current.has(caseId)) {
      return;
    }
    autoProgressingCasesRef.current.add(caseId);
    updateCaseStatus(caseId, 'IN_PROGRESS', { source: 'task', reason: 'Auto-progress from active task' })
      .catch(() => {})
      .finally(() => {
        autoProgressingCasesRef.current.delete(caseId);
      });
  }, [updateCaseStatus]);

  const caseHasActiveTasks = useCallback((caseData: Case) => {
    if (!caseData || !Array.isArray(caseData.tasks)) {
      return false;
    }
    return caseData.tasks.some(task => {
      const status = (task.status ?? '').toString().toUpperCase();
      if (['IN_PROGRESS', 'STARTED', 'RUNNING', 'ACTIVE'].includes(status)) {
        return true;
      }
      if (!status && task.startedAt && !task.completedAt) {
        return true;
      }
      return false;
    });
  }, []);

  const shouldAutoProgressCase = useCallback((caseData: Case) => {
    const normalized = normalizeStatus(caseData.status);
    if (normalized !== 'NEW') {
      return false;
    }
    return caseHasActiveTasks(caseData);
  }, [caseHasActiveTasks]);

  const normalizeCaseDetail = useCallback((caseData: Case): Case => {
    if (!caseData || !Array.isArray(caseData.events)) {
      return caseData;
    }
    const sortedEvents = [...caseData.events].sort((a, b) => {
      const timeA = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
      const timeB = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
      return timeA - timeB;
    });
    return { ...caseData, events: sortedEvents };
  }, []);

  const saveCaseWorkflow = useCallback(
    async (caseItem: Case) => {
      const caseId = String(caseItem?.id || '').trim();
      if (!caseId) return;
      const current = getCaseWorkflowCurrent(caseItem);
      const draft = getCaseWorkflowDraft(caseItem);
      const currentTaskGroup = resolveTaskGroupCode(current.taskGroup || '');
      const nextTaskGroup = resolveTaskGroupCode(draft.taskGroup || '');
      const currentAssigneeIds = normalizeAssigneeIdList(current.assigneeIds);
      const draftAssigneeIds = normalizeAssigneeIdList(draft.assigneeIds);
      const taskGroupChanged = canAssignCases && nextTaskGroup !== currentTaskGroup;
      const statusChanged =
        canUpdateCaseStatus &&
        String(draft.status || '').toUpperCase() !== String(current.status || '').toUpperCase();
      const priorityChanged =
        canUpdateCasePriority &&
        String(draft.priority || '').toUpperCase() !== String(current.priority || '').toUpperCase();
      const assigneeChanged =
        canAssignCases &&
        !areSameAssigneeLists(draftAssigneeIds, currentAssigneeIds);
      const nextAssigneePayloadIds = (() => {
        if (!assigneeChanged) return [];
        const ids = [...draftAssigneeIds];
        const currentPrimary = currentAssigneeIds[0];
        if (currentPrimary && ids.includes(currentPrimary)) {
          return [currentPrimary, ...ids.filter(value => value !== currentPrimary)];
        }
        return ids;
      })();

      if (!statusChanged && !priorityChanged && !assigneeChanged && !taskGroupChanged) {
        resetCaseWorkflowDraft(caseId);
        return;
      }

      if (taskGroupChanged && !nextTaskGroup) {
        setCaseWorkflowErrorById(prev => ({
          ...prev,
          [caseId]: 'Task group cannot be cleared. Select a task group before saving.'
        }));
        return;
      }

      if (assigneeChanged && !taskGroupChanged && !currentTaskGroup) {
        setCaseWorkflowErrorById(prev => ({
          ...prev,
          [caseId]: 'Select a task group first, then assign one or more users.'
        }));
        return;
      }

      let applyAssigneeChange = assigneeChanged;
      if (assigneeChanged) {
        const removingAllAssignees = draftAssigneeIds.length === 0;
          const confirmed = await showInAppConfirm(
            removingAllAssignees
            ? 'No user will be assigned to this case. No one will be able to work the case tasks until a user is assigned. Continue?'
            : 'You are changing the assigned technicians for this case. Are you sure you want to continue?',
          {
            title: removingAllAssignees ? 'Confirm Unassigned Task' : 'Confirm Assignee Change',
            confirmLabel: removingAllAssignees ? 'Yes, leave task unassigned' : 'Yes, change assignees',
            cancelLabel: 'No'
          }
        );
        if (!confirmed) {
          applyAssigneeChange = false;
        }
      }

      if (!statusChanged && !priorityChanged && !applyAssigneeChange && !taskGroupChanged) {
        resetCaseWorkflowDraft(caseId);
        return;
      }

      if (statusChanged && String(draft.status || '').toUpperCase() === 'RESOLVED') {
        const confirmed = await showInAppConfirm(RESOLVE_CASE_CONFIRM_MESSAGE, {
          title: RESOLVE_CASE_CONFIRM_TITLE,
          confirmLabel: 'Yes, mark resolved',
          cancelLabel: 'Cancel'
        });
        if (!confirmed) {
          return;
        }
      }

      setCaseWorkflowSavingById(prev => ({ ...prev, [caseId]: true }));
      setCaseWorkflowErrorById(prev => ({ ...prev, [caseId]: '' }));
      try {
        const payload: {
          taskGroup?: string;
          status?: string;
          priority?: string;
          assigneeUserIds?: string[];
        } = {};
        if (taskGroupChanged && nextTaskGroup) {
          payload.taskGroup = nextTaskGroup;
        }
        if (applyAssigneeChange) {
          payload.assigneeUserIds = nextAssigneePayloadIds;
        }
        if (priorityChanged) {
          payload.priority = String(draft.priority || '').toUpperCase();
        }
        if (statusChanged) {
          payload.status = draft.status;
        }

        const response = await caseApi.updateWorkflow(caseId, payload);
        const updatedCase = response?.case ? normalizeCaseDetail(response.case as Case) : null;
        if (updatedCase) {
          setCases(prev => prev.map(item => (item.id === caseId ? updatedCase : item)));
          setSelectedCase(prev => (prev?.id === caseId ? updatedCase : prev));
          setCaseDetailModal(prev => (prev?.id === caseId ? updatedCase : prev));
        } else {
          await loadData({ silent: true });
          const refreshedCase = await caseApi.getById(caseId);
          const normalizedCase = normalizeCaseDetail(refreshedCase as Case);
          setSelectedCase(prev => (prev?.id === caseId ? normalizedCase : prev));
          setCaseDetailModal(prev => (prev?.id === caseId ? normalizedCase : prev));
        }
        resetCaseWorkflowDraft(caseId);
      } catch (error: any) {
        setCaseWorkflowErrorById(prev => ({
          ...prev,
          [caseId]: error?.message || 'Failed to save workflow updates.'
        }));
      } finally {
        setCaseWorkflowSavingById(prev => ({ ...prev, [caseId]: false }));
      }
    },
    [
      canAssignCases,
      canUpdateCasePriority,
      canUpdateCaseStatus,
      getCaseWorkflowCurrent,
      getCaseWorkflowDraft,
      loadData,
      normalizeCaseDetail,
      resetCaseWorkflowDraft
    ]
  );

  const closeCaseWorkflow = useCallback(
    async (caseItem: Case) => {
      const caseId = String(caseItem?.id || '').trim();
      if (!caseId || !canCloseCases) return false;

      const confirmed = await showInAppConfirm(
        'Closing this case is a service-team signoff. The case will be marked closed and a customer survey may be released. Admin and superuser status controls can reopen or resolve it later. Continue?',
        {
          title: 'Confirm Case Close',
          confirmLabel: 'Yes, close case',
          cancelLabel: 'No'
        }
      );
      if (!confirmed) return false;

      setCaseWorkflowClosingById(prev => ({ ...prev, [caseId]: true }));
      setCaseWorkflowErrorById(prev => ({ ...prev, [caseId]: '' }));
      try {
        const response = await caseApi.updateWorkflow(caseId, { status: 'CLOSED' });
        const updatedCase = response?.case ? normalizeCaseDetail(response.case as Case) : null;
        if (updatedCase) {
          setCases(prev => prev.map(item => (item.id === caseId ? updatedCase : item)));
          setSelectedCase(prev => (prev?.id === caseId ? updatedCase : prev));
          setCaseDetailModal(prev => (prev?.id === caseId ? updatedCase : prev));
        } else {
          await loadData({ silent: true });
          const refreshedCase = await caseApi.getById(caseId);
          const normalizedCase = normalizeCaseDetail(refreshedCase as Case);
          setSelectedCase(prev => (prev?.id === caseId ? normalizedCase : prev));
          setCaseDetailModal(prev => (prev?.id === caseId ? normalizedCase : prev));
        }
        resetCaseWorkflowDraft(caseId);
        return true;
      } catch (error: any) {
        setCaseWorkflowErrorById(prev => ({
          ...prev,
          [caseId]: error?.message || 'Failed to close case.'
        }));
        return false;
      } finally {
        setCaseWorkflowClosingById(prev => ({ ...prev, [caseId]: false }));
      }
    },
    [canCloseCases, loadData, normalizeCaseDetail, resetCaseWorkflowDraft]
  );

  const resolveCaseWorkflow = useCallback(
    async (caseItem: Case) => {
      const caseId = String(caseItem?.id || '').trim();
      if (!caseId || !canCloseCases) return false;

      const confirmed = await showInAppConfirm(RESOLVE_CASE_CONFIRM_MESSAGE, {
        title: RESOLVE_CASE_CONFIRM_TITLE,
        confirmLabel: 'Yes, mark resolved',
        cancelLabel: 'Cancel'
      });
      if (!confirmed) return false;

      setCaseWorkflowResolvingById(prev => ({ ...prev, [caseId]: true }));
      setCaseWorkflowErrorById(prev => ({ ...prev, [caseId]: '' }));
      try {
        const response = await caseApi.updateWorkflow(caseId, { status: 'RESOLVED' });
        const updatedCase = response?.case ? normalizeCaseDetail(response.case as Case) : null;
        if (updatedCase) {
          setCases(prev => prev.map(item => (item.id === caseId ? updatedCase : item)));
          setSelectedCase(prev => (prev?.id === caseId ? updatedCase : prev));
          setCaseDetailModal(prev => (prev?.id === caseId ? updatedCase : prev));
        } else {
          await loadData({ silent: true });
          const refreshedCase = await caseApi.getById(caseId);
          const normalizedCase = normalizeCaseDetail(refreshedCase as Case);
          setSelectedCase(prev => (prev?.id === caseId ? normalizedCase : prev));
          setCaseDetailModal(prev => (prev?.id === caseId ? normalizedCase : prev));
        }
        resetCaseWorkflowDraft(caseId);
        return true;
      } catch (error: any) {
        setCaseWorkflowErrorById(prev => ({
          ...prev,
          [caseId]: error?.message || 'Failed to resolve case.'
        }));
        return false;
      } finally {
        setCaseWorkflowResolvingById(prev => ({ ...prev, [caseId]: false }));
      }
    },
    [canCloseCases, loadData, normalizeCaseDetail, resetCaseWorkflowDraft]
  );

  const closeCaseDetailModal = useCallback(() => {
    const closingCaseId = caseDetailModal?.id || null;
    if (caseDetailAbortRef.current) {
      caseDetailAbortRef.current.abort();
      caseDetailAbortRef.current = null;
    }
    setCaseDetailLoading(false);
    setCaseQuotePreview(null);
    setCaseQuotePreviewContext(null);
    setCaseQuotesByApi([]);
    setCaseQuotesLoading(false);
    setCaseDetailModal(null);
    if (closingCaseId) {
      resetCaseWorkflowDraft(closingCaseId);
    }
  }, [caseDetailModal?.id, resetCaseWorkflowDraft]);

  const closeCaseQuickView = useCallback(() => {
    const closingCaseId = selectedCase?.id || null;
    const returnTab = caseReturnTabAfterCloseRef.current;
    caseReturnTabAfterCloseRef.current = null;
    setAssignmentPickerCaseId(null);
    setAssignmentError(null);
    setEditingCaseId(null);
    setProblemEditError(null);
    setSelectedCase(null);
    if (closingCaseId) {
      resetCaseWorkflowDraft(closingCaseId);
    }
    if (returnTab) {
      setActiveTab(returnTab);
    }
  }, [selectedCase?.id, resetCaseWorkflowDraft]);

  const applyCaseQuickViewRefresh = useCallback((caseData: Case | null | undefined) => {
    if (!caseData) return;
    const normalizedCase = normalizeCaseDetail(caseData);
    setCases(prev =>
      prev.map(item => (item.id === normalizedCase.id ? normalizedCase : item))
    );
    setSelectedCase(prev => (prev?.id === normalizedCase.id ? normalizedCase : prev));
    setCaseDetailModal(prev => (prev?.id === normalizedCase.id ? normalizedCase : prev));
  }, [normalizeCaseDetail]);

  const verifySubmittedDevice = useCallback(
    async (
      caseItem: Case,
      payload: {
        modelNumber?: string;
        manufacturer?: string | null;
        serialNumber?: string;
        assetNumber?: string | null;
        locationId?: string | null;
        deviceId?: string;
      }
    ) => {
      const caseId = String(caseItem?.id || '').trim();
      if (!caseId) {
        throw new Error('Case is not available for device verification.');
      }
      const verifiedCase = await caseApi.verifyModel(caseId, {
        action: 'edit',
        ...payload
      });
      applyCaseQuickViewRefresh(verifiedCase);
      resetCaseWorkflowDraft(caseId);
      await loadData({ silent: true });
      return verifiedCase;
    },
    [applyCaseQuickViewRefresh, loadData, resetCaseWorkflowDraft]
  );

  const loadCaseDetails = useCallback(async (caseSummary: Case) => {
    setCaseDetailModal(normalizeCaseDetail(caseSummary));
    if (caseDetailAbortRef.current) {
      caseDetailAbortRef.current.abort();
    }
    const controller = new AbortController();
    caseDetailAbortRef.current = controller;
    setCaseDetailLoading(true);
    try {
      const fullCase = await caseApi.getById(caseSummary.id);
      if (!controller.signal.aborted && fullCase) {
        setCaseDetailModal(normalizeCaseDetail(fullCase));
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error('Failed to load case details:', error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setCaseDetailLoading(false);
      }
      if (caseDetailAbortRef.current === controller) {
        caseDetailAbortRef.current = null;
      }
    }
  }, [normalizeCaseDetail]);

  useEffect(() => {
    if (!Array.isArray(cases) || cases.length === 0) {
      return;
    }

    cases.forEach(caseItem => {
      if (shouldAutoProgressCase(caseItem)) {
        requestAutoProgress(caseItem.id);
      }
    });
  }, [cases, shouldAutoProgressCase, requestAutoProgress]);

  useEffect(() => {
    if (!caseDetailModal) {
      return;
    }
    if (shouldAutoProgressCase(caseDetailModal)) {
      requestAutoProgress(caseDetailModal.id);
    }
  }, [caseDetailModal, shouldAutoProgressCase, requestAutoProgress]);

  useEffect(() => {
    if (!selectedCase) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeCaseQuickView();
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [selectedCase, closeCaseQuickView]);

  const caseDetailId = caseDetailModal?.id || null;
  const assigneeContextCaseId =
    caseDetailId || selectedCase?.id || (Array.isArray(cases) && cases.length > 0 ? cases[0].id : null);
  useEffect(() => {
    if (!canAssignCases) {
      setCaseAssigneeOptions([]);
      setCaseAssigneesLoading(false);
      setCaseAssigneesError('');
      return;
    }
    if (!assigneeContextCaseId) {
      setCaseAssigneesLoading(false);
      setCaseAssigneesError('');
      return;
    }
    let cancelled = false;
    setCaseAssigneesLoading(true);
    setCaseAssigneesError('');
    caseApi
      .getAssignees(assigneeContextCaseId)
      .then(payload => {
        if (cancelled) return;
        const list = (Array.isArray(payload?.assignees) ? payload.assignees : []).filter(
          (option: CaseAssigneeOption) => option?.isActive !== false
        );
        setCaseAssigneeOptions(list);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load case assignees:', error);
        setCaseAssigneeOptions([]);
        setCaseAssigneesError(error?.message || 'Unable to load assignees');
      })
      .finally(() => {
        if (!cancelled) {
          setCaseAssigneesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assigneeContextCaseId, canAssignCases, cases]);

  const verifyModel = async (action: 'approve' | 'edit' | 'reject') => {
    const targetCase = caseDetailModal || selectedCase;
    if (!targetCase) return;

    try {
      const body: any = { action };
      if (action === 'edit') {
        body.modelNumber = editingModel.modelNumber;
        body.manufacturer = editingModel.manufacturer;
        body.serialNumber = editingModel.serialNumber;
        body.assetNumber = editingModel.assetNumber;
        body.deviceId = editingModel.deviceId;
      }

      const verifiedCase = await caseApi.verifyModel(targetCase.id, body);
      applyCaseQuickViewRefresh(verifiedCase);

      await loadData({ silent: true });

      if (caseDetailModal?.id === targetCase.id) {
        loadCaseDetails(verifiedCase);
      }

      setShowEditModal(false);
      setEditingModel({ modelNumber: '', manufacturer: '', serialNumber: '', assetNumber: '', deviceId: '' });
    } catch (error) {
      console.error('Failed to verify model:', error);
      showInAppAlert((error as any)?.message || 'Failed to verify device or model', {
        title: 'Verification failed'
      });
    }
  };

  const resolveCaseCustomer = useCallback((caseData: Case) => {
    const rawCustomer: any = caseData.customer ?? (caseData as any).customer ?? {};
    const fallbackId = (caseData as any).customerId;
    const id = typeof rawCustomer?.id === 'string'
      ? rawCustomer.id
      : typeof fallbackId === 'string'
        ? fallbackId
        : '';
    const rawLabel =
      rawCustomer?.companyName ??
      rawCustomer?.company ??
      rawCustomer?.name ??
      rawCustomer?.email ??
      (typeof fallbackId === 'string' && fallbackId ? fallbackId : '');
    const label = typeof rawLabel === 'string' && rawLabel.trim().length > 0 ? rawLabel : 'Customer';
    return { id, label };
  }, []);

  const deviceById = useMemo(() => {
    const map = new Map<string, Device>();
    devices.forEach(device => {
      if (device?.id) {
        map.set(device.id, device);
      }
    });
    return map;
  }, [devices]);

  const resolveCaseDevice = useCallback((caseData: Case) => {
    const rawDevice: any = caseData.device ?? (caseData as any).device ?? {};
    const fallbackId = (caseData as any).deviceId ?? caseData.deviceId;
    const id =
      typeof rawDevice?.id === 'string'
        ? rawDevice.id
        : typeof fallbackId === 'string'
        ? fallbackId
        : '';
    const model =
      rawDevice?.modelNumber ??
      rawDevice?.modelName ??
      rawDevice?.model?.modelNumber ??
      rawDevice?.model?.modelName ??
      '';
    const serial = rawDevice?.serialNumber ?? rawDevice?.serial ?? '';
    const asset = rawDevice?.assetNumber ?? rawDevice?.asset ?? '';
    const labelParts = [model, asset || serial].map(part =>
      typeof part === 'string' ? part.trim() : ''
    );
    const label = labelParts.filter(Boolean).join(' | ') || rawDevice?.name || 'Device';
    return { id, label };
  }, []);

  const resolveCaseDeviceModel = useCallback((caseData: Case) => {
    const rawDevice: any = caseData.device ?? (caseData as any).device ?? {};
    const fallbackDeviceId = (caseData as any).deviceId ?? caseData.deviceId;
    const deviceId =
      typeof rawDevice?.id === 'string'
        ? rawDevice.id
        : typeof fallbackDeviceId === 'string'
        ? fallbackDeviceId
        : '';
    const linkedDevice = deviceId ? deviceById.get(deviceId) ?? null : null;
    const customerId = resolveCaseCustomer(caseData).id;
    const modelId =
      String(
        rawDevice?.modelId ||
          rawDevice?.model?.id ||
          linkedDevice?.modelId ||
          linkedDevice?.model?.id ||
          ''
      ).trim();
    const modelNumber =
      String(
        rawDevice?.modelNumber ||
          rawDevice?.model?.modelNumber ||
          linkedDevice?.modelNumber ||
          linkedDevice?.model?.modelNumber ||
          ''
      ).trim();
    const manufacturer =
      String(
        rawDevice?.manufacturer ||
          rawDevice?.model?.manufacturer ||
          linkedDevice?.manufacturer ||
          linkedDevice?.model?.manufacturer ||
          ''
      ).trim();
    const label = manufacturer ? `${modelNumber} - ${manufacturer}` : modelNumber || 'Unknown model';
    const value =
      modelId || (modelNumber ? `MODEL::${modelNumber.toUpperCase()}::${manufacturer.toUpperCase()}` : '');
    return {
      value,
      label,
      customerId
    };
  }, [deviceById, resolveCaseCustomer]);

  const resolveCaseDeviceSerial = useCallback((caseData: Case) => {
    const { id } = resolveCaseDevice(caseData);
    const modelMeta = resolveCaseDeviceModel(caseData);
    const rawDevice: any = caseData.device ?? (caseData as any).device ?? {};
    const fallbackDeviceId = (caseData as any).deviceId ?? caseData.deviceId;
    const linkedDevice =
      (typeof rawDevice?.id === 'string' && rawDevice.id ? deviceById.get(rawDevice.id) ?? null : null) ||
      (typeof fallbackDeviceId === 'string' && fallbackDeviceId
        ? deviceById.get(fallbackDeviceId) ?? null
        : null);
    const serial = String(rawDevice?.serialNumber || linkedDevice?.serialNumber || '').trim();
    const asset = String(rawDevice?.assetNumber || linkedDevice?.assetNumber || '').trim();
    const customerId =
      String(rawDevice?.customerId || linkedDevice?.customerId || resolveCaseCustomer(caseData).id || '').trim();
    const identifierLabel = asset
      ? `Asset ${asset}${serial ? ` | Serial ${serial}` : ''}`
      : serial;
    const label = identifierLabel
      ? `${identifierLabel} | ${modelMeta.label}`
      : modelMeta.label !== 'Unknown model'
      ? modelMeta.label
      : 'Unknown device';
    return {
      value: id,
      label,
      serial,
      asset,
      customerId,
      modelValue: modelMeta.value
    };
  }, [deviceById, resolveCaseCustomer, resolveCaseDevice, resolveCaseDeviceModel]);

  const caseCustomerOptions = useMemo(() => {
    const optionMap = new Map<string, string>();

    customers.forEach(customer => {
      if (!customer?.id) return;
      const label =
        customer.companyName ??
        (customer as any).company ??
        customer.email ??
        'Unnamed Customer';
      optionMap.set(customer.id, label);
    });

    cases.forEach(caseItem => {
      const { id, label } = resolveCaseCustomer(caseItem);
      if (id) {
        optionMap.set(id, label);
      }
    });

    const options = Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const hasUnassigned = cases.some(caseItem => {
      const { id } = resolveCaseCustomer(caseItem);
      return !id;
    });

    if (hasUnassigned) {
      options.push({ value: '__unassigned__', label: 'Unassigned' });
    }

    return options;
  }, [cases, customers, resolveCaseCustomer]);

  const caseDeviceModelOptions = useMemo(() => {
    const optionMap = new Map<string, { value: string; label: string; customerIds: Set<string>; searchText: string }>();

    cases.forEach(caseItem => {
      const modelMeta = resolveCaseDeviceModel(caseItem);
      if (!modelMeta.value) return;
      const existing = optionMap.get(modelMeta.value) || {
        value: modelMeta.value,
        label: modelMeta.label,
        customerIds: new Set<string>(),
        searchText: modelMeta.label
      };
      if (modelMeta.customerId) {
        existing.customerIds.add(modelMeta.customerId);
      }
      optionMap.set(modelMeta.value, existing);
    });

    return Array.from(optionMap.values())
      .filter(option => {
        if (customerFilter === 'all') return true;
        if (customerFilter === '__unassigned__') return false;
        return option.customerIds.has(customerFilter);
      })
      .map(({ customerIds, ...option }) => option)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cases, customerFilter, resolveCaseDeviceModel]);

  const caseDeviceSerialOptions = useMemo(() => {
    const optionMap = new Map<
      string,
      {
        value: string;
        label: string;
        customerId: string;
        modelValue: string;
        searchText: string;
      }
    >();

    cases.forEach(caseItem => {
      const deviceMeta = resolveCaseDeviceSerial(caseItem);
      if (!deviceMeta.value) return;
      optionMap.set(deviceMeta.value, {
        value: deviceMeta.value,
        label: deviceMeta.label,
        customerId: deviceMeta.customerId,
        modelValue: deviceMeta.modelValue,
        searchText: [deviceMeta.serial, deviceMeta.asset, deviceMeta.label].filter(Boolean).join(' ')
      });
    });

    const options = Array.from(optionMap.values())
      .filter(option => {
        if (customerFilter !== 'all') {
          if (customerFilter === '__unassigned__') return false;
          if (option.customerId !== customerFilter) return false;
        }
        if (deviceModelFilter !== 'all' && option.modelValue !== deviceModelFilter) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const hasUnassigned =
      customerFilter === 'all' &&
      deviceModelFilter === 'all' &&
      cases.some(caseItem => {
        const { id } = resolveCaseDevice(caseItem);
        return !id;
      });

    if (hasUnassigned) {
      options.push({
        value: '__no_device__',
        label: 'No Device',
        customerId: '',
        modelValue: '',
        searchText: 'No device'
      });
    }

    return options;
  }, [cases, customerFilter, deviceModelFilter, resolveCaseDevice, resolveCaseDeviceSerial]);

  const caseUserOptions = useMemo(() => {
    const optionMap = new Map<string, { value: string; label: string; searchText: string }>();

    const addOption = (value: unknown, label: unknown, searchParts: unknown[] = []) => {
      const normalizedValue = String(value || '').trim();
      const normalizedLabel = String(label || '').trim();
      if (!normalizedValue || !normalizedLabel) return;
      if (!isInternalAssigneeValue(normalizedValue) && !isInternalAssigneeValue(normalizedLabel)) return;

      const existing = optionMap.get(normalizedValue);
      const nextSearchText = [normalizedValue, normalizedLabel, ...searchParts]
        .map(part => String(part || '').trim())
        .filter(Boolean)
        .join(' ');

      optionMap.set(normalizedValue, {
        value: normalizedValue,
        label: existing?.label || normalizedLabel,
        searchText: [existing?.searchText, nextSearchText].filter(Boolean).join(' ')
      });
    };

    const assigneeById = new Map<string, CaseAssigneeOption>();
    const assigneeByUsername = new Map<string, CaseAssigneeOption>();
    const assigneeByName = new Map<string, CaseAssigneeOption>();

    caseAssigneeOptions.forEach(option => {
      const id = String(option?.id || '').trim();
      const username = String(option?.username || '').trim();
      const fullName = String(option?.fullName || '').trim();
      const label = fullName || username || id;
      if (id) assigneeById.set(id, option);
      if (username) assigneeByUsername.set(username, option);
      if (fullName) assigneeByName.set(fullName.toLowerCase(), option);
      addOption(id, label, [username, option?.roleLabel, option?.roleKey]);
    });

    cases.forEach(caseItem => {
      const lifecycleTask = caseItem.displayTask || caseItem.activeTask || getActiveCaseTask(caseItem.tasks);
      if (!lifecycleTask) return;

      if (Array.isArray(lifecycleTask.assignees)) {
        lifecycleTask.assignees.forEach(assignee => {
          const id = String(assignee?.id || '').trim();
          const username = String(assignee?.username || '').trim();
          const fullName = String(assignee?.fullName || '').trim();
          addOption(id || username || fullName, fullName || username || id, [
            username,
            assignee?.roleLabel,
            assignee?.roleKey
          ]);
        });
      }

      getTaskAssigneeIds(lifecycleTask).forEach(rawAssigneeId => {
        const match =
          assigneeById.get(rawAssigneeId) ||
          assigneeByUsername.get(rawAssigneeId) ||
          assigneeByName.get(rawAssigneeId.toLowerCase());
        addOption(
          match?.id || rawAssigneeId,
          match?.fullName || match?.username || rawAssigneeId,
          [match?.username, match?.roleLabel, match?.roleKey]
        );
      });

      getTaskAssigneeDisplayNames(lifecycleTask).forEach(rawAssigneeName => {
        const match = assigneeByName.get(rawAssigneeName.toLowerCase());
        addOption(
          match?.id || rawAssigneeName,
          match?.fullName || match?.username || rawAssigneeName,
          [match?.username, match?.roleLabel, match?.roleKey]
        );
      });
    });

    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [caseAssigneeOptions, cases]);

  const internalJobAssigneeOptions = useMemo(() => {
    const optionMap = new Map<
      string,
      { id: string; username: string; fullName?: string | null; roleKey?: string | null; roleLabel?: string | null }
    >();
    internalMessageContacts.forEach(entry => {
      if (!entry?.id) return;
      optionMap.set(entry.id, entry);
    });
    const selfId = String((user as any)?.id || '').trim();
    if (selfId && !optionMap.has(selfId)) {
      optionMap.set(selfId, {
        id: selfId,
        username: String((user as any)?.username || '').trim() || 'me',
        fullName: String((user as any)?.fullName || (user as any)?.username || 'Me'),
        roleKey: String((user as any)?.role || '').trim() || null,
        roleLabel: String((user as any)?.role || '').trim() || null
      });
    }
    return Array.from(optionMap.values()).sort((a, b) => {
      const left = String(a.fullName || a.username || '').trim().toLowerCase();
      const right = String(b.fullName || b.username || '').trim().toLowerCase();
      return left.localeCompare(right);
    });
  }, [internalMessageContacts, user]);

  const splitSerialSuffix = (value: string) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return null;
    const match = trimmed.match(/^(.*?)(\d+)$/);
    if (!match) return null;
    try {
      return {
        prefix: match[1] || '',
        digits: match[2] || '',
        number: BigInt(match[2])
      };
    } catch (error) {
      return null;
    }
  };

  const parseExactDeviceIdentifiers = (value: string) => {
    const seen = new Set<string>();
    return String(value || '')
      .split(/[\n,]/)
      .map(entry => entry.trim())
      .filter(Boolean)
      .filter(entry => {
        const key = entry.toUpperCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const validateSerialRangeInput = (fromSerial: string, toSerial: string) => {
    const start = splitSerialSuffix(fromSerial);
    const end = splitSerialSuffix(toSerial);
    if (!start || !end) {
      throw new Error('Serial range values must end with a numeric sequence.');
    }
    if (start.prefix !== end.prefix) {
      throw new Error('Serial range start and end must use the same serial prefix.');
    }
    if (end.number < start.number) {
      throw new Error('Serial range end must be greater than or equal to the start.');
    }
  };

  useEffect(() => {
    if (deviceModelFilter !== 'all' && !caseDeviceModelOptions.some(option => option.value === deviceModelFilter)) {
      setDeviceModelFilter('all');
    }
  }, [caseDeviceModelOptions, deviceModelFilter]);

  useEffect(() => {
    if (deviceFilter !== 'all' && !caseDeviceSerialOptions.some(option => option.value === deviceFilter)) {
      setDeviceFilter('all');
    }
  }, [caseDeviceSerialOptions, deviceFilter]);

  useEffect(() => {
    if (!canUseCaseUserFilter && caseUserFilter !== 'all') {
      setCaseUserFilter('all');
    }
  }, [canUseCaseUserFilter, caseUserFilter]);

  useEffect(() => {
    if (caseUserFilter !== 'all' && !caseUserOptions.some(option => option.value === caseUserFilter)) {
      setCaseUserFilter('all');
    }
  }, [caseUserFilter, caseUserOptions]);

  const handleCustomerFilterChange = useCallback((value: string) => {
    setCustomerFilter(value);
  }, []);

  const handleDeviceModelFilterChange = useCallback((value: string) => {
    setDeviceModelFilter(value);
    if (value === 'all') return;
    const matchingSerial = caseDeviceSerialOptions.find(option => option.value === deviceFilter);
    if (matchingSerial?.modelValue && matchingSerial.modelValue !== value) {
      setDeviceFilter('all');
    }
  }, [caseDeviceSerialOptions, deviceFilter]);

  const handleDeviceFilterChange = useCallback((value: string) => {
    setDeviceFilter(value);
    if (!value || value === 'all' || value === '__no_device__') {
      return;
    }
    const selectedDevice = caseDeviceSerialOptions.find(option => option.value === value);
    if (!selectedDevice) return;
    if (selectedDevice.customerId) {
      setCustomerFilter(selectedDevice.customerId);
    }
    if (selectedDevice.modelValue) {
      setDeviceModelFilter(selectedDevice.modelValue);
    }
  }, [caseDeviceSerialOptions]);

  const clearCaseFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCustomerFilter('all');
    setAssignmentFilter('all');
    setCaseUserFilter('all');
    setTaskGroupFilter('all');
    setDeviceModelFilter('all');
    setDeviceFilter('all');
    setCaseSortOrder('newest');
    setCasesPage(1);
  }, []);

  const openInsightsCase = useCallback(
    async (caseId: string) => {
      const resolvedCaseId = String(caseId || '').trim();
      if (!resolvedCaseId) return;
      try {
        const fullCase = normalizeCaseDetail((await caseApi.getById(resolvedCaseId)) as Case);
        if (!fullCase?.id) {
          throw new Error('The selected case could not be loaded.');
        }
        setCases(prev => {
          const exists = prev.some(item => item.id === fullCase.id);
          if (exists) {
            return prev.map(item => (item.id === fullCase.id ? fullCase : item));
          }
          return [fullCase, ...prev];
        });
        clearCaseFilters();
        setSearchQuery(getCaseDisplayNumber(fullCase));
        caseReturnTabAfterCloseRef.current = 'metrics';
        setSelectedCase(fullCase);
        setActiveTab('cases');
        requestAnimationFrame(() => {
          scrollCaseCardIntoView(fullCase.id);
        });
      } catch (error: any) {
        showInAppAlert(error?.message || 'Failed to open the selected case.', {
          title: 'Case unavailable'
        });
      }
    },
    [clearCaseFilters, normalizeCaseDetail, scrollCaseCardIntoView]
  );

  const activeCaseFilterCount = [
    searchQuery.trim().length > 0,
    statusFilter !== 'all',
    priorityFilter !== 'all',
    assignmentFilter !== 'all',
    canUseCaseUserFilter && caseUserFilter !== 'all',
    taskGroupFilter !== 'all',
    customerFilter !== 'all',
    deviceModelFilter !== 'all',
    deviceFilter !== 'all',
    caseSortOrder !== 'newest'
  ].filter(Boolean).length;
  const hasActiveCaseFilters = activeCaseFilterCount > 0;

  const resolveSimpleTargetDevice = () => {
    if (!internalJobForm.deviceId) return null;
    return devices.find(device => device.id === internalJobForm.deviceId) || null;
  };

  const resolveInternalJobType = () => {
    const resolvedTaskGroup = resolveTaskGroupCode(String(internalJobForm.taskGroup || '').trim());
    return resolvedTaskGroup || 'OTHER';
  };

  const buildInternalJobTitle = (targetDevice?: Device | null) => {
    const manualTitle = internalJobForm.title.trim();
    if (manualTitle) return manualTitle;
    if (internalJobForm.mode !== 'SIMPLE') return '';

    const selectedCustomer = customers.find(customer => customer.id === internalJobForm.customerId);
    const customerLabel =
      selectedCustomer?.companyName ||
      (selectedCustomer as any)?.company ||
      selectedCustomer?.email ||
      '';
    const serialLabel =
      targetDevice?.assetNumber ||
      targetDevice?.serialNumber ||
      'Unspecified device';
    const typeLabel = resolveInternalJobType();
    return customerLabel
      ? `${typeLabel} | ${serialLabel} | ${customerLabel}`
      : `${typeLabel} | ${serialLabel}`;
  };

  const resolveInternalJobCaseCap = () => {
    const raw = String(internalJobForm.caseCap ?? '100').trim() || '100';
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      throw new Error('Case cap must be a positive number.');
    }
    const cap = Math.floor(parsed);
    if (cap > 5000) {
      throw new Error('Case cap cannot exceed 5000.');
    }
    return cap;
  };

  const resolveInternalJobSerialRange = () => {
    const from = String(internalJobForm.serialRangeFrom || '').trim();
    const to = String(internalJobForm.serialRangeTo || '').trim();
    if (!from && !to) return null;
    if (!from || !to) {
      throw new Error('Enter both From serial and To serial for a serial range.');
    }
    if (!String(internalJobForm.modelId || '').trim()) {
      throw new Error('Select a device model before checking a serial range.');
    }
    validateSerialRangeInput(from, to);
    return { from, to };
  };

  const getInternalJobManufacturedDateFilters = () => {
    const manufacturedFrom = String(internalJobForm.manufacturedFrom || '').trim();
    const manufacturedTo = String(internalJobForm.manufacturedTo || '').trim();
    return {
      manufacturedFrom: manufacturedFrom || null,
      manufacturedTo: manufacturedTo || null
    };
  };

  const buildInternalJobPreviewLimit = (requestedIdentifierCount: number, caseCap: number) =>
    Math.max(500, requestedIdentifierCount + 25, caseCap + 1);

  const buildInternalJobFilters = (options: { targetDevice?: Device | null } = {}) => {
    const filters: Record<string, unknown> = {};
    if (internalJobForm.mode === 'SIMPLE') {
      const targetDevice = options.targetDevice || resolveSimpleTargetDevice();
      const selectedCustomerId = String(internalJobForm.customerId || '').trim();
      if (targetDevice?.modelId) {
        filters.modelIds = [targetDevice.modelId];
      } else if (internalJobForm.modelId) {
        filters.modelIds = [internalJobForm.modelId];
      }
      if (targetDevice?.customerId) {
        filters.customerIds = [targetDevice.customerId];
      } else if (selectedCustomerId) {
        filters.fallbackCustomerId = selectedCustomerId;
        if (!targetDevice) {
          filters.customerIds = [selectedCustomerId];
        }
      }
      if (targetDevice) {
        if (targetDevice.locationId) {
          filters.locationIds = [targetDevice.locationId];
        }
      } else if (internalJobForm.locationId) {
        filters.locationIds = [internalJobForm.locationId];
      }
      const resolvedSerial = targetDevice?.serialNumber;
      if (resolvedSerial) {
        filters.serialNumbers = [resolvedSerial];
      }
      const resolvedAsset = targetDevice?.assetNumber;
      if (resolvedAsset) {
        filters.assetNumbers = [resolvedAsset];
      }
      filters.includeInactive = true;
      return filters;
    }

    if (internalJobForm.modelId) filters.modelIds = [internalJobForm.modelId];
    const identifiers = parseExactDeviceIdentifiers(internalJobForm.serialNumbers);
    if (identifiers.length > 0) {
      filters.deviceIdentifiers = identifiers;
    }
    const serialRange = resolveInternalJobSerialRange();
    if (serialRange) {
      filters.serialRange = serialRange;
    }
    const { manufacturedFrom, manufacturedTo } = getInternalJobManufacturedDateFilters();
    if (manufacturedFrom) filters.manufacturedFrom = manufacturedFrom;
    if (manufacturedTo) filters.manufacturedTo = manufacturedTo;
    filters.caseCap = resolveInternalJobCaseCap();
    filters.includeInactive = true;
    return filters;
  };

  useEffect(() => {
    if (!showInternalJobModal) return;
    setInternalJobSerialAudit([]);
  }, [
    showInternalJobModal,
    internalJobForm.mode,
    internalJobForm.modelId,
    internalJobForm.serialNumbers,
    internalJobForm.serialRangeFrom,
    internalJobForm.serialRangeTo,
    internalJobForm.manufacturedFrom,
    internalJobForm.manufacturedTo,
    internalJobForm.caseCap
  ]);

  const resetInternalJobForm = () => {
    setInternalJobForm({
      mode: 'SIMPLE',
      title: '',
      description: '',
      instructionRef: '',
      taskGroup: 'SAFETY',
      priority: 'HIGH',
      assigneeUserId: '',
      deviceId: '',
      modelId: '',
      customerId: '',
      locationId: '',
      serialPrefix: '',
      serialNumbers: '',
      serialRangeFrom: '',
      serialRangeTo: '',
      manufacturedFrom: '',
      manufacturedTo: '',
      caseCap: '100',
      warrantyStatus: '',
      minAgeDays: '',
      maxAgeDays: '',
      includeInactive: false
    });
    setInternalJobDraft(null);
    setInternalJobSerialAudit([]);
    setInternalJobError(null);
    setInternalJobUploads([]);
    setInternalJobUploadError(null);
  };

  const uploadInternalJobDocuments = async (files: File[]) => {
    if (!files.length) return;
    setInternalJobUploadError(null);
    try {
      const response = await internalJobApi.uploadAttachments(files);
      const attachments = response?.attachments || [];
      if (attachments.length > 0) {
        setInternalJobUploads(prev => [...prev, ...attachments]);
      }
    } catch (error: any) {
      setInternalJobUploadError(error?.message || 'Upload failed');
    }
  };

  const handleInternalJobFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      uploadInternalJobDocuments(files);
    }
    event.target.value = '';
  };

  const handleCreateInternalJobUniqueDevice = useCallback(
    async (payload: {
      modelId: string;
      customerId: string;
      serialNumber: string;
      assetNumber?: string | null;
      assetNumbersEnabled?: boolean;
      lifecycleStatus?: string;
      manufacturedAt?: string | null;
      installedAt?: string | null;
      commissionedAt?: string | null;
      warrantyStartAt?: string | null;
      warrantyEndAt?: string | null;
    }) => {
      const customerId = String(payload.customerId || '').trim();
      const modelId = String(payload.modelId || '').trim();
      const serialNumber = String(payload.serialNumber || '').trim();
      const assetNumber = String(payload.assetNumber || '').trim();
      if (!customerId || !modelId || !serialNumber) {
        throw new Error('Customer, device model, and serial number are required.');
      }

      const selectedCustomer = customers.find(customer => customer.id === customerId);
      if (payload.assetNumbersEnabled && selectedCustomer && !selectedCustomer.assetNumbersEnabled) {
        const updatedCustomer = await customerApi.update(customerId, { assetNumbersEnabled: true });
        setCustomers(current =>
          current.map(customer =>
            customer.id === customerId
              ? { ...customer, ...(updatedCustomer || {}), assetNumbersEnabled: true }
              : customer
          )
        );
      }

      const createdDevice = await deviceApi.create({
        modelId,
        customerId,
        serialNumber,
        lifecycleStatus: payload.lifecycleStatus || 'DEPLOYED',
        ...(payload.assetNumbersEnabled ? { assetNumber: assetNumber || null } : {}),
        ...(payload.manufacturedAt ? { manufacturedAt: payload.manufacturedAt } : {}),
        ...(payload.installedAt ? { installedAt: payload.installedAt } : {}),
        ...(payload.commissionedAt ? { commissionedAt: payload.commissionedAt } : {}),
        ...(payload.warrantyStartAt ? { warrantyStartAt: payload.warrantyStartAt } : {}),
        ...(payload.warrantyEndAt ? { warrantyEndAt: payload.warrantyEndAt } : {}),
        notes: 'Created during internal job setup'
      }) as Device;

      setDevices(current => [
        createdDevice,
        ...current.filter(device => device.id !== createdDevice.id)
      ]);
      await refreshCustomerState(customerId);
      return createdDevice;
    },
    [customers, refreshCustomerState]
  );

  const ensureSimpleDeviceForGeneration = async () => {
    if (internalJobForm.mode !== 'SIMPLE') return null;
    const selectedDevice = resolveSimpleTargetDevice();
    if (selectedDevice) return selectedDevice;
    throw new Error('Select a registered asset or serial number before generating this job.');
  };

  const ensureInternalJobDraft = async (options: { targetDevice?: Device | null } = {}) => {
    if (internalJobDraft) return internalJobDraft;
    const resolvedTitle = buildInternalJobTitle(options.targetDevice);
    if (!resolvedTitle) {
      throw new Error('Title is required');
    }
    const created = await internalJobApi.create({
      type: resolveInternalJobType(),
      title: resolvedTitle,
      description: internalJobForm.description.trim() || undefined,
      instructionRef: internalJobForm.instructionRef.trim() || undefined,
      status: 'DRAFT',
      filters: buildInternalJobFilters({ targetDevice: options.targetDevice })
    });
    setInternalJobDraft(created);
    return created;
  };

  const buildInternalJobSerialAuditRows = (
    requestedIdentifiers: string[],
    matchedDevices: Device[],
    options: { includeMatchedDevices?: boolean } = {}
  ): InternalJobSerialAuditRow[] => {
    const index = new Map<string, Array<{ device: Device; matchType: 'asset' | 'serial' }>>();
    const addMatch = (identifier: string | null | undefined, device: Device, matchType: 'asset' | 'serial') => {
      const key = String(identifier || '').trim().toUpperCase();
      if (!key) return;
      const matches = index.get(key) || [];
      matches.push({ device, matchType });
      index.set(key, matches);
    };

    matchedDevices.forEach(device => {
      addMatch(device?.serialNumber, device, 'serial');
      addMatch(device?.assetNumber, device, 'asset');
    });

    const buildMatchedRow = (
      identifier: string,
      device: Device,
      matchType: 'asset' | 'serial'
    ): InternalJobSerialAuditRow => {
      const customerLabel =
        String(device?.customer?.companyName || (device?.customer as any)?.company || device?.customer?.email || '').trim() ||
        'No customer';
      const hasCustomer = Boolean(String(device?.customerId || '').trim());
      const isActive = device?.isActive !== false;
      const lifecycleStatus = String(device?.lifecycleStatus || '').trim().toUpperCase();
      const isDeployed = lifecycleStatus === 'DEPLOYED';
      const statusLabel = lifecycleStatus || (isActive ? 'Active' : 'Inactive');
      const modelNumber = String(device?.modelNumber || device?.model?.modelNumber || '').trim();
      const manufacturer = String(device?.manufacturer || device?.model?.manufacturer || '').trim();
      const deviceModelLabel = [modelNumber, manufacturer].filter(Boolean).join(' - ') || 'Registered device';
      let reason = 'Ready for case creation.';
      if (!hasCustomer) {
        reason = 'Registered device is not linked to a customer.';
      } else if (!isActive) {
        reason = 'Registered device is inactive.';
      } else if (!isDeployed) {
        reason = `Device status is ${statusLabel}; only deployed devices are included.`;
      }
      return {
        serial: identifier,
        submittedIdentifier: identifier,
        matchType,
        assetNumber: device?.assetNumber || null,
        serialNumber: device?.serialNumber || null,
        deviceModelLabel,
        customerLabel,
        statusLabel,
        included: hasCustomer && isActive && isDeployed,
        reason,
        deviceId: device.id || null
      };
    };

    const rows: InternalJobSerialAuditRow[] = requestedIdentifiers.map(identifier => {
      const identifierKey = identifier.toUpperCase();
      const matches = index.get(identifierKey) || [];
      const uniqueMatches: Array<{ device: Device; matchType: 'asset' | 'serial' }> = [];
      const seenDeviceIds = new Set<string>();
      matches.forEach(match => {
        const deviceId = String(match.device?.id || '').trim();
        if (!deviceId || seenDeviceIds.has(deviceId)) return;
        seenDeviceIds.add(deviceId);
        uniqueMatches.push(match);
      });

      if (uniqueMatches.length === 0) {
        return {
          serial: identifier,
          submittedIdentifier: identifier,
          matchType: 'unknown',
          assetNumber: null,
          serialNumber: null,
          deviceModelLabel: 'No matching registered device',
          customerLabel: 'No customer',
          statusLabel: 'Not found',
          included: false,
          reason: 'No registered device matched this serial or asset number.',
          deviceId: null
        };
      }

      if (uniqueMatches.length > 1) {
        return {
          serial: identifier,
          submittedIdentifier: identifier,
          matchType: 'unknown',
          assetNumber: null,
          serialNumber: null,
          deviceModelLabel: `${uniqueMatches.length} matching devices`,
          customerLabel: 'Multiple customers',
          statusLabel: 'Ambiguous',
          included: false,
          reason: 'Identifier matches more than one device; create these jobs individually.',
          deviceId: null
        };
      }

      const { device, matchType } = uniqueMatches[0];
      return buildMatchedRow(identifier, device, matchType);
    });

    if (!options.includeMatchedDevices) {
      return rows;
    }

    const representedDeviceIds = new Set(rows.map(row => row.deviceId).filter(Boolean).map(String));
    matchedDevices.forEach(device => {
      const deviceId = String(device?.id || '').trim();
      if (!deviceId || representedDeviceIds.has(deviceId)) return;
      const identifier = String(device?.serialNumber || device?.assetNumber || deviceId).trim();
      rows.push(buildMatchedRow(identifier, device, device?.serialNumber ? 'serial' : 'asset'));
      representedDeviceIds.add(deviceId);
    });

    return rows;
  };

  const handleCheckInternalJobSerials = async () => {
    setInternalJobSubmitting(true);
    setInternalJobError(null);
    setInternalJobSerialAudit([]);
    try {
      const requestedIdentifiers = parseExactDeviceIdentifiers(internalJobForm.serialNumbers);
      const serialRange = resolveInternalJobSerialRange();
      const caseCap = resolveInternalJobCaseCap();
      if (requestedIdentifiers.length === 0 && !serialRange) {
        throw new Error('Enter exact serials/assets or a serial range to check.');
      }
      const filters = buildInternalJobFilters();
      const preview = await internalJobApi.previewDevices({
        filters,
        limit: buildInternalJobPreviewLimit(requestedIdentifiers.length, caseCap)
      });
      const previewDevices = Array.isArray(preview?.devices) ? (preview.devices as Device[]) : [];
      const rows = buildInternalJobSerialAuditRows(requestedIdentifiers, previewDevices, {
        includeMatchedDevices: Boolean(serialRange)
      });
      setInternalJobSerialAudit(rows);
      const eligibleDeviceIds = Array.from(
        new Set(rows.filter(row => row.included && row.deviceId).map(row => String(row.deviceId)))
      );
      if (rows.length === 0) {
        setInternalJobError('No registered devices matched the selected identifiers or range.');
      } else if (eligibleDeviceIds.length > caseCap) {
        setInternalJobError(
          `${eligibleDeviceIds.length} eligible devices found. Current case cap is ${caseCap}. Increase cap or narrow the selection before generating.`
        );
      }
    } catch (error: any) {
      setInternalJobError(error?.message || 'Failed to check devices');
    } finally {
      setInternalJobSubmitting(false);
    }
  };

  const handleGenerateInternalJob = async () => {
    setInternalJobSubmitting(true);
    setInternalJobError(null);
    try {
      const targetDevice =
        internalJobForm.mode === 'SIMPLE' ? await ensureSimpleDeviceForGeneration() : null;
      const requestedIdentifiers =
        internalJobForm.mode !== 'SIMPLE'
          ? parseExactDeviceIdentifiers(internalJobForm.serialNumbers)
          : [];
      const serialRange =
        internalJobForm.mode !== 'SIMPLE'
          ? resolveInternalJobSerialRange()
          : null;
      const caseCap =
        internalJobForm.mode !== 'SIMPLE'
          ? resolveInternalJobCaseCap()
          : null;
      if (internalJobForm.mode !== 'SIMPLE' && requestedIdentifiers.length === 0 && !serialRange) {
        throw new Error('Enter exact serials/assets or a serial range for multi-case creation.');
      }
      const job = await ensureInternalJobDraft({ targetDevice });
      let generationFilters = buildInternalJobFilters({ targetDevice });

      if (internalJobForm.mode !== 'SIMPLE') {
        const preview = await internalJobApi.preview(job.id, {
          filters: generationFilters,
          limit: buildInternalJobPreviewLimit(requestedIdentifiers.length, caseCap || 100)
        });
        const previewDevices = Array.isArray(preview?.devices) ? (preview.devices as Device[]) : [];
        const rows = buildInternalJobSerialAuditRows(requestedIdentifiers, previewDevices, {
          includeMatchedDevices: Boolean(serialRange)
        });
        setInternalJobSerialAudit(rows);
        const eligibleDeviceIds = Array.from(
          new Set(rows.filter(row => row.included && row.deviceId).map(row => String(row.deviceId)))
        );
        if (eligibleDeviceIds.length === 0) {
          throw new Error('No ready deployed customer devices found. No cases were created.');
        }
        if (caseCap && eligibleDeviceIds.length > caseCap) {
          throw new Error(
            `${eligibleDeviceIds.length} eligible devices found. Current case cap is ${caseCap}. Increase cap or narrow the selection before generating.`
          );
        }
        generationFilters = {
          ...generationFilters,
          deviceIds: eligibleDeviceIds,
          requireDeployed: true,
          caseCap: caseCap || 100
        };
        delete generationFilters.deviceIdentifiers;
        delete generationFilters.serialNumbers;
        delete generationFilters.assetNumbers;
        delete generationFilters.serialRange;
        delete generationFilters.serialRangeFrom;
        delete generationFilters.serialRangeTo;
        delete generationFilters.manufacturedFrom;
        delete generationFilters.manufacturedTo;
        delete generationFilters.manufacturedAtFrom;
        delete generationFilters.manufacturedAtTo;
      }

      await internalJobApi.generate(job.id, {
        filters: generationFilters,
        taskGroup: internalJobForm.taskGroup,
        assigneeUserId: internalJobForm.assigneeUserId.trim() || undefined,
        priority: internalJobForm.priority,
        attachments: internalJobUploads
      });

      await loadData({ silent: true });
      setShowInternalJobModal(false);
      resetInternalJobForm();
    } catch (error: any) {
      setInternalJobError(error?.message || 'Failed to generate cases');
    } finally {
      setInternalJobSubmitting(false);
    }
  };


  const handleAssignTaskGroup = useCallback(
    async (caseId: string, group: TaskGroupCode) => {
      try {
        if (assignmentError?.caseId === caseId) {
          setAssignmentError(null);
        }
        setAssigningCaseId(caseId);
        const nowIso = new Date().toISOString();
        const optimisticTask: CaseTask = {
          id: `optimistic-${caseId}-${group}`,
          taskCode: null,
          status: 'PLANNED',
          assignedUserId: null,
          assignedUserName: null,
          assigneeUserIds: [],
          assignees: [],
          assigneeCount: 0,
          plannedStart: null,
          plannedDue: null,
          startedAt: null,
          completedAt: null,
          createdAt: nowIso,
          updatedAt: nowIso,
          type: {
            code: group,
            label: TASK_GROUP_META[group]?.label || group,
            category: TASK_GROUP_META[group]?.parentCode || group
          },
          stage: null,
          runs: []
        };
        const applyAssignedTask = (source: Case, nextTask: CaseTask): Case => {
          const existingTasks = Array.isArray(source.tasks) ? source.tasks : [];
          const nextCode = nextTask?.type?.code ? String(nextTask.type.code).toUpperCase() : '';
          const nextTasks = existingTasks
            .filter(task => {
              const code = task?.type?.code ? String(task.type.code).toUpperCase() : '';
              return code !== nextCode;
            })
            .map(task => {
              const status = String(task?.status || '').toUpperCase();
              if (status === 'COMPLETED' || status === 'CANCELLED' || status === 'ARCHIVED') {
                return task;
              }
              return { ...task, status: 'CANCELLED' };
            });
          nextTasks.push(nextTask);
          return { ...source, tasks: nextTasks };
        };
        setCases(prev => prev.map(item => (item.id === caseId ? applyAssignedTask(item, optimisticTask) : item)));
        setSelectedCase(prev => (prev?.id === caseId ? applyAssignedTask(prev, optimisticTask) : prev));

        const response = await caseApi.updateWorkflow(caseId, { taskGroup: group });
        const returnedTask = (response?.task || null) as CaseTask | null;
        if (returnedTask) {
          setCases(prev => prev.map(item => (item.id === caseId ? applyAssignedTask(item, returnedTask) : item)));
          setSelectedCase(prev => (prev?.id === caseId ? applyAssignedTask(prev, returnedTask) : prev));
        }
        setAssignmentPickerCaseId(null);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
            ? error
            : 'Failed to assign task group.';
        setAssignmentError({ caseId, message });
      } finally {
        setAssigningCaseId(null);
      }
    },
    [assignmentError?.caseId]
  );

  const handleOpenInstallPhase = useCallback(
    (caseId: string, phaseId: string) => {
      if (phaseId === 'PREP') {
        navigate(`/install/${encodeURIComponent(caseId)}/pre`);
      } else if (phaseId === 'EXECUTION') {
        navigate(`/install/${encodeURIComponent(caseId)}/exec`);
      } else if (phaseId === 'VERIFICATION') {
        navigate(`/install/${encodeURIComponent(caseId)}/verify`);
      }
    },
    [navigate]
  );
  const handleOpenDecommission = useCallback(
    (caseId: string) => {
      navigate(`/install/${encodeURIComponent(caseId)}/decom`);
    },
    [navigate]
  );

  const startProblemEdit = useCallback((caseItem: Case) => {
    const source = (caseItem as any).problemDescription ?? (caseItem as any).problemDesc ?? '';
    const parsed = parseProblemDetails(source);
    setEditProblemSummary(parsed.summary);
    setEditProblemDetails(parsed.details.length > 0 ? parsed.details : ['']);
    setEditingCaseId(caseItem.id);
    setProblemEditError(null);
  }, []);

  const cancelProblemEdit = useCallback(() => {
    setEditingCaseId(null);
    setProblemEditError(null);
  }, []);

  const updateProblemDetail = useCallback((index: number, value: string) => {
    setEditProblemDetails(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const addProblemDetailLine = useCallback(() => {
    setEditProblemDetails(prev => [...prev, '']);
  }, []);

  const removeProblemDetailLine = useCallback((index: number) => {
    setEditProblemDetails(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const saveProblemDetails = useCallback(
    async (caseItem: Case) => {
      const summary = editProblemSummary.trim();
      const details = editProblemDetails.map(entry => entry.trim()).filter(Boolean);
      if (!summary && details.length === 0) {
        setProblemEditError('Add a summary or at least one detail.');
        return;
      }

      setSavingProblemId(caseItem.id);
      setProblemEditError(null);
      try {
        const updated = await caseApi.updateProblem(caseItem.id, { summary, details });
        setCases(prev => prev.map(item => (item.id === caseItem.id ? updated : item)));
        setSelectedCase(prev => (prev?.id === caseItem.id ? updated : prev));
        setCaseDetailModal(prev => (prev?.id === caseItem.id ? updated : prev));
        setEditingCaseId(null);
      } catch (error: any) {
        setProblemEditError(error?.message || 'Failed to save problem details.');
      } finally {
        setSavingProblemId(null);
      }
    },
    [editProblemDetails, editProblemSummary]
  );
  const filteredCases = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const normalizeUserMatchValue = (value: unknown) => String(value || '').trim().toLowerCase();
    const selectedCaseUserOption =
      caseUserFilter === 'all'
        ? null
        : caseUserOptions.find(option => option.value === caseUserFilter) || null;
    const selectedCaseUserAssignee =
      caseUserFilter === 'all'
        ? null
        : caseAssigneeOptions.find(option => {
            const target = normalizeUserMatchValue(caseUserFilter);
            return (
              normalizeUserMatchValue(option.id) === target ||
              normalizeUserMatchValue(option.username) === target ||
              normalizeUserMatchValue(option.fullName) === target
            );
          }) || null;
    const selectedCaseUserNeedles = new Set(
      [
        caseUserFilter,
        selectedCaseUserOption?.label,
        selectedCaseUserAssignee?.id,
        selectedCaseUserAssignee?.username,
        selectedCaseUserAssignee?.fullName
      ]
        .map(normalizeUserMatchValue)
        .filter(Boolean)
    );

    const filtered = cases.filter(c => {
      const desc = (c as any).problemDescription ?? (c as any).problemDesc ?? '';
      const caseNo = getCaseDisplayNumber(c);
      const normStatus = normalizeStatus(c.status as unknown as string);
      const matchesSearch =
        !normalizedSearch ||
        desc.toLowerCase().includes(normalizedSearch) ||
        caseNo.toLowerCase().includes(normalizedSearch);
      const matchesStatus = statusFilter === 'all' || normStatus === statusFilter;
      const priorityValue = (c.priority || 'MEDIUM').toString().toUpperCase();
      const matchesPriority = priorityFilter === 'all' || priorityValue === priorityFilter;
      const { id: caseCustomerId } = resolveCaseCustomer(c);
      const matchesCustomer =
        customerFilter === 'all' ||
        (customerFilter === '__unassigned__' ? !caseCustomerId : caseCustomerId === customerFilter);
      const caseDeviceModel = resolveCaseDeviceModel(c);
      const matchesDeviceModel =
        deviceModelFilter === 'all' || caseDeviceModel.value === deviceModelFilter;
      const lifecycleTask = c.displayTask || c.activeTask || getActiveCaseTask(c.tasks);
      const lifecycleTaskGroup = resolveTaskGroupCode(lifecycleTask?.type?.code);
      const lifecycleTaskAssigneeIds = getTaskAssigneeIds(lifecycleTask);
      const lifecycleTaskAssigneeNames = getTaskAssigneeDisplayNames(lifecycleTask);
      const lifecycleTaskAssignees =
        lifecycleTask && Array.isArray(lifecycleTask.assignees) ? lifecycleTask.assignees : [];
      const lifecycleTaskAssigneeValues = [
        ...lifecycleTaskAssigneeIds,
        ...lifecycleTaskAssigneeNames,
        ...lifecycleTaskAssignees.flatMap(assignee => [
          assignee?.id,
          assignee?.username,
          assignee?.fullName
        ])
      ];
      const hasTaskAssignee = Boolean(
        lifecycleTask &&
          (
            lifecycleTaskAssigneeIds.some(value => isInternalAssigneeValue(value)) ||
            lifecycleTaskAssigneeNames.some(value => isInternalAssigneeValue(value))
          )
      );
      const matchesAssignment =
        assignmentFilter === 'all' ||
        (assignmentFilter === 'assigned' ? hasTaskAssignee : !hasTaskAssignee);
      const matchesCaseUser =
        !canUseCaseUserFilter ||
        caseUserFilter === 'all' ||
        lifecycleTaskAssigneeValues.some(value =>
          selectedCaseUserNeedles.has(normalizeUserMatchValue(value))
        );
      const matchesTaskGroup =
        taskGroupFilter === 'all' ||
        (taskGroupFilter === 'UNASSIGNED'
          ? !lifecycleTaskGroup
          : lifecycleTaskGroup === taskGroupFilter);
      const { value: caseDeviceId } = resolveCaseDeviceSerial(c);
      const matchesDevice =
        deviceFilter === 'all' ||
        (deviceFilter === '__no_device__' ? !caseDeviceId : caseDeviceId === deviceFilter);
      return (
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesCustomer &&
        matchesDeviceModel &&
        matchesAssignment &&
        matchesCaseUser &&
        matchesTaskGroup &&
        matchesDevice
      );
    });

    return filtered
      .slice()
      .sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return caseSortOrder === 'newest' ? timeB - timeA : timeA - timeB;
      });
  }, [
    cases,
    searchQuery,
    statusFilter,
    priorityFilter,
    customerFilter,
    deviceModelFilter,
    assignmentFilter,
    canUseCaseUserFilter,
    caseUserFilter,
    caseUserOptions,
    caseAssigneeOptions,
    taskGroupFilter,
    deviceFilter,
    caseSortOrder,
    resolveCaseCustomer,
    resolveCaseDeviceModel,
    resolveCaseDeviceSerial
  ]);

  useEffect(() => {
    setCasesPage(1);
  }, [
    searchQuery,
    statusFilter,
    priorityFilter,
    customerFilter,
    deviceModelFilter,
    assignmentFilter,
    caseUserFilter,
    taskGroupFilter,
    deviceFilter,
    caseSortOrder
  ]);

  const casesPageCount = Math.max(1, Math.ceil(filteredCases.length / casesPageSize));

  useEffect(() => {
    setCasesPage(current => Math.min(current, casesPageCount));
  }, [casesPageCount]);

  const paginatedCases = useMemo(() => {
    const startIndex = (casesPage - 1) * casesPageSize;
    return filteredCases.slice(startIndex, startIndex + casesPageSize);
  }, [casesPage, casesPageSize, filteredCases]);

  const casesPageStart = filteredCases.length === 0 ? 0 : (casesPage - 1) * casesPageSize + 1;
  const casesPageEnd = filteredCases.length === 0 ? 0 : Math.min(casesPage * casesPageSize, filteredCases.length);

  useEffect(() => {
    const customerId = String(customerResourceModal?.customerId || '').trim();
    if (!customerId) return;
    void loadCustomerDetail(customerId).catch(error => {
      console.warn('Failed to load customer resource detail:', error);
    });
  }, [customerResourceModal?.customerId, loadCustomerDetail]);

  const activeModalCustomerSummary = useMemo(() => {
    if (!customerResourceModal) return null;
    return customers.find(c => c.id === customerResourceModal.customerId) ?? null;
  }, [customerResourceModal, customers]);

  const activeModalCustomerDetail = useMemo(() => {
    if (!activeModalCustomerSummary?.id) return null;
    return customerDetailsById[activeModalCustomerSummary.id] ?? null;
  }, [activeModalCustomerSummary, customerDetailsById]);

  const activeModalCustomer = useMemo(() => {
    if (!activeModalCustomerSummary) return null;
    return activeModalCustomerDetail || activeModalCustomerSummary;
  }, [activeModalCustomerDetail, activeModalCustomerSummary]);

  const activeModalCustomerDetailReady = Boolean(
    activeModalCustomerSummary?.id && activeModalCustomerDetail?.id
  );
  const activeModalCustomerDetailLoading =
    Boolean(customerResourceModal?.customerId) &&
    customerDetailLoadingId === customerResourceModal?.customerId &&
    !activeModalCustomerDetailReady;
  const activeModalCustomerDetailError =
    customerDetailError?.customerId === customerResourceModal?.customerId
      ? customerDetailError?.message ?? null
      : null;

  const modalCases = useMemo(() => {
    if (!customerResourceModal || customerResourceModal.mode !== 'cases') return [];
    return cases
      .filter(caseItem => {
        const { id: caseCustomerId } = resolveCaseCustomer(caseItem);
        return caseCustomerId && caseCustomerId === customerResourceModal.customerId;
      })
      .sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      });
  }, [cases, customerResourceModal, resolveCaseCustomer]);

  const modalDevices = useMemo(() => {
    return activeModalCustomerDetail?.devices ?? [];
  }, [activeModalCustomerDetail]);

  const modalContacts = useMemo(() => {
    const list = activeModalCustomerDetail?.contacts ?? [];
    return [...list].sort((a, b) => {
      const primaryOrder = (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0);
      if (primaryOrder !== 0) return primaryOrder;
      return String(a.fullName || '').localeCompare(String(b.fullName || ''));
    });
  }, [activeModalCustomerDetail]);

  const modalLocationTree = useMemo(() => {
    return buildLocationTree(activeModalCustomerDetail?.locations ?? []);
  }, [activeModalCustomerDetail]);

  const modalLocationMap = useMemo(() => {
    const map = new Map<string, CustomerLocation>();
    if (!activeModalCustomerDetail?.locations) {
      return map;
    }
    activeModalCustomerDetail.locations.forEach(location => {
      if (location?.id) {
        map.set(location.id, location);
      }
    });
    return map;
  }, [activeModalCustomerDetail]);

  const openCustomerResourceModal = useCallback((customerId: string, mode: 'devices' | 'cases' | 'contacts' | 'locations') => {
    if (!customerId) return;
    setCustomerResourceModal({ customerId, mode });
  }, []);

  const closeCustomerResourceModal = useCallback(() => {
    setCustomerResourceModal(null);
  }, []);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'OPEN':
      case 'NEW':
        return { ...styles.caseStatus, ...styles.statusNew };
      case 'IN_PROGRESS':
        return { ...styles.caseStatus, ...styles.statusInProgress };
      case 'ON_HOLD':
      case 'WAITING_CUSTOMER':
      case 'WAITING_PARTS':
        return { ...styles.caseStatus, ...styles.statusOnHold };
      case 'RESOLVED':
        return { ...styles.caseStatus, ...styles.statusResolved };
      case 'CLOSED':
        return { ...styles.caseStatus, ...styles.statusClosed };
      default:
        return styles.caseStatus;
    }
  };

  const getPriorityStyle = (priority?: string | null) => {
    switch ((priority || '').toUpperCase()) {
      case 'CRITICAL':
        return { ...styles.priority, ...styles.priorityCritical };
      case 'HIGH':
        return { ...styles.priority, ...styles.priorityHigh };
      case 'MEDIUM':
        return { ...styles.priority, ...styles.priorityMedium };
      case 'LOW':
        return { ...styles.priority, ...styles.priorityLow };
      default:
        return styles.priority;
    }
  };

  const formatPriorityLabel = (priority?: string | null) => {
    const value = (priority || 'MEDIUM').toUpperCase();
    if (value === 'MEDIUM') return 'Standard';
    return value;
  };
  const getQuoteDecisionBadge = (quote: CaseQuoteRecord) => {
    const label = getInsightQuoteDecisionLabel(quote);
    if (label === 'INACTIVE') {
      return { label: 'INACTIVE', style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionDenied } };
    }
    if (label === 'DRAFT') {
      return { label: 'DRAFT', style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionPending } };
    }
    if (label === 'WAITING RESPONSE') {
      return {
        label: 'WAITING RESPONSE',
        style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionPending }
      };
    }
    if (label === 'ACCEPTED') {
      return { label: 'ACCEPTED', style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionAccepted } };
    }
    if (label === 'DENIED') {
      return { label: 'DENIED', style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionDenied } };
    }
    if (label === 'ADJUST REQUESTED') {
      return {
        label: 'ADJUST REQUESTED',
        style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionAdjust }
      };
    }
    return { label: 'PENDING', style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionPending } };
  };
  const getQuotePartsBadge = (quote: CaseQuoteRecord) => {
    const summary = getInsightQuotePartsSummary(quote);
    if (!summary) return null;
    const etaAt = summary.etaAt ? new Date(summary.etaAt) : null;
    const etaLabel =
      etaAt && Number.isFinite(etaAt.getTime())
        ? `ETA ${etaAt.toLocaleDateString()}`
        : null;
    const style =
      summary.tone === 'success'
        ? { ...styles.quotePartsPill, ...styles.quotePartsSuccess }
        : summary.tone === 'info'
        ? { ...styles.quotePartsPill, ...styles.quotePartsInfo }
        : summary.tone === 'danger'
        ? { ...styles.quotePartsPill, ...styles.quotePartsDanger }
        : { ...styles.quotePartsPill, ...styles.quotePartsWarning };
    return {
      label: etaLabel ? `${summary.label} (${etaLabel})` : summary.label,
      detail: summary.detail,
      style
    };
  };
  const metricsDashboardConfig = useMemo(
    () => normalizeMetricsDashboardConfig(metricsDashboard?.config),
    [metricsDashboard?.config]
  );
  const metricsDashboardDraftConfig = useMemo(
    () => normalizeMetricsDashboardConfig(metricsConfigDraft),
    [metricsConfigDraft]
  );
  const metricsOverviewIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'overview')),
    [metricsDashboardConfig]
  );
  const metricsServiceIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'service')),
    [metricsDashboardConfig]
  );
  const metricsReliabilityIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'reliability')),
    [metricsDashboardConfig]
  );
  const metricsCustomersIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'customers')),
    [metricsDashboardConfig]
  );
  const metricsSlaIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'sla')),
    [metricsDashboardConfig]
  );
  const metricsInventoryIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'inventory')),
    [metricsDashboardConfig]
  );
  const metricsTeamIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, 'team')),
    [metricsDashboardConfig]
  );
  const metricsCurrentWorkspaceIncludedBlocks = useMemo(
    () => new Set(getMetricsWorkspaceIncludes(metricsDashboardConfig, metricsWorkspaceTab)),
    [metricsDashboardConfig, metricsWorkspaceTab]
  );
  const metricsServiceGridEnabled = useMemo(
    () => SERVICE_METRICS_GRID_BLOCK_IDS.some(blockId => metricsServiceIncludedBlocks.has(blockId)),
    [metricsServiceIncludedBlocks]
  );
  const metricsReliabilityGridEnabled = useMemo(
    () => RELIABILITY_METRICS_GRID_BLOCK_IDS.some(blockId => metricsReliabilityIncludedBlocks.has(blockId)),
    [metricsReliabilityIncludedBlocks]
  );
  const metricsCustomersGridEnabled = useMemo(
    () => CUSTOMERS_METRICS_GRID_BLOCK_IDS.some(blockId => metricsCustomersIncludedBlocks.has(blockId)),
    [metricsCustomersIncludedBlocks]
  );
  const metricsSlaGridEnabled = useMemo(
    () => SLA_METRICS_GRID_BLOCK_IDS.some(blockId => metricsSlaIncludedBlocks.has(blockId)),
    [metricsSlaIncludedBlocks]
  );
  const metricsInventoryGridEnabled = useMemo(
    () => INVENTORY_METRICS_GRID_BLOCK_IDS.some(blockId => metricsInventoryIncludedBlocks.has(blockId)),
    [metricsInventoryIncludedBlocks]
  );
  const metricsTeamGridEnabled = useMemo(
    () => TEAM_METRICS_GRID_BLOCK_IDS.some(blockId => metricsTeamIncludedBlocks.has(blockId)),
    [metricsTeamIncludedBlocks]
  );
  const metricsCurrentWorkspaceDraftIncludes = useMemo(
    () => getMetricsWorkspaceIncludes(metricsDashboardDraftConfig, metricsWorkspaceTab),
    [metricsDashboardDraftConfig, metricsWorkspaceTab]
  );
  const metricsCurrentWorkspaceIncludeIds = useMemo(
    () => Array.from(metricsCurrentWorkspaceIncludedBlocks),
    [metricsCurrentWorkspaceIncludedBlocks]
  );
  const metricsWorkspaceIncludeBlocks = useMemo(
    () => METRICS_WORKSPACE_INCLUDE_BLOCKS[metricsWorkspaceTab] || [],
    [metricsWorkspaceTab]
  );
  const canConfigureMetricsWorkspace = metricsWorkspaceIncludeBlocks.length > 0;
  const isMetricsWorkspaceBlockEnabled = useCallback(
    (blockId: string) => {
      if (metricsWorkspaceTab === 'overview') {
        return metricsOverviewIncludedBlocks.has(blockId);
      }
      if (metricsWorkspaceTab === 'service') {
        return metricsServiceIncludedBlocks.has(blockId);
      }
      if (metricsWorkspaceTab === 'reliability') {
        return metricsReliabilityIncludedBlocks.has(blockId);
      }
      if (metricsWorkspaceTab === 'customers') {
        return metricsCustomersIncludedBlocks.has(blockId);
      }
      if (metricsWorkspaceTab === 'sla') {
        return metricsSlaIncludedBlocks.has(blockId);
      }
      if (metricsWorkspaceTab === 'inventory') {
        return metricsInventoryIncludedBlocks.has(blockId);
      }
      if (metricsWorkspaceTab === 'team') {
        return metricsTeamIncludedBlocks.has(blockId);
      }
      return true;
    },
    [
      metricsCustomersIncludedBlocks,
      metricsInventoryIncludedBlocks,
      metricsOverviewIncludedBlocks,
      metricsReliabilityIncludedBlocks,
      metricsServiceIncludedBlocks,
      metricsSlaIncludedBlocks,
      metricsTeamIncludedBlocks,
      metricsWorkspaceTab
    ]
  );
  const isMetricsOverviewGridEnabled = useMemo(
    () => OVERVIEW_METRICS_GRID_BLOCK_IDS.some(blockId => metricsOverviewIncludedBlocks.has(blockId)),
    [metricsOverviewIncludedBlocks]
  );
  const isMetricsWidgetEnabled = useCallback(
    (widgetId: string) => {
      if (metricsWorkspaceTab === 'overview') {
        if (widgetId === 'signals') return isMetricsWorkspaceBlockEnabled('signals');
        if (widgetId === 'overview-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'overview-grid') return isMetricsOverviewGridEnabled;
      }
      if (metricsWorkspaceTab === 'service') {
        if (widgetId === 'service-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'service-grid') return metricsServiceGridEnabled;
      }
      if (metricsWorkspaceTab === 'reliability') {
        if (widgetId === 'reliability-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'reliability-grid') return metricsReliabilityGridEnabled;
      }
      if (metricsWorkspaceTab === 'customers') {
        if (widgetId === 'customers-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'customers-grid') return metricsCustomersGridEnabled;
      }
      if (metricsWorkspaceTab === 'sla') {
        if (widgetId === 'sla-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'sla-grid') return metricsSlaGridEnabled;
      }
      if (metricsWorkspaceTab === 'inventory') {
        if (widgetId === 'inventory-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'inventory-grid') return metricsInventoryGridEnabled;
      }
      if (metricsWorkspaceTab === 'team') {
        if (widgetId === 'team-kpis') return isMetricsWorkspaceBlockEnabled('kpis');
        if (widgetId === 'team-grid') return metricsTeamGridEnabled;
      }
      return true;
    },
    [
      isMetricsWorkspaceBlockEnabled,
      metricsCustomersGridEnabled,
      isMetricsOverviewGridEnabled,
      metricsInventoryGridEnabled,
      metricsReliabilityGridEnabled,
      metricsServiceGridEnabled,
      metricsSlaGridEnabled,
      metricsTeamGridEnabled,
      metricsWorkspaceTab
    ]
  );

  const caseStats = {
    open: cases.filter(c => normalizeStatus(c.status as unknown as string) === 'NEW').length,
    inProgress: cases.filter(c => normalizeStatus(c.status as unknown as string) === 'IN_PROGRESS').length,
    waitingCustomer: cases.filter(
      c => normalizeStatus(c.status as unknown as string) === 'WAITING_CUSTOMER'
    ).length,
    waitingParts: cases.filter(c => normalizeStatus(c.status as unknown as string) === 'WAITING_PARTS').length,
    resolved: cases.filter(c => normalizeStatus(c.status as unknown as string) === 'RESOLVED').length,
    closed: cases.filter(c => normalizeStatus(c.status as unknown as string) === 'CLOSED').length,
    needsVerification: unverifiedModelCount
  };
  const caseStatusCards: Array<{ key: string; label: string; count: number }> = [
    { key: 'all', label: 'All Cases', count: cases.length },
    { key: 'NEW', label: 'Open', count: caseStats.open },
    { key: 'IN_PROGRESS', label: 'In Progress', count: caseStats.inProgress },
    { key: 'WAITING_CUSTOMER', label: 'Waiting Customer', count: caseStats.waitingCustomer },
    { key: 'WAITING_PARTS', label: 'Waiting Parts', count: caseStats.waitingParts },
    { key: 'RESOLVED', label: 'Resolved', count: caseStats.resolved },
    { key: 'CLOSED', label: 'Closed', count: caseStats.closed }
  ];

  const getDeviceDisplayLabel = (device?: Device | null) => {
    if (!device) return 'Unknown device';
    const model = device.modelNumber || device.model?.modelNumber || 'Unknown model';
    const manufacturer = device.manufacturer || device.model?.manufacturer || '';
    return manufacturer ? `${model} - ${manufacturer}` : model;
  };
  const getMessageDeviceDisplayLabel = (device?: Device | null) => {
    if (!device) return 'Unknown unique device';
    const modelLabel = getDeviceDisplayLabel(device);
    const asset = String((device as any)?.assetNumber || '').trim();
    const serial = String((device as any)?.serialNumber || '').trim();
    const customerLabel =
      String((device as any)?.customer?.companyName || (device as any)?.customer?.company || '').trim();
    const locationLabel =
      String((device as any)?.location?.name || (device as any)?.locationName || (device as any)?.customerLocationName || '').trim();
    const identifierLabel = [asset ? `Asset ${asset}` : '', serial ? `Serial ${serial}` : '']
      .filter(Boolean)
      .join(' | ');
    return [identifierLabel || 'Unidentified unit', modelLabel, customerLabel, locationLabel]
      .filter(Boolean)
      .join(' | ');
  };
  const getMetricsDeviceCustomerId = useCallback(
    (device?: Device | null) => String(device?.customerId || device?.customer?.id || '').trim(),
    []
  );
  const getMetricsDeviceModelId = useCallback(
    (device?: Device | null) => String(device?.modelId || device?.model?.id || '').trim(),
    []
  );
  const getMetricsCustomerLabel = useCallback(
    (customer?: Customer | null) =>
      String(customer?.companyName ?? (customer as any)?.company ?? customer?.email ?? 'Customer').trim() ||
      'Customer',
    []
  );

  const messageCaseOptions = useMemo(() => {
    return cases.slice().sort((a, b) => {
      const aTime = toDate(a.updatedAt || a.createdAt)?.getTime() ?? 0;
      const bTime = toDate(b.updatedAt || b.createdAt)?.getTime() ?? 0;
      return bTime - aTime;
    });
  }, [cases]);

  const messageDeviceOptions = useMemo(() => {
    return devices.slice().sort((a, b) => getMessageDeviceDisplayLabel(a).localeCompare(getMessageDeviceDisplayLabel(b)));
  }, [devices]);

  const overviewMetricsFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.filterRows)
        ? metricsData.filterRows
        : [],
    [metricsData.filterRows, metricsWorkspaceTab]
  );
  const shouldUseOverviewMetricsFilterRows =
    metricsWorkspaceTab === 'overview' &&
    overviewMetricsFilterRows.length > 0 &&
    (deviceModels.length === 0 || devices.length === 0);
  const serviceCaseFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.serviceCaseFilterRows)
        ? metricsData.serviceCaseFilterRows
        : [],
    [metricsData.serviceCaseFilterRows, metricsWorkspaceTab]
  );
  const reliabilityCaseFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityCaseFilterRows)
        ? metricsData.reliabilityCaseFilterRows
        : [],
    [metricsData.reliabilityCaseFilterRows, metricsWorkspaceTab]
  );
  const customerCaseFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerCaseFilterRows)
        ? metricsData.customerCaseFilterRows
        : [],
    [metricsData.customerCaseFilterRows, metricsWorkspaceTab]
  );
  const slaCaseFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.slaCaseFilterRows)
        ? slaInsightsData.slaCaseFilterRows
        : [],
    [metricsWorkspaceTab, slaInsightsData.slaCaseFilterRows]
  );
  const inventoryCaseFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'inventory' && Array.isArray(inventoryInsightsData.inventoryCaseFilterRows)
        ? inventoryInsightsData.inventoryCaseFilterRows
        : [],
    [inventoryInsightsData.inventoryCaseFilterRows, metricsWorkspaceTab]
  );
  const teamCaseFilterRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamCaseFilterRows)
        ? metricsData.teamCaseFilterRows
        : [],
    [metricsData.teamCaseFilterRows, metricsWorkspaceTab]
  );
  const inventorySupportsCaseDrill =
    metricsWorkspaceTab === 'inventory' &&
    metricsCurrentWorkspaceIncludeIds.some(blockId =>
      ['kpis', 'delayed-parts-repair-delays', 'contributing-cases'].includes(blockId)
    );
  const teamSupportsCaseDrill =
    metricsWorkspaceTab === 'team' &&
    metricsCurrentWorkspaceIncludeIds.some(blockId =>
      [
        'kpis',
        'backlog-by-user',
        'hours-on-task-by-user',
        'customer-satisfaction-by-user',
        'user-mtbf',
        'first-time-fix-by-user',
        'scheduled-load-by-user',
        'repair-performance',
        'workload-detail',
        'contributing-cases'
      ].includes(blockId)
    );
  const metricsWorkspaceSupportsCaseDrill =
    metricsWorkspaceTab === 'service' ||
    metricsWorkspaceTab === 'reliability' ||
    metricsWorkspaceTab === 'customers' ||
    metricsWorkspaceTab === 'sla' ||
    inventorySupportsCaseDrill ||
    teamSupportsCaseDrill;
  const activeMetricsCaseFilterRows = useMemo(() => {
    if (metricsWorkspaceTab === 'service') return serviceCaseFilterRows;
    if (metricsWorkspaceTab === 'reliability') return reliabilityCaseFilterRows;
    if (metricsWorkspaceTab === 'customers') return customerCaseFilterRows;
    if (metricsWorkspaceTab === 'sla') return slaCaseFilterRows;
    if (metricsWorkspaceTab === 'inventory') return inventoryCaseFilterRows;
    if (metricsWorkspaceTab === 'team') return teamCaseFilterRows;
    return [];
  }, [
    customerCaseFilterRows,
    inventoryCaseFilterRows,
    metricsWorkspaceTab,
    reliabilityCaseFilterRows,
    serviceCaseFilterRows,
    slaCaseFilterRows,
    teamCaseFilterRows
  ]);
  const metricsStatusOptions = useMemo(() => {
    if (!metricsWorkspaceSupportsCaseDrill) return [];
    const optionMap = new Map<string, { id: string; label: string }>();
    METRICS_CASE_STATUS_FILTERS.forEach(status => {
      optionMap.set(status.id, status);
    });
    activeMetricsCaseFilterRows.forEach(row => {
      const id = String(row.statusKey || row.statusLabel || '').trim().toUpperCase().replace(/\s+/g, '_');
      if (!id) return;
      optionMap.set(id, {
        id,
        label: String(row.statusLabel || id).trim() || id
      });
    });
    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [activeMetricsCaseFilterRows, metricsWorkspaceSupportsCaseDrill]);
  const metricsCaseOptions = useMemo(() => {
    if (!metricsWorkspaceSupportsCaseDrill) return [];
    return activeMetricsCaseFilterRows
      .map(row => ({
        id: String(row.caseId || row.id || '').trim(),
        label: `${row.caseCode || row.caseId || 'Case'} | ${row.customerName || 'Customer'}`
      }))
      .filter(row => row.id)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeMetricsCaseFilterRows, metricsWorkspaceSupportsCaseDrill]);
  const metricsCustomerOptions = useMemo(() => {
    const sortedCustomers = customers
      .slice()
      .sort((a, b) => getMetricsCustomerLabel(a).localeCompare(getMetricsCustomerLabel(b)));
    if (!devices.length || (!metricsModelId && !metricsDeviceId)) {
      return sortedCustomers;
    }
    return sortedCustomers.filter(customer =>
      devices.some(device => {
        if (metricsDeviceId && device.id !== metricsDeviceId) return false;
        if (metricsModelId && getMetricsDeviceModelId(device) !== metricsModelId) return false;
        return getMetricsDeviceCustomerId(device) === String(customer.id || '').trim();
      })
    );
  }, [
    customers,
    devices,
    getMetricsCustomerLabel,
    getMetricsDeviceCustomerId,
    getMetricsDeviceModelId,
    metricsDeviceId,
    metricsModelId
  ]);
  const metricsModelOptions = useMemo(() => {
    if (shouldUseOverviewMetricsFilterRows) {
      const optionMap = new Map<string, { id: string; label: string }>();
      overviewMetricsFilterRows.forEach(row => {
        const id = String(row.modelId || '').trim();
        if (!id) return;
        optionMap.set(id, {
          id,
          label: String(row.modelLabel || 'Unknown model').trim() || 'Unknown model'
        });
      });
      return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    const sortedModels = deviceModels
      .slice()
      .map(model => ({
        id: String(model.id || '').trim(),
        label: `${model.modelNumber || ''} ${model.modelName || ''}`.trim() || model.id
      }))
      .filter(model => model.id)
      .sort((a, b) => a.label.localeCompare(b.label));
    if (!devices.length || (!metricsCustomerId && !metricsDeviceId)) {
      return sortedModels;
    }
    return sortedModels.filter(model =>
      devices.some(device => {
        if (metricsCustomerId && getMetricsDeviceCustomerId(device) !== metricsCustomerId) return false;
        if (metricsDeviceId && device.id !== metricsDeviceId) return false;
        return getMetricsDeviceModelId(device) === String(model.id || '').trim();
      })
    );
  }, [
    deviceModels,
    devices,
    getMetricsDeviceCustomerId,
    getMetricsDeviceModelId,
    metricsCustomerId,
    metricsDeviceId,
    overviewMetricsFilterRows,
    shouldUseOverviewMetricsFilterRows
  ]);
  const metricsDeviceOptions = useMemo(() => {
    if (shouldUseOverviewMetricsFilterRows) {
      const optionMap = new Map<string, { id: string; label: string }>();
      overviewMetricsFilterRows.forEach(row => {
        const id = String(row.deviceId || '').trim();
        if (!id) return;
        optionMap.set(id, {
          id,
          label:
            String(row.deviceLabel || '').trim() ||
            String(row.serialNumber || '').trim() ||
            'Unknown device'
        });
      });
      return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    return devices
      .filter(device => {
        if (metricsCustomerId && getMetricsDeviceCustomerId(device) !== metricsCustomerId) {
          return false;
        }
        if (metricsModelId && getMetricsDeviceModelId(device) !== metricsModelId) {
          return false;
        }
        return true;
      })
      .map(device => ({
        id: device.id,
        label: `${getDeviceDisplayLabel(device)} | ${device.serialNumber || 'Unknown'}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    devices,
    getMetricsDeviceCustomerId,
    getMetricsDeviceModelId,
    metricsCustomerId,
    metricsModelId,
    overviewMetricsFilterRows,
    shouldUseOverviewMetricsFilterRows
  ]);
  useEffect(() => {
    if (metricsCustomerId && !metricsCustomerOptions.some(customer => customer.id === metricsCustomerId)) {
      setMetricsCustomerId('');
    }
  }, [metricsCustomerId, metricsCustomerOptions]);
  useEffect(() => {
    if (metricsModelId && !metricsModelOptions.some(model => model.id === metricsModelId)) {
      setMetricsModelId('');
    }
  }, [metricsModelId, metricsModelOptions]);
  useEffect(() => {
    if (metricsDeviceId && !metricsDeviceOptions.some(device => device.id === metricsDeviceId)) {
      setMetricsDeviceId('');
    }
  }, [metricsDeviceId, metricsDeviceOptions]);
  useEffect(() => {
    if (
      metricsWorkspaceSupportsCaseDrill &&
      metricsStatus &&
      metricsStatusOptions.length > 0 &&
      !metricsStatusOptions.some(status => status.id === metricsStatus)
    ) {
      setMetricsStatus('');
      setMetricsCaseId('');
    }
  }, [metricsStatus, metricsStatusOptions, metricsWorkspaceSupportsCaseDrill]);
  useEffect(() => {
    if (
      metricsWorkspaceSupportsCaseDrill &&
      metricsCaseId &&
      metricsCaseOptions.length > 0 &&
      !metricsCaseOptions.some(caseOption => caseOption.id === metricsCaseId)
    ) {
      setMetricsCaseId('');
    }
  }, [metricsCaseId, metricsCaseOptions, metricsWorkspaceSupportsCaseDrill]);
  const metricsStartDate = useMemo(() => {
    const parsed = metricsStart ? new Date(`${metricsStart}T00:00:00`) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }, [metricsStart]);
  const metricsEndDate = useMemo(() => {
    const parsed = metricsEnd ? new Date(`${metricsEnd}T23:59:59`) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }, [metricsEnd]);
  const metricsReferenceDate = useMemo(() => metricsEndDate || new Date(), [metricsEndDate]);
  const customerNameById = useMemo(
    () =>
      new Map(
        customers.map(customer => [
          customer.id,
          customer.companyName || (customer as any).company || customer.email || 'Customer'
        ])
      ),
    [customers]
  );
  const metricsUserLabelById = useMemo(
    () => new Map(metricsUserOptions.map(option => [option.id, option.label])),
    [metricsUserOptions]
  );
  const selectedMetricsUserIdSet = useMemo(
    () => new Set(metricsUserIds),
    [metricsUserIds]
  );
  const activeMetricsUserOptionIds = useMemo(
    () => metricsUserOptions.filter(option => option.isActive).map(option => option.id),
    [metricsUserOptions]
  );
  const effectiveMetricsUserIds = useMemo(() => {
    if (metricsUserIds.length === 0) return [];
    const selectedIds = new Set(metricsUserIds);
    const allActiveUsersSelected =
      activeMetricsUserOptionIds.length > 0 &&
      activeMetricsUserOptionIds.every(id => selectedIds.has(id));
    return allActiveUsersSelected ? [] : metricsUserIds;
  }, [activeMetricsUserOptionIds, metricsUserIds]);
  const metricsUserFilterLabel = useMemo(() => {
    if (effectiveMetricsUserIds.length === 0) return 'All users';
    if (metricsUserIds.length === 1) {
      return metricsUserLabelById.get(metricsUserIds[0]) || '1 user';
    }
    return `${metricsUserIds.length} users`;
  }, [effectiveMetricsUserIds.length, metricsUserIds, metricsUserLabelById]);
  const clearMetricsUserFilter = useCallback(() => {
    setMetricsUserIds([]);
    setMetricsCaseId('');
  }, []);
  const toggleMetricsUserFilter = useCallback((userId: string) => {
    const nextUserId = String(userId || '').trim();
    if (!nextUserId) return;
    setMetricsUserIds(prev =>
      prev.includes(nextUserId)
        ? prev.filter(id => id !== nextUserId)
        : [...prev, nextUserId]
    );
    setMetricsCaseId('');
  }, []);
  const handleMetricsDatePresetChange = useCallback((preset: MetricsDatePreset) => {
    setMetricsDatePreset(preset);
    setMetricsCaseId('');
    if (preset === 'custom') return;
    const range = buildMetricsPresetRange(preset);
    setMetricsStart(range.start);
    setMetricsEnd(range.end);
  }, []);
  const handleMetricsStartChange = useCallback((value: string) => {
    setMetricsDatePreset('custom');
    setMetricsCaseId('');
    setMetricsStart(value);
    if (metricsEnd && value && value > metricsEnd) {
      setMetricsEnd(value);
    }
  }, [metricsEnd]);
  const handleMetricsEndChange = useCallback((value: string) => {
    setMetricsDatePreset('custom');
    setMetricsCaseId('');
    if (metricsStart && value && value < metricsStart) {
      setMetricsEnd(metricsStart);
      return;
    }
    setMetricsEnd(value);
  }, [metricsStart]);
  const handleMetricsCustomerChange = useCallback((value: string) => {
    setMetricsCustomerId(value);
    setMetricsCaseId('');
  }, []);
  const handleMetricsModelChange = useCallback((value: string) => {
    setMetricsModelId(value);
    setMetricsCaseId('');
  }, []);
  const handleMetricsDeviceChange = useCallback((value: string) => {
    setMetricsDeviceId(value);
    setMetricsCaseId('');
  }, []);
  const handleMetricsTaskGroupChange = useCallback((value: string) => {
    setMetricsTaskGroup(value);
    setMetricsCaseId('');
  }, []);
  const handleMetricsStatusChange = useCallback((value: string) => {
    setMetricsStatus(value);
    setMetricsCaseId('');
  }, []);
  const getMetricsCaseTasks = useCallback((caseItem: Case): CaseTask[] => {
    if (Array.isArray(caseItem.tasks) && caseItem.tasks.length > 0) {
      return caseItem.tasks.filter(Boolean);
    }
    const fallback = [caseItem.displayTask, caseItem.activeTask].filter(Boolean) as CaseTask[];
    return fallback;
  }, []);
  const getMetricsCasePrimaryTask = useCallback(
    (caseItem: Case): CaseTask | null =>
      caseItem.displayTask || caseItem.activeTask || getActiveCaseTask(getMetricsCaseTasks(caseItem)),
    [getMetricsCaseTasks]
  );
  const getMetricsCaseTaskGroup = useCallback(
    (caseItem: Case): TaskGroupCode | '' => {
      const primaryTask = getMetricsCasePrimaryTask(caseItem);
      return resolveTaskGroupCode(primaryTask?.type?.code || '');
    },
    [getMetricsCasePrimaryTask]
  );
  const caseHasMetricsTaskGroup = useCallback(
    (caseItem: Case, taskGroup: TaskGroupCode) =>
      getMetricsCaseTasks(caseItem).some(task => resolveTaskGroupCode(task?.type?.code || '') === taskGroup),
    [getMetricsCaseTasks]
  );
  const getMetricsCaseReferenceDate = useCallback(
    (caseItem: Case) => toDate(caseItem.openedAt || caseItem.createdAt || caseItem.updatedAt),
    []
  );
  const getMetricsCaseAgeDays = useCallback(
    (caseItem: Case) => {
      const reference = getMetricsCaseReferenceDate(caseItem);
      if (!reference) return null;
      return Math.max(0, Math.floor((Date.now() - reference.getTime()) / (24 * 60 * 60 * 1000)));
    },
    [getMetricsCaseReferenceDate]
  );
  const resolveCaseActiveSlaLabel = useCallback((caseItem?: Case | null) => {
    const assignments = Array.isArray((caseItem?.device as any)?.slaAssignments)
      ? (((caseItem?.device as any)?.slaAssignments as any[]) || [])
      : [];
    const activeAssignment = assignments.find(entry => !entry?.endedAt) || assignments[0] || null;
    const template = activeAssignment?.slaTemplate || null;
    const label = String(
      template?.brandingName ||
        template?.tierBrandingName ||
        template?.name ||
        template?.slaType ||
        ''
    ).trim();
    return label || null;
  }, []);
  const scopedInsightCases = useMemo(() => {
    return cases.filter(caseItem => {
      if (metricsCustomerId && caseItem.customerId !== metricsCustomerId) {
        return false;
      }
      if (metricsDeviceId && String(caseItem.deviceId || '') !== metricsDeviceId) {
        return false;
      }
      if (metricsModelId) {
        const modelId = String(
          caseItem.device?.modelId ||
            deviceById.get(String(caseItem.deviceId || ''))?.modelId ||
            ''
        ).trim();
        if (modelId !== metricsModelId) {
          return false;
        }
      }
      if (metricsTaskGroup && getMetricsCaseTaskGroup(caseItem) !== metricsTaskGroup) {
        return false;
      }
      return true;
    });
  }, [
    cases,
    deviceById,
    getMetricsCaseTaskGroup,
    metricsCustomerId,
    metricsDeviceId,
    metricsModelId,
    metricsTaskGroup
  ]);
  const filteredInsightCases = useMemo(() => {
    return scopedInsightCases.filter(caseItem => {
      const referenceDate = getMetricsCaseReferenceDate(caseItem);
      if (metricsStartDate && (!referenceDate || referenceDate.getTime() < metricsStartDate.getTime())) {
        return false;
      }
      if (metricsEndDate && (!referenceDate || referenceDate.getTime() > metricsEndDate.getTime())) {
        return false;
      }
      return true;
    });
  }, [getMetricsCaseReferenceDate, metricsEndDate, metricsStartDate, scopedInsightCases]);
  const openInsightCases = useMemo(
    () =>
      filteredInsightCases.filter(
        caseItem => !CLOSED_CASE_STATUSES.has(String(caseItem.status || '').toUpperCase())
      ),
    [filteredInsightCases]
  );
  const highPriorityAgedCases = useMemo(
    () =>
      openInsightCases.filter(caseItem => {
        const priority = String(caseItem.priority || '').toUpperCase();
        const age = getMetricsCaseAgeDays(caseItem);
        return ['HIGH', 'CRITICAL'].includes(priority) && age != null && age >= 14;
      }),
    [getMetricsCaseAgeDays, openInsightCases]
  );
  const insightTaskRows = useMemo(() => {
    return filteredInsightCases.flatMap(caseItem => {
      const taskRows = getMetricsCaseTasks(caseItem)
        .filter(task => task?.id && !INACTIVE_TASK_STATUSES.has(normaliseTaskStatus(task.status)))
        .map(task => {
          const assigneeNames = getTaskAssigneeDisplayNames(task);
          const assigneeIds = getTaskAssigneeIds(task);
          const taskGroup = resolveTaskGroupCode(task.type?.code || '');
          return {
            id: task.id,
            caseId: caseItem.id,
            caseCode: getCaseDisplayNumber(caseItem),
            customerName:
              customerNameById.get(caseItem.customerId) ||
              caseItem.customer?.companyName ||
              'Customer',
            task,
            taskGroup,
            plannedStart: toDate(task.plannedStart),
            plannedDue: toDate(task.plannedDue),
            scheduleConfirmationStatus: String(task.scheduleConfirmationStatus || '').toUpperCase(),
            assigneeLabel:
              assigneeNames.length > 0
                ? assigneeNames.join(', ')
                : String(task.assignedUserName || '').trim() || 'Unassigned',
            isAssigned:
              assigneeIds.length > 0 ||
              assigneeNames.length > 0 ||
              Boolean(String(task.assignedUserId || '').trim() || String(task.assignedUserName || '').trim())
          };
        });
      return taskRows;
    });
  }, [customerNameById, filteredInsightCases, getMetricsCaseTasks]);
  const scheduledTaskRows = useMemo(
    () => insightTaskRows.filter(row => row.plannedStart || row.plannedDue),
    [insightTaskRows]
  );
  const scheduledThisWeekRows = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    end.setDate(end.getDate() + 7);
    return scheduledTaskRows.filter(row => {
      const planned = row.plannedStart || row.plannedDue;
      return planned ? planned >= now && planned <= end : false;
    });
  }, [scheduledTaskRows]);
  const upcomingWorkRows = useMemo(
    () =>
      scheduledThisWeekRows
        .slice()
        .sort((a, b) => {
          const timeA = ((a as any).plannedStart || (a as any).plannedDue)?.getTime?.() || 0;
          const timeB = ((b as any).plannedStart || (b as any).plannedDue)?.getTime?.() || 0;
          return timeA - timeB;
        }),
    [scheduledThisWeekRows]
  );
  const unassignedTaskRows = useMemo(
    () => insightTaskRows.filter(row => !row.isAssigned),
    [insightTaskRows]
  );
  const pendingScheduleRows = useMemo(
    () =>
      scheduledTaskRows.filter(
        row => row.scheduleConfirmationStatus && row.scheduleConfirmationStatus !== 'CONFIRMED'
      ),
    [scheduledTaskRows]
  );
  const completedTaskRows = useMemo(() => {
    return scopedInsightCases
      .flatMap(caseItem =>
        getMetricsCaseTasks(caseItem)
          .filter(task => task?.id && normaliseTaskStatus(task.status) === 'COMPLETED')
          .map(task => {
            const completedAt = toDate(task.completedAt || task.updatedAt || task.createdAt);
            return {
              id: task.id,
              caseCode: getCaseDisplayNumber(caseItem),
              customerName:
                customerNameById.get(caseItem.customerId) ||
                caseItem.customer?.companyName ||
                'Customer',
              taskGroup: resolveTaskGroupCode(task.type?.code || '') || 'UNASSIGNED',
              completedAt
            };
          })
      )
      .filter(row => {
        if (!row.completedAt) return false;
        if (metricsStartDate && row.completedAt.getTime() < metricsStartDate.getTime()) {
          return false;
        }
        if (metricsEndDate && row.completedAt.getTime() > metricsEndDate.getTime()) {
          return false;
        }
        return true;
      })
      .sort((a, b) => (b.completedAt?.getTime() || 0) - (a.completedAt?.getTime() || 0));
  }, [
    customerNameById,
    getMetricsCaseTasks,
    metricsEndDate,
    metricsStartDate,
    scopedInsightCases
  ]);
  const completedTaskGroupRows = useMemo(() => {
    const grouped = new Map<string, number>();
    completedTaskRows.forEach(row => {
      const key = row.taskGroup || 'UNASSIGNED';
      grouped.set(key, (grouped.get(key) || 0) + 1);
    });
    return Array.from(grouped.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [completedTaskRows]);
  const customerCompletedWorkRows = useMemo(() => {
    const grouped = new Map<
      string,
      { customer: string; tasks: number; cases: Set<string>; latestCompletedAt: Date | null }
    >();
    completedTaskRows.forEach(row => {
      const customer = String(row.customerName || 'Customer').trim() || 'Customer';
      const current =
        grouped.get(customer) || { customer, tasks: 0, cases: new Set<string>(), latestCompletedAt: null };
      current.tasks += 1;
      if (row.caseCode) current.cases.add(row.caseCode);
      const completedAt = toDate(row.completedAt);
      if (completedAt && (!current.latestCompletedAt || completedAt.getTime() > current.latestCompletedAt.getTime())) {
        current.latestCompletedAt = completedAt;
      }
      grouped.set(customer, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        customer: row.customer,
        tasks: row.tasks,
        cases: row.cases.size,
        latestCompletedAt: row.latestCompletedAt
      }))
      .sort((a, b) => {
        const latestA = a.latestCompletedAt?.getTime() || 0;
        const latestB = b.latestCompletedAt?.getTime() || 0;
        return b.tasks - a.tasks || b.cases - a.cases || latestB - latestA || a.customer.localeCompare(b.customer);
      });
  }, [completedTaskRows]);
  const surveyRows = useMemo(
    () =>
      filteredInsightCases.flatMap(caseItem =>
        Array.isArray(caseItem.caseSurveys)
          ? caseItem.caseSurveys.map(survey => ({
              ...survey,
              caseCode: getCaseDisplayNumber(caseItem),
              customerName:
                customerNameById.get(caseItem.customerId) ||
                caseItem.customer?.companyName ||
                'Customer'
            }))
          : []
      ),
    [customerNameById, filteredInsightCases]
  );
  const completedSurveyRows = useMemo(
    () =>
      surveyRows.filter(
        survey =>
          typeof survey.scoreEarned === 'number' &&
          typeof survey.scorePossible === 'number' &&
          survey.scorePossible > 0
      ),
    [surveyRows]
  );
  const pendingSurveyRows = useMemo(
    () =>
      surveyRows.filter(survey => !['COMPLETED', 'SENT'].includes(String(survey.status || '').toUpperCase())),
    [surveyRows]
  );
  const surveyAveragePercent = useMemo(() => {
    if (completedSurveyRows.length === 0) return null;
    const totals = completedSurveyRows.reduce(
      (acc, survey) => {
        acc.earned += Number(survey.scoreEarned || 0);
        acc.possible += Number(survey.scorePossible || 0);
        return acc;
      },
      { earned: 0, possible: 0 }
    );
    if (!totals.possible) return null;
    return (totals.earned / totals.possible) * 100;
  }, [completedSurveyRows]);
  const surveyByTechnicianRows = useMemo(() => {
    const grouped = new Map<string, { technician: string; earned: number; possible: number; surveys: number }>();
    completedSurveyRows.forEach(survey => {
      const technician = String(survey.technicianName || 'Unassigned').trim() || 'Unassigned';
      const current = grouped.get(technician) || {
        technician,
        earned: 0,
        possible: 0,
        surveys: 0
      };
      current.earned += Number(survey.scoreEarned || 0);
      current.possible += Number(survey.scorePossible || 0);
      current.surveys += 1;
      grouped.set(technician, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        ...row,
        average: row.possible ? (row.earned / row.possible) * 100 : null
      }))
      .sort((a, b) => (b.average || 0) - (a.average || 0));
  }, [completedSurveyRows]);
  const customerLoadRows = useMemo(() => {
    const grouped = new Map<
      string,
      { id: string; customer: string; totalCases: number; taskCount: number; openCases: number }
    >();
    filteredInsightCases.forEach(caseItem => {
      const customerId = String(caseItem.customerId || '').trim() || 'unknown';
      const current =
        grouped.get(customerId) || {
          id: customerId,
          customer:
            customerNameById.get(customerId) ||
            caseItem.customer?.companyName ||
            'Customer',
          totalCases: 0,
          taskCount: 0,
          openCases: 0
        };
      current.totalCases += 1;
      current.taskCount += getMetricsCaseTasks(caseItem).filter(
        task => task?.id && !INACTIVE_TASK_STATUSES.has(normaliseTaskStatus(task.status))
      ).length;
      if (!CLOSED_CASE_STATUSES.has(String(caseItem.status || '').toUpperCase())) {
        current.openCases += 1;
      }
      grouped.set(customerId, current);
    });
    return Array.from(grouped.values()).sort(
      (a, b) => b.taskCount - a.taskCount || b.totalCases - a.totalCases || a.customer.localeCompare(b.customer)
    );
  }, [customerNameById, filteredInsightCases, getMetricsCaseTasks]);
  const filteredInsightCaseById = useMemo(
    () => new Map(filteredInsightCases.map(caseItem => [caseItem.id, caseItem])),
    [filteredInsightCases]
  );
  const serviceQuoteCaseContextByAnalysisId = useMemo(() => {
    const context = new Map<
      string,
      {
        caseId: string;
        caseCode: string;
        customerName: string;
      }
    >();
    filteredInsightCases.forEach(caseItem => {
      const analysisId = String(caseItem.tsfadAnalyses?.find(Boolean)?.id || '').trim();
      if (!analysisId) return;
      context.set(analysisId, {
        caseId: caseItem.id,
        caseCode: getCaseDisplayNumber(caseItem),
        customerName:
          customerNameById.get(caseItem.customerId) ||
          caseItem.customer?.companyName ||
          'Customer'
      });
    });
    return context;
  }, [customerNameById, filteredInsightCases]);
  useEffect(() => {
    if (activeTab !== 'metrics') return;
    if (serviceInsightQuotes.length === 0 && !serviceInsightQuotesLoading) return;
    setServiceInsightQuotes([]);
    setServiceInsightQuotesLoading(false);
  }, [activeTab, metricsWorkspaceTab, serviceInsightQuotes.length, serviceInsightQuotesLoading]);
  const serviceLatestQuoteRows = useMemo(() => {
    const latestByCase = new Map<
      string,
      {
        caseId: string;
        caseCode: string;
        customerName: string;
        quoteId: string;
        savedAt: string | null;
        statusLabel: string;
        partsSummary: ReturnType<typeof getInsightQuotePartsSummary>;
      }
    >();
    serviceInsightQuotes.forEach(quote => {
      const analysisId = String(quote.analysisId || '').trim();
      if (!analysisId) return;
      const context = serviceQuoteCaseContextByAnalysisId.get(analysisId);
      if (!context) return;
      const savedAt = quote.savedAt || quote.decisionAt || null;
      const row = {
        ...context,
        quoteId: quote.quoteId,
        savedAt,
        statusLabel: getInsightQuoteDecisionLabel(quote),
        partsSummary: getInsightQuotePartsSummary(quote)
      };
      const existing = latestByCase.get(context.caseId);
      const existingAt = existing?.savedAt ? new Date(existing.savedAt).getTime() : 0;
      const nextAt = savedAt ? new Date(savedAt).getTime() : 0;
      if (!existing || nextAt >= existingAt) {
        latestByCase.set(context.caseId, row);
      }
    });
    return Array.from(latestByCase.values()).sort((a, b) => {
      const timeA = a.savedAt ? new Date(a.savedAt).getTime() : 0;
      const timeB = b.savedAt ? new Date(b.savedAt).getTime() : 0;
      return timeB - timeA;
    });
  }, [serviceInsightQuotes, serviceQuoteCaseContextByAnalysisId]);
  const customerQuoteTimelineByCaseId = useMemo(() => {
    const grouped = new Map<
      string,
      {
        caseId: string;
        caseCode: string;
        customerName: string;
        firstQuoteSavedAt: Date | null;
        latestDecisionAt: Date | null;
        latestStatusLabel: string | null;
        latestStatusAt: Date | null;
      }
    >();
    serviceInsightQuotes.forEach(quote => {
      const analysisId = String(quote.analysisId || '').trim();
      if (!analysisId) return;
      const context = serviceQuoteCaseContextByAnalysisId.get(analysisId);
      if (!context) return;
      const savedAt = toDate(quote.savedAt || quote.decisionAt);
      const decisionAt = toDate(quote.decisionAt);
      const statusAt = decisionAt || savedAt;
      const current =
        grouped.get(context.caseId) || {
          caseId: context.caseId,
          caseCode: context.caseCode,
          customerName: context.customerName,
          firstQuoteSavedAt: null,
          latestDecisionAt: null,
          latestStatusLabel: null,
          latestStatusAt: null
        };
      if (savedAt && (!current.firstQuoteSavedAt || savedAt.getTime() < current.firstQuoteSavedAt.getTime())) {
        current.firstQuoteSavedAt = savedAt;
      }
      if (decisionAt && (!current.latestDecisionAt || decisionAt.getTime() > current.latestDecisionAt.getTime())) {
        current.latestDecisionAt = decisionAt;
      }
      if (statusAt && (!current.latestStatusAt || statusAt.getTime() >= current.latestStatusAt.getTime())) {
        current.latestStatusAt = statusAt;
        current.latestStatusLabel = getInsightQuoteDecisionLabel(quote);
      }
      grouped.set(context.caseId, current);
    });
    return grouped;
  }, [serviceInsightQuotes, serviceQuoteCaseContextByAnalysisId]);
  const serviceQuoteStatusRows = useMemo(() => {
    const grouped = new Map<string, number>();
    serviceLatestQuoteRows.forEach(row => {
      grouped.set(row.statusLabel, (grouped.get(row.statusLabel) || 0) + 1);
    });
    return Array.from(grouped.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => {
        const orderA = SERVICE_QUOTE_STATUS_ORDER.indexOf(a.label);
        const orderB = SERVICE_QUOTE_STATUS_ORDER.indexOf(b.label);
        if (orderA !== orderB) {
          return (orderA === -1 ? Number.MAX_SAFE_INTEGER : orderA) - (orderB === -1 ? Number.MAX_SAFE_INTEGER : orderB);
        }
        return b.count - a.count || a.label.localeCompare(b.label);
      });
  }, [serviceLatestQuoteRows]);
  const partsWaitingRows = useMemo(() => {
    return serviceLatestQuoteRows
      .filter(row => row.partsSummary && row.partsSummary.tone !== 'success')
      .map(row => ({
        id: row.caseId,
        caseCode: row.caseCode,
        customerName: row.customerName,
        statusLabel: row.partsSummary?.label || 'Parts status',
        etaAt: row.partsSummary?.etaAt || null,
        detail: row.partsSummary?.detail || ''
      }))
      .sort((a, b) => {
        const etaA = a.etaAt ? new Date(a.etaAt).getTime() : Number.MAX_SAFE_INTEGER;
        const etaB = b.etaAt ? new Date(b.etaAt).getTime() : Number.MAX_SAFE_INTEGER;
        return etaA - etaB || a.caseCode.localeCompare(b.caseCode);
      });
  }, [serviceLatestQuoteRows]);
  const upcomingSlaRows = useMemo(() => {
    return upcomingWorkRows
      .map(row => {
        const caseItem = filteredInsightCaseById.get(String((row as any).caseId || ''));
        const slaLabel = resolveCaseActiveSlaLabel(caseItem);
        if (!slaLabel) return null;
        return {
          ...row,
          slaLabel
        };
      })
      .filter((row): row is typeof upcomingWorkRows[number] & { slaLabel: string } => Boolean(row));
  }, [filteredInsightCaseById, resolveCaseActiveSlaLabel, upcomingWorkRows]);
  const customerScheduledRows = useMemo(() => {
    const grouped = new Map<
      string,
      { customer: string; jobs: number; cases: Set<string>; nextPlannedAt: Date | null }
    >();
    upcomingWorkRows.forEach(row => {
      const customer = String(row.customerName || 'Customer').trim() || 'Customer';
      const current =
        grouped.get(customer) || { customer, jobs: 0, cases: new Set<string>(), nextPlannedAt: null };
      current.jobs += 1;
      if (row.caseCode) current.cases.add(row.caseCode);
      const plannedAt = toDate((row as any).plannedStart || (row as any).plannedDue);
      if (plannedAt && (!current.nextPlannedAt || plannedAt.getTime() < current.nextPlannedAt.getTime())) {
        current.nextPlannedAt = plannedAt;
      }
      grouped.set(customer, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        customer: row.customer,
        jobs: row.jobs,
        cases: row.cases.size,
        nextPlannedAt: row.nextPlannedAt
      }))
      .sort((a, b) => {
        const nextA = a.nextPlannedAt?.getTime() || Number.MAX_SAFE_INTEGER;
        const nextB = b.nextPlannedAt?.getTime() || Number.MAX_SAFE_INTEGER;
        return b.jobs - a.jobs || nextA - nextB || a.customer.localeCompare(b.customer);
      });
  }, [upcomingWorkRows]);
  const repairCaseRows = useMemo(() => {
    return scopedInsightCases
      .filter(caseItem => caseHasMetricsTaskGroup(caseItem, 'REPAIR'))
      .map(caseItem => {
        const repairTasks = getMetricsCaseTasks(caseItem).filter(
          task => resolveTaskGroupCode(task?.type?.code || '') === 'REPAIR'
        );
        const assignedUsers = Array.from(
          new Set(
            repairTasks.flatMap(task => {
              const names = getTaskAssigneeDisplayNames(task);
              if (names.length > 0) return names;
              const fallback = String(task.assignedUserName || '').trim();
              return fallback ? [fallback] : [];
            })
          )
        );
        const device =
          caseItem.device || deviceById.get(String(caseItem.deviceId || '').trim()) || null;
        const modelLabel = device?.modelNumber || device?.model?.modelNumber || 'Unknown model';
        const serialLabel = device?.serialNumber || 'Unknown serial';
        return {
          caseId: caseItem.id,
          caseCode: getCaseDisplayNumber(caseItem),
          deviceId: String(caseItem.deviceId || '').trim() || null,
          modelId: String(device?.modelId || '').trim() || null,
          modelLabel,
          deviceLabel:
            metricsGroupBy === 'device'
              ? `${modelLabel} | ${serialLabel}`
              : modelLabel,
          customerName:
            customerNameById.get(caseItem.customerId) ||
            caseItem.customer?.companyName ||
            'Customer',
          openedAt: toDate(caseItem.openedAt || caseItem.createdAt || caseItem.updatedAt),
          closedAt: toDate(caseItem.closedAt),
          isClosed: CLOSED_CASE_STATUSES.has(String(caseItem.status || '').toUpperCase()),
          assignedUsers,
          warrantyStatus: resolveFrontendWarrantyStatus(device, caseItem.openedAt || caseItem.createdAt)
        };
      })
      .filter(row => row.openedAt)
      .sort((a, b) => (a.openedAt?.getTime() || 0) - (b.openedAt?.getTime() || 0));
  }, [
    caseHasMetricsTaskGroup,
    customerNameById,
    deviceById,
    getMetricsCaseTasks,
    metricsGroupBy,
    scopedInsightCases
  ]);
  const deviceRepairMtbfRows = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; deviceCount: Set<string>; dates: Date[] }
    >();
    repairCaseRows.forEach(row => {
      const key = metricsGroupBy === 'device' ? row.deviceId || row.caseId : row.modelId || row.modelLabel;
      const current =
        grouped.get(key) || { key, label: row.deviceLabel, deviceCount: new Set<string>(), dates: [] };
      if (row.deviceId) current.deviceCount.add(row.deviceId);
      if (row.openedAt) current.dates.push(row.openedAt);
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .map(entry => {
        const sorted = entry.dates.slice().sort((a, b) => a.getTime() - b.getTime());
        const intervals: number[] = [];
        for (let index = 1; index < sorted.length; index += 1) {
          intervals.push((sorted[index].getTime() - sorted[index - 1].getTime()) / (24 * 60 * 60 * 1000));
        }
        const mtbfDays =
          intervals.length > 0 ? intervals.reduce((sum, value) => sum + value, 0) / intervals.length : null;
        return {
          key: entry.key,
          label: entry.label,
          failures: sorted.length,
          deviceCount: entry.deviceCount.size,
          mtbfDays
        };
      })
      .sort((a, b) => {
        const mtbfA = a.mtbfDays ?? Number.MAX_SAFE_INTEGER;
        const mtbfB = b.mtbfDays ?? Number.MAX_SAFE_INTEGER;
        return mtbfA - mtbfB || b.failures - a.failures || a.label.localeCompare(b.label);
      });
  }, [metricsGroupBy, repairCaseRows]);
  const userRepairMtbfRows = useMemo(() => {
    const grouped = new Map<
      string,
      { technician: string; intervals: number[]; repairedCases: number }
    >();
    const byDevice = new Map<string, typeof repairCaseRows>();
    repairCaseRows.forEach(row => {
      if (!row.deviceId) return;
      const current = byDevice.get(row.deviceId) || [];
      current.push(row);
      byDevice.set(row.deviceId, current);
    });
    byDevice.forEach(rows => {
      const sorted = rows
        .slice()
        .filter(row => row.openedAt)
        .sort((a, b) => (a.openedAt?.getTime() || 0) - (b.openedAt?.getTime() || 0));
      for (let index = 0; index < sorted.length - 1; index += 1) {
        const current = sorted[index];
        const next = sorted[index + 1];
        const fromDate = current.closedAt || current.openedAt;
        const toDateValue = next.openedAt;
        if (!fromDate || !toDateValue || toDateValue.getTime() <= fromDate.getTime()) continue;
        const intervalDays = (toDateValue.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000);
        current.assignedUsers.forEach(technician => {
          const existing = grouped.get(technician) || {
            technician,
            intervals: [],
            repairedCases: 0
          };
          existing.intervals.push(intervalDays);
          existing.repairedCases += 1;
          grouped.set(technician, existing);
        });
      }
    });
    return Array.from(grouped.values())
      .map(entry => ({
        technician: entry.technician,
        mtbfDays:
          entry.intervals.length > 0
            ? entry.intervals.reduce((sum, value) => sum + value, 0) / entry.intervals.length
            : null,
        repeatRepairs: entry.intervals.length,
        repairedCases: entry.repairedCases
      }))
      .sort((a, b) => {
        const mtbfA = a.mtbfDays ?? Number.MAX_SAFE_INTEGER;
        const mtbfB = b.mtbfDays ?? Number.MAX_SAFE_INTEGER;
        return mtbfA - mtbfB || b.repeatRepairs - a.repeatRepairs || a.technician.localeCompare(b.technician);
      });
  }, [repairCaseRows]);
  const firstTimeFixSummary = useMemo(() => {
    const eligibleRows = repairCaseRows.filter(row => {
      if (!row.deviceId || !row.closedAt) return false;
      if (metricsStartDate && row.closedAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && row.closedAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    let fixedFirstTime = 0;
    eligibleRows.forEach(row => {
      const repeatWithinWindow = repairCaseRows.some(candidate => {
        if (candidate.caseId === row.caseId || candidate.deviceId !== row.deviceId) return false;
        if (!candidate.openedAt || !row.closedAt) return false;
        if (candidate.openedAt.getTime() <= row.closedAt.getTime()) return false;
        const deltaDays = (candidate.openedAt.getTime() - row.closedAt.getTime()) / (24 * 60 * 60 * 1000);
        return deltaDays <= FIRST_TIME_FIX_WINDOW_DAYS;
      });
      if (!repeatWithinWindow) fixedFirstTime += 1;
    });
    const rate = eligibleRows.length > 0 ? (fixedFirstTime / eligibleRows.length) * 100 : null;
    return {
      eligible: eligibleRows.length,
      fixedFirstTime,
      rate
    };
  }, [metricsEndDate, metricsStartDate, repairCaseRows]);
  const userFirstTimeFixRows = useMemo(() => {
    const grouped = new Map<string, { technician: string; eligible: number; fixed: number }>();
    const eligibleRows = repairCaseRows.filter(row => {
      if (!row.deviceId || !row.closedAt) return false;
      if (metricsStartDate && row.closedAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && row.closedAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    eligibleRows.forEach(row => {
      const repeatWithinWindow = repairCaseRows.some(candidate => {
        if (candidate.caseId === row.caseId || candidate.deviceId !== row.deviceId) return false;
        if (!candidate.openedAt || !row.closedAt) return false;
        if (candidate.openedAt.getTime() <= row.closedAt.getTime()) return false;
        const deltaDays = (candidate.openedAt.getTime() - row.closedAt.getTime()) / (24 * 60 * 60 * 1000);
        return deltaDays <= FIRST_TIME_FIX_WINDOW_DAYS;
      });
      row.assignedUsers.forEach(technician => {
        const current = grouped.get(technician) || { technician, eligible: 0, fixed: 0 };
        current.eligible += 1;
        if (!repeatWithinWindow) {
          current.fixed += 1;
        }
        grouped.set(technician, current);
      });
    });
    return Array.from(grouped.values())
      .map(row => ({
        ...row,
        rate: row.eligible > 0 ? (row.fixed / row.eligible) * 100 : null
      }))
      .sort((a, b) => {
        const rateA = a.rate ?? -1;
        const rateB = b.rate ?? -1;
        return rateB - rateA || b.eligible - a.eligible || a.technician.localeCompare(b.technician);
      });
  }, [metricsEndDate, metricsStartDate, repairCaseRows]);
  const teamRepairPerformanceRows = useMemo(() => {
    const grouped = new Map<
      string,
      {
        technician: string;
        focusLabel: string;
        repairs: number;
        intervals: number[];
        eligible: number;
        fixed: number;
      }
    >();
    const byDevice = new Map<string, typeof repairCaseRows>();
    repairCaseRows.forEach(row => {
      if (!row.deviceId) return;
      const current = byDevice.get(row.deviceId) || [];
      current.push(row);
      byDevice.set(row.deviceId, current);
      row.assignedUsers.forEach(technician => {
        const key = `${technician}::${row.deviceLabel}`;
        const existing =
          grouped.get(key) || {
            technician,
            focusLabel: row.deviceLabel,
            repairs: 0,
            intervals: [],
            eligible: 0,
            fixed: 0
          };
        existing.repairs += 1;
        grouped.set(key, existing);
      });
    });
    byDevice.forEach(rows => {
      const sorted = rows
        .slice()
        .filter(row => row.openedAt)
        .sort((a, b) => (a.openedAt?.getTime() || 0) - (b.openedAt?.getTime() || 0));
      for (let index = 0; index < sorted.length - 1; index += 1) {
        const current = sorted[index];
        const next = sorted[index + 1];
        const fromDate = current.closedAt || current.openedAt;
        const toDateValue = next.openedAt;
        if (!fromDate || !toDateValue || toDateValue.getTime() <= fromDate.getTime()) continue;
        const intervalDays = (toDateValue.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000);
        current.assignedUsers.forEach(technician => {
          const key = `${technician}::${current.deviceLabel}`;
          const existing = grouped.get(key);
          if (!existing) return;
          existing.intervals.push(intervalDays);
        });
      }
    });
    const eligibleRows = repairCaseRows.filter(row => {
      if (!row.deviceId || !row.closedAt) return false;
      if (metricsStartDate && row.closedAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && row.closedAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    eligibleRows.forEach(row => {
      const repeatWithinWindow = repairCaseRows.some(candidate => {
        if (candidate.caseId === row.caseId || candidate.deviceId !== row.deviceId) return false;
        if (!candidate.openedAt || !row.closedAt) return false;
        if (candidate.openedAt.getTime() <= row.closedAt.getTime()) return false;
        const deltaDays = (candidate.openedAt.getTime() - row.closedAt.getTime()) / (24 * 60 * 60 * 1000);
        return deltaDays <= FIRST_TIME_FIX_WINDOW_DAYS;
      });
      row.assignedUsers.forEach(technician => {
        const key = `${technician}::${row.deviceLabel}`;
        const existing = grouped.get(key);
        if (!existing) return;
        existing.eligible += 1;
        if (!repeatWithinWindow) {
          existing.fixed += 1;
        }
      });
    });
    return Array.from(grouped.values())
      .map(row => ({
        technician: row.technician,
        focusLabel: row.focusLabel,
        repairs: row.repairs,
        mtbfDays:
          row.intervals.length > 0
            ? row.intervals.reduce((sum, value) => sum + value, 0) / row.intervals.length
            : null,
        firstTimeFixRate: row.eligible > 0 ? (row.fixed / row.eligible) * 100 : null,
        eligible: row.eligible
      }))
      .sort((a, b) => {
        const ftfA = a.firstTimeFixRate ?? -1;
        const ftfB = b.firstTimeFixRate ?? -1;
        const mtbfA = a.mtbfDays ?? Number.MAX_SAFE_INTEGER;
        const mtbfB = b.mtbfDays ?? Number.MAX_SAFE_INTEGER;
        return ftfB - ftfA || mtbfA - mtbfB || b.repairs - a.repairs || a.technician.localeCompare(b.technician);
      });
  }, [metricsEndDate, metricsStartDate, repairCaseRows]);
  const repairDurationRows = useMemo(() => {
    const grouped = new Map<string, { key: string; label: string; durations: number[]; cases: number }>();
    repairCaseRows.forEach(row => {
      if (!row.openedAt || !row.closedAt) return;
      if (metricsStartDate && row.closedAt.getTime() < metricsStartDate.getTime()) return;
      if (metricsEndDate && row.closedAt.getTime() > metricsEndDate.getTime()) return;
      const durationDays = (row.closedAt.getTime() - row.openedAt.getTime()) / (24 * 60 * 60 * 1000);
      if (!Number.isFinite(durationDays) || durationDays < 0) return;
      const key = metricsGroupBy === 'device' ? row.deviceId || row.caseId : row.modelId || row.modelLabel;
      const current = grouped.get(key) || { key, label: row.deviceLabel, durations: [], cases: 0 };
      current.durations.push(durationDays);
      current.cases += 1;
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .map(entry => ({
        key: entry.key,
        label: entry.label,
        cases: entry.cases,
        averageDays:
          entry.durations.length > 0
            ? entry.durations.reduce((sum, value) => sum + value, 0) / entry.durations.length
            : null
      }))
      .sort((a, b) => (b.averageDays || 0) - (a.averageDays || 0) || b.cases - a.cases || a.label.localeCompare(b.label));
  }, [metricsEndDate, metricsGroupBy, metricsStartDate, repairCaseRows]);
  const repairWarrantySummary = useMemo(() => {
    const rows = repairCaseRows.filter(row => {
      if (!row.openedAt) return false;
      if (metricsStartDate && row.openedAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && row.openedAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    const inWarranty = rows.filter(row => row.warrantyStatus === 'IN').length;
    const outWarranty = rows.length - inWarranty;
    return {
      total: rows.length,
      inWarranty,
      outWarranty
    };
  }, [metricsEndDate, metricsStartDate, repairCaseRows]);
  const reliabilityHoldReasonRows = useMemo(() => {
    const waitingQuoteCount = serviceLatestQuoteRows.filter(
      row => row.statusLabel === 'WAITING RESPONSE' || row.statusLabel === 'ADJUST REQUESTED'
    ).length;
    const grouped = new Map<string, number>([
      ['Waiting on parts', partsWaitingRows.length],
      ['Waiting on quote', waitingQuoteCount],
      ['Pending schedule', pendingScheduleRows.length],
      ['Unassigned', unassignedTaskRows.length]
    ]);
    return RELIABILITY_HOLD_REASON_ORDER.map(label => ({ label, count: grouped.get(label) || 0 })).filter(
      row => row.count > 0
    );
  }, [partsWaitingRows.length, pendingScheduleRows.length, serviceLatestQuoteRows, unassignedTaskRows.length]);
  const activeScopedDevices = useMemo(() => {
    return devices.filter(device => {
      if (metricsCustomerId && String(device.customerId || '') !== metricsCustomerId) return false;
      if (metricsDeviceId && device.id !== metricsDeviceId) return false;
      if (metricsModelId && String(device.modelId || '') !== metricsModelId) return false;
      if (device.isActive === false) return false;
      if (String(device.lifecycleStatus || '').toUpperCase() === 'RETIRED') return false;
      return true;
    });
  }, [devices, metricsCustomerId, metricsDeviceId, metricsModelId]);
  const repairFrequencyRows = useMemo(() => {
    const installedBaseByModel = new Map<string, { label: string; devices: number }>();
    activeScopedDevices.forEach(device => {
      const key = String(device.modelId || device.modelNumber || '').trim();
      if (!key) return;
      const current = installedBaseByModel.get(key) || {
        label: device.modelNumber || device.model?.modelNumber || 'Unknown model',
        devices: 0
      };
      current.devices += 1;
      installedBaseByModel.set(key, current);
    });
    const repairsByModel = new Map<string, { label: string; repairs: number; affectedDevices: Set<string> }>();
    repairCaseRows.forEach(row => {
      if (!row.openedAt) return;
      if (metricsStartDate && row.openedAt.getTime() < metricsStartDate.getTime()) return;
      if (metricsEndDate && row.openedAt.getTime() > metricsEndDate.getTime()) return;
      const key = String(row.modelId || row.modelLabel || '').trim();
      if (!key) return;
      const current = repairsByModel.get(key) || {
        label: row.modelLabel,
        repairs: 0,
        affectedDevices: new Set<string>()
      };
      current.repairs += 1;
      if (row.deviceId) current.affectedDevices.add(row.deviceId);
      repairsByModel.set(key, current);
    });
    return Array.from(repairsByModel.entries())
      .map(([key, repair]) => {
        const installedBase = installedBaseByModel.get(key)?.devices || 0;
        return {
          key,
          label: repair.label,
          repairs: repair.repairs,
          installedBase,
          affectedDevices: repair.affectedDevices.size,
          percent: installedBase > 0 ? (repair.repairs / installedBase) * 100 : null
        };
      })
      .sort((a, b) => (b.percent || 0) - (a.percent || 0) || b.repairs - a.repairs || a.label.localeCompare(b.label));
  }, [activeScopedDevices, metricsEndDate, metricsStartDate, repairCaseRows]);
  const maintenanceTaskRows = useMemo(() => {
    return scopedInsightCases.flatMap(caseItem =>
      getMetricsCaseTasks(caseItem)
        .filter(task => resolveTaskGroupCode(task?.type?.code || '') === 'MAINTENANCE')
        .map(task => {
          const deviceId = String(caseItem.deviceId || '').trim() || null;
          const deviceRecord = deviceById.get(deviceId || '');
          const modelLabel =
            caseItem.device?.modelNumber ||
            deviceRecord?.modelNumber ||
            'Unknown model';
          const serialLabel =
            String(caseItem.device?.serialNumber || '').trim() ||
            String(deviceRecord?.serialNumber || '').trim() ||
            'Unknown serial';
          return {
            caseId: caseItem.id,
            deviceId,
            modelLabel,
            deviceLabel: `${modelLabel} | ${serialLabel}`,
            plannedStart: toDate(task.plannedStart),
            plannedDue: toDate(task.plannedDue),
            completedAt: toDate(task.completedAt),
            taskCode: task.taskCode || null
          };
        })
    );
  }, [deviceById, getMetricsCaseTasks, scopedInsightCases]);
  const maintenanceComplianceSummary = useMemo(() => {
    const dueRows = maintenanceTaskRows.filter(row => {
      const dueAt = row.plannedDue || row.plannedStart;
      if (!dueAt) return false;
      if (metricsStartDate && dueAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && dueAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    const byDevice = new Map<string, { total: number; compliant: number }>();
    dueRows.forEach(row => {
      const key = row.deviceId || row.caseId;
      const dueAt = row.plannedDue || row.plannedStart;
      const current = byDevice.get(key) || { total: 0, compliant: 0 };
      current.total += 1;
      if (dueAt && row.completedAt && row.completedAt.getTime() <= dueAt.getTime()) {
        current.compliant += 1;
      }
      byDevice.set(key, current);
    });
    const devicesDue = byDevice.size;
    const compliantDevices = Array.from(byDevice.values()).filter(
      value => value.total > 0 && value.total === value.compliant
    ).length;
    const rate = devicesDue > 0 ? (compliantDevices / devicesDue) * 100 : null;
    return {
      devicesDue,
      compliantDevices,
      rate
    };
  }, [maintenanceTaskRows, metricsEndDate, metricsStartDate]);
  const slaAssignmentHistoryRows = useMemo(() => {
    const rawRows = Array.isArray(slaInsightsData.assignments) ? slaInsightsData.assignments : [];
    return rawRows
      .map(row => {
        const modelLabel =
          String(row.device?.modelNumber || '').trim() ||
          String(deviceById.get(String(row.deviceId || '').trim())?.modelNumber || '').trim() ||
          'Unknown model';
        const serialLabel =
          String(row.device?.serialNumber || '').trim() ||
          String(deviceById.get(String(row.deviceId || '').trim())?.serialNumber || '').trim() ||
          'Unknown serial';
        const endAt = resolveDeviceSlaEndDate(row as any);
        return {
          ...row,
          startAt: toDate(row.startDate),
          assignedAtDate: toDate(row.assignedAt),
          endedAtDate: toDate(row.endedAt),
          resolvedEndAt: endAt,
          customerName: String(row.customer?.company || customerNameById.get(row.customerId) || 'Customer').trim() || 'Customer',
          modelLabel,
          deviceLabel: `${modelLabel} | ${serialLabel}`,
          slaLabel: getDeviceSlaLabel(row as any),
          serialLabel
        };
      })
      .sort((a, b) => {
        const startA = a.startAt?.getTime() || 0;
        const startB = b.startAt?.getTime() || 0;
        return startB - startA || a.deviceLabel.localeCompare(b.deviceLabel);
      });
  }, [customerNameById, deviceById, slaInsightsData.assignments]);
  const slaAssignmentsByDeviceId = useMemo(() => {
    const grouped = new Map<string, typeof slaAssignmentHistoryRows>();
    slaAssignmentHistoryRows.forEach(row => {
      const key = String(row.deviceId || '').trim();
      if (!key) return;
      const current = grouped.get(key) || [];
      current.push(row);
      grouped.set(key, current);
    });
    grouped.forEach(rows => {
      rows.sort((left, right) => {
        const startLeft = left.startAt?.getTime() || 0;
        const startRight = right.startAt?.getTime() || 0;
        return startRight - startLeft || (right.assignedAtDate?.getTime() || 0) - (left.assignedAtDate?.getTime() || 0);
      });
    });
    return grouped;
  }, [slaAssignmentHistoryRows]);
  const getSlaAssignmentForDeviceAt = useCallback(
    (deviceId?: string | null, referenceInput?: string | Date | null) => {
      const key = String(deviceId || '').trim();
      const referenceDate = toDate(referenceInput);
      if (!key || !referenceDate) return null;
      return getCurrentDeviceSlaAssignment((slaAssignmentsByDeviceId.get(key) as any) || [], referenceDate) as
        | (typeof slaAssignmentHistoryRows[number] & { resolvedEndAt?: Date | null })
        | null;
    },
    [slaAssignmentsByDeviceId]
  );
  const currentSlaAssignmentRows = useMemo(() => {
    return Array.from(slaAssignmentsByDeviceId.values())
      .map(rows => getCurrentDeviceSlaAssignment((rows as any) || [], metricsReferenceDate) as
        | (typeof slaAssignmentHistoryRows[number] & { resolvedEndAt?: Date | null })
        | null)
      .filter((row): row is typeof slaAssignmentHistoryRows[number] => Boolean(row));
  }, [metricsReferenceDate, slaAssignmentsByDeviceId]);
  const slaCoverageRows = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; devices: number; customers: Set<string>; templateLabels: Set<string> }
    >();
    currentSlaAssignmentRows.forEach(row => {
      const key = metricsGroupBy === 'device' ? row.deviceId : String(row.device?.modelId || row.modelLabel);
      const label = metricsGroupBy === 'device' ? row.deviceLabel : row.modelLabel;
      const current = grouped.get(key) || {
        key,
        label,
        devices: 0,
        customers: new Set<string>(),
        templateLabels: new Set<string>()
      };
      current.devices += 1;
      current.customers.add(row.customerName);
      current.templateLabels.add(row.slaLabel);
      grouped.set(key, current);
    });
    return Array.from(grouped.values()).sort(
      (a, b) => b.devices - a.devices || b.customers.size - a.customers.size || a.label.localeCompare(b.label)
    );
  }, [currentSlaAssignmentRows, metricsGroupBy]);
  const expiringSlaRows = useMemo(() => {
    return currentSlaAssignmentRows
      .map(row => {
        const endAt = row.resolvedEndAt || null;
        if (!endAt) return null;
        const daysRemaining = Math.ceil((endAt.getTime() - metricsReferenceDate.getTime()) / (24 * 60 * 60 * 1000));
        if (daysRemaining < 0 || daysRemaining > 90) return null;
        return {
          ...row,
          endAt,
          daysRemaining
        };
      })
      .filter((row): row is NonNullable<typeof currentSlaAssignmentRows[number]> & { endAt: Date; daysRemaining: number } => Boolean(row))
      .sort((a, b) => a.daysRemaining - b.daysRemaining || a.deviceLabel.localeCompare(b.deviceLabel));
  }, [currentSlaAssignmentRows, metricsReferenceDate]);
  const expiringSla30Rows = useMemo(
    () => expiringSlaRows.filter(row => row.daysRemaining <= 30),
    [expiringSlaRows]
  );
  const expiringSla90Rows = useMemo(
    () => expiringSlaRows.filter(row => row.daysRemaining <= 90),
    [expiringSlaRows]
  );
  const slaLifecycleSummary = useMemo(() => {
    let newCount = 0;
    let renewedCount = 0;
    let expiredCount = 0;

    slaAssignmentsByDeviceId.forEach(rows => {
      const sortedRows = rows
        .slice()
        .sort((a, b) => (a.startAt?.getTime() || 0) - (b.startAt?.getTime() || 0));
      sortedRows.forEach((row, index) => {
        const startAt = row.startAt;
        if (startAt && (!metricsStartDate || startAt.getTime() >= metricsStartDate.getTime()) && (!metricsEndDate || startAt.getTime() <= metricsEndDate.getTime())) {
          if (index === 0) {
            newCount += 1;
          } else {
            renewedCount += 1;
          }
        }
        const endAt = row.resolvedEndAt || null;
        if (
          endAt &&
          endAt.getTime() <= metricsReferenceDate.getTime() &&
          (!metricsStartDate || endAt.getTime() >= metricsStartDate.getTime()) &&
          (!metricsEndDate || endAt.getTime() <= metricsEndDate.getTime())
        ) {
          expiredCount += 1;
        }
      });
    });

    return {
      newCount,
      renewedCount,
      expiredCount
    };
  }, [metricsEndDate, metricsReferenceDate, metricsStartDate, slaAssignmentsByDeviceId]);
  const slaCoveredTaskRows = useMemo(() => {
    return filteredInsightCases.flatMap(caseItem =>
      getMetricsCaseTasks(caseItem)
        .map(task => {
          const referenceDate =
            toDate(task.completedAt) ||
            toDate(task.plannedStart) ||
            toDate(task.plannedDue) ||
            toDate(task.createdAt) ||
            getMetricsCaseReferenceDate(caseItem);
          const assignment = getSlaAssignmentForDeviceAt(caseItem.deviceId, referenceDate);
          if (!assignment) return null;
          return {
            caseId: caseItem.id,
            caseCode: getCaseDisplayNumber(caseItem),
            customerName: customerNameById.get(caseItem.customerId) || caseItem.customer?.companyName || 'Customer',
            taskId: String(task.id || '').trim(),
            deviceId: String(caseItem.deviceId || '').trim() || null,
            taskGroup: resolveTaskGroupCode(task?.type?.code || ''),
            referenceDate,
            plannedStart: toDate(task.plannedStart),
            plannedDue: toDate(task.plannedDue),
            slaLabel: assignment.slaLabel,
            deviceLabel: assignment.deviceLabel,
            modelLabel: assignment.modelLabel
          };
        })
        .filter(
          (row): row is NonNullable<typeof row> => Boolean(row)
        )
    );
  }, [customerNameById, filteredInsightCases, getMetricsCaseReferenceDate, getMetricsCaseTasks, getSlaAssignmentForDeviceAt]);
  const slaCoveredCustomerWorkRows = useMemo(() => {
    const grouped = new Map<
      string,
      { customer: string; cases: Set<string>; tasks: number; scheduled: number }
    >();
    slaCoveredTaskRows.forEach(row => {
      const key = row.customerName;
      const current = grouped.get(key) || {
        customer: key,
        cases: new Set<string>(),
        tasks: 0,
        scheduled: 0
      };
      current.cases.add(row.caseCode);
      current.tasks += 1;
      if (row.plannedStart || row.plannedDue) {
        current.scheduled += 1;
      }
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        customer: row.customer,
        cases: row.cases.size,
        tasks: row.tasks,
        scheduled: row.scheduled
      }))
      .sort((a, b) => b.tasks - a.tasks || b.cases - a.cases || a.customer.localeCompare(b.customer));
  }, [slaCoveredTaskRows]);
  const scheduledSlaRows = useMemo(() => {
    return upcomingWorkRows
      .map(row => {
        const plannedAt = toDate((row as any).plannedStart || (row as any).plannedDue);
        const assignment = getSlaAssignmentForDeviceAt((row as any).deviceId || null, plannedAt);
        if (!assignment) return null;
        return {
          ...row,
          slaLabel: assignment.slaLabel
        };
      })
      .filter((row): row is typeof upcomingWorkRows[number] & { slaLabel: string } => Boolean(row));
  }, [getSlaAssignmentForDeviceAt, upcomingWorkRows]);
  const slaRepairCaseRows = useMemo(() => {
    return repairCaseRows.filter(row => {
      const referenceDate = row.openedAt || row.closedAt;
      return Boolean(getSlaAssignmentForDeviceAt(row.deviceId, referenceDate));
    });
  }, [getSlaAssignmentForDeviceAt, repairCaseRows]);
  const slaFirstTimeFixSummary = useMemo(() => {
    const eligibleRows = slaRepairCaseRows.filter(row => {
      if (!row.deviceId || !row.closedAt) return false;
      if (metricsStartDate && row.closedAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && row.closedAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    let fixedFirstTime = 0;
    eligibleRows.forEach(row => {
      const repeatWithinWindow = slaRepairCaseRows.some(candidate => {
        if (candidate.caseId === row.caseId || candidate.deviceId !== row.deviceId) return false;
        if (!candidate.openedAt || !row.closedAt) return false;
        if (candidate.openedAt.getTime() <= row.closedAt.getTime()) return false;
        const deltaDays = (candidate.openedAt.getTime() - row.closedAt.getTime()) / (24 * 60 * 60 * 1000);
        return deltaDays <= FIRST_TIME_FIX_WINDOW_DAYS;
      });
      if (!repeatWithinWindow) fixedFirstTime += 1;
    });
    return {
      eligible: eligibleRows.length,
      fixedFirstTime,
      rate: eligibleRows.length > 0 ? (fixedFirstTime / eligibleRows.length) * 100 : null
    };
  }, [metricsEndDate, metricsStartDate, slaRepairCaseRows]);
  const slaMtbfRows = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; repairs: number; intervals: number[]; devices: Set<string> }
    >();
    const byDevice = new Map<string, typeof slaRepairCaseRows>();
    slaRepairCaseRows.forEach(row => {
      if (!row.deviceId) return;
      const bucketKey = metricsGroupBy === 'device' ? row.deviceId : String(row.modelId || row.modelLabel);
      const bucketLabel = metricsGroupBy === 'device' ? row.deviceLabel : row.modelLabel;
      const current = grouped.get(bucketKey) || {
        key: bucketKey,
        label: bucketLabel,
        repairs: 0,
        intervals: [],
        devices: new Set<string>()
      };
      current.repairs += 1;
      current.devices.add(row.deviceId);
      grouped.set(bucketKey, current);
      const rows = byDevice.get(row.deviceId) || [];
      rows.push(row);
      byDevice.set(row.deviceId, rows);
    });
    byDevice.forEach(rows => {
      const sorted = rows
        .slice()
        .filter(row => row.openedAt)
        .sort((a, b) => (a.openedAt?.getTime() || 0) - (b.openedAt?.getTime() || 0));
      for (let index = 0; index < sorted.length - 1; index += 1) {
        const current = sorted[index];
        const next = sorted[index + 1];
        const fromDate = current.closedAt || current.openedAt;
        const toDateValue = next.openedAt;
        if (!fromDate || !toDateValue || toDateValue.getTime() <= fromDate.getTime()) continue;
        const intervalDays = (toDateValue.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000);
        const bucketKey =
          metricsGroupBy === 'device'
            ? String(current.deviceId || '').trim()
            : String(current.modelId || current.modelLabel);
        if (!bucketKey) continue;
        const bucket = grouped.get(bucketKey);
        if (bucket) {
          bucket.intervals.push(intervalDays);
        }
      }
    });
    return Array.from(grouped.values())
      .map(row => ({
        key: row.key,
        label: row.label,
        repairs: row.repairs,
        deviceCount: row.devices.size,
        mtbfDays:
          row.intervals.length > 0
            ? row.intervals.reduce((sum, value) => sum + value, 0) / row.intervals.length
            : null
      }))
      .sort((a, b) => (a.mtbfDays ?? Number.MAX_SAFE_INTEGER) - (b.mtbfDays ?? Number.MAX_SAFE_INTEGER) || b.repairs - a.repairs || a.label.localeCompare(b.label));
  }, [metricsGroupBy, slaRepairCaseRows]);
  const slaMaintenanceRows = useMemo(() => {
    return maintenanceTaskRows.filter(row => {
      const referenceDate = row.plannedDue || row.plannedStart || row.completedAt;
      return Boolean(getSlaAssignmentForDeviceAt(row.deviceId, referenceDate));
    });
  }, [getSlaAssignmentForDeviceAt, maintenanceTaskRows]);
  const slaMaintenanceComplianceSummary = useMemo(() => {
    const dueRows = slaMaintenanceRows.filter(row => {
      const dueAt = row.plannedDue || row.plannedStart;
      if (!dueAt) return false;
      if (metricsStartDate && dueAt.getTime() < metricsStartDate.getTime()) return false;
      if (metricsEndDate && dueAt.getTime() > metricsEndDate.getTime()) return false;
      return true;
    });
    const byDevice = new Map<string, { total: number; compliant: number }>();
    dueRows.forEach(row => {
      const key = row.deviceId || row.caseId;
      const dueAt = row.plannedDue || row.plannedStart;
      const current = byDevice.get(key) || { total: 0, compliant: 0 };
      current.total += 1;
      if (dueAt && row.completedAt && row.completedAt.getTime() <= dueAt.getTime()) {
        current.compliant += 1;
      }
      byDevice.set(key, current);
    });
    const devicesDue = byDevice.size;
    const compliantDevices = Array.from(byDevice.values()).filter(
      value => value.total > 0 && value.total === value.compliant
    ).length;
    return {
      devicesDue,
      compliantDevices,
      rate: devicesDue > 0 ? (compliantDevices / devicesDue) * 100 : null
    };
  }, [metricsEndDate, metricsStartDate, slaMaintenanceRows]);
  const slaMaintenanceDeviationRows = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; totalDeviationDays: number; completed: number; onTime: number }
    >();
    slaMaintenanceRows.forEach(row => {
      const dueAt = row.plannedDue || row.plannedStart;
      if (!dueAt || !row.completedAt) return;
      if (metricsStartDate && dueAt.getTime() < metricsStartDate.getTime()) return;
      if (metricsEndDate && dueAt.getTime() > metricsEndDate.getTime()) return;
      const monthKey = dueAt.toISOString().slice(0, 7);
      const label = dueAt.toLocaleString('en-US', { month: 'short', year: 'numeric' });
      const deviationDays = (row.completedAt.getTime() - dueAt.getTime()) / (24 * 60 * 60 * 1000);
      const current = grouped.get(monthKey) || {
        key: monthKey,
        label,
        totalDeviationDays: 0,
        completed: 0,
        onTime: 0
      };
      current.totalDeviationDays += deviationDays;
      current.completed += 1;
      if (deviationDays <= 0) current.onTime += 1;
      grouped.set(monthKey, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        key: row.key,
        label: row.label,
        averageDeviationDays: row.completed > 0 ? row.totalDeviationDays / row.completed : null,
        onTimeRate: row.completed > 0 ? (row.onTime / row.completed) * 100 : null,
        completed: row.completed
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [metricsEndDate, metricsStartDate, slaMaintenanceRows]);
  const breakdownAfterMaintenanceRows = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; breakdowns: number; devices: Set<string>; deltas: number[] }
    >();
    slaMaintenanceRows.forEach(row => {
      const completedAt = row.completedAt;
      if (!row.deviceId || !completedAt) return;
      const nextRepair = slaRepairCaseRows
        .filter(repairRow => repairRow.deviceId === row.deviceId && repairRow.openedAt && repairRow.openedAt.getTime() > completedAt.getTime())
        .sort((a, b) => (a.openedAt?.getTime() || 0) - (b.openedAt?.getTime() || 0))[0];
      if (!nextRepair?.openedAt) return;
      const deltaDays = (nextRepair.openedAt.getTime() - completedAt.getTime()) / (24 * 60 * 60 * 1000);
      if (deltaDays < 0 || deltaDays > 30) return;
      const key = metricsGroupBy === 'device' ? row.deviceId : row.modelLabel;
      const label = metricsGroupBy === 'device' ? row.deviceLabel : row.modelLabel;
      const current = grouped.get(key) || {
        key,
        label,
        breakdowns: 0,
        devices: new Set<string>(),
        deltas: []
      };
      current.breakdowns += 1;
      current.devices.add(row.deviceId);
      current.deltas.push(deltaDays);
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        key: row.key,
        label: row.label,
        breakdowns: row.breakdowns,
        devices: row.devices.size,
        averageDaysAfterMaintenance:
          row.deltas.length > 0 ? row.deltas.reduce((sum, value) => sum + value, 0) / row.deltas.length : null
      }))
      .sort((a, b) => b.breakdowns - a.breakdowns || a.label.localeCompare(b.label));
  }, [metricsGroupBy, slaMaintenanceRows, slaRepairCaseRows]);
  const slaSummaryData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && slaInsightsData.slaSummary
        ? slaInsightsData.slaSummary
        : null,
    [metricsWorkspaceTab, slaInsightsData.slaSummary]
  );
  const slaLifecycleSummaryData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && slaInsightsData.slaLifecycleSummary
        ? slaInsightsData.slaLifecycleSummary
        : slaLifecycleSummary,
    [metricsWorkspaceTab, slaInsightsData.slaLifecycleSummary, slaLifecycleSummary]
  );
  const slaCoveredCustomerWorkRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.slaCoveredCustomerWorkRows)
        ? slaInsightsData.slaCoveredCustomerWorkRows
        : slaCoveredCustomerWorkRows,
    [metricsWorkspaceTab, slaCoveredCustomerWorkRows, slaInsightsData.slaCoveredCustomerWorkRows]
  );
  const scheduledSlaRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.scheduledSlaRows)
        ? slaInsightsData.scheduledSlaRows.map(row => ({
            ...row,
            plannedStart: row.plannedStart || null,
            plannedDue: row.plannedDue || null
          }))
        : scheduledSlaRows,
    [metricsWorkspaceTab, scheduledSlaRows, slaInsightsData.scheduledSlaRows]
  );
  const slaFirstTimeFixSummaryData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && slaInsightsData.slaFirstTimeFixSummary
        ? slaInsightsData.slaFirstTimeFixSummary
        : slaFirstTimeFixSummary,
    [metricsWorkspaceTab, slaFirstTimeFixSummary, slaInsightsData.slaFirstTimeFixSummary]
  );
  const slaMtbfRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.slaMtbfRows)
        ? slaInsightsData.slaMtbfRows
        : slaMtbfRows,
    [metricsWorkspaceTab, slaInsightsData.slaMtbfRows, slaMtbfRows]
  );
  const slaMaintenanceComplianceSummaryData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && slaInsightsData.slaMaintenanceComplianceSummary
        ? slaInsightsData.slaMaintenanceComplianceSummary
        : slaMaintenanceComplianceSummary,
    [metricsWorkspaceTab, slaInsightsData.slaMaintenanceComplianceSummary, slaMaintenanceComplianceSummary]
  );
  const slaMaintenanceDeviationRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.slaMaintenanceDeviationRows)
        ? slaInsightsData.slaMaintenanceDeviationRows
        : slaMaintenanceDeviationRows,
    [metricsWorkspaceTab, slaInsightsData.slaMaintenanceDeviationRows, slaMaintenanceDeviationRows]
  );
  const breakdownAfterMaintenanceRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.breakdownAfterMaintenanceRows)
        ? slaInsightsData.breakdownAfterMaintenanceRows
        : breakdownAfterMaintenanceRows,
    [breakdownAfterMaintenanceRows, metricsWorkspaceTab, slaInsightsData.breakdownAfterMaintenanceRows]
  );
  const taskGroupRows = useMemo(() => {
    const grouped = new Map<string, number>();
    openInsightCases.forEach(caseItem => {
      const label = getMetricsCaseTaskGroup(caseItem) || 'UNASSIGNED';
      grouped.set(label, (grouped.get(label) || 0) + 1);
    });
    return Array.from(grouped.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [getMetricsCaseTaskGroup, openInsightCases]);
  const ageBucketRows = useMemo(() => {
    const buckets = [
      { label: '0-2 days', min: 0, max: 2 },
      { label: '3-7 days', min: 3, max: 7 },
      { label: '8-14 days', min: 8, max: 14 },
      { label: '15+ days', min: 15, max: Number.POSITIVE_INFINITY }
    ];
    return buckets.map(bucket => ({
      ...bucket,
      count: openInsightCases.filter(caseItem => {
        const age = getMetricsCaseAgeDays(caseItem);
        return age != null && age >= bucket.min && age <= bucket.max;
      }).length
    }));
  }, [getMetricsCaseAgeDays, openInsightCases]);
  const statusDistributionRows = useMemo(() => {
    const grouped = new Map<string, number>();
    filteredInsightCases.forEach(caseItem => {
      const label = String(caseItem.status || 'UNKNOWN')
        .trim()
        .toUpperCase()
        .replace(/_/g, ' ');
      grouped.set(label, (grouped.get(label) || 0) + 1);
    });
    return Array.from(grouped.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [filteredInsightCases]);
  const loadByAssigneeRows = useMemo(() => {
    const grouped = new Map<
      string,
      { assignee: string; tasks: number; scheduled: number; pendingSchedule: number }
    >();
    insightTaskRows.forEach(row => {
      const key = row.assigneeLabel || 'Unassigned';
      const current = grouped.get(key) || { assignee: key, tasks: 0, scheduled: 0, pendingSchedule: 0 };
      current.tasks += 1;
      if (row.plannedStart || row.plannedDue) {
        current.scheduled += 1;
      }
      if (row.scheduleConfirmationStatus && row.scheduleConfirmationStatus !== 'CONFIRMED') {
        current.pendingSchedule += 1;
      }
      grouped.set(key, current);
    });
    return Array.from(grouped.values()).sort(
      (a, b) => b.tasks - a.tasks || b.scheduled - a.scheduled || a.assignee.localeCompare(b.assignee)
    );
  }, [insightTaskRows]);
  const scheduleDayRows = useMemo(() => {
    const grouped = new Map<string, { day: string; tasks: number; assignees: Set<string> }>();
    scheduledThisWeekRows.forEach(row => {
      const planned = row.plannedStart || row.plannedDue;
      if (!planned) return;
      const key = planned.toISOString().slice(0, 10);
      const current = grouped.get(key) || { day: key, tasks: 0, assignees: new Set<string>() };
      current.tasks += 1;
      if (row.assigneeLabel && row.assigneeLabel !== 'Unassigned') {
        row.assigneeLabel.split(',').forEach(value => current.assignees.add(value.trim()));
      }
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        day: row.day,
        tasks: row.tasks,
        assignees: row.assignees.size
      }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [scheduledThisWeekRows]);
  const userTaskHoursRows = useMemo(() => {
    const grouped = new Map<
      string,
      { technician: string; totalHours: number; taskCount: number; averageHours: number | null }
    >();
    scopedInsightCases.forEach(caseItem => {
      getMetricsCaseTasks(caseItem).forEach(task => {
        if (!task?.id) return;
        const startedAt = toDate(task.startedAt);
        const completedAt = toDate(task.completedAt);
        if (!startedAt || !completedAt || completedAt.getTime() <= startedAt.getTime()) return;
        if (metricsStartDate && completedAt.getTime() < metricsStartDate.getTime()) return;
        if (metricsEndDate && completedAt.getTime() > metricsEndDate.getTime()) return;
        const durationHours = (completedAt.getTime() - startedAt.getTime()) / (60 * 60 * 1000);
        if (!Number.isFinite(durationHours) || durationHours <= 0) return;
        const assignees = getTaskAssigneeDisplayNames(task);
        const names =
          assignees.length > 0
            ? assignees
            : (() => {
                const fallback = String(task.assignedUserName || '').trim();
                return fallback ? [fallback] : [];
              })();
        names.forEach(technician => {
          const current =
            grouped.get(technician) || {
              technician,
              totalHours: 0,
              taskCount: 0,
              averageHours: null
            };
          current.totalHours += durationHours;
          current.taskCount += 1;
          current.averageHours = current.taskCount > 0 ? current.totalHours / current.taskCount : null;
          grouped.set(technician, current);
        });
      });
    });
    return Array.from(grouped.values()).sort(
      (a, b) =>
        b.totalHours - a.totalHours ||
        b.taskCount - a.taskCount ||
        a.technician.localeCompare(b.technician)
    );
  }, [getMetricsCaseTasks, metricsEndDate, metricsStartDate, scopedInsightCases]);
  const scheduledUserRows = useMemo(() => {
    const grouped = new Map<
      string,
      { technician: string; tasks: number; nextPlannedAt: Date | null; customers: Set<string> }
    >();
    upcomingWorkRows.forEach(row => {
      const names = String(row.assigneeLabel || '')
        .split(',')
        .map(value => value.trim())
        .filter(value => value && value !== 'Unassigned');
      names.forEach(technician => {
        const current =
          grouped.get(technician) || {
            technician,
            tasks: 0,
            nextPlannedAt: null,
            customers: new Set<string>()
          };
        current.tasks += 1;
        if (row.customerName) current.customers.add(String(row.customerName));
        const plannedAt = toDate((row as any).plannedStart || (row as any).plannedDue);
        if (plannedAt && (!current.nextPlannedAt || plannedAt.getTime() < current.nextPlannedAt.getTime())) {
          current.nextPlannedAt = plannedAt;
        }
        grouped.set(technician, current);
      });
    });
    return Array.from(grouped.values())
      .map(row => ({
        technician: row.technician,
        tasks: row.tasks,
        customers: row.customers.size,
        nextPlannedAt: row.nextPlannedAt
      }))
      .sort((a, b) => {
        const nextA = a.nextPlannedAt?.getTime() || Number.MAX_SAFE_INTEGER;
        const nextB = b.nextPlannedAt?.getTime() || Number.MAX_SAFE_INTEGER;
        return b.tasks - a.tasks || nextA - nextB || a.technician.localeCompare(b.technician);
      });
  }, [upcomingWorkRows]);
  const customerSurveyRows = useMemo(() => {
    const grouped = new Map<string, { customer: string; earned: number; possible: number; surveys: number }>();
    completedSurveyRows.forEach(survey => {
      const key = String(survey.customerName || 'Customer').trim() || 'Customer';
      const current = grouped.get(key) || { customer: key, earned: 0, possible: 0, surveys: 0 };
      current.earned += Number(survey.scoreEarned || 0);
      current.possible += Number(survey.scorePossible || 0);
      current.surveys += 1;
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .map(row => ({
        ...row,
        average: row.possible ? (row.earned / row.possible) * 100 : null
      }))
      .sort((a, b) => {
        const averageA = a.average ?? Number.MAX_SAFE_INTEGER;
        const averageB = b.average ?? Number.MAX_SAFE_INTEGER;
        return averageA - averageB || b.surveys - a.surveys || a.customer.localeCompare(b.customer);
      });
  }, [completedSurveyRows]);
  const customerStageTimingRows = useMemo(() => {
    const stageValues = {
      requestToResponse: [] as number[],
      assignmentToQuote: [] as number[],
      timeToRepair: [] as number[],
      timeOnRepair: [] as number[],
      timeToClose: [] as number[],
      requestToTaskClose: [] as number[]
    };

    filteredInsightCases.forEach(caseItem => {
      const tasks = getMetricsCaseTasks(caseItem).filter(task => task?.id);
      if (tasks.length === 0) return;
      const requestAt = toDate(caseItem.openedAt || caseItem.createdAt || caseItem.updatedAt);
      const firstAssignmentAt = getEarliestTaskAssignmentAt(tasks);
      const firstStartedAt = getEarliestTaskStartedAt(tasks);
      const taskClosedAt = getLatestTaskCompletedAt(tasks);
      const caseClosedAt = toDate(caseItem.closedAt);
      const quoteTimeline = customerQuoteTimelineByCaseId.get(caseItem.id);

      const requestToResponse = diffDurationDays(requestAt, firstAssignmentAt);
      const assignmentToQuote = diffDurationDays(firstAssignmentAt, quoteTimeline?.firstQuoteSavedAt || null);
      const repairReadyAt =
        quoteTimeline?.latestDecisionAt || quoteTimeline?.firstQuoteSavedAt || firstAssignmentAt;
      const timeToRepair = diffDurationDays(repairReadyAt, firstStartedAt);
      const timeOnRepair = diffDurationDays(firstStartedAt, taskClosedAt);
      const timeToClose = diffDurationDays(taskClosedAt, caseClosedAt);
      const requestToTaskClose = diffDurationDays(requestAt, taskClosedAt);

      if (requestToResponse != null) stageValues.requestToResponse.push(requestToResponse);
      if (assignmentToQuote != null) stageValues.assignmentToQuote.push(assignmentToQuote);
      if (timeToRepair != null) stageValues.timeToRepair.push(timeToRepair);
      if (timeOnRepair != null) stageValues.timeOnRepair.push(timeOnRepair);
      if (timeToClose != null) stageValues.timeToClose.push(timeToClose);
      if (requestToTaskClose != null) stageValues.requestToTaskClose.push(requestToTaskClose);
    });

    return [
      {
        key: 'request-to-response',
        label: 'Request to response',
        averageDays: averageDurationDays(stageValues.requestToResponse),
        sampleCount: stageValues.requestToResponse.length
      },
      {
        key: 'assignment-to-quote',
        label: 'Assignment to quote',
        averageDays: averageDurationDays(stageValues.assignmentToQuote),
        sampleCount: stageValues.assignmentToQuote.length
      },
      {
        key: 'time-to-repair',
        label: 'Time to repair',
        averageDays: averageDurationDays(stageValues.timeToRepair),
        sampleCount: stageValues.timeToRepair.length
      },
      {
        key: 'time-on-repair',
        label: 'Time on repair',
        averageDays: averageDurationDays(stageValues.timeOnRepair),
        sampleCount: stageValues.timeOnRepair.length
      },
      {
        key: 'time-to-close',
        label: 'Time to close',
        averageDays: averageDurationDays(stageValues.timeToClose),
        sampleCount: stageValues.timeToClose.length
      },
      {
        key: 'request-to-task-close',
        label: 'Request to task close',
        averageDays: averageDurationDays(stageValues.requestToTaskClose),
        sampleCount: stageValues.requestToTaskClose.length
      }
    ];
  }, [customerQuoteTimelineByCaseId, filteredInsightCases, getMetricsCaseTasks]);
  const repeatFailureRows = useMemo(
    () =>
      (metricsData.mtbf || [])
        .filter(row => Number(row.failureCount || 0) >= 2)
        .sort((a, b) => b.failureCount - a.failureCount || a.label.localeCompare(b.label)),
    [metricsData.mtbf]
  );
  const commonFailureModeRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityCommonFailureModeRows)
        ? metricsData.reliabilityCommonFailureModeRows
            .map(row => ({
              label: String(row.label || 'UNSPECIFIED').trim() || 'UNSPECIFIED',
              count: Number(row.count || 0)
            }))
            .filter(row => row.count > 0)
            .slice(0, 10)
        : [],
    [metricsData.reliabilityCommonFailureModeRows, metricsWorkspaceTab]
  );
  const commonPartUsageRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityCommonPartUsageRows)
        ? metricsData.reliabilityCommonPartUsageRows
            .map(row => ({
              label: String(row.label || 'Unspecified part').trim() || 'Unspecified part',
              count: Number(row.count || 0),
              quantity: Number(row.quantity || 0)
            }))
            .filter(row => row.count > 0 || row.quantity > 0)
            .slice(0, 10)
        : [],
    [metricsData.reliabilityCommonPartUsageRows, metricsWorkspaceTab]
  );
  const reliabilityDeviceRepairMtbfSummary = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && metricsData.reliabilityDeviceRepairMtbfSummary
        ? metricsData.reliabilityDeviceRepairMtbfSummary
        : null,
    [metricsData.reliabilityDeviceRepairMtbfSummary, metricsWorkspaceTab]
  );
  const reliabilityUserRepairMtbfSummary = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && metricsData.reliabilityUserRepairMtbfSummary
        ? metricsData.reliabilityUserRepairMtbfSummary
        : null,
    [metricsData.reliabilityUserRepairMtbfSummary, metricsWorkspaceTab]
  );
  const reliabilityRepairDurationSummary = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && metricsData.reliabilityRepairDurationSummary
        ? metricsData.reliabilityRepairDurationSummary
        : null,
    [metricsData.reliabilityRepairDurationSummary, metricsWorkspaceTab]
  );
  const reliabilityFirstTimeFixSummary = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && metricsData.reliabilityFirstTimeFixSummary
        ? metricsData.reliabilityFirstTimeFixSummary
        : firstTimeFixSummary,
    [firstTimeFixSummary, metricsData.reliabilityFirstTimeFixSummary, metricsWorkspaceTab]
  );
  const reliabilityDeviceRepairMtbfRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityDeviceRepairMtbfRows)
        ? metricsData.reliabilityDeviceRepairMtbfRows
        : deviceRepairMtbfRows,
    [deviceRepairMtbfRows, metricsData.reliabilityDeviceRepairMtbfRows, metricsWorkspaceTab]
  );
  const reliabilityUserRepairMtbfRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityUserRepairMtbfRows)
        ? metricsData.reliabilityUserRepairMtbfRows
        : userRepairMtbfRows,
    [metricsData.reliabilityUserRepairMtbfRows, metricsWorkspaceTab, userRepairMtbfRows]
  );
  const reliabilityRepairDurationRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityRepairDurationRows)
        ? metricsData.reliabilityRepairDurationRows
        : repairDurationRows,
    [metricsData.reliabilityRepairDurationRows, metricsWorkspaceTab, repairDurationRows]
  );
  const reliabilityHoldReasonRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityHoldReasonRows)
        ? metricsData.reliabilityHoldReasonRows
        : reliabilityHoldReasonRows,
    [metricsData.reliabilityHoldReasonRows, metricsWorkspaceTab, reliabilityHoldReasonRows]
  );
  const reliabilityRepairWarrantySummary = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && metricsData.reliabilityRepairWarrantySummary
        ? metricsData.reliabilityRepairWarrantySummary
        : repairWarrantySummary,
    [metricsData.reliabilityRepairWarrantySummary, metricsWorkspaceTab, repairWarrantySummary]
  );
  const reliabilityMaintenanceComplianceSummary = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && metricsData.reliabilityMaintenanceComplianceSummary
        ? metricsData.reliabilityMaintenanceComplianceSummary
        : maintenanceComplianceSummary,
    [
      maintenanceComplianceSummary,
      metricsData.reliabilityMaintenanceComplianceSummary,
      metricsWorkspaceTab
    ]
  );
  const reliabilityRepairFrequencyRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityRepairFrequencyRows)
        ? metricsData.reliabilityRepairFrequencyRows
        : repairFrequencyRows,
    [metricsData.reliabilityRepairFrequencyRows, metricsWorkspaceTab, repairFrequencyRows]
  );
  const deviceMtbfAverage = useMemo(() => {
    if (reliabilityDeviceRepairMtbfSummary) {
      return reliabilityDeviceRepairMtbfSummary.averageDays;
    }
    const rows = reliabilityDeviceRepairMtbfRows.filter(row => typeof row.mtbfDays === 'number');
    if (rows.length === 0) return null;
    return rows.reduce((sum, row) => sum + Number(row.mtbfDays || 0), 0) / rows.length;
  }, [reliabilityDeviceRepairMtbfRows, reliabilityDeviceRepairMtbfSummary]);
  const userMtbfAverage = useMemo(() => {
    if (reliabilityUserRepairMtbfSummary) {
      return reliabilityUserRepairMtbfSummary.averageDays;
    }
    const rows = reliabilityUserRepairMtbfRows.filter(row => typeof row.mtbfDays === 'number');
    if (rows.length === 0) return null;
    return rows.reduce((sum, row) => sum + Number(row.mtbfDays || 0), 0) / rows.length;
  }, [reliabilityUserRepairMtbfRows, reliabilityUserRepairMtbfSummary]);
  const averageRepairDuration = useMemo(() => {
    if (reliabilityRepairDurationSummary) {
      return reliabilityRepairDurationSummary.averageDays;
    }
    const rows = reliabilityRepairDurationRows.filter(row => typeof row.averageDays === 'number');
    if (rows.length === 0) return null;
    return rows.reduce((sum, row) => sum + Number(row.averageDays || 0), 0) / rows.length;
  }, [reliabilityRepairDurationRows, reliabilityRepairDurationSummary]);
  const inventorySummary = useMemo(
    () =>
      inventoryInsightsData.summary || {
        stockOnHandValue: 0,
        stockInTransitValue: 0,
        stockOnOrderValue: 0,
        vanStockValue: 0,
        varianceValue: 0,
        delayedRepairCases: 0,
        stockWarehouseCount: 0,
        vanStockWarehouseCount: 0,
        approvedCountSessionCount: 0
      },
    [inventoryInsightsData.summary]
  );
  const inventoryWarehouseValueRows = useMemo(
    () => (Array.isArray(inventoryInsightsData.warehouseValueRows) ? inventoryInsightsData.warehouseValueRows : []),
    [inventoryInsightsData.warehouseValueRows]
  );
  const inventoryTransitRows = useMemo(
    () => {
      const sourceRows = Array.isArray(inventoryInsightsData.transitRows)
        ? inventoryInsightsData.transitRows
        : inventoryWarehouseValueRows.map(row => ({
            warehouseId: row.warehouseId,
            warehouseName: row.warehouseName,
            quantityInTransit: row.quantityInTransit,
            quantityOnOrder: row.quantityOnOrder,
            inTransitValue: row.inTransitValue,
            onOrderValue: row.onOrderValue
          }));
      return sourceRows
        .slice()
        .sort(
          (a, b) =>
            b.inTransitValue + b.onOrderValue - (a.inTransitValue + a.onOrderValue) ||
            b.quantityInTransit + b.quantityOnOrder - (a.quantityInTransit + a.quantityOnOrder) ||
            a.warehouseName.localeCompare(b.warehouseName)
        );
    },
    [inventoryInsightsData.transitRows, inventoryWarehouseValueRows]
  );
  const inventoryVarianceCategoryRows = useMemo(
    () => (Array.isArray(inventoryInsightsData.varianceCategoryRows) ? inventoryInsightsData.varianceCategoryRows : []),
    [inventoryInsightsData.varianceCategoryRows]
  );
  const inventoryCountSessionRows = useMemo(
    () => (Array.isArray(inventoryInsightsData.countSessionRows) ? inventoryInsightsData.countSessionRows : []),
    [inventoryInsightsData.countSessionRows]
  );
  const inventoryVanStockRows = useMemo(
    () => (Array.isArray(inventoryInsightsData.vanStockRows) ? inventoryInsightsData.vanStockRows : []),
    [inventoryInsightsData.vanStockRows]
  );
  const inventoryDelayedPartRows = useMemo(
    () => (Array.isArray(inventoryInsightsData.delayedPartRows) ? inventoryInsightsData.delayedPartRows : []),
    [inventoryInsightsData.delayedPartRows]
  );
  const inventoryCaseDrilldownRows = useMemo(
    () =>
      metricsWorkspaceTab === 'inventory' && Array.isArray(inventoryInsightsData.inventoryCaseDrilldownRows)
        ? inventoryInsightsData.inventoryCaseDrilldownRows
        : [],
    [inventoryInsightsData.inventoryCaseDrilldownRows, metricsWorkspaceTab]
  );
  const inventoryStockWarehouseCount = inventorySummary.stockWarehouseCount ?? inventoryWarehouseValueRows.length;
  const inventoryVanStockWarehouseCount = inventorySummary.vanStockWarehouseCount ?? inventoryVanStockRows.length;
  const inventoryApprovedCountSessionCount =
    inventorySummary.approvedCountSessionCount ?? inventoryCountSessionRows.length;
  const metricsTeamSummary = useMemo(
    () => (metricsWorkspaceTab === 'team' && metricsData.teamSummary ? metricsData.teamSummary : null),
    [metricsData.teamSummary, metricsWorkspaceTab]
  );
  const teamLoadByAssigneeRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamLoadByAssigneeRows)
        ? metricsData.teamLoadByAssigneeRows
        : metricsWorkspaceTab === 'team'
        ? []
        : loadByAssigneeRows,
    [loadByAssigneeRows, metricsData.teamLoadByAssigneeRows, metricsWorkspaceTab]
  );
  const teamUserTaskHoursRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamUserTaskHoursRows)
        ? metricsData.teamUserTaskHoursRows
        : metricsWorkspaceTab === 'team'
        ? []
        : userTaskHoursRows,
    [metricsData.teamUserTaskHoursRows, metricsWorkspaceTab, userTaskHoursRows]
  );
  const teamScheduledUserRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamScheduledUserRows)
        ? metricsData.teamScheduledUserRows.map(row => ({
            ...row,
            nextPlannedAt: row.nextPlannedAt ? new Date(row.nextPlannedAt) : null
          }))
        : metricsWorkspaceTab === 'team'
        ? []
        : scheduledUserRows,
    [metricsData.teamScheduledUserRows, metricsWorkspaceTab, scheduledUserRows]
  );
  const teamSurveyByTechnicianRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamSurveyByTechnicianRows)
        ? metricsData.teamSurveyByTechnicianRows
        : metricsWorkspaceTab === 'team'
        ? []
        : surveyByTechnicianRows,
    [metricsData.teamSurveyByTechnicianRows, metricsWorkspaceTab, surveyByTechnicianRows]
  );
  const teamUserRepairMtbfRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamUserRepairMtbfRows)
        ? metricsData.teamUserRepairMtbfRows
        : metricsWorkspaceTab === 'team'
        ? []
        : userRepairMtbfRows,
    [metricsData.teamUserRepairMtbfRows, metricsWorkspaceTab, userRepairMtbfRows]
  );
  const teamUserFirstTimeFixRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamUserFirstTimeFixRows)
        ? metricsData.teamUserFirstTimeFixRows
        : metricsWorkspaceTab === 'team'
        ? []
        : userFirstTimeFixRows,
    [metricsData.teamUserFirstTimeFixRows, metricsWorkspaceTab, userFirstTimeFixRows]
  );
  const teamRepairPerformanceRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamRepairPerformanceRows)
        ? metricsData.teamRepairPerformanceRows
        : metricsWorkspaceTab === 'team'
        ? []
        : teamRepairPerformanceRows,
    [metricsData.teamRepairPerformanceRows, metricsWorkspaceTab, teamRepairPerformanceRows]
  );
  const teamWorkloadDetailRowsData = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamWorkloadDetailRows)
        ? metricsData.teamWorkloadDetailRows
        : [],
    [metricsData.teamWorkloadDetailRows, metricsWorkspaceTab]
  );
  const teamCaseDrilldownRows = useMemo(
    () =>
      metricsWorkspaceTab === 'team' && Array.isArray(metricsData.teamCaseDrilldownRows)
        ? metricsData.teamCaseDrilldownRows
        : [],
    [metricsData.teamCaseDrilldownRows, metricsWorkspaceTab]
  );
  const teamUserMtbfAverage = useMemo(() => {
    if (metricsTeamSummary?.userMtbfAverage != null) {
      return metricsTeamSummary.userMtbfAverage;
    }
    const rows = teamUserRepairMtbfRows.filter(row => typeof row.mtbfDays === 'number');
    if (rows.length === 0) return null;
    return rows.reduce((sum, row) => sum + Number(row.mtbfDays || 0), 0) / rows.length;
  }, [metricsTeamSummary, teamUserRepairMtbfRows]);
  const teamTotalTaskHours = useMemo(
    () =>
      metricsTeamSummary?.totalTaskHours != null
        ? metricsTeamSummary.totalTaskHours
        : sumDurationHours(teamUserTaskHoursRows.map(row => row.totalHours)),
    [metricsTeamSummary, teamUserTaskHoursRows]
  );
  const teamFirstTimeFixAverage = useMemo(() => {
    if (metricsTeamSummary?.firstTimeFixAverage != null) {
      return metricsTeamSummary.firstTimeFixAverage;
    }
    const rows = teamUserFirstTimeFixRows.filter(row => typeof row.rate === 'number');
    if (rows.length === 0) return null;
    return rows.reduce((sum, row) => sum + Number(row.rate || 0), 0) / rows.length;
  }, [metricsTeamSummary, teamUserFirstTimeFixRows]);
  const teamSurveyAveragePercent = useMemo(
    () =>
      metricsTeamSummary?.surveyAveragePercent != null
        ? metricsTeamSummary.surveyAveragePercent
        : surveyAveragePercent,
    [metricsTeamSummary, surveyAveragePercent]
  );
  const averageOpenAge = useMemo(() => {
    if (openInsightCases.length === 0) return null;
    const ages = openInsightCases
      .map(caseItem => getMetricsCaseAgeDays(caseItem))
      .filter((value): value is number => typeof value === 'number');
    if (ages.length === 0) return null;
    return ages.reduce((sum, value) => sum + value, 0) / ages.length;
  }, [getMetricsCaseAgeDays, openInsightCases]);
  const insightSignals = useMemo(() => {
    const signals: Array<{ id: string; tone: 'danger' | 'warning' | 'info'; title: string; detail: string }> = [];
    if (highPriorityAgedCases.length > 0) {
      signals.push({
        id: 'aged-high-priority',
        tone: highPriorityAgedCases.length >= 3 ? 'danger' : 'warning',
        title: `${highPriorityAgedCases.length} aged high-priority cases`,
        detail: 'High or critical work has remained open for 14+ days.'
      });
    }
    if (unassignedTaskRows.length > 0) {
      signals.push({
        id: 'unassigned-work',
        tone: unassignedTaskRows.length >= 3 ? 'danger' : 'warning',
        title: `${unassignedTaskRows.length} active tasks are unassigned`,
        detail: 'Service work is active without an explicit assignee.'
      });
    }
    if (pendingScheduleRows.length > 0) {
      signals.push({
        id: 'pending-schedule',
        tone: 'warning',
        title: `${pendingScheduleRows.length} planned tasks need schedule attention`,
        detail: 'Planned work exists without a confirmed visit window.'
      });
    }
    if (repeatFailureRows.length > 0) {
      signals.push({
        id: 'repeat-failure',
        tone: 'info',
        title: `${repeatFailureRows.length} devices show repeat failures`,
        detail: 'Reliability review is recommended for recurring faults.'
      });
    }
    if (pendingSurveyRows.length > 0) {
      signals.push({
        id: 'pending-surveys',
        tone: 'info',
        title: `${pendingSurveyRows.length} surveys are still waiting`,
        detail: 'Recent service outcomes have feedback requests awaiting completion.'
      });
    }
    return signals.slice(0, 4);
  }, [highPriorityAgedCases.length, pendingScheduleRows.length, pendingSurveyRows.length, repeatFailureRows.length, unassignedTaskRows.length]);
  const metricsOverviewSummary = metricsData.overviewSummary;
  const metricsServiceSummary = metricsData.serviceSummary;
  const metricsCustomerSummary = metricsData.customerSummary;
  const metricsWorkspaceKpis = useMemo(() => {
    const overviewKpis = metricsOverviewSummary
      ? [
          {
            label: 'Open backlog',
            value: metricsOverviewSummary.openCases,
            meta: `${metricsOverviewSummary.totalCases} total cases in range`
          },
          {
            label: 'Upcoming work',
            value: metricsOverviewSummary.upcomingWork,
            meta: 'Planned visits in the next 7 days'
          },
          {
            label: 'Aged open work',
            value: metricsOverviewSummary.agedOpenCases,
            meta: 'Open work aged 15+ days'
          },
          {
            label: 'Work completed',
            value: metricsOverviewSummary.completedTasks,
            meta: 'Completed tasks in selected range'
          },
          {
            label: 'Pending schedule',
            value: metricsOverviewSummary.pendingSchedule,
            meta: 'Planned work awaiting confirmation'
          },
          {
            label: 'Survey average',
            value:
              metricsOverviewSummary.surveyAveragePercent != null
                ? `${Math.round(metricsOverviewSummary.surveyAveragePercent)}%`
                : '-',
            meta: `${metricsOverviewSummary.completedSurveys} completed surveys`
          }
        ]
      : [
          { label: 'Open backlog', value: openInsightCases.length, meta: `${filteredInsightCases.length} total cases in range` },
          { label: 'Upcoming work', value: upcomingWorkRows.length, meta: 'Planned visits in the next 7 days' },
          { label: 'Aged open work', value: ageBucketRows.find(bucket => bucket.label === '15+ days')?.count || 0, meta: 'Open work aged 15+ days' },
          { label: 'Work completed', value: completedTaskRows.length, meta: 'Completed tasks in selected range' },
          { label: 'Pending schedule', value: pendingScheduleRows.length, meta: 'Planned work awaiting confirmation' },
          { label: 'Survey average', value: surveyAveragePercent != null ? `${Math.round(surveyAveragePercent)}%` : '-', meta: `${completedSurveyRows.length} completed surveys` }
        ];
    const serviceKpis = metricsWorkspaceTab === 'service' && metricsServiceSummary
      ? [
          { label: 'Open cases', value: metricsServiceSummary.openCases, meta: 'Current open case volume' },
          { label: 'Active tasks', value: metricsServiceSummary.activeTasks, meta: 'Open task workload in current scope' },
          { label: 'Unassigned work', value: metricsServiceSummary.unassignedTasks, meta: 'Active tasks needing ownership' },
          {
            label: 'Avg open age',
            value:
              metricsServiceSummary.averageOpenAgeDays != null
                ? `${Number(metricsServiceSummary.averageOpenAgeDays).toFixed(1)}d`
                : '-',
            meta: 'Average age of open cases'
          },
          { label: 'Scheduled in range', value: metricsServiceSummary.scheduledThisWeek, meta: 'Planned visits matching date filter' },
          { label: 'Cases with quotes', value: metricsServiceSummary.casesWithQuotes, meta: 'Current cases carrying quote decisions' }
        ]
      : [
          { label: 'Open cases', value: openInsightCases.length, meta: 'Current open case volume' },
          { label: 'Active tasks', value: insightTaskRows.length, meta: 'Open task workload in current scope' },
          { label: 'Unassigned work', value: unassignedTaskRows.length, meta: 'Active tasks needing ownership' },
          { label: 'Avg open age', value: averageOpenAge != null ? `${averageOpenAge.toFixed(1)}d` : '-', meta: 'Average age of open cases' },
          { label: 'Scheduled in range', value: scheduledThisWeekRows.length, meta: 'Planned visits matching date filter' },
          { label: 'Cases with quotes', value: serviceLatestQuoteRows.length, meta: 'Current cases carrying quote decisions' }
        ];

    return {
      overview: overviewKpis,
      service: serviceKpis,
      reliability: [
        {
          label: 'First time fix',
          value: reliabilityFirstTimeFixSummary.rate != null ? `${Math.round(reliabilityFirstTimeFixSummary.rate)}%` : '-',
          meta: `${reliabilityFirstTimeFixSummary.fixedFirstTime} of ${reliabilityFirstTimeFixSummary.eligible} closed repairs had no repeat within ${FIRST_TIME_FIX_WINDOW_DAYS} days`
        },
        {
          label: 'Device MTBF',
          value: formatDurationDaysLabel(deviceMtbfAverage),
          meta: `${
            reliabilityDeviceRepairMtbfSummary?.groupedViews ??
            reliabilityDeviceRepairMtbfRows.filter(row => row.mtbfDays != null).length
          } grouped device views with repeat repair history`
        },
        {
          label: 'User MTBF',
          value: formatDurationDaysLabel(userMtbfAverage),
          meta: `${
            reliabilityUserRepairMtbfSummary?.technicians ??
            reliabilityUserRepairMtbfRows.filter(row => row.mtbfDays != null).length
          } technicians with repeat repair intervals`
        },
        {
          label: 'Avg repair time',
          value: formatDurationDaysLabel(averageRepairDuration),
          meta: `${
            reliabilityRepairDurationSummary?.groupedViews ?? reliabilityRepairDurationRows.length
          } ${metricsGroupBy === 'device' ? 'devices' : 'models'} with closed repair duration data`
        },
        {
          label: 'Warranty repairs',
          value: reliabilityRepairWarrantySummary.inWarranty,
          meta: `${reliabilityRepairWarrantySummary.inWarranty} in warranty | ${reliabilityRepairWarrantySummary.outWarranty} out of warranty`
        },
        {
          label: 'Maintenance compliance',
          value: reliabilityMaintenanceComplianceSummary.rate != null ? `${Math.round(reliabilityMaintenanceComplianceSummary.rate)}%` : '-',
          meta: `${reliabilityMaintenanceComplianceSummary.compliantDevices} of ${reliabilityMaintenanceComplianceSummary.devicesDue} devices had maintenance completed on time`
        }
      ],
      customers:
        metricsWorkspaceTab === 'customers' && metricsCustomerSummary
          ? [
              { label: 'Open customer cases', value: metricsCustomerSummary.openCases, meta: 'Current customer-facing workload' },
              { label: 'Scheduled jobs', value: metricsCustomerSummary.scheduledJobs, meta: 'Planned visits in the next 7 days' },
              { label: 'Cases with quotes', value: metricsCustomerSummary.casesWithQuotes, meta: 'Cases currently carrying quote decisions' },
              {
                label: 'Avg response time',
                value: formatDurationDaysLabel(metricsCustomerSummary.requestToResponseAverageDays),
                meta: `${metricsCustomerSummary.requestToResponseSampleCount} cases with assignment activity`
              },
              {
                label: 'Request to task close',
                value: formatDurationDaysLabel(metricsCustomerSummary.requestToTaskCloseAverageDays),
                meta: `${metricsCustomerSummary.requestToTaskCloseSampleCount} cases with completed task closure`
              },
              {
                label: 'Survey average',
                value:
                  metricsCustomerSummary.surveyAveragePercent != null
                    ? `${Math.round(metricsCustomerSummary.surveyAveragePercent)}%`
                    : '-',
                meta: `${metricsCustomerSummary.completedSurveys} completed surveys`
              }
            ]
          : [
              { label: 'Open customer cases', value: openInsightCases.length, meta: 'Current customer-facing workload' },
              { label: 'Scheduled jobs', value: upcomingWorkRows.length, meta: 'Planned visits in the next 7 days' },
              { label: 'Cases with quotes', value: serviceLatestQuoteRows.length, meta: 'Cases currently carrying quote decisions' },
              {
                label: 'Avg response time',
                value: formatDurationDaysLabel(customerStageTimingRows.find(row => row.key === 'request-to-response')?.averageDays ?? null),
                meta: `${customerStageTimingRows.find(row => row.key === 'request-to-response')?.sampleCount || 0} cases with assignment activity`
              },
              {
                label: 'Request to task close',
                value: formatDurationDaysLabel(customerStageTimingRows.find(row => row.key === 'request-to-task-close')?.averageDays ?? null),
                meta: `${customerStageTimingRows.find(row => row.key === 'request-to-task-close')?.sampleCount || 0} cases with completed task closure`
              },
              { label: 'Survey average', value: surveyAveragePercent != null ? `${Math.round(surveyAveragePercent)}%` : '-', meta: `${completedSurveyRows.length} completed surveys` }
            ],
      sla: [
        {
          label: 'Active SLA devices',
          value: slaSummaryData?.activeDevices ?? currentSlaAssignmentRows.length,
          meta: `${slaSummaryData?.coverageViews ?? slaCoverageRows.length} ${metricsGroupBy === 'device' ? 'device' : 'model'} coverage views currently open`
        },
        {
          label: 'Expiring <30d',
          value: slaSummaryData?.expiring30 ?? expiringSla30Rows.length,
          meta: 'Active SLA assignments ending within the next 30 days'
        },
        {
          label: 'Expiring <90d',
          value: slaSummaryData?.expiring90 ?? expiringSla90Rows.length,
          meta: 'Active SLA assignments ending within the next 90 days'
        },
        {
          label: 'SLA jobs',
          value: slaSummaryData?.scheduledJobs ?? scheduledSlaRowsData.length,
          meta: `${slaSummaryData?.scheduledJobs ?? scheduledSlaRowsData.length} upcoming scheduled jobs currently tied to SLA coverage`
        },
        {
          label: 'SLA first time fix',
          value: slaFirstTimeFixSummaryData.rate != null ? `${Math.round(slaFirstTimeFixSummaryData.rate)}%` : '-',
          meta: `${slaFirstTimeFixSummaryData.fixedFirstTime} of ${slaFirstTimeFixSummaryData.eligible} eligible SLA repairs had no repeat within ${FIRST_TIME_FIX_WINDOW_DAYS} days`
        },
        {
          label: 'Maintenance compliance',
          value: slaMaintenanceComplianceSummaryData.rate != null ? `${Math.round(slaMaintenanceComplianceSummaryData.rate)}%` : '-',
          meta: `${slaMaintenanceComplianceSummaryData.compliantDevices} of ${slaMaintenanceComplianceSummaryData.devicesDue} SLA devices completed maintenance on time`
        }
      ],
      inventory: [
        {
          label: 'Stock on hand value',
          value: formatInsightsCurrency(inventorySummary.stockOnHandValue),
          meta: `${inventoryStockWarehouseCount} accessible warehouses with active landed-cost stock posture`
        },
        {
          label: 'Stock in transit value',
          value: formatInsightsCurrency(inventorySummary.stockInTransitValue),
          meta: 'Current in-transit stock across visible warehouses at landed cost'
        },
        {
          label: 'Stock on order value',
          value: formatInsightsCurrency(inventorySummary.stockOnOrderValue),
          meta: 'Current on-order replenishment value in visible warehouses at landed cost'
        },
        {
          label: 'Van stock value',
          value: formatInsightsCurrency(inventorySummary.vanStockValue),
          meta: `${inventoryVanStockWarehouseCount} user warehouses carrying stock on hand at landed cost`
        },
        {
          label: 'Variance value',
          value: formatInsightsCurrency(inventorySummary.varianceValue),
          meta: `${inventoryApprovedCountSessionCount} approved counts in the selected range at landed cost`
        },
        {
          label: 'Delayed repair cases',
          value: inventorySummary.delayedRepairCases,
          meta: 'Open cases currently waiting on missing parts'
        }
      ],
      team: [
        {
          label: 'Assigned backlog',
          value:
            metricsTeamSummary?.assignedBacklog != null
              ? metricsTeamSummary.assignedBacklog
              : teamLoadByAssigneeRows.reduce((sum, row) => sum + row.tasks, 0),
          meta: 'Open tasks with an assignee'
        },
        {
          label: 'Hours on task',
          value: teamTotalTaskHours != null ? `${teamTotalTaskHours.toFixed(1)}h` : '-',
          meta: `${
            metricsTeamSummary?.completedTaskCount ?? teamUserTaskHoursRows.reduce((sum, row) => sum + row.taskCount, 0)
          } completed tasks with tracked duration`
        },
        {
          label: 'User MTBF',
          value: formatDurationDaysLabel(teamUserMtbfAverage),
          meta: `${
            metricsTeamSummary?.mtbfTechnicians ?? teamUserRepairMtbfRows.filter(row => row.mtbfDays != null).length
          } technicians with repeat repair intervals`
        },
        {
          label: 'First time fix',
          value: teamFirstTimeFixAverage != null ? `${Math.round(teamFirstTimeFixAverage)}%` : '-',
          meta: `${
            metricsTeamSummary?.firstTimeFixTechnicians ?? teamUserFirstTimeFixRows.filter(row => row.eligible > 0).length
          } technicians with eligible repair closures`
        },
        {
          label: 'Survey average',
          value: teamSurveyAveragePercent != null ? `${Math.round(teamSurveyAveragePercent)}%` : '-',
          meta: `${metricsTeamSummary?.completedSurveys ?? completedSurveyRows.length} completed surveys`
        },
        {
          label: 'Scheduled users',
          value: metricsTeamSummary?.scheduledUsers ?? teamScheduledUserRows.length,
          meta: 'Users carrying scheduled work next 7 days'
        }
      ]
    } satisfies Record<
      MetricsWorkspaceTab,
      Array<{ label: string; value: string | number; meta: string }>
    >;
  }, [
    ageBucketRows,
    averageRepairDuration,
    averageOpenAge,
    customerStageTimingRows,
    completedTaskRows.length,
    completedSurveyRows.length,
    currentSlaAssignmentRows.length,
    inventoryApprovedCountSessionCount,
    inventorySummary.delayedRepairCases,
    inventorySummary.stockInTransitValue,
    inventorySummary.stockOnHandValue,
    inventorySummary.stockOnOrderValue,
    inventorySummary.vanStockValue,
    inventorySummary.varianceValue,
    inventoryStockWarehouseCount,
    inventoryVanStockWarehouseCount,
    deviceMtbfAverage,
    expiringSla30Rows.length,
    expiringSla90Rows.length,
    filteredInsightCases.length,
    insightTaskRows,
    metricsGroupBy,
    metricsServiceSummary,
    openInsightCases.length,
    pendingScheduleRows.length,
    reliabilityDeviceRepairMtbfSummary,
    reliabilityDeviceRepairMtbfRows,
    reliabilityFirstTimeFixSummary.eligible,
    reliabilityFirstTimeFixSummary.fixedFirstTime,
    reliabilityFirstTimeFixSummary.rate,
    reliabilityMaintenanceComplianceSummary,
    reliabilityRepairDurationSummary,
    reliabilityRepairDurationRows.length,
    reliabilityRepairWarrantySummary.inWarranty,
    reliabilityRepairWarrantySummary.outWarranty,
    scheduledSlaRowsData.length,
    slaSummaryData,
    serviceLatestQuoteRows.length,
    slaCoverageRows.length,
    slaFirstTimeFixSummaryData.eligible,
    slaFirstTimeFixSummaryData.fixedFirstTime,
    slaFirstTimeFixSummaryData.rate,
    slaMaintenanceComplianceSummaryData.compliantDevices,
    slaMaintenanceComplianceSummaryData.devicesDue,
    slaMaintenanceComplianceSummaryData.rate,
    metricsTeamSummary,
    teamLoadByAssigneeRows,
    teamScheduledUserRows.length,
    teamFirstTimeFixAverage,
    teamSurveyAveragePercent,
    teamTotalTaskHours,
    teamUserFirstTimeFixRows,
    teamUserMtbfAverage,
    teamUserRepairMtbfRows,
    teamUserTaskHoursRows,
    upcomingWorkRows.length,
    scheduledThisWeekRows,
    surveyAveragePercent,
    reliabilityUserRepairMtbfSummary,
    reliabilityUserRepairMtbfRows,
    userMtbfAverage,
    unassignedTaskRows.length,
    metricsOverviewSummary,
    metricsCustomerSummary,
    metricsWorkspaceTab
  ]);
  const overviewInsightSignals = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.signals)
        ? metricsData.signals
        : insightSignals,
    [insightSignals, metricsData.signals, metricsWorkspaceTab]
  );
  const overviewTaskGroupRows = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.taskGroupRows)
        ? metricsData.taskGroupRows
        : taskGroupRows,
    [metricsData.taskGroupRows, metricsWorkspaceTab, taskGroupRows]
  );
  const overviewAgeBucketRows = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.ageBucketRows)
        ? metricsData.ageBucketRows
        : ageBucketRows,
    [ageBucketRows, metricsData.ageBucketRows, metricsWorkspaceTab]
  );
  const overviewUpcomingWorkRows = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.upcomingWorkRows)
        ? metricsData.upcomingWorkRows
        : upcomingWorkRows,
    [metricsData.upcomingWorkRows, metricsWorkspaceTab, upcomingWorkRows]
  );
  const overviewCompletedTaskGroupRows = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.completedTaskGroupRows)
        ? metricsData.completedTaskGroupRows
        : completedTaskGroupRows,
    [completedTaskGroupRows, metricsData.completedTaskGroupRows, metricsWorkspaceTab]
  );
  const overviewCustomerLoadRows = useMemo(
    () =>
      metricsWorkspaceTab === 'overview' && Array.isArray(metricsData.customerLoadRows)
        ? metricsData.customerLoadRows
        : customerLoadRows,
    [customerLoadRows, metricsData.customerLoadRows, metricsWorkspaceTab]
  );
  const serviceStatusDistributionRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.serviceStatusDistributionRows)
        ? metricsData.serviceStatusDistributionRows
        : statusDistributionRows,
    [metricsData.serviceStatusDistributionRows, metricsWorkspaceTab, statusDistributionRows]
  );
  const serviceUpcomingWorkRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.serviceUpcomingWorkRows)
        ? metricsData.serviceUpcomingWorkRows
        : scheduledThisWeekRows,
    [metricsData.serviceUpcomingWorkRows, metricsWorkspaceTab, scheduledThisWeekRows]
  );
  const serviceQuotePipelineRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.serviceQuoteStatusRows)
        ? metricsData.serviceQuoteStatusRows
        : serviceQuoteStatusRows,
    [metricsData.serviceQuoteStatusRows, metricsWorkspaceTab, serviceQuoteStatusRows]
  );
  const servicePartWaitingRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.servicePartsWaitingRows)
        ? metricsData.servicePartsWaitingRows.map(row => ({
            ...row,
            etaAt: row.etaAt ?? null
          }))
        : partsWaitingRows,
    [metricsData.servicePartsWaitingRows, metricsWorkspaceTab, partsWaitingRows]
  );
  const serviceUpcomingCoveredRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.serviceUpcomingSlaRows)
        ? metricsData.serviceUpcomingSlaRows
        : upcomingSlaRows,
    [metricsData.serviceUpcomingSlaRows, metricsWorkspaceTab, upcomingSlaRows]
  );
  const serviceCaseDrilldownRows = useMemo(
    () =>
      metricsWorkspaceTab === 'service' && Array.isArray(metricsData.serviceCaseDrilldownRows)
        ? metricsData.serviceCaseDrilldownRows
        : [],
    [metricsData.serviceCaseDrilldownRows, metricsWorkspaceTab]
  );
  const reliabilityCaseDrilldownRows = useMemo(
    () =>
      metricsWorkspaceTab === 'reliability' && Array.isArray(metricsData.reliabilityCaseDrilldownRows)
        ? metricsData.reliabilityCaseDrilldownRows
        : [],
    [metricsData.reliabilityCaseDrilldownRows, metricsWorkspaceTab]
  );
  const customerCaseDrilldownRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerCaseDrilldownRows)
        ? metricsData.customerCaseDrilldownRows
        : [],
    [metricsData.customerCaseDrilldownRows, metricsWorkspaceTab]
  );
  const slaCaseDrilldownRows = useMemo(
    () =>
      metricsWorkspaceTab === 'sla' && Array.isArray(slaInsightsData.slaCaseDrilldownRows)
        ? slaInsightsData.slaCaseDrilldownRows
        : [],
    [metricsWorkspaceTab, slaInsightsData.slaCaseDrilldownRows]
  );
  const customerStatusDistributionRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerStatusDistributionRows)
        ? metricsData.customerStatusDistributionRows
        : statusDistributionRows,
    [metricsData.customerStatusDistributionRows, metricsWorkspaceTab, statusDistributionRows]
  );
  const customerQuotePipelineRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerQuoteStatusRows)
        ? metricsData.customerQuoteStatusRows
        : serviceQuoteStatusRows,
    [metricsData.customerQuoteStatusRows, metricsWorkspaceTab, serviceQuoteStatusRows]
  );
  const customerWorkspaceLoadRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerOpenLoadRows)
        ? metricsData.customerOpenLoadRows
        : customerLoadRows,
    [customerLoadRows, metricsData.customerOpenLoadRows, metricsWorkspaceTab]
  );
  const customerWorkspaceScheduledRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerScheduledRows)
        ? metricsData.customerScheduledRows.map(row => ({
            ...row,
            nextPlannedAt: row.nextPlannedAt ? toDate(row.nextPlannedAt) : null
          }))
        : customerScheduledRows,
    [customerScheduledRows, metricsData.customerScheduledRows, metricsWorkspaceTab]
  );
  const customerWorkspaceCompletedRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerCompletedWorkRows)
        ? metricsData.customerCompletedWorkRows.map(row => ({
            ...row,
            latestCompletedAt: row.latestCompletedAt ? toDate(row.latestCompletedAt) : null
          }))
        : customerCompletedWorkRows,
    [customerCompletedWorkRows, metricsData.customerCompletedWorkRows, metricsWorkspaceTab]
  );
  const customerWorkspaceSurveyRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerSurveyRows)
        ? metricsData.customerSurveyRows
        : customerSurveyRows,
    [customerSurveyRows, metricsData.customerSurveyRows, metricsWorkspaceTab]
  );
  const customerWorkspaceStageTimingRows = useMemo(
    () =>
      metricsWorkspaceTab === 'customers' && Array.isArray(metricsData.customerStageTimingRows)
        ? metricsData.customerStageTimingRows
        : customerStageTimingRows,
    [customerStageTimingRows, metricsData.customerStageTimingRows, metricsWorkspaceTab]
  );

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const base = await getBaseUrl();
      const params = new URLSearchParams();
      if (metricsStart) params.set('start', metricsStart);
      if (metricsEnd) params.set('end', metricsEnd);
      if (metricsCustomerId) params.set('customerId', metricsCustomerId);
      if (metricsDeviceId) params.set('deviceId', metricsDeviceId);
      if (metricsModelId) params.set('modelId', metricsModelId);
      if (metricsTaskGroup) params.set('taskGroup', metricsTaskGroup);
      if (canUseMetricsUserFilter && effectiveMetricsUserIds.length > 0) {
        params.set('userIds', effectiveMetricsUserIds.join(','));
      }
      if (metricsWorkspaceSupportsCaseDrill && metricsStatus) {
        params.set('status', metricsStatus);
      }
      if (metricsWorkspaceSupportsCaseDrill && metricsCaseId) {
        params.set('caseId', metricsCaseId);
      }
      if (metricsGroupBy) params.set('groupBy', metricsGroupBy);
      if (metricsWorkspaceIncludeBlocks.length > 0) {
        params.set('includes', metricsCurrentWorkspaceIncludeIds.join(','));
      }

      if (metricsWorkspaceTab === 'sla') {
        setSlaInsightsLoading(true);
        const response = await fetch(`${base}/api/metrics/sla-insights?${params.toString()}`);
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `Insights failed (${response.status})`);
        }
        setSlaInsightsData(payload || {});
        return;
      }

      if (metricsWorkspaceTab === 'inventory') {
        setInventoryInsightsLoading(true);
        const response = await fetch(`${base}/api/metrics/inventory-insights?${params.toString()}`);
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `Insights failed (${response.status})`);
        }
        setInventoryInsightsData(payload || {});
        return;
      }

      const endpoint =
        metricsWorkspaceTab === 'service'
          ? '/api/metrics/service-insights'
          : metricsWorkspaceTab === 'customers'
          ? '/api/metrics/customer-insights'
          : metricsWorkspaceTab === 'reliability'
          ? '/api/metrics/reliability-insights'
          : metricsWorkspaceTab === 'team'
          ? '/api/metrics/team-insights'
          : '/api/metrics/overview';
      const response = await fetch(`${base}${endpoint}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Insights failed (${response.status})`);
      }
      const data = await response.json();
      setMetricsData(data || {});
    } catch (error: any) {
      if (metricsWorkspaceTab === 'sla') {
        setSlaInsightsData({});
      }
      if (metricsWorkspaceTab === 'inventory') {
        setInventoryInsightsData({});
      }
      setMetricsError(error?.message || 'Failed to load insights');
    } finally {
      if (metricsWorkspaceTab === 'sla') {
        setSlaInsightsLoading(false);
      }
      if (metricsWorkspaceTab === 'inventory') {
        setInventoryInsightsLoading(false);
      }
      setMetricsLoading(false);
    }
  }, [
    metricsStart,
    metricsEnd,
    metricsCustomerId,
    metricsDeviceId,
    metricsModelId,
    metricsTaskGroup,
    effectiveMetricsUserIds,
    canUseMetricsUserFilter,
    metricsStatus,
    metricsCaseId,
    metricsGroupBy,
    metricsCurrentWorkspaceIncludeIds,
    metricsWorkspaceIncludeBlocks,
    metricsWorkspaceSupportsCaseDrill,
    metricsWorkspaceTab
  ]);

  const loadMetricsDashboard = useCallback(async () => {
    try {
      const base = await getBaseUrl();
      const response = await fetch(`${base}/api/metrics/dashboard`);
      if (!response.ok) {
        throw new Error(`Dashboard load failed (${response.status})`);
      }
      const data = await response.json();
      const normalizedConfig = normalizeMetricsDashboardConfig(data?.config);
      setMetricsDashboard({
        id: data?.id,
        name: data?.name,
        config: normalizedConfig
      });
      setMetricsConfigDraft(normalizedConfig);
    } catch (error: any) {
      console.error('Failed to load metrics dashboard config:', error);
      setMetricsDashboard(prev =>
        prev || {
          name: 'Default',
          config: buildDefaultMetricsDashboardConfig()
        }
      );
      setMetricsConfigDraft(buildDefaultMetricsDashboardConfig());
    } finally {
      setMetricsDashboardReady(true);
    }
  }, []);

  const cancelMetricsDashboardEdit = useCallback(() => {
    setMetricsConfigDraft(metricsDashboardConfig);
    setShowMetricsConfig(false);
  }, [metricsDashboardConfig]);

  const setMetricsWorkspaceIncludesInDraft = useCallback(
    (workspace: MetricsWorkspaceTab, includes: string[]) => {
      setMetricsConfigDraft(prev => {
        const normalized = normalizeMetricsDashboardConfig(prev);
        return {
          ...normalized,
          workspaces: {
            ...normalized.workspaces,
            [workspace]: {
              includes
            }
          }
        };
      });
    },
    []
  );

  const saveMetricsDashboard = useCallback(async () => {
    try {
      const base = await getBaseUrl();
      const payload = {
        name: metricsDashboard?.name || 'Default',
        config: metricsDashboardDraftConfig
      };
      const response = await fetch(`${base}/api/metrics/dashboard`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`Dashboard save failed (${response.status})`);
      }
      const data = await response.json();
      const normalizedConfig = normalizeMetricsDashboardConfig(data?.config);
      setMetricsDashboard({
        id: data?.id,
        name: data?.name,
        config: normalizedConfig
      });
      setMetricsConfigDraft(normalizedConfig);
      setShowMetricsConfig(false);
    } catch (error: any) {
      console.error('Failed to save metrics dashboard config:', error);
      setMetricsError(error?.message || 'Failed to save dashboard');
    }
  }, [metricsDashboard, metricsDashboardDraftConfig]);

  useEffect(() => {
    if (activeTab !== 'metrics') return;
    setMetricsDashboardReady(false);
    void loadMetricsDashboard();
  }, [activeTab, loadMetricsDashboard]);

  useEffect(() => {
    if (activeTab !== 'metrics' || !metricsDashboardReady) return;
    void loadMetrics();
  }, [activeTab, loadMetrics, metricsDashboardReady]);

  useEffect(() => {
    if (activeTab !== 'metrics' || !metricsWorkspaceNeedsSourceData || metricsSourceDataLoaded) {
      return;
    }
    void loadData({ silent: true });
  }, [activeTab, loadData, metricsSourceDataLoaded, metricsWorkspaceNeedsSourceData]);

  const composeCustomerOptions = useMemo(() => {
    const map = new Map<string, Customer>();
    customers.forEach(customer => {
      if (customer?.id) map.set(customer.id, customer);
    });
    techMessages.forEach(message => {
      if (!message.customerId || map.has(message.customerId)) return;
      map.set(message.customerId, {
        id: message.customerId,
        companyName: message.customerName || 'Customer',
        company: message.customerName || 'Customer'
      } as Customer);
    });
    return Array.from(map.values()).sort((a, b) => {
      const aName = a.companyName || a.company || '';
      const bName = b.companyName || b.company || '';
      return aName.localeCompare(bName);
    });
  }, [customers, techMessages]);

  const composeCaseOptions = useMemo(() => {
    if (messageChannelType === 'internal') return cases;
    if (!messageComposeCustomerId) return cases;
    return cases.filter(entry => entry.customerId === messageComposeCustomerId);
  }, [cases, messageChannelType, messageComposeCustomerId]);

  const composeDeviceOptions = useMemo(() => {
    if (messageChannelType === 'internal') return devices;
    if (messageComposeFocus === 'device') return devices;
    if (!messageComposeCustomerId) return devices;
    return devices.filter(device => String(device.customerId || device.customer?.id || '') === messageComposeCustomerId);
  }, [devices, messageChannelType, messageComposeCustomerId, messageComposeFocus]);

  const composeInternalRecipientOptions = useMemo(() => {
    return internalMessageContacts
      .slice()
      .sort((a, b) => {
        const aName = a.fullName || a.username || '';
        const bName = b.fullName || b.username || '';
        return aName.localeCompare(bName);
      });
  }, [internalMessageContacts]);

  const selectedComposeCustomer = useMemo(() => {
    if (!messageComposeCustomerId) return null;
    return (
      customerDetailsById[messageComposeCustomerId] ||
      customers.find(customer => customer.id === messageComposeCustomerId) ||
      null
    );
  }, [customerDetailsById, customers, messageComposeCustomerId]);

  const composeExternalRecipientOptions = useMemo(() => {
    const customerContacts = selectedComposeCustomer?.contacts;
    const contacts = Array.isArray(customerContacts) ? customerContacts : [];
    const customerLocations = Array.isArray((selectedComposeCustomer as any)?.locations)
      ? (selectedComposeCustomer as any).locations
      : [];
    const locationParentById = new Map<string, string>();
    customerLocations.forEach((location: any) => {
      const locationId = String(location?.id || '').trim();
      if (!locationId) return;
      locationParentById.set(locationId, String(location?.parentLocationId || '').trim());
    });
    const selectedMessageDevice =
      messageComposeFocus === 'device'
        ? devices.find(device => device.id === messageComposeDeviceId)
        : null;
    const selectedMessageCase =
      messageComposeFocus === 'case'
        ? cases.find(entry => entry.id === messageComposeCaseId)
        : null;
    const selectedCaseDevice =
      selectedMessageCase?.device ||
      (selectedMessageCase?.deviceId ? deviceById.get(String(selectedMessageCase.deviceId)) : null);
    const targetLocationId =
      messageComposeFocus === 'device'
        ? String(
            (selectedMessageDevice as any)?.locationId ||
              (selectedMessageDevice as any)?.location?.id ||
              ''
          ).trim()
        : messageComposeFocus === 'case'
        ? String(
            (selectedMessageCase as any)?.locationId ||
              (selectedMessageCase as any)?.customerLocationId ||
              (selectedMessageCase as any)?.location?.id ||
              (selectedMessageCase as any)?.device?.locationId ||
              (selectedMessageCase as any)?.device?.location?.id ||
              (selectedCaseDevice as any)?.locationId ||
              (selectedCaseDevice as any)?.location?.id ||
              ''
          ).trim()
        : '';
    const isLocationScopedMessage = messageComposeFocus === 'device' || messageComposeFocus === 'case';
    const getContactDirectLocationIds = (contact: any): string[] => {
      const linkedIds: string[] = Array.isArray(contact?.locationLinks)
        ? contact.locationLinks
            .map((link: any) => String(link?.locationId || '').trim())
            .filter((value: string) => value.length > 0)
        : [];
      const explicitIds: string[] = Array.isArray(contact?.locationIds)
        ? contact.locationIds
            .map((value: unknown) => String(value || '').trim())
            .filter((value: string) => value.length > 0)
        : [];
      const rawIds = linkedIds.length
        ? linkedIds
        : (explicitIds.length ? explicitIds : [contact?.locationId]);
      return Array.from(
        new Set(
          rawIds
            .map((value: unknown) => String(value || '').trim())
            .filter((value: string) => value.length > 0)
        )
      );
    };
    const contactCoversLocation = (contact: any) => {
      if (!isLocationScopedMessage) return true;
      if (!targetLocationId) return false;
      const directLocationIds = getContactDirectLocationIds(contact);
      if (!directLocationIds.length) return false;
      const directLocationSet = new Set(directLocationIds);
      let currentLocationId = targetLocationId;
      const seenLocationIds = new Set<string>();
      while (currentLocationId && !seenLocationIds.has(currentLocationId)) {
        if (directLocationSet.has(currentLocationId)) return true;
        seenLocationIds.add(currentLocationId);
        currentLocationId = locationParentById.get(currentLocationId) || '';
      }
      return false;
    };
    return contacts
      .filter(contact => contact?.isActive !== false && contact?.portalUser?.id && contact.portalUser.isActive !== false)
      .map(contact => ({
        id: contact.portalUser?.id || '',
        contactId: contact.id,
        fullName: contact.fullName || contact.portalUser?.fullName || contact.portalUser?.username || 'Customer contact',
        username: contact.portalUser?.username || contact.email || '',
        roleLabel: contact.role || '',
        roleKey: contact.location?.name || '',
        email: contact.email || contact.portalUser?.email || '',
        isPrimary: contact.isPrimary === true,
        isContextMatched: contactCoversLocation(contact)
      }))
      .filter(contact => contact.id)
      .sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return (a.fullName || '').localeCompare(b.fullName || '');
      });
  }, [cases, deviceById, devices, messageComposeCaseId, messageComposeDeviceId, messageComposeFocus, selectedComposeCustomer]);

  useEffect(() => {
    if (activeTab !== 'messages') return;
    if (messageChannelType !== 'external') return;
    if (!messageComposeCustomerId) return;
    void loadCustomerDetail(messageComposeCustomerId).catch(error => {
      console.warn('Failed to load customer contacts for message compose:', error);
    });
  }, [activeTab, loadCustomerDetail, messageChannelType, messageComposeCustomerId]);

  useEffect(() => {
    const validRecipientIds = new Set(composeExternalRecipientOptions.map(contact => contact.id));
    setMessageComposeExternalRecipientUserIds(prev => {
      const next = prev.filter(id => validRecipientIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [composeExternalRecipientOptions]);

  const filteredTechMessages = useMemo(() => {
    const filtered = techMessages.filter(message => {
      const isUnread =
        messageChannelType === 'internal'
          ? message.senderUserId !== user?.id && !readTechMessageIds.includes(message.id)
          : message.senderRole === 'customer' && !readTechMessageIds.includes(message.id);
      if (showUnreadOnly && !isUnread) return false;
      if (messageChannelType === 'external' && !messageShowSent && message.senderRole !== 'customer') return false;
      if (messageFocusFilter !== 'all' && message.focusType !== messageFocusFilter) return false;
      if (messageCaseFilter && message.caseId !== messageCaseFilter) return false;
      if (messageDeviceFilter && message.deviceId !== messageDeviceFilter) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return messageSortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }, [
    techMessages,
    messageShowSent,
    messageChannelType,
    messageFocusFilter,
    messageCaseFilter,
    messageDeviceFilter,
    showUnreadOnly,
    readTechMessageIds,
    messageSortOrder,
    user?.id
  ]);

  const unreadTechNotificationCount = notificationPopupItems.filter(item => !item.readAt && !item.dismissedAt).length;
  const hasUnreadTechMessages =
    messageUnreadCounts.external > 0 || messageUnreadCounts.internal > 0 || unreadTechNotificationCount > 0;

  useEffect(() => {
    setMessageVisibleCount(10);
  }, [
    messageChannelType,
    messageView,
    messageFocusFilter,
    messageCaseFilter,
    messageDeviceFilter,
    messageSortOrder,
    showUnreadOnly,
    messageShowSent
  ]);

  const selectedTechMessage = useMemo(() => {
    if (!selectedTechMessageId) return null;
    return techMessages.find(message => message.id === selectedTechMessageId) || null;
  }, [techMessages, selectedTechMessageId]);
  const selectedNotification = useMemo(() => {
    if (!selectedNotificationId) return null;
    return techNotifications.find(notification => notification.id === selectedNotificationId) || null;
  }, [selectedNotificationId, techNotifications]);

  const getTechMessageFocusLabel = useCallback(
    (message: PortalMessage) => {
      if (message.focusType === 'device' && message.deviceId) {
        const device = devices.find(item => item.id === message.deviceId);
        return device ? `Device - ${getDeviceDisplayLabel(device)}` : 'Device';
      }
      if (message.focusType === 'case' && message.caseId) {
        const caseItem = cases.find(item => item.id === message.caseId);
        return caseItem ? `Case - ${getCaseDisplayNumber(caseItem)}` : 'Case';
      }
      return 'General';
    },
    [cases, devices]
  );

  const formatAttachmentSize = (size: number) => {
    if (!size) return '0 KB';
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getMessagePreview = (value: string) => {
    const trimmed = value.replace(/\s+/g, ' ').trim();
    if (!trimmed) return 'No message content.';
    if (trimmed.length <= 160) return trimmed;
    return `${trimmed.slice(0, 160)}...`;
  };

  const handleTechMessageAttachmentChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const attachments = Array.from(files).map((file, index) => ({
      id: `tech-att-${Date.now()}-${index}`,
      name: file.name,
      type: file.type || 'file',
      size: file.size,
      file
    }));
    setMessageComposeAttachments(prev => [...prev, ...attachments]);
  };

  const handleRemoveTechMessageAttachment = (attachmentId: string) => {
    setMessageComposeAttachments(prev => prev.filter(item => item.id !== attachmentId));
  };

  const handleTechReplyAttachmentChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const attachments = Array.from(files).map((file, index) => ({
      id: `tech-reply-att-${Date.now()}-${index}`,
      name: file.name,
      type: file.type || 'file',
      size: file.size,
      file
    }));
    setMessageReplyAttachments(prev => [...prev, ...attachments]);
  };

  const handleRemoveTechReplyAttachment = (attachmentId: string) => {
    setMessageReplyAttachments(prev => prev.filter(item => item.id !== attachmentId));
  };

  const resolveTechMessageCustomerId = useCallback(
    ({
      customerId,
      caseId,
      deviceId
    }: {
      customerId?: string | null;
      caseId?: string | null;
      deviceId?: string | null;
    }) => {
      if (customerId) return customerId;
      if (caseId) {
        const caseMatch = cases.find(item => item.id === caseId);
        if (caseMatch?.customerId) return caseMatch.customerId;
      }
      if (deviceId) {
        const deviceMatch = devices.find(item => item.id === deviceId);
        if (deviceMatch?.customerId) return deviceMatch.customerId;
      }
      return '';
    },
    [cases, devices]
  );

  const handleSendTechReply = async () => {
    if (!selectedTechMessage) return;
    if (!messageReplyBody.trim()) return;
    const selectedChannel = selectedTechMessage.channelType || 'external';
    let payload: {
      body: string;
      focusType: 'general' | 'device' | 'case';
      channelType: 'external' | 'internal';
      customerId?: string;
      recipientUserId?: string;
      deviceId?: string;
      caseId?: string;
      attachments?: PortalMessageAttachment[];
    } = {
      body: messageReplyBody.trim(),
      focusType: selectedTechMessage.focusType,
      channelType: selectedChannel
    };

    if (selectedChannel === 'internal') {
      const counterpartUserId =
        selectedTechMessage.senderUserId === user?.id
          ? selectedTechMessage.recipientUserId || null
          : selectedTechMessage.senderUserId || null;
      if (!counterpartUserId) {
        window.alert('Unable to identify the internal recipient for this reply.');
        return;
      }
      payload = {
        ...payload,
        recipientUserId: counterpartUserId,
        deviceId: selectedTechMessage.deviceId || undefined,
        caseId: selectedTechMessage.caseId || undefined
      };
    } else {
      const resolvedCustomerId = resolveTechMessageCustomerId({
        customerId: selectedTechMessage.customerId,
        caseId: selectedTechMessage.caseId,
        deviceId: selectedTechMessage.deviceId
      });
      if (!resolvedCustomerId) {
        window.alert('Unable to identify the customer for this reply. Refresh and try again.');
        return;
      }
      payload = {
        ...payload,
        customerId: resolvedCustomerId,
        recipientUserId: selectedTechMessage.recipientUserId || undefined,
        deviceId: selectedTechMessage.deviceId || undefined,
        caseId: selectedTechMessage.caseId || undefined
      };
    }

    setMessageActionBusy('reply');
    try {
      const created = (await messageApi.create({
        ...payload,
        attachments: messageReplyAttachments.length > 0 ? messageReplyAttachments : undefined
      })) as PortalMessage;
      setMessageReplyBody('');
      setMessageReplyAttachments([]);
      if (created?.id) {
        setSelectedTechMessageId(created.id);
      }
      await Promise.all([loadTechMessages(), loadMessageUnreadCounts()]);
    } catch (error) {
      console.error('Failed to send tech reply:', error);
      window.alert('Failed to send reply');
    } finally {
      setMessageActionBusy(null);
    }
  };

  const handleSendOutboundMessage = async () => {
    if (!messageComposeBody.trim()) return;
    if (messageComposeFocus === 'case' && !messageComposeCaseId) return;
    if (messageComposeFocus === 'device' && !messageComposeDeviceId) return;
    const isInternal = messageChannelType === 'internal';
    const resolvedCustomerId = resolveTechMessageCustomerId({
      customerId: messageComposeCustomerId,
      caseId: messageComposeFocus === 'case' ? messageComposeCaseId : null,
      deviceId: messageComposeFocus === 'device' ? messageComposeDeviceId : null
    });
    if (!isInternal && !resolvedCustomerId) {
      window.alert('Please select a customer, case, or device before sending.');
      return;
    }
    const internalRecipientIds = Array.from(new Set(messageComposeRecipientUserIds.filter(Boolean)));
    if (isInternal && internalRecipientIds.length === 0) {
      window.alert('Please select at least one recipient for the internal message.');
      return;
    }
    const externalRecipientIds = Array.from(new Set(messageComposeExternalRecipientUserIds.filter(Boolean)));
    setMessageActionBusy('compose');
    try {
      const sharedPayload = {
        body: messageComposeBody.trim(),
        focusType: messageComposeFocus,
        channelType: messageChannelType,
        caseId: messageComposeFocus === 'case' ? messageComposeCaseId : undefined,
        deviceId: messageComposeFocus === 'device' ? messageComposeDeviceId : undefined,
        attachments: messageComposeAttachments.length > 0 ? messageComposeAttachments : undefined
      };
      const outboundMessages = isInternal
        ? await Promise.all(
            internalRecipientIds.map(recipientUserId =>
              messageApi.create({
                ...sharedPayload,
                recipientUserId
              }) as Promise<PortalMessage>
            )
          )
        : [
            ...(await Promise.all(
              (externalRecipientIds.length > 0 ? externalRecipientIds : [undefined]).map(recipientUserId =>
                messageApi.create({
                  ...sharedPayload,
                  customerId: resolvedCustomerId,
                  recipientUserId
                }) as Promise<PortalMessage>
              )
            ))
          ];
      const outbound = outboundMessages[0] || null;
      setMessageComposeBody('');
      setMessageComposeAttachments([]);
      setMessageComposeRecipientUserIds([]);
      setMessageComposeExternalRecipientUserIds([]);
      setSelectedTechMessageId(outbound?.id || null);
      setShowTechComposePanel(false);
      await Promise.all([loadTechMessages(), loadMessageUnreadCounts()]);
    } catch (error) {
      console.error('Failed to send outbound message:', error);
      window.alert('Failed to send message');
    } finally {
      setMessageActionBusy(null);
    }
  };

  useEffect(() => {
    setMessageReplyBody('');
    setMessageReplyAttachments([]);
  }, [selectedTechMessageId]);

  const handleArchiveSelectedTechMessage = async (archived: boolean) => {
    if (!selectedTechMessage) return;
    try {
      await messageApi.archive(selectedTechMessage.id, archived);
      if (archived && messageView === 'active') {
        setSelectedTechMessageId(null);
      }
      await Promise.all([loadTechMessages(), loadMessageUnreadCounts()]);
    } catch (error) {
      console.error('Failed to update message archive state:', error);
      window.alert('Failed to update archive state');
    }
  };

  const handleDeleteSelectedTechMessage = async () => {
    if (!selectedTechMessage?.threadId) return;
    const nextArchived = messageView !== 'archived';
    try {
      await messageApi.archiveThread(selectedTechMessage.threadId, nextArchived);
      setSelectedTechMessageId(null);
      await Promise.all([loadTechMessages(), loadMessageUnreadCounts()]);
    } catch (error) {
      console.error('Failed to update message thread archive state:', error);
      window.alert('Failed to update message thread archive state');
    }
  };

  const handleRestoreSelectedTechMessage = async () => {
    if (!selectedTechMessage) return;
    try {
      await messageApi.restoreForMe(selectedTechMessage.id);
      if (messageView === 'archived') {
        setSelectedTechMessageId(null);
      }
      await Promise.all([loadTechMessages(), loadMessageUnreadCounts()]);
    } catch (error) {
      console.error('Failed to restore message:', error);
      window.alert('Failed to restore message');
    }
  };

  const handleDownloadTechMessageAttachment = async (messageId: string, attachment: PortalMessageAttachment) => {
    if (!messageId || !attachment?.id) return;
    try {
      await messageApi.downloadAttachment(messageId, attachment.id, attachment.name);
    } catch (error) {
      console.error('Failed to download message attachment:', error);
    }
  };

  const handleOpenTechNotificationAttachment = async (
    notification: AppNotification,
    attachment: AppNotificationAttachment
  ) => {
    if (!notification?.id || !attachment?.id) return;
    try {
      await notificationApi.openAttachment(notification.id, attachment.id, attachment.name);
    } catch (error) {
      console.error('Failed to open notification attachment:', error);
    }
  };

  const styles = useMemo(() => {
    const denseHeaderPadding = isTabletDashboard
      ? '0.85rem 1rem'
      : isNarrowDashboard
      ? '0.9rem 1.25rem'
      : techDashboardStyles.header.padding;
    const denseMainPadding = isTabletDashboard
      ? isCompactDashboard
        ? '0.85rem'
        : '1rem'
      : isNarrowDashboard
      ? '1.25rem'
      : techDashboardStyles.main.padding;

    return {
      ...techDashboardStyles,
      container: {
        ...techDashboardStyles.container,
        overflow: 'hidden'
      },
      sidebar: isTabletDashboard
        ? {
            ...techDashboardStyles.sidebar,
            width: isCompactDashboard ? '260px' : '280px',
            position: 'fixed' as const,
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 1000,
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(calc(-100% - 12px))',
            transition: 'transform 0.2s ease',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.24)'
          }
        : isNarrowDashboard
        ? {
            ...techDashboardStyles.sidebar,
            width: '214px'
          }
        : techDashboardStyles.sidebar,
      logo: isTabletDashboard
        ? {
            ...techDashboardStyles.logo,
            padding: '1.25rem 1.2rem',
            marginBottom: '1rem',
            fontSize: '1.35rem'
          }
        : isNarrowDashboard
        ? {
            ...techDashboardStyles.logo,
            padding: '1.25rem',
            marginBottom: '1.25rem',
            fontSize: '1.35rem'
          }
        : techDashboardStyles.logo,
      navItem: isTabletDashboard
        ? {
            ...techDashboardStyles.navItem,
            padding: '0.9rem 1.1rem',
            fontSize: '0.95rem'
          }
        : isNarrowDashboard
        ? {
            ...techDashboardStyles.navItem,
            padding: '0.9rem 1.1rem',
            fontSize: '0.94rem'
          }
        : techDashboardStyles.navItem,
      userSection: isTabletDashboard
        ? {
            ...techDashboardStyles.userSection,
            padding: '1.1rem'
          }
        : isNarrowDashboard
        ? {
            ...techDashboardStyles.userSection,
            padding: '1.25rem'
          }
        : techDashboardStyles.userSection,
      header: {
        ...techDashboardStyles.header,
        padding: denseHeaderPadding
      },
      pageTitle: {
        ...techDashboardStyles.pageTitle,
        fontSize: isTabletDashboard
          ? '1.35rem'
          : isNarrowDashboard
          ? '1.45rem'
          : techDashboardStyles.pageTitle.fontSize
      },
      pageSubtitle: {
        ...techDashboardStyles.pageSubtitle,
        fontSize: isTabletDashboard
          ? '0.85rem'
          : isNarrowDashboard
          ? '0.9rem'
          : techDashboardStyles.pageSubtitle.fontSize
      },
      casesHeaderPanel: {
        ...techDashboardStyles.casesHeaderPanel,
        gap: isTabletDashboard ? '0.7rem' : isNarrowDashboard ? '0.8rem' : techDashboardStyles.casesHeaderPanel.gap
      },
      casesHeaderTop: {
        ...techDashboardStyles.casesHeaderTop,
        alignItems: isTabletDashboard ? 'center' : techDashboardStyles.casesHeaderTop.alignItems,
        gap: isTabletDashboard ? '0.6rem' : techDashboardStyles.casesHeaderTop.gap
      },
      searchInput: {
        ...techDashboardStyles.searchInput,
        padding: isTabletDashboard
          ? '0.6rem 0.8rem'
          : isNarrowDashboard
          ? '0.65rem 0.85rem'
          : techDashboardStyles.searchInput.padding,
        minWidth: isTabletDashboard ? '0' : techDashboardStyles.searchInput.minWidth,
        flex: isTabletDashboard ? '1 1 100%' : techDashboardStyles.searchInput.flex,
        fontSize: isTabletDashboard ? '0.9rem' : techDashboardStyles.searchInput.fontSize
      },
      casesFilterPanels: {
        ...techDashboardStyles.casesFilterPanels,
        gridTemplateColumns: isCompactDashboard
          ? '1fr'
          : techDashboardStyles.casesFilterPanels.gridTemplateColumns,
        gap: isTabletDashboard ? '0.75rem' : techDashboardStyles.casesFilterPanels.gap
      },
      casesFilterPanel: {
        ...techDashboardStyles.casesFilterPanel,
        padding: isTabletDashboard
          ? '0.75rem'
          : isNarrowDashboard
          ? '0.8rem'
          : techDashboardStyles.casesFilterPanel.padding,
        gap: isTabletDashboard ? '0.6rem' : techDashboardStyles.casesFilterPanel.gap
      },
      casesFilterPanelHeader: {
        ...techDashboardStyles.casesFilterPanelHeader,
        gap: isTabletDashboard ? '0.5rem' : techDashboardStyles.casesFilterPanelHeader.gap
      },
      casesFilterPanelHint: {
        ...techDashboardStyles.casesFilterPanelHint,
        fontSize: isTabletDashboard
          ? '0.78rem'
          : isNarrowDashboard
          ? '0.8rem'
          : techDashboardStyles.casesFilterPanelHint.fontSize,
        lineHeight: isTabletDashboard ? 1.3 : techDashboardStyles.casesFilterPanelHint.lineHeight
      },
      caseFilterActionButton: {
        ...techDashboardStyles.caseFilterActionButton,
        padding: isTabletDashboard ? '0.4rem 0.75rem' : techDashboardStyles.caseFilterActionButton.padding,
        fontSize: isTabletDashboard ? '0.78rem' : techDashboardStyles.caseFilterActionButton.fontSize
      },
      casesFilterGrid: {
        ...techDashboardStyles.casesFilterGrid,
        gridTemplateColumns: isTabletDashboard
          ? 'repeat(auto-fit, minmax(140px, 1fr))'
          : techDashboardStyles.casesFilterGrid.gridTemplateColumns,
        gap: isTabletDashboard ? '0.6rem' : techDashboardStyles.casesFilterGrid.gap
      },
      casesSearchableFilterGrid: {
        ...techDashboardStyles.casesSearchableFilterGrid,
        gridTemplateColumns: isTabletDashboard
          ? 'repeat(auto-fit, minmax(150px, 1fr))'
          : techDashboardStyles.casesSearchableFilterGrid.gridTemplateColumns,
        gap: isTabletDashboard ? '0.6rem' : techDashboardStyles.casesSearchableFilterGrid.gap
      },
      filterSelect: {
        ...techDashboardStyles.filterSelect,
        padding: isTabletDashboard ? '0.6rem 0.75rem' : techDashboardStyles.filterSelect.padding,
        minWidth: isTabletDashboard ? '0' : techDashboardStyles.filterSelect.minWidth,
        fontSize: isTabletDashboard ? '0.88rem' : techDashboardStyles.filterSelect.fontSize
      },
      searchableFilterLabel: {
        ...techDashboardStyles.searchableFilterLabel,
        fontSize: isTabletDashboard ? '0.76rem' : techDashboardStyles.searchableFilterLabel.fontSize
      },
      searchableFilterTrigger: {
        ...techDashboardStyles.searchableFilterTrigger,
        padding: isTabletDashboard ? '0.6rem 0.75rem' : techDashboardStyles.searchableFilterTrigger.padding,
        minHeight: isTabletDashboard ? '40px' : techDashboardStyles.searchableFilterTrigger.minHeight
      },
      searchableFilterTriggerValue: {
        ...techDashboardStyles.searchableFilterTriggerValue,
        fontSize: isTabletDashboard ? '0.88rem' : techDashboardStyles.searchableFilterTriggerValue.fontSize
      },
      newCaseButton: {
        ...techDashboardStyles.newCaseButton,
        padding: isTabletDashboard
          ? '0.55rem 0.9rem'
          : isNarrowDashboard
          ? '0.6rem 1rem'
          : techDashboardStyles.newCaseButton.padding,
        fontSize: isTabletDashboard
          ? '0.88rem'
          : isNarrowDashboard
          ? '0.9rem'
          : techDashboardStyles.newCaseButton.fontSize,
        boxShadow: isTabletDashboard
          ? '0 4px 12px rgba(37, 99, 235, 0.16)'
          : techDashboardStyles.newCaseButton.boxShadow
      },
      main: {
        ...techDashboardStyles.main,
        padding: denseMainPadding
      },
      statsGrid: {
        ...techDashboardStyles.statsGrid,
        gridTemplateColumns: isTabletDashboard
          ? 'repeat(5, minmax(120px, 1fr))'
          : isNarrowDashboard
          ? 'repeat(5, minmax(140px, 1fr))'
          : techDashboardStyles.statsGrid.gridTemplateColumns,
        gap: isTabletDashboard ? '0.75rem' : techDashboardStyles.statsGrid.gap,
        minWidth: isTabletDashboard
          ? '0'
          : isNarrowDashboard
          ? '760px'
          : techDashboardStyles.statsGrid.minWidth
      },
      statsGridWrap: {
        ...techDashboardStyles.statsGridWrap,
        overflowX: isTabletDashboard ? 'visible' : techDashboardStyles.statsGridWrap.overflowX,
        marginBottom: isTabletDashboard ? '0.75rem' : techDashboardStyles.statsGridWrap.marginBottom
      },
      statsAuxRow: {
        ...techDashboardStyles.statsAuxRow,
        marginBottom: isTabletDashboard ? '1rem' : techDashboardStyles.statsAuxRow.marginBottom
      },
      statCard: {
        ...techDashboardStyles.statCard,
        padding: isTabletDashboard
          ? '1rem'
          : isNarrowDashboard
          ? '1.15rem'
          : techDashboardStyles.statCard.padding
      },
      statNumber: {
        ...techDashboardStyles.statNumber,
        fontSize: isTabletDashboard
          ? '1.6rem'
          : isNarrowDashboard
          ? '1.75rem'
          : techDashboardStyles.statNumber.fontSize,
        marginBottom: isTabletDashboard ? '0.25rem' : techDashboardStyles.statNumber.marginBottom
      },
      statLabel: {
        ...techDashboardStyles.statLabel,
        fontSize: isTabletDashboard ? '0.8rem' : techDashboardStyles.statLabel.fontSize
      },
      caseListHeaderRow: {
        ...techDashboardStyles.caseListHeaderRow,
        padding: isTabletDashboard ? '0.85rem 1rem' : techDashboardStyles.caseListHeaderRow.padding
      },
      caseListHeader: {
        ...techDashboardStyles.caseListHeader,
        fontSize: isTabletDashboard ? '1rem' : techDashboardStyles.caseListHeader.fontSize
      }
    };
  }, [
    isCompactDashboard,
    isNarrowDashboard,
    isTabletDashboard,
    sidebarOpen
  ]);
  const dashboardSidebarBackdropStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.35)',
      border: 'none',
      padding: 0,
      margin: 0,
      zIndex: 999
    }),
    []
  );
  const dashboardSidebarHeaderStyle = useMemo<React.CSSProperties>(
    () => ({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 1rem 0'
    }),
    []
  );
  const dashboardMenuRowStyle = useMemo<React.CSSProperties>(
    () => ({
      padding: '0.75rem 1rem 0',
      backgroundColor: '#F2EEE7'
    }),
    []
  );
  const dashboardMenuButtonStyle = useMemo<React.CSSProperties>(
    () => ({
      border: '1px solid #DDD4C8',
      backgroundColor: '#FFFDF9',
      color: '#123F43',
      borderRadius: '0.75rem',
      padding: '0.55rem 0.85rem',
      fontWeight: 700,
      fontSize: '0.9rem',
      lineHeight: 1.2,
      boxShadow: '0 1px 3px rgba(18, 63, 67, 0.08)',
      cursor: 'pointer'
    }),
    []
  );
  const formatInsightsDate = useCallback((value?: string | Date | null) => {
    const parsed = toDate(value);
    if (!parsed) return '-';
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);
  const formatInsightsDateTime = useCallback((value?: string | Date | null) => {
    const parsed = toDate(value);
    if (!parsed) return '-';
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);
  const getInsightsSignalStyle = useCallback(
    (tone: 'danger' | 'warning' | 'info') =>
      tone === 'danger'
        ? { ...styles.insightsSignalCard, ...styles.insightsSignalDanger }
        : tone === 'warning'
        ? { ...styles.insightsSignalCard, ...styles.insightsSignalWarning }
        : { ...styles.insightsSignalCard, ...styles.insightsSignalInfo },
    [styles]
  );

  if (!hasLoadedOnce && loading) {
    return (
      <div style={styles.container}>
        <div style={styles.sidebar}>
          <div style={styles.logo}>
            <img src={BRAND_WORDMARK_SRC} alt="bonzaiQ" style={styles.logoImage} />
          </div>
        </div>
        <div style={styles.content}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {isTabletDashboard && sidebarOpen && (
        <button
          type="button"
          style={dashboardSidebarBackdropStyle}
          onClick={() => setSidebarOpen(false)}
          aria-label="Close dashboard menu"
        />
      )}
      {/* Sidebar */}
      <div style={styles.sidebar}>
        {isTabletDashboard && (
          <div style={dashboardSidebarHeaderStyle}>
            <div
              style={{
                color: '#F2EEE7',
                fontSize: '0.82rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              Menu
            </div>
            <button
              type="button"
              style={dashboardMenuButtonStyle}
              onClick={() => setSidebarOpen(false)}
            >
              Close
            </button>
          </div>
        )}
        <div style={styles.logo}>
          <img src={BRAND_WORDMARK_SRC} alt="bonzaiQ" style={styles.logoImage} />
        </div>
        <ul style={styles.nav}>
          {canViewCasesTab && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'cases' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('cases')}
            >
              <span>Cases</span>
              {unverifiedModelCount > 0 && (
                <span style={styles.navNotification}>{unverifiedModelCount}</span>
              )}
            </li>
          )}
          {canViewMessagesTab && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'messages' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('messages')}
            >
              <span>Messages</span>
              {hasUnreadTechMessages && (
                <span
                  style={styles.navBadgeStack}
                  title={`External: ${messageUnreadCounts.external}, Internal: ${messageUnreadCounts.internal}, Notifications: ${unreadTechNotificationCount}`}
                >
                  <span style={styles.navBadgeExternal}>{messageUnreadCounts.external}</span>
                  <span style={styles.navBadgeInternal}>{messageUnreadCounts.internal}</span>
                  {unreadTechNotificationCount > 0 && (
                    <span style={styles.navBadge}>{unreadTechNotificationCount}</span>
                  )}
                </span>
              )}
            </li>
          )}
          {canAccessOrgNotifications && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'orgNotifications' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('orgNotifications')}
            >
              Notifications
            </li>
          )}
          {canAccessKnowledge && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'knowledge' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('knowledge')}
            >
              Device Expert
            </li>
          )}
          {canViewCustomersTab && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'customers' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('customers')}
            >
              Customers
            </li>
          )}
          {canViewModelsTab && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'models' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('models')}
            >
              Devices
            </li>
          )}
          {canAccessSla && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'sla' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('sla')}
            >
              Contracts
            </li>
          )}
          {allowedTabs.includes('scheduling') && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'scheduling' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('scheduling')}
            >
              Scheduling
            </li>
          )}
          {canAccessDocuments && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'documents' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('documents')}
            >
              Documents
            </li>
          )}
          {canAccessInventory && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'inventory' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('inventory')}
            >
              Inventory
            </li>
          )}
          {canAccessMetrics && (
            <li
            style={{
              ...styles.navItem,
              ...(activeTab === 'metrics' ? styles.navItemActive : {})
            }}
            onClick={() => handleDashboardTabSelect('metrics')}
          >
            Insights
          </li>
          )}
          {canAccessFeedbackReview && (
            <li
            style={{
              ...styles.navItem,
              ...(activeTab === 'feedback' ? styles.navItemActive : {})
            }}
            onClick={() => handleDashboardTabSelect('feedback')}
          >
            Feedback
          </li>
          )}
          {canAccessLibraryMaintenance && (
            <li
            style={{
              ...styles.navItem,
              ...(activeTab === 'quality' ? styles.navItemActive : {})
            }}
            onClick={() => handleDashboardTabSelect('quality')}
          >
            Knowledge Graph
          </li>
          )}
          {canManageUsers && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'users' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('users')}
            >
              <span>Users</span>
              {pendingLeaveApprovalCount > 0 ? (
                <span style={styles.navBadge} title={`${pendingLeaveApprovalCount} leave approvals waiting`}>
                  {pendingLeaveApprovalCount}
                </span>
              ) : null}
            </li>
          )}
          {allowedTabs.includes('settings') && (
            <li
              style={{
                ...styles.navItem,
                ...(activeTab === 'settings' ? styles.navItemActive : {})
              }}
              onClick={() => handleDashboardTabSelect('settings')}
            >
              Account
            </li>
          )}
        </ul>
        <SupportTicketLauncher surface="TECH_DASHBOARD" pageLabel={`Dashboard · ${activeTab}`} />
        <div style={styles.userSection}>
          <div>{user?.fullName || user?.username || 'User'}</div>
          <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>{user?.email || user?.organizationName || ''}</div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {isTabletDashboard && (
          <div style={dashboardMenuRowStyle}>
            <button
              type="button"
              style={dashboardMenuButtonStyle}
              onClick={() => setSidebarOpen(true)}
            >
              Menu
            </button>
          </div>
        )}
        {activeTab === 'cases' && (
          <>
            <TechDashboardCasesHeader
              styles={styles}
              canOpenInternalJobs={canOpenInternalJobs}
              onCreateCase={() => {
                resetInternalJobForm();
                setShowInternalJobModal(true);
              }}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              priorityFilter={priorityFilter}
              onPriorityFilterChange={setPriorityFilter}
              assignmentFilter={assignmentFilter}
              onAssignmentFilterChange={setAssignmentFilter}
              caseUserFilter={caseUserFilter}
              onCaseUserFilterChange={setCaseUserFilter}
              caseUserOptions={caseUserOptions}
              canUseCaseUserFilter={canUseCaseUserFilter}
              taskGroupFilter={taskGroupFilter}
              onTaskGroupFilterChange={(value) =>
                setTaskGroupFilter(value as 'all' | 'UNASSIGNED' | TaskGroupCode)
              }
              taskGroups={TASK_GROUPS}
              customerFilter={customerFilter}
              onCustomerFilterChange={handleCustomerFilterChange}
              caseCustomerOptions={caseCustomerOptions}
              deviceModelFilter={deviceModelFilter}
              onDeviceModelFilterChange={handleDeviceModelFilterChange}
              caseDeviceModelOptions={caseDeviceModelOptions}
              deviceSerialFilter={deviceFilter}
              onDeviceSerialFilterChange={handleDeviceFilterChange}
              caseDeviceSerialOptions={caseDeviceSerialOptions}
              caseSortOrder={caseSortOrder}
              onCaseSortOrderChange={setCaseSortOrder}
              hasActiveFilters={hasActiveCaseFilters}
              activeFilterCount={activeCaseFilterCount}
              onClearFilters={clearCaseFilters}
              topPanelCollapsed={casesTopPanelCollapsed}
              onToggleTopPanel={() => setCasesTopPanelCollapsed(current => !current)}
            />

            <div style={styles.main}>
              {!casesTopPanelCollapsed && (
                <TechDashboardCasesStats
                  styles={styles}
                  caseStatusCards={caseStatusCards}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  caseStats={caseStats}
                />
              )}

              {/* Case List */}
              <TechDashboardCasesList
                styles={styles}
                baseUrl={baseUrl}
                paginatedCases={paginatedCases}
                filteredCaseCount={filteredCases.length}
                casesPage={casesPage}
                casesPageCount={casesPageCount}
                pageStart={casesPageStart}
                pageEnd={casesPageEnd}
                onCasesPageChange={setCasesPage}
                casesPageSize={casesPageSize}
                onCasesPageSizeChange={(value: number) => {
                  setCasesPageSize(value);
                  setCasesPage(1);
                }}
                selectedCase={selectedCase}
                assignmentPickerCaseId={assignmentPickerCaseId}
                assigningCaseId={assigningCaseId}
                assignmentError={assignmentError}
                caseCardRefs={caseCardRefs}
                canAssignCases={canAssignCases}
                canUpdateCaseStatus={canUpdateCaseStatus}
                canCloseCases={canCloseCases}
                canUpdateCasePriority={canUpdateCasePriority}
                canEditCaseProblem={canEditCaseProblem}
                caseAssigneesLoading={caseAssigneesLoading}
                caseAssigneesError={caseAssigneesError}
                caseAssigneeOptions={caseAssigneeOptions}
                selectedCaseQuotes={selectedCaseQuotes}
                selectedCaseQuotesLoading={selectedCaseQuotesLoading}
                caseServiceReportPreviewLoadingId={caseServiceReportPreviewLoadingId}
                openCaseServiceReportPreview={openCaseServiceReportPreview}
                editingCaseId={editingCaseId}
                savingProblemId={savingProblemId}
                editProblemSummary={editProblemSummary}
                editProblemDetails={editProblemDetails}
                problemEditError={problemEditError}
                caseWorkflowSavingById={caseWorkflowSavingById}
                caseWorkflowClosingById={caseWorkflowClosingById}
                caseWorkflowResolvingById={caseWorkflowResolvingById}
                caseWorkflowErrorById={caseWorkflowErrorById}
                TASK_GROUPS={TASK_GROUPS}
                TASK_GROUP_META={TASK_GROUP_META}
                INSTALL_PHASES={INSTALL_PHASES}
                MAINTENANCE_VARIANTS={MAINTENANCE_VARIANTS}
                MAINTENANCE_COLOR={MAINTENANCE_COLOR}
                MAINTENANCE_VARIANT_ROUTES={MAINTENANCE_VARIANT_ROUTES}
                getTsfadStatusMeta={getTsfadStatusMeta}
                getActiveCaseTask={getActiveCaseTask}
                resolveTaskGroupCode={resolveTaskGroupCode}
                formatDateTime={formatDateTime}
                formatTaskStatus={formatTaskStatus}
                parseProblemDetails={parseProblemDetails}
                buildCaseSummarySnippet={buildCaseSummarySnippet}
                getCaseDisplayNumber={getCaseDisplayNumber}
                needsModelVerification={needsModelVerification}
                getPendingDeviceInfo={getPendingDeviceInfo}
                canVerifySubmittedDevices={canVerifySubmittedDevices}
                verifySubmittedDevice={verifySubmittedDevice}
                devices={devices}
                getStatusStyle={getStatusStyle}
                formatCaseStatusLabel={formatCaseStatusLabel}
                getPriorityStyle={getPriorityStyle}
                formatPriorityLabel={formatPriorityLabel}
                getCaseAttachmentCount={getCaseAttachmentCount}
                closeCaseQuickView={closeCaseQuickView}
                getCaseWorkflowDraft={getCaseWorkflowDraft}
                isCaseWorkflowDirty={isCaseWorkflowDirty}
                stageCaseWorkflowDraft={stageCaseWorkflowDraft}
                saveCaseWorkflow={saveCaseWorkflow}
                closeCaseWorkflow={closeCaseWorkflow}
                resolveCaseWorkflow={resolveCaseWorkflow}
                resetCaseWorkflowDraft={resetCaseWorkflowDraft}
                setAssignmentPickerCaseId={setAssignmentPickerCaseId}
                setAssignmentError={setAssignmentError}
                setSelectedCase={setSelectedCase}
                applyCaseQuickViewRefresh={applyCaseQuickViewRefresh}
                loadCaseDetails={loadCaseDetails}
                handleAssignTaskGroup={handleAssignTaskGroup}
                handleOpenInstallPhase={handleOpenInstallPhase}
                handleOpenDecommission={handleOpenDecommission}
                guardTaskWorkspaceOpen={guardTaskWorkspaceOpen}
                navigate={navigate}
                startProblemEdit={startProblemEdit}
                cancelProblemEdit={cancelProblemEdit}
                updateProblemDetail={updateProblemDetail}
                addProblemDetailLine={addProblemDetailLine}
                removeProblemDetailLine={removeProblemDetailLine}
                setEditProblemSummary={setEditProblemSummary}
                saveProblemDetails={saveProblemDetails}
                setCaseQuotePreview={setCaseQuotePreview}
                setCaseQuotePreviewContext={setCaseQuotePreviewContext}
                getQuoteDecisionBadge={getQuoteDecisionBadge}
                getQuotePartsBadge={getQuotePartsBadge}
              />
            </div>
          </>
        )}

        {activeTab === 'metrics' && (
          <>
            <div style={styles.header}>
              <div style={styles.headerTitleGroup}>
                <h1 style={styles.pageTitle}>Insights</h1>
                <div style={styles.pageSubtitle}>
                  {isWarehouseInsightsOnlyUser
                    ? 'Inventory insight dashboards across accessible warehouse stock, movement, counts, and repair-part pressure.'
                    : canViewFullInsights
                    ? 'Operational insight dashboards across service delivery, reliability, customers, SLAs, inventory, and team performance.'
                    : 'Self-scoped operational insights across your service delivery, reliability, customers, and SLA work.'}
                </div>
              </div>
              <div style={styles.insightsHeaderActions}>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={loadMetrics}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    ...(canConfigureMetricsWorkspace ? {} : { opacity: 0.55, cursor: 'not-allowed' })
                  }}
                  onClick={() => {
                    if (!canConfigureMetricsWorkspace) return;
                    setShowMetricsConfig(true);
                  }}
                  disabled={!canConfigureMetricsWorkspace}
                >
                  Include
                </button>
              </div>
            </div>

            <div style={styles.main}>
              <div style={styles.insightsWorkspaceTabs}>
                {metricsWorkspaceTabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    style={{
                      ...styles.insightsWorkspaceTab,
                      ...(metricsWorkspaceTab === tab.id ? styles.insightsWorkspaceTabActive : {})
                    }}
                    onClick={() => setMetricsWorkspaceTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div style={styles.insightsFilterBar}>
                <div style={styles.insightsFilterGrid}>
                  <select
                    style={styles.filterSelect}
                    value={metricsDatePreset}
                    onChange={(e) => handleMetricsDatePresetChange(e.target.value as MetricsDatePreset)}
                  >
                    {METRICS_DATE_PRESETS.map(preset => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    style={styles.insightsFilterInput}
                    value={metricsStart}
                    onChange={(e) => handleMetricsStartChange(e.target.value)}
                  />
                  <input
                    type="date"
                    style={styles.insightsFilterInput}
                    min={metricsStart || undefined}
                    value={metricsEnd}
                    onChange={(e) => handleMetricsEndChange(e.target.value)}
                  />
                  {canUseMetricsUserFilter && (
                    <div style={{ position: 'relative', minWidth: 0 }}>
                      <button
                        type="button"
                        style={{
                          ...styles.filterSelect,
                          width: '100%',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => setMetricsUserFilterOpen(open => !open)}
                        aria-expanded={metricsUserFilterOpen}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {metricsUserFilterLabel}
                        </span>
                        <span style={{ color: '#5f6f72', fontSize: '0.85rem' }}>select</span>
                      </button>
                      {metricsUserFilterOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            zIndex: 25,
                            top: 'calc(100% + 0.45rem)',
                            left: 0,
                            right: 0,
                            minWidth: '260px',
                            maxHeight: '340px',
                            overflowY: 'auto',
                            backgroundColor: '#fffdf9',
                            border: '1px solid #d8d2c6',
                            borderRadius: '0.9rem',
                            boxShadow: '0 18px 36px rgba(18, 63, 67, 0.18)',
                            padding: '0.55rem'
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              width: '100%',
                              border: '1px solid #d8d2c6',
                              borderRadius: '0.7rem',
                              backgroundColor: effectiveMetricsUserIds.length === 0 ? '#0b3f42' : '#ffffff',
                              color: effectiveMetricsUserIds.length === 0 ? '#ffffff' : '#0b3f42',
                              fontWeight: 700,
                              padding: '0.55rem 0.7rem',
                              textAlign: 'left',
                              cursor: 'pointer'
                            }}
                            onClick={clearMetricsUserFilter}
                          >
                            All users
                          </button>
                          <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.5rem' }}>
                            {metricsUserOptionsLoading ? (
                              <div style={{ color: '#5f6f72', padding: '0.55rem 0.7rem' }}>
                                Loading users...
                              </div>
                            ) : metricsUserOptions.length === 0 ? (
                              <div style={{ color: '#5f6f72', padding: '0.55rem 0.7rem' }}>
                                No selectable users are available.
                              </div>
                            ) : (
                              metricsUserOptions.map(option => (
                                <label
                                  key={option.id}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '18px 1fr',
                                    alignItems: 'center',
                                    gap: '0.55rem',
                                    border: option.isActive ? '1px solid #ebe4d8' : '1px solid #e7b9b4',
                                    borderRadius: '0.7rem',
                                    backgroundColor: option.isActive
                                      ? selectedMetricsUserIdSet.has(option.id)
                                        ? '#eef7f3'
                                        : '#ffffff'
                                      : '#fff4f2',
                                    padding: '0.55rem 0.7rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedMetricsUserIdSet.has(option.id)}
                                    onChange={() => toggleMetricsUserFilter(option.id)}
                                  />
                                  <span style={{ display: 'grid', gap: '0.1rem', minWidth: 0 }}>
                                    <span
                                      style={{
                                        color: option.isActive ? '#0b3f42' : '#9f2f2a',
                                        fontWeight: 700,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {option.label}
                                    </span>
                                    <span
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        minWidth: 0
                                      }}
                                    >
                                      {option.detail && (
                                        <span
                                          style={{
                                            color: '#5f6f72',
                                            fontSize: '0.78rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {option.detail}
                                        </span>
                                      )}
                                      {!option.isActive && (
                                        <span
                                          style={{
                                            color: '#a63b32',
                                            backgroundColor: '#fde7e2',
                                            border: '1px solid #e7b9b4',
                                            borderRadius: '999px',
                                            fontSize: '0.68rem',
                                            fontWeight: 800,
                                            letterSpacing: '0.02em',
                                            padding: '0.08rem 0.4rem',
                                            textTransform: 'uppercase'
                                          }}
                                        >
                                          Inactive
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                </label>
                              ))
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.55rem' }}>
                            <button
                              type="button"
                              style={{ ...styles.button, ...styles.secondaryButton }}
                              onClick={() => setMetricsUserFilterOpen(false)}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <select
                    style={styles.filterSelect}
                    value={metricsCustomerId}
                    onChange={(e) => handleMetricsCustomerChange(e.target.value)}
                  >
                    <option value="">All customers</option>
                    {metricsCustomerOptions.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.companyName ?? (customer as any).company ?? customer.email ?? 'Customer'}
                      </option>
                    ))}
                  </select>
                  <select
                    style={styles.filterSelect}
                    value={metricsModelId}
                    onChange={(e) => handleMetricsModelChange(e.target.value)}
                  >
                    <option value="">All models</option>
                    {metricsModelOptions.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <select
                    style={styles.filterSelect}
                    value={metricsDeviceId}
                    onChange={(e) => handleMetricsDeviceChange(e.target.value)}
                  >
                    <option value="">All devices</option>
                    {metricsDeviceOptions.map(device => (
                      <option key={device.id} value={device.id}>
                        {device.label}
                      </option>
                    ))}
                  </select>
                  <select
                    style={styles.filterSelect}
                    value={metricsTaskGroup}
                    onChange={(e) => handleMetricsTaskGroupChange(e.target.value)}
                  >
                    <option value="">All task groups</option>
                    {TASK_GROUPS.map(group => (
                      <option key={group.code} value={group.code}>
                        {group.label}
                      </option>
                    ))}
                  </select>
                  {metricsWorkspaceSupportsCaseDrill && (
                    <select
                      style={styles.filterSelect}
                      value={metricsStatus}
                      onChange={(e) => handleMetricsStatusChange(e.target.value)}
                    >
                      <option value="">All statuses</option>
                      {metricsStatusOptions.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {metricsWorkspaceSupportsCaseDrill && (
                    <select
                      style={styles.filterSelect}
                      value={metricsCaseId}
                      onChange={(e) => setMetricsCaseId(e.target.value)}
                    >
                      <option value="">All cases</option>
                      {metricsCaseOptions.map(caseOption => (
                        <option key={caseOption.id} value={caseOption.id}>
                          {caseOption.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    style={styles.filterSelect}
                    value={metricsGroupBy}
                    onChange={(e) => setMetricsGroupBy(e.target.value as 'model' | 'device')}
                  >
                    <option value="model">View by model</option>
                    <option value="device">View by serial</option>
                  </select>
                </div>
              </div>

              {metricsError && (
                <div
                  style={{
                    marginBottom: '1rem',
                    border: '1px solid #E7B9B4',
                    backgroundColor: '#FDEDEC',
                    color: '#A63B32',
                    borderRadius: '0.85rem',
                    padding: '0.85rem 1rem',
                    fontWeight: 600
                  }}
                >
                  {metricsError}
                </div>
              )}
              {metricsLoading ? (
                <div style={styles.metricsPanel}>Loading insights...</div>
              ) : (
                <TechDashboardInsightsTab
                  styles={styles}
                  metricsWorkspaceTab={metricsWorkspaceTab}
                  isMetricsWidgetEnabled={isMetricsWidgetEnabled}
                  isMetricsWorkspaceBlockEnabled={isMetricsWorkspaceBlockEnabled}
                  insightSignals={overviewInsightSignals}
                  getInsightsSignalStyle={getInsightsSignalStyle}
                  metricsWorkspaceKpis={metricsWorkspaceKpis}
                  taskGroupRows={overviewTaskGroupRows}
                  ageBucketRows={overviewAgeBucketRows}
                  completedTaskGroupRows={overviewCompletedTaskGroupRows}
                  metricsGroupBy={metricsGroupBy}
                  metricsData={metricsData}
                  customerLoadRows={metricsWorkspaceTab === 'customers' ? customerWorkspaceLoadRows : overviewCustomerLoadRows}
                  serviceSummary={metricsWorkspaceTab === 'service' ? metricsServiceSummary ?? null : null}
                  statusDistributionRows={metricsWorkspaceTab === 'customers' ? customerStatusDistributionRows : serviceStatusDistributionRows}
                  scheduledTaskRows={scheduledTaskRows}
                  pendingScheduleRows={pendingScheduleRows}
                  unassignedTaskRows={unassignedTaskRows}
                  scheduledThisWeekRows={serviceUpcomingWorkRows}
                  upcomingWorkRows={overviewUpcomingWorkRows}
                  serviceInsightQuotesLoading={
                    metricsWorkspaceTab === 'service' || metricsWorkspaceTab === 'customers'
                      ? false
                      : serviceInsightQuotesLoading
                  }
                  serviceQuoteStatusRows={metricsWorkspaceTab === 'customers' ? customerQuotePipelineRows : serviceQuotePipelineRows}
                  partsWaitingRows={servicePartWaitingRows}
                  upcomingSlaRows={serviceUpcomingCoveredRows}
                  serviceCaseDrilldownRows={serviceCaseDrilldownRows}
                  reliabilityCaseDrilldownRows={reliabilityCaseDrilldownRows}
                  customerCaseDrilldownRows={customerCaseDrilldownRows}
                  slaCaseDrilldownRows={slaCaseDrilldownRows}
                  onOpenInsightsCase={openInsightsCase}
                  slaInsightsLoading={slaInsightsLoading}
                  slaCoverageRows={slaCoverageRows}
                  expiringSlaRows={expiringSla90Rows}
                  slaLifecycleSummary={slaLifecycleSummaryData}
                  slaCoveredCustomerWorkRows={slaCoveredCustomerWorkRowsData}
                  scheduledSlaRows={scheduledSlaRowsData}
                  slaFirstTimeFixSummary={slaFirstTimeFixSummaryData}
                  slaMtbfRows={slaMtbfRowsData}
                  slaMaintenanceComplianceSummary={slaMaintenanceComplianceSummaryData}
                  slaMaintenanceDeviationRows={slaMaintenanceDeviationRowsData}
                  breakdownAfterMaintenanceRows={breakdownAfterMaintenanceRowsData}
                  inventoryInsightsLoading={inventoryInsightsLoading}
                  inventoryWarehouseValueRows={inventoryWarehouseValueRows}
                  inventoryTransitRows={inventoryTransitRows}
                  inventoryVarianceCategoryRows={inventoryVarianceCategoryRows}
                  inventoryCountSessionRows={inventoryCountSessionRows}
                  inventoryVanStockRows={inventoryVanStockRows}
                  inventoryDelayedPartRows={inventoryDelayedPartRows}
                  inventoryCaseDrilldownRows={inventoryCaseDrilldownRows}
                  reliabilityInsightsLoading={metricsWorkspaceTab === 'reliability' ? metricsLoading : false}
                  deviceRepairMtbfRows={reliabilityDeviceRepairMtbfRows}
                  userRepairMtbfRows={
                    metricsWorkspaceTab === 'team' ? teamUserRepairMtbfRows : reliabilityUserRepairMtbfRows
                  }
                  repairDurationRows={reliabilityRepairDurationRows}
                  reliabilityHoldReasonRows={reliabilityHoldReasonRowsData}
                  repairWarrantySummary={reliabilityRepairWarrantySummary}
                  maintenanceComplianceSummary={reliabilityMaintenanceComplianceSummary}
                  commonFailureModeRows={commonFailureModeRows}
                  commonPartUsageRows={commonPartUsageRows}
                  repairFrequencyRows={reliabilityRepairFrequencyRows}
                  formatInsightsDate={formatInsightsDate}
                  formatInsightsDateTime={formatInsightsDateTime}
                  repeatFailureRows={repeatFailureRows}
                  customerSurveyRows={customerWorkspaceSurveyRows}
                  customerScheduledRows={customerWorkspaceScheduledRows}
                  customerCompletedWorkRows={customerWorkspaceCompletedRows}
                  customerStageTimingRows={customerWorkspaceStageTimingRows}
                  userTaskHoursRows={teamUserTaskHoursRows}
                  scheduledUserRows={teamScheduledUserRows}
                  loadByAssigneeRows={teamLoadByAssigneeRows}
                  scheduleDayRows={scheduleDayRows}
                  surveyByTechnicianRows={teamSurveyByTechnicianRows}
                  userFirstTimeFixRows={teamUserFirstTimeFixRows}
                  teamRepairPerformanceRows={teamRepairPerformanceRowsData}
                  teamWorkloadDetailRows={teamWorkloadDetailRowsData}
                  teamCaseDrilldownRows={teamCaseDrilldownRows}
                />
              )}
            </div>
          </>
        )}

        {activeTab === 'messages' && (
          <TechDashboardMessagesTab
            styles={styles}
            messageWorkspaceMode={messageWorkspaceMode}
            setMessageWorkspaceMode={setMessageWorkspaceMode}
            messagesLoading={messagesLoading}
            messagesQuerySettled={messagesQuerySettled}
            showTechComposePanel={showTechComposePanel}
            setShowTechComposePanel={setShowTechComposePanel}
            setSelectedTechMessageId={setSelectedTechMessageId}
            messageChannelType={messageChannelType}
            setMessageChannelType={setMessageChannelType}
            messageUnreadCounts={messageUnreadCounts}
            messageView={messageView}
            setMessageView={setMessageView}
            messageFocusFilter={messageFocusFilter}
            setMessageFocusFilter={setMessageFocusFilter}
            messageCaseFilter={messageCaseFilter}
            setMessageCaseFilter={setMessageCaseFilter}
            messageDeviceFilter={messageDeviceFilter}
            setMessageDeviceFilter={setMessageDeviceFilter}
            messageCaseOptions={messageCaseOptions}
            getCaseDisplayNumber={getCaseDisplayNumber}
            messageDeviceOptions={messageDeviceOptions}
            getDeviceDisplayLabel={getMessageDeviceDisplayLabel}
            messageSortOrder={messageSortOrder}
            setMessageSortOrder={setMessageSortOrder}
            showUnreadOnly={showUnreadOnly}
            setShowUnreadOnly={setShowUnreadOnly}
            messageShowSent={messageShowSent}
            setMessageShowSent={setMessageShowSent}
            techMessages={techMessages}
            filteredTechMessages={filteredTechMessages}
            messageVisibleCount={messageVisibleCount}
            setMessageVisibleCount={setMessageVisibleCount}
            readTechMessageIds={readTechMessageIds}
            setReadTechMessageIds={setReadTechMessageIds}
            selectedTechMessageId={selectedTechMessageId}
            getTechMessageFocusLabel={getTechMessageFocusLabel}
            getMessagePreview={getMessagePreview}
            formatDateTime={formatDateTime}
            user={user}
            markTechMessageRead={(messageId) => {
              void messageApi
                .markRead(messageId)
                .then(() => {
                  setMessageUnreadCounts(prev => ({
                    ...prev,
                    [messageChannelType]: Math.max(0, prev[messageChannelType] - 1)
                  }));
                })
                .catch(error => console.warn('Failed to mark tech message as read:', error));
            }}
            selectedTechMessage={selectedTechMessage}
            composeInternalRecipientOptions={composeInternalRecipientOptions}
            messageComposeRecipientUserIds={messageComposeRecipientUserIds}
            setMessageComposeRecipientUserIds={setMessageComposeRecipientUserIds}
            composeExternalRecipientOptions={composeExternalRecipientOptions}
            messageComposeExternalRecipientUserIds={messageComposeExternalRecipientUserIds}
            setMessageComposeExternalRecipientUserIds={setMessageComposeExternalRecipientUserIds}
            messageComposeCustomerContactsLoading={customerDetailLoadingId === messageComposeCustomerId}
            messageComposeCustomerId={messageComposeCustomerId}
            setMessageComposeCustomerId={setMessageComposeCustomerId}
            composeCustomerOptions={composeCustomerOptions}
            messageComposeFocus={messageComposeFocus}
            setMessageComposeFocus={setMessageComposeFocus}
            messageComposeCaseId={messageComposeCaseId}
            setMessageComposeCaseId={setMessageComposeCaseId}
            cases={cases}
            composeCaseOptions={composeCaseOptions}
            devices={devices}
            composeDeviceOptions={composeDeviceOptions}
            messageComposeDeviceId={messageComposeDeviceId}
            setMessageComposeDeviceId={setMessageComposeDeviceId}
            messageComposeBody={messageComposeBody}
            setMessageComposeBody={setMessageComposeBody}
            handleTechMessageAttachmentChange={handleTechMessageAttachmentChange}
            messageComposeAttachments={messageComposeAttachments}
            formatAttachmentSize={formatAttachmentSize}
            handleRemoveTechMessageAttachment={handleRemoveTechMessageAttachment}
            messageActionBusy={messageActionBusy}
            handleSendOutboundMessage={handleSendOutboundMessage}
            messageReplyBody={messageReplyBody}
            setMessageReplyBody={setMessageReplyBody}
            handleTechReplyAttachmentChange={handleTechReplyAttachmentChange}
            messageReplyAttachments={messageReplyAttachments}
            handleRemoveTechReplyAttachment={handleRemoveTechReplyAttachment}
            handleArchiveSelectedTechMessage={handleArchiveSelectedTechMessage}
            handleRestoreSelectedTechMessage={handleRestoreSelectedTechMessage}
            handleDeleteSelectedTechMessage={handleDeleteSelectedTechMessage}
            handleDownloadTechMessageAttachment={handleDownloadTechMessageAttachment}
            handleOpenTechNotificationAttachment={handleOpenTechNotificationAttachment}
            handleSendTechReply={handleSendTechReply}
            notifications={techNotifications}
            notificationsLoading={notificationsLoading}
            notificationView={notificationView}
            setNotificationView={setNotificationView}
            selectedNotificationId={selectedNotificationId}
            setSelectedNotificationId={setSelectedNotificationId}
            selectedNotification={selectedNotification}
            markNotificationRead={handleMarkNotificationRead}
            dismissNotification={handleDismissNotification}
            canCreateNotifications={canOpenInternalJobs}
            onCreateNotification={() => {
              setNotificationModalMode('unit');
              setShowNotificationModal(true);
            }}
          />
        )}
        {activeTab === 'orgNotifications' && (
          <TechDashboardOrgNotificationsTab
            styles={styles}
            units={orgNotificationUnits}
            unitsLoading={orgNotificationUnitsLoading}
            campaigns={orgNotificationCampaigns}
            campaignsLoading={orgNotificationCampaignsLoading}
            formatDateTime={formatDateTime}
            onReload={loadOrgNotificationWorkspace}
            onCreateNotification={() => {
              setNotificationModalMode('org');
              setShowNotificationModal(true);
            }}
          />
        )}
        {activeTab === 'customers' && (
          <TechDashboardCustomersTab
            addingCustomer={addingCustomer}
            armedDeviceActionById={armedDeviceActionById}
            buildLocationTree={buildLocationTree}
            cancelStagedDeviceAction={cancelStagedDeviceAction}
            canManageSerializedDevices={canManageSerializedDevices}
            canManageCustomers={canManageCustomers}
            canAccessSla={canAccessSla}
            canReadDevices={canReadDevices}
            confirmDeviceLocationChange={confirmDeviceLocationChange}
            contactForm={contactForm}
            contactFormCustomerId={contactFormCustomerId}
            contactFormRef={contactFormRef}
            customers={customers}
            customerSearch={customerSearch}
            customerSort={customerSort}
            customerStatusFilter={customerStatusFilter}
            customerQuickFilter={customerQuickFilter}
            customerQuickOptions={customerQuickOptions}
            customerLocationFilter={customerLocationFilter}
            customerLocationOptions={customerLocationOptions}
            customerPage={customerPage}
            customerPageCount={customerPageCount}
            customerPageSize={customerPageSize}
            customerPageStart={customerPageStart}
            customerPageEnd={customerPageEnd}
            deriveCustomerCity={deriveCustomerCity}
            deviceUpdateInFlight={deviceUpdateInFlight}
            editCustomer={editCustomer}
            editingCustomerId={editingCustomerId}
            expandedContactIds={expandedContactIds}
            filteredCustomers={filteredCustomers}
            paginatedCustomers={paginatedCustomers}
            flattenLocationOptions={flattenLocationOptions}
            formatLocationAddress={formatLocationAddress}
            handleCancelEditCustomer={handleCancelEditCustomer}
            handleCreateCustomer={handleCreateCustomer}
            handleDeleteCustomer={handleDeleteCustomer}
            handleSaveCustomerNotes={handleSaveCustomerNotes}
            handleSetCustomerActive={handleSetCustomerActive}
            handleDeviceActiveToggle={handleDeviceActiveToggle}
            handleDeviceLocationChange={handleDeviceLocationChange}
            handleInvitePortalUser={handleInvitePortalUser}
            handleSaveEditedCustomer={handleSaveEditedCustomer}
            handleSetPortalUserActive={handleSetPortalUserActive}
            handleSetPrimaryContact={handleSetPrimaryContact}
            handleSetPrimaryLocation={handleSetPrimaryLocation}
            handleSubmitContact={handleSubmitContact}
            handleSubmitLocation={handleSubmitLocation}
            handleToggleContactStatus={handleToggleContactStatus}
            handleToggleLocationStatus={handleToggleLocationStatus}
            locationDisplayPath={locationDisplayPath}
            locationForm={locationForm}
            locationFormCustomerId={locationFormCustomerId}
            locationFormRef={locationFormRef}
            newCustomer={newCustomer}
            openContactForm={openContactForm}
            openContactFormForAdditionalLocation={openContactFormForAdditionalLocation}
            openCustomerResourceModal={openCustomerResourceModal}
            openDevicesTabForCustomer={openDevicesTabForCustomer}
            openLocationForm={openLocationForm}
            portalContactActionKey={portalContactActionKey}
            resetContactForm={resetContactForm}
            resetLocationForm={resetLocationForm}
            resetNewCustomerForm={resetNewCustomerForm}
            savingContact={savingContact}
            savingCustomerId={savingCustomerId}
            savingLocation={savingLocation}
            setActiveTab={setActiveTab}
            setContactForm={setContactForm}
            setCustomerFilter={setCustomerFilter}
            setCustomerPage={setCustomerPage}
            setCustomerPageSize={setCustomerPageSize}
            setCustomerLocationFilter={setCustomerLocationFilter}
            setCustomerQuickFilter={setCustomerQuickFilter}
            setCustomerSearch={setCustomerSearch}
            setCustomerSort={setCustomerSort}
            setCustomerStatusFilter={setCustomerStatusFilter}
            setEditCustomer={setEditCustomer}
            setLocationForm={setLocationForm}
            setNewCustomer={setNewCustomer}
            setShowAddCustomerForm={setShowAddCustomerForm}
            showAddCustomerForm={showAddCustomerForm}
            stagedDeviceLocationById={stagedDeviceLocationById}
            startEditCustomer={startEditCustomer}
            styles={styles}
            toggleContactExpanded={toggleContactExpanded}
            deviceModels={deviceModels}
            customerDetailsById={customerDetailsById}
            customerDetailLoadingId={customerDetailLoadingId}
            customerDetailError={customerDetailError}
            loadCustomerDetail={loadCustomerDetail}
            refreshCustomerState={refreshCustomerState}
            clearCustomerFilters={clearCustomerFilters}
            hasActiveCustomerFilters={
              customerSearch.trim().length > 0 ||
              customerSort !== 'recent' ||
              customerStatusFilter !== 'all' ||
              customerLocationFilter !== 'all' ||
              customerQuickFilter !== 'all'
            }
            customerQuickViewRequest={customerQuickViewRequest}
            clearCustomerQuickViewRequest={() => setCustomerQuickViewRequest(null)}
            topPanelCollapseDefault={isNarrowDashboard}
          />
        )}

        {activeTab === 'models' && (
          <Suspense fallback={tabFallback}>
            <DeviceModels
              customerWorkspaceFilter={deviceWorkspaceCustomerFilter}
              onClearCustomerWorkspaceFilter={clearDeviceWorkspaceCustomerFilter}
              onOpenCustomer={openCustomersTabForCustomer}
              topPanelCollapseDefault={isNarrowDashboard}
            />
          </Suspense>
        )}
        {activeTab === 'sla' && canAccessSla && (
          <Suspense fallback={tabFallback}>
            <TechDashboardSlaTab
              customers={customers}
              isGlobalScopeUser={isGlobalScopeUser}
              topPanelCollapseDefault={isNarrowDashboard}
            />
          </Suspense>
        )}
        {activeTab === 'scheduling' && (
          <Suspense fallback={tabFallback}>
            <TechDashboardSchedulingTab
              cases={cases}
              canReadCases={canReadCases}
              canAccessPlanner={canAccessSchedulingPlanner}
              topPanelCollapseDefault={isNarrowDashboard}
            />
          </Suspense>
        )}
        {activeTab === 'documents' && (
          <Suspense fallback={tabFallback}>
            <Documents topPanelCollapseDefault={isNarrowDashboard} />
          </Suspense>
        )}
        {activeTab === 'inventory' && (
          <Suspense fallback={tabFallback}>
            <Inventory topPanelCollapseDefault={isNarrowDashboard} />
          </Suspense>
        )}
        {activeTab === 'knowledge' && (
          <Suspense fallback={tabFallback}>
            <DeviceExpertWorkspace topPanelCollapseDefault={isNarrowDashboard} />
          </Suspense>
        )}

        {activeTab === 'feedback' && (
          <Suspense fallback={tabFallback}>
            <DocumentationFeedbackReport
              modelId={selectedFeedbackModel}
              models={deviceModels}
              onModelChange={setSelectedFeedbackModel}
              topPanelCollapseDefault={isNarrowDashboard}
            />
          </Suspense>
        )}

        {activeTab === 'quality' && (
          <Suspense fallback={tabFallback}>
            <KnowledgeQualityWorkbench
              modelId={selectedQualityModel}
              models={deviceModels}
              onModelChange={setSelectedQualityModel}
              topPanelCollapseDefault={isNarrowDashboard}
            />
          </Suspense>
        )}

        {activeTab === 'settings' && (
          <Suspense fallback={tabFallback}>
            <Settings topPanelCollapseDefault={isNarrowDashboard} />
          </Suspense>
        )}

        {activeTab === 'users' && canManageUsers && (
          <Suspense fallback={tabFallback}>
            <AdminUsers
              topPanelCollapseDefault={isNarrowDashboard}
              onPendingLeaveApprovalCountChange={setPendingLeaveApprovalCount}
            />
          </Suspense>
        )}
      </div>

      <TechDashboardCustomerResourceModal
        customerResourceModal={customerResourceModal}
        activeModalCustomer={activeModalCustomer}
        activeModalCustomerDetailReady={activeModalCustomerDetailReady}
        activeModalCustomerDetailLoading={activeModalCustomerDetailLoading}
        activeModalCustomerDetailError={activeModalCustomerDetailError}
        styles={styles}
        closeCustomerResourceModal={closeCustomerResourceModal}
        modalCases={modalCases}
        modalDevices={modalDevices}
        modalContacts={modalContacts}
        modalLocationTree={modalLocationTree}
        modalLocationMap={modalLocationMap}
        getCaseDisplayNumber={getCaseDisplayNumber}
        locationDisplayPath={locationDisplayPath}
        formatLocationAddress={formatLocationAddress}
        flattenLocationOptions={flattenLocationOptions}
        buildLocationTree={buildLocationTree}
        setSelectedCase={setSelectedCase}
        setActiveTab={setActiveTab}
        setCustomerFilter={setCustomerFilter}
        canManageCustomers={canManageCustomers}
        openContactForm={openContactForm}
        contactFormCustomerId={contactFormCustomerId}
        contactForm={contactForm}
        setContactForm={setContactForm}
        handleSubmitContact={async () => {
          await handleSubmitContact();
        }}
        savingContact={savingContact}
        resetContactForm={resetContactForm}
        portalContactActionKey={portalContactActionKey}
        handleToggleContactStatus={handleToggleContactStatus}
        handleInvitePortalUser={handleInvitePortalUser}
        handleSetPortalUserActive={handleSetPortalUserActive}
        handleSetPrimaryContact={handleSetPrimaryContact}
        handleSetPrimaryLocation={handleSetPrimaryLocation}
        openContactFormForAdditionalLocation={openContactFormForAdditionalLocation}
        openLocationForm={openLocationForm}
        locationFormCustomerId={locationFormCustomerId}
        locationForm={locationForm}
        setLocationForm={setLocationForm}
        handleSubmitLocation={handleSubmitLocation}
        savingLocation={savingLocation}
        resetLocationForm={resetLocationForm}
        handleToggleLocationStatus={handleToggleLocationStatus}
        setCustomerResourceModal={setCustomerResourceModal}
      />
      <TechDashboardCaseDetailModal
        caseDetailModal={caseDetailModal}
        styles={styles}
        closeCaseDetailModal={closeCaseDetailModal}
        getCaseDisplayNumber={getCaseDisplayNumber}
        needsModelVerification={needsModelVerification}
        getCustomModelInfo={getCustomModelInfo}
        getPendingDeviceInfo={getPendingDeviceInfo}
        canVerifySubmittedDevices={canVerifySubmittedDevices}
        verifyModel={verifyModel}
        setEditingModel={setEditingModel}
        setShowEditModal={setShowEditModal}
        baseUrl={baseUrl}
        resolveMediaCategory={resolveMediaCategory}
        modalCaseAnalysisMeta={modalCaseAnalysisMeta}
        caseDetailLoading={caseDetailLoading}
        caseQuotesLoading={caseQuotesLoading}
        modalCaseQuotes={modalCaseQuotes}
        modalCaseServiceReports={caseDetailModal?.serviceReports || []}
        modalCaseSurveys={caseDetailModal?.caseSurveys || []}
        caseServiceReportPreviewLoadingId={caseServiceReportPreviewLoadingId}
        getQuoteDecisionBadge={getQuoteDecisionBadge}
        getQuotePartsBadge={getQuotePartsBadge}
        setCaseQuotePreview={setCaseQuotePreview}
        setCaseQuotePreviewContext={setCaseQuotePreviewContext}
        openCaseServiceReportPreview={openCaseServiceReportPreview}
        modalTimelineEntries={modalTimelineEntries}
        formatTimelineEventLabel={formatTimelineEventLabel}
      />
      <TechDashboardQuotePreviewModal
        caseQuotePreview={caseQuotePreview}
        caseQuoteTemplate={caseQuoteTemplate}
        caseQuotePreviewHeaderBlocks={caseQuotePreviewHeaderBlocks}
        styles={styles}
        onClose={() => {
          setCaseQuotePreview(null);
          setCaseQuotePreviewContext(null);
        }}
      />
      <RepairServiceReportModal
        open={Boolean(caseServiceReportPreview)}
        document={caseServiceReportPreview?.document || null}
        saving={false}
        error={null}
        mode="view"
        canSend={false}
        subtitle={
          caseServiceReportPreview
            ? `${caseServiceReportPreview.title || 'Service report'} | Template: ${caseServiceReportPreview.templateName || 'Service Report'} | Saved case service-report snapshot${
                caseServiceReportPreviewContext?.caseCode || caseServiceReportPreviewContext?.caseNumber
                  ? ` for ${caseServiceReportPreviewContext.caseCode || caseServiceReportPreviewContext.caseNumber}`
                  : ''
              }`
            : undefined
        }
        hint="This is the saved service report snapshot linked to the case."
        onClose={() => {
          setCaseServiceReportPreview(null);
          setCaseServiceReportPreviewContext(null);
        }}
        onSave={() => {}}
      />

      <AppNotificationPopups
        notifications={notificationPopupItems}
        formatDateTime={formatDateTime}
        formatAttachmentSize={formatAttachmentSize}
        onDismiss={handleDismissNotification}
        onOpenAttachment={handleOpenTechNotificationAttachment}
        styles={styles}
      />

      <TechDashboardNotificationModal
        open={showNotificationModal}
        mode={notificationModalMode}
        styles={styles}
        onClose={() => setShowNotificationModal(false)}
        onCreated={async () => {
          if (notificationModalMode === 'org') {
            await loadOrgNotificationWorkspace();
            return;
          }
          await Promise.all([loadTechNotifications(), loadNotificationPopups()]);
        }}
        deviceModels={deviceModels}
        orgUnits={orgNotificationUnits}
      />

      <TechDashboardInternalJobModal
        showInternalJobModal={showInternalJobModal}
        styles={styles}
        onClose={() => setShowInternalJobModal(false)}
        internalJobForm={internalJobForm}
        setInternalJobForm={setInternalJobForm}
        isInternalJobDragging={isInternalJobDragging}
        setIsInternalJobDragging={setIsInternalJobDragging}
        uploadInternalJobDocuments={uploadInternalJobDocuments}
        handleInternalJobFileInput={handleInternalJobFileInput}
        internalJobUploadError={internalJobUploadError}
        internalJobUploads={internalJobUploads}
        setInternalJobUploads={setInternalJobUploads}
        deviceModels={deviceModels}
        devices={devices}
        customers={customers}
        assigneeOptions={internalJobAssigneeOptions}
        taskGroupOptions={INTERNAL_JOB_TASK_GROUP_OPTIONS}
        internalJobSerialAudit={internalJobSerialAudit}
        internalJobError={internalJobError}
        onDismissInternalJobError={() => setInternalJobError(null)}
        handleCheckInternalJobSerials={handleCheckInternalJobSerials}
        handleGenerateInternalJob={handleGenerateInternalJob}
        onCreateUniqueDevice={handleCreateInternalJobUniqueDevice}
        internalJobSubmitting={internalJobSubmitting}
      />

      <TechDashboardUtilityModals
        styles={styles}
        showMetricsConfig={showMetricsConfig}
        metricsWorkspaceTab={metricsWorkspaceTab}
        metricsWorkspaceLabel={currentMetricsWorkspace?.label || 'Overview'}
        metricsWorkspaceIncludeBlocks={metricsWorkspaceIncludeBlocks}
        metricsWorkspaceDraftIncludes={metricsCurrentWorkspaceDraftIncludes}
        setMetricsWorkspaceIncludesInDraft={setMetricsWorkspaceIncludesInDraft}
        cancelMetricsDashboardEdit={cancelMetricsDashboardEdit}
        saveMetricsDashboard={saveMetricsDashboard}
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        editingModel={editingModel}
        setEditingModel={setEditingModel}
        devices={devices}
        verifyModel={verifyModel}
        portalInvitePreview={portalInvitePreview}
        setPortalInvitePreview={setPortalInvitePreview}
        portalInviteCopied={portalInviteCopied}
        copyPortalInviteValue={copyPortalInviteValue}
      />
    </div>
  );
}





